/* ============================================================================
   ApnaCircle — the local Indian community app for the U.S. (React)
   ----------------------------------------------------------------------------
   Drop-in page component. No router, CSS framework or icon library required.
   Files (keep in the same folder):
     ApnaCircleApp.jsx          ← this file
     apnaData.js                translations (Hindi + English), categories, states, airports, icons
     legal.js                   Privacy Policy + Terms of Use (Hindi + English)
     realtime.js                chat over WebSocket (/realtime/) with HTTP fallback
     sounds.js                  notification sounds + preferences
     zipData.js                 compact US ZIP → city/state/lat/lng table
     apnaAssets.js              logo images (inlined)

   Usage:
     import ApnaCircleMarketplace from "./components/ApnaCircleApp";
     <ApnaCircleMarketplace />

   Languages: English (default) and Hindi; the header toggle switches them.
   Data: listings, chats, reviews and profiles come from the Django API only.
   localStorage keeps device preferences (language, theme, area, saved items,
   sound settings) and the unfinished post draft — never listings or messages.
   ========================================================================== */
import React, { useState, useEffect, useRef, useReducer, useMemo, useCallback } from "react";
import { I18N, CATS, CAT, STATES, STATE, ICON, H, REGIONS, AIRPORTS } from "./apnaData";
import { LEGAL, EFFECTIVE, SUPPORT_EMAIL } from "./legal";
import { LANG_OPTS, FESTIVALS, VISAS, DIETS, NEEDS, SCAMS, REPORT_LINKS, METROS, PROMISES } from "./needs";
import { createChat } from "./realtime";
import * as Sound from "./sounds";
import ZIPDATA from "./zipData";
import { EMBLEM_LIGHT, EMBLEM_DARK, LOGO_FULL } from "./apnaAssets";

/* ─── utilities ──────────────────────────────────────────────────────────── */
const hasWindow = typeof window !== "undefined";
const LS = {
  get(k, d) { try { const v = window.localStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch (e) { return d; } },
  set(k, v) { try { window.localStorage.setItem(k, JSON.stringify(v)); return true; } catch (e) { return false; } },
  del(k) { try { window.localStorage.removeItem(k); } catch (e) {} },
};
const PHOTO_ORIGIN = (() => {
  if (process.env.NODE_ENV === "development") return "";   // photos also go through the dev proxy
  const raw = String(process.env.REACT_APP_API_URL || "").trim();
  if (!raw || /^(https?:\/\/)?(localhost|127\.0\.0\.1):8000/i.test(raw) || raw.startsWith("/")) return "";
  try { return new URL(/^https?:\/\//i.test(raw) ? raw : "https://" + raw).origin; } catch (e) { return ""; }
})();
/* photos: data URLs while editing, cached /api/photos/ URLs from the server */
const safeImg = (u) => {
  if (typeof u !== "string") return null;
  if (/^data:image\/(png|jpe?g|webp|gif);base64,[A-Za-z0-9+/=]+$/.test(u)) return u;
  if (/^\/api\/photos\/[A-Za-z0-9_-]+\/(\d+|thumb)\/\?v=\d+$/.test(u)) return PHOTO_ORIGIN + u;
  if (/^\/api\/rooms\/photos\/\d+\/\d+\/\?v=\d+$/.test(u)) return PHOTO_ORIGIN + u;
  if (/^\/api\/avatars\/\d+\/\?v=\d+$/.test(u)) return PHOTO_ORIGIN + u;
  if (/^https:\/\/[A-Za-z0-9.-]+(:\d+)?\/api\/photos\/[A-Za-z0-9_-]+\/(\d+|thumb)\/\?v=\d+$/.test(u)) return u;
  return null;
};
const clampStr = (s, n) => String(s ?? "").slice(0, n);
const tr = (lang, k, vars) => {
  let s = I18N[lang][k] ?? INSTALL_TXT[lang][k] ?? UI_TXT[lang][k] ?? I18N.th[k] ?? INSTALL_TXT.th[k] ?? UI_TXT.th[k] ?? k;
  if (vars && typeof s === "string") for (const [a, b] of Object.entries(vars)) s = s.split("{" + a + "}").join(b);
  return s;
};

/* ─── ZIP database ───────────────────────────────────────────────────────── */
let ZIPS = null;
function zipDb() {
  if (ZIPS) return ZIPS;
  const z = ZIPDATA.z, p = (s) => parseInt(s, 36), m = new Map();
  let i = 0, prev = 0;
  while (i < z.length) {
    let dz;
    if (z[i] === "_") { dz = p(z.substr(i + 1, 3)); i += 4; } else { dz = p(z[i]); i += 1; }
    const zip = prev + dz; prev = zip;
    m.set(String(zip).padStart(5, "0"), [(p(z.substr(i, 3)) + 1000) / 100, (p(z.substr(i + 3, 3)) - 18000) / 100, p(z.substr(i + 6, 3))]);
    i += 9;
  }
  ZIPS = { m, cities: ZIPDATA.c.split("|"), st: (ci) => ZIPDATA.st[p(ZIPDATA.s.substr(ci * 2, 2))] };
  return ZIPS;
}
export function zipInfo(zip) {
  if (!/^\d{5}$/.test(zip || "")) return null;
  const d = zipDb(), r = d.m.get(zip);
  return r ? { zip, lat: r[0], lng: r[1], city: d.cities[r[2]], state: d.st(r[2]) } : null;
}
function nearestZip(lat, lng) {
  const d = zipDb(); let best = null, bd = 1e9;
  for (const [zip, r] of d.m) { const dd = (r[0] - lat) ** 2 + ((r[1] - lng) * Math.cos((lat * Math.PI) / 180)) ** 2; if (dd < bd) { bd = dd; best = zip; } }
  return best ? zipInfo(best) : null;
}
function miles(a, b, c, d) {
  const R = 3958.8, r = (x) => (x * Math.PI) / 180, dl = r(c - a), dg = r(d - b);
  const h = Math.sin(dl / 2) ** 2 + Math.cos(r(a)) * Math.cos(r(c)) * Math.sin(dg / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/* ─── listings ───────────────────────────────────────────────────────────── */
function normListing(id, d) {
  if (!d || typeof d !== "object" || !CAT[d.cat] || d.cat === "all") return null;
  const z = zipInfo(d.zip) || {};
  return {
    id: String(id), cat: d.cat, sub: CAT[d.cat].subs.some((s) => s[0] === d.sub) ? d.sub : CAT[d.cat].subs[0][0],
    title: clampStr(d.title, 90), desc: clampStr(d.desc, 3000),
    price: typeof d.price === "number" && isFinite(d.price) ? d.price : null,
    unit: ["fixed", "month", "hour", "free", "ask", "tier", "kg"].includes(d.unit) ? d.unit : "fixed",
    meta: d.meta && typeof d.meta === "object" && !Array.isArray(d.meta) ? d.meta : null,
    isExample: !!d.isExample, metro: clampStr(d.metro, 12),
    sellerSocial: Array.isArray(d.sellerSocial) ? d.sellerSocial.slice(0, 3) : [], sellerFounding: clampStr(d.sellerFounding, 12),
    trip: d.cat === "carry" && d.trip && typeof d.trip === "object" ? {
      dir: d.trip.dir === "in_us" ? "in_us" : "us_in", state: clampStr(d.trip.state, 2), city: clampStr(d.trip.city, 60),
      province: clampStr(d.trip.province, 40), depart: clampStr(d.trip.depart, 10), ret: d.trip.return ? clampStr(d.trip.return, 10) : "",
      kg: Number(d.trip.kg) || 0, accepts: Array.isArray(d.trip.accepts) ? d.trip.accepts.slice(0, 7) : [], airline: clampStr(d.trip.airline, 40),
      airport: clampStr(d.trip.airport, 3), companion: !!d.trip.companion, flight: clampStr(d.trip.flight, 12),
      langs: Array.isArray(d.trip.langs) ? d.trip.langs.slice(0, 6) : [],
    } : null,
    neg: !!d.neg, cond: ["new", "likenew", "good", "fair"].includes(d.cond) ? d.cond : null,
    zip: z.zip || "", city: z.city || "", state: z.state || "", lat: z.lat, lng: z.lng,
    photos: Array.isArray(d.photos) ? d.photos.map(safeImg).filter(Boolean).slice(0, 6) : [],
    sellerName: clampStr(d.sellerName, 40) || "—", line: clampStr(d.line, 40), phone: clampStr(d.phone, 24),
    ownerId: String(d.ownerId || ""), status: d.status === "sold" ? "sold" : "active",
    sellerAvatar: safeImg(d.sellerAvatar), sellerLogo: !!d.sellerLogo, sellerCity: clampStr(d.sellerCity, 60),
    sellerVerified: !!d.sellerVerified, sellerSince: Number(d.sellerSince) || null,
    createdAt: Number(d.createdAt) || Date.now(), partial: !!d.partial,
  };
}

/* ─── API ────────────────────────────────────────────────────────────────── */
function normalizeApiBase(raw) {
  let base = String(raw || "").trim();
  // npm start: always same-origin. src/setupProxy.js forwards /api to the backend
  // named in REACT_APP_API_URL (or DEV_BACKEND), so the browser never makes a
  // cross-site call (no CORS errors, and the login cookie works).
  if (process.env.NODE_ENV === "development") return "/api";
  if (!base) return "/api";
  // Local development: stay same-origin so src/setupProxy.js forwards to Django
  // and the session cookie works for both HTTP and the websocket.
  if (/^(https?:\/\/)?(localhost|127\.0\.0\.1):8000(?:\/api)?\/?$/i.test(base)) return "/api";
  if (base.startsWith("/")) return base.replace(/\/$/, "") || "/api";
  if (!/^https?:\/\//i.test(base)) base = `https://${base}`;
  base = base.replace(/\/$/, "");
  if (!/\/api$/i.test(base)) base += "/api";
  return base;
}

const API_BASE = normalizeApiBase(process.env.REACT_APP_API_URL);
/* Django's CSRF protection: echo the csrftoken cookie (set by /auth/me/) in a header. */
const csrfToken = () => (typeof document !== "undefined" && (document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/) || [])[1]) || "";
async function api(path, options = {}, retried = false) {
  const method = (options.method || "GET").toUpperCase();
  const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) headers["X-CSRFToken"] = csrfToken();
  let res;
  try {
    res = await fetch(API_BASE + path, { credentials: "include", ...options, headers });
  } catch (cause) {
    const err = new Error("network_error"); err.code = "network_error"; err.cause = cause; throw err;
  }
  let data = null;
  try { data = await res.json(); } catch (e) { /* empty/non-JSON response */ }
  if (!res.ok) {
    // expired or missing token: fetch a fresh one once, then retry
    if (data && data.error === "csrf_failed" && !retried) {
      try { await fetch(API_BASE + "/auth/me/", { credentials: "include" }); } catch (e) { /* offline */ }
      return api(path, options, true);
    }
    const err = new Error((data && data.error) || `API ${res.status}`);
    err.status = res.status; err.data = data;
    if (data && data.error === "verify_required" && typeof window !== "undefined") window.dispatchEvent(new CustomEvent("ac-verify", { detail: data.missing }));
    throw err;
  }
  return data;
}
/* Production only: send uncaught browser errors to the server log / Sentry (max 5 per page load). */
if (typeof window !== "undefined" && process.env.NODE_ENV === "production") {
  let sent = 0;
  const report = (message, stack) => {
    if (sent >= 5) return; sent += 1;
    fetch(API_BASE + "/client-errors/", { method: "POST", credentials: "include", keepalive: true,
      headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken() },
      body: JSON.stringify({ message: String(message).slice(0, 300), stack: String(stack || "").slice(0, 1500), url: window.location.hash }) }).catch(() => {});
  };
  window.addEventListener("error", (e) => report(e.message, e.error && e.error.stack));
  window.addEventListener("unhandledrejection", (e) => report(e.reason && (e.reason.message || e.reason), e.reason && e.reason.stack));
}

/* ─── realtime chat (see realtime.js) ───────────────────────────────────── */
const Chat = hasWindow ? createChat({ api, apiBase: API_BASE }) : null;

/* ─── data store: the Django API is the only source of truth ────────────── */
const OLD_KEYS = ["ac_listings", "ac_threads", "ac_msgs", "ac_seen", "ac_reviews", "ac_profile", "ac_reports"];
const Store = {
  ready: false, me: null, user: null, remote: [], mine: [], listeners: new Set(),
  syncing: false, syncingChanges: false, loadingMore: false, tripsLoaded: false, tripsLoading: false, tripsError: false,
  changeCursor: 0, nextBefore: null, hasMoreListings: true, initialSynced: false, lastRefreshAt: 0, autoRefreshStop: null,
  detail: {},            // id -> "loading" | "missing"
  onNewListing: null,    // (listing) => void, used for "new post" sounds
  init() {
    if (this.ready || !hasWindow) return;
    OLD_KEYS.forEach((k) => LS.del(k)); // older builds cached listings/chats on the device
    this.ready = true;
    if (Chat) Chat.subscribe(() => this.emit());
    this.sync();
  },
  setUser(user) {
    this.user = user || null;
    this.me = user && user.userKey ? String(user.userKey) : null;
    this.mine = [];
    if (Chat) Chat.setUser(this.me);
    if (this.me) this.syncMine();
    this.emit();
  },
  subscribe(f) { this.listeners.add(f); return () => this.listeners.delete(f); },
  emit() { this.listeners.forEach((f) => { try { f(); } catch (e) { console.error(e); } }); },
  _merge(items, notify = false) {
    const byId = new Map(this.remote.map((x) => [String(x.id), x]));
    for (const raw of items || []) {
      const x = normListing(raw.id, raw);
      if (!x) continue;
      const prev = byId.get(x.id);
      // feed rows carry one photo; keep the full gallery if detail was loaded
      if (prev && prev.photos.length > x.photos.length && !raw.full) x.photos = prev.photos;
      if (!prev && notify && this.onNewListing && x.ownerId !== this.me && x.status === "active") this.onNewListing(x);
      byId.set(x.id, x);
    }
    this.remote = [...byId.values()];
    const mineIdx = new Map(this.mine.map((x, i) => [x.id, i]));
    for (const x of this.remote) if (mineIdx.has(x.id)) this.mine[mineIdx.get(x.id)] = x;
  },
  async sync() {
    if (this.syncing) return;
    this.syncing = true;
    try {
      const data = await api("/listings/?limit=60" + (this.metro ? "&metro=" + this.metro : ""));
      this.remote = [];
      this._merge(data.listings || []);
      this.changeCursor = Number(data.changeCursor) || 0;
      this.initialSynced = true;
      this.nextBefore = data.nextBefore || null;
      this.hasMoreListings = !!data.hasMore;
      this.lastRefreshAt = Date.now();
      this.error = false;
    } catch (e) { this.error = true; }
    finally { this.syncing = false; this.emit(); }
  },
  async syncMine() {
    if (!this.me) return;
    try {
      const data = await api("/listings/?mine=1&limit=100");
      this.mine = (data.listings || []).map((d) => normListing(d.id, d)).filter(Boolean);
      this.emit();
    } catch (e) { /* shown as empty until next try */ }
  },
  async loadMore() {
    if (this.loadingMore || !this.hasMoreListings || !this.nextBefore) return;
    this.loadingMore = true; this.emit();
    try {
      const data = await api(`/listings/?limit=60&before=${encodeURIComponent(this.nextBefore)}${this.metro ? "&metro=" + this.metro : ""}`);
      this._merge(data.listings || []);
      this.nextBefore = data.nextBefore || null;
      this.hasMoreListings = !!data.hasMore;
    } catch (e) { /* keep current page */ }
    finally { this.loadingMore = false; this.emit(); }
  },
  async syncChanges() {
    if (this.syncingChanges || !this.ready || !this.initialSynced) return;
    this.syncingChanges = true;
    try {
      let more = true, loops = 0;
      while (more && loops < 3) {
        const data = await api(`/listings/changes/?since=${encodeURIComponent(this.changeCursor)}&limit=100`);
        for (const ch of data.changes || []) {
          if (ch.action === "delete") {
            const id = String(ch.id || "");
            this.remote = this.remote.filter((x) => x.id !== id);
            this.mine = this.mine.filter((x) => x.id !== id);
          } else if (ch.listing) {
            this._merge([ch.listing], true);
          }
        }
        this.changeCursor = Number(data.cursor) || this.changeCursor;
        more = !!data.hasMore; loops += 1;
      }
      this.lastRefreshAt = Date.now();
      this.emit();
    } catch (e) { /* retry on the next lightweight poll */ }
    finally { this.syncingChanges = false; }
  },
  startAutoRefresh() {
    if (!hasWindow || this.autoRefreshStop) return this.autoRefreshStop || (() => {});
    let timer = 0, stopped = false;
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const baseDelay = conn && (conn.saveData || /2g/.test(conn.effectiveType || "")) ? 60000 : 25000;
    const clear = () => { if (timer) { window.clearTimeout(timer); timer = 0; } };
    const schedule = () => {
      clear();
      if (stopped || document.visibilityState !== "visible") return;
      const jitter = Math.floor(Math.random() * 5000);
      timer = window.setTimeout(async () => { await this.syncChanges(); schedule(); }, baseDelay + jitter);
    };
    const refreshSoon = () => {
      if (stopped || document.visibilityState !== "visible") return;
      if (!this.initialSynced) this.sync();
      else if (Date.now() - this.lastRefreshAt > 8000) this.syncChanges();
      schedule();
    };
    const onVisibility = () => (document.visibilityState === "visible" ? refreshSoon() : clear());
    window.addEventListener("focus", refreshSoon);
    window.addEventListener("online", refreshSoon);
    document.addEventListener("visibilitychange", onVisibility);
    schedule();
    this.autoRefreshStop = () => {
      stopped = true; clear();
      window.removeEventListener("focus", refreshSoon);
      window.removeEventListener("online", refreshSoon);
      document.removeEventListener("visibilitychange", onVisibility);
      this.autoRefreshStop = null;
    };
    return this.autoRefreshStop;
  },
  all() { return this.remote; },
  /* luggage-space trips (upcoming, soonest first) */
  async loadTrips() {
    if (this.tripsLoading) return;
    this.tripsLoading = true; this.emit();
    try {
      const data = await api("/listings/?cat=carry");
      this._merge(data.listings || []);
      this.tripsLoaded = true; this.tripsError = false;
    } catch (e) { this.tripsError = true; }
    this.tripsLoading = false; this.emit();
  },
  trips() {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const cut = today.getTime() - 864e5;
    return this.remote.filter((x) => x.cat === "carry" && x.trip && Date.parse(x.trip.depart + "T00:00:00") >= cut)
      .sort((a, b) => a.trip.depart.localeCompare(b.trip.depart) || b.createdAt - a.createdAt);
  },
  get(id) { return this.remote.find((x) => x.id === id) || this.mine.find((x) => x.id === id) || null; },
  async fetchDetail(id) {
    if (!id) return;
    if (!this.get(id)) { this.detail[id] = "loading"; this.emit(); }
    try {
      const data = await api(`/listings/${encodeURIComponent(id)}/`);
      this._merge([{ ...data, full: true }]);
      delete this.detail[id];
    } catch (e) {
      if (e.status === 404) { this.remote = this.remote.filter((x) => x.id !== id); this.detail[id] = "missing"; }
      else if (!this.get(id)) this.detail[id] = "error";
    }
    this.emit();
  },
  /* listing snapshot for a chat thread, when the listing isn't in the feed */
  listingFor(th) {
    const x = this.get(th.listingId);
    if (x) return x;
    if (!th.listing) return null;
    return normListing(th.listing.id, { ...th.listing, ownerId: th.sellerId, sellerName: th.sellerName, partial: true });
  },
  async addListing(data) {
    const saved = await api("/listings/", { method: "POST", body: JSON.stringify(data) });
    this._merge([{ ...saved, full: true }]);
    const x = this.get(saved.id);
    if (x) this.mine = [x, ...this.mine.filter((y) => y.id !== x.id)];
    this.emit();
    return saved.id;
  },
  async updateListing(id, patch) {
    const saved = await api(`/listings/${encodeURIComponent(id)}/`, { method: "PATCH", body: JSON.stringify(patch) });
    this._merge([{ ...saved, full: true }]);
    this.emit();
  },
  async deleteListing(id) {
    await api(`/listings/${encodeURIComponent(id)}/`, { method: "DELETE" });
    this.remote = this.remote.filter((x) => x.id !== id);
    this.mine = this.mine.filter((x) => x.id !== id);
    this.emit();
  },
  async report(listing, reason) {
    await api("/reports/", { method: "POST", body: JSON.stringify({ listingId: listing.id, reason }) });
  },
  /* chat (delegates to the realtime client) */
  threads() { return Chat ? Chat.threadList() : []; },
  unread() { return Chat ? Chat.unreadThreads() : 0; },
  dealtWith(key) { return Chat ? Chat.dealtWith(key) : false; },
  /* public profile: stored on the server with the account */
  profile() {
    const p = (this.user && this.user.profile) || {};
    return { avatar: safeImg(p.avatar), useLogo: !!p.useLogo, zip: p.zip || "", city: p.city || "", state: p.state || "", since: Number(p.since) || null };
  },
  async saveProfile(patch) {
    const d = await api("/auth/profile/", { method: "PATCH", body: JSON.stringify(patch) });
    this.user = d.user; if (this.onUser) this.onUser(d.user);
    this.emit();
    return this.profile();
  },
  /* reviews: one per person, allowed after a real conversation */
  reviewsCache: {},
  async syncReviews(key, force) {
    if (!key || (!force && this.reviewsCache[key] !== undefined)) return;
    if (this.reviewsCache[key] === undefined) this.reviewsCache[key] = [];
    try { const d = await api(`/reviews/?user=${encodeURIComponent(key)}`); this.reviewsCache[key] = d.reviews || []; this.emit(); } catch (e) {}
  },
  reviewsFor(key) {
    if (!key) return [];
    if (this.reviewsCache[key] === undefined) this.syncReviews(key);
    return this.reviewsCache[key] || [];
  },
  rating(key) {
    const rs = this.reviewsFor(key).filter((r) => Number(r.rating) >= 1);
    if (!rs.length) return { count: 0, avg: 0 };
    return { count: rs.length, avg: rs.reduce((a, r) => a + Number(r.rating), 0) / rs.length };
  },
  myReviewFor(key) { return this.reviewsFor(key).find((r) => r.by === this.me) || null; },
  async addReview(r) {
    const saved = await api("/reviews/", { method: "POST", body: JSON.stringify(r) });
    await this.syncReviews(r.about, true);
    return saved;
  },
};
/* ─── install as an app (PWA) — Chrome/Edge/Brave/Android prompt + Safari/iPhone guides ─ */
const INSTALL_TXT = {
  hi: {
    install_app: "ऐप इंस्टॉल करें", install_benefit: "तेज़ी से खुलता है, पूरी स्क्रीन पर असली ऐप जैसा",
    install_title: "ApnaCircle को अपने फ़ोन पर इंस्टॉल करें", install_done: "इंस्टॉल हो गया। अब होम स्क्रीन से ApnaCircle खोलें",
    install_later: "बाद में", install_copy: "लिंक कॉपी करें", install_copied: "लिंक कॉपी हो गया, अब ब्राउज़र में पेस्ट करें",
    install_hint: "अगर इंस्टॉल का विकल्प नहीं दिख रहा, तो ऐप शायद पहले से इंस्टॉल है। होम स्क्रीन या ऐप लिस्ट में “ApnaCircle” खोजें",
    ins_ios_safari: ["नीचे की पट्टी में शेयर बटन (ऊपर तीर वाला चौकोर) दबाएँ। iOS 26 में पहले ⋯ दबाएँ, फिर शेयर चुनें", "नीचे स्क्रॉल करके “Add to Home Screen” दबाएँ", "ऊपर दाईं ओर “Add” दबाएँ। ApnaCircle का आइकन होम स्क्रीन पर आ जाएगा"],
    ins_ios_chrome: ["Chrome की एड्रेस बार में शेयर बटन दबाएँ", "“Add to Home Screen” चुनें", "“Add” दबाएँ (iOS 16.4 या नया चाहिए; यह विकल्प न दिखे तो Safari में खोलें)"],
    ins_ios_other: ["नीचे दिया लिंक कॉपी करें और Safari में खोलें", "Safari में शेयर दबाएँ और “Add to Home Screen” चुनें", "“Add” दबाएँ"],
    ins_inapp: ["आप ApnaCircle को WhatsApp, Instagram या Facebook जैसे किसी ऐप के अंदर खोल रहे हैं, जहाँ से इंस्टॉल नहीं हो सकता", "⋯ या ⋮ दबाकर “ब्राउज़र में खोलें” (Safari या Chrome) चुनें, या नीचे दिया लिंक कॉपी करें", "ब्राउज़र में खुलने के बाद फिर से “ऐप इंस्टॉल करें” दबाएँ"],
    ins_android_chrome: ["Chrome में ऊपर दाईं ओर ⋮ दबाएँ", "“Install app” या “Add to Home screen” दबाएँ", "“Install” दबाएँ"],
    ins_android_firefox: ["Firefox में ⋮ दबाएँ", "“Install” या “Add to Home screen” दबाएँ", "जोड़ने की पुष्टि करें"],
    ins_mac_safari: ["स्क्रीन के ऊपर मेन्यू बार में File पर क्लिक करें, या Safari टूलबार में शेयर बटन", "“Add to Dock” चुनें", "“Add” पर क्लिक करें (macOS Sonoma / Safari 17 या नया चाहिए)"],
    ins_desktop_chrome: ["एड्रेस बार के दाईं ओर इंस्टॉल आइकन (तीर वाली स्क्रीन) पर क्लिक करें", "या ⋮ दबाकर “Cast, save and share” → “Install page as app” चुनें (Edge: ⋯ → Apps → Install this site as an app)", "“Install” पर क्लिक करें"],
    ins_desktop_firefox: ["कंप्यूटर पर Firefox अभी वेब ऐप इंस्टॉल नहीं कर सकता", "लिंक कॉपी करें और Chrome, Edge या Safari में खोलें", "फिर से “ऐप इंस्टॉल करें” दबाएँ"],
  },
  en: {
    install_app: "Install app", install_benefit: "Opens faster and runs full screen like a real app",
    install_title: "Install ApnaCircle on this device", install_done: "Installed. Open ApnaCircle from your home screen.",
    install_later: "Not now", install_copy: "Copy link", install_copied: "Link copied. Paste it into your browser.",
    install_hint: "Don't see an install option? The app may already be installed. Look for “ApnaCircle” on your home screen or app list.",
    ins_ios_safari: ["Tap the Share button (square with an up arrow) in the bottom bar. On iOS 26, tap ⋯ first, then Share.", "Scroll down and tap “Add to Home Screen”.", "Tap “Add” in the top right. ApnaCircle now sits on your home screen."],
    ins_ios_chrome: ["Tap the Share button in Chrome's address bar.", "Choose “Add to Home Screen”.", "Tap “Add” (needs iOS 16.4 or later; otherwise open this page in Safari)."],
    ins_ios_other: ["Copy the link below and open it in Safari.", "In Safari, tap Share, then “Add to Home Screen”.", "Tap “Add”."],
    ins_inapp: ["You're viewing ApnaCircle inside an app like LINE or Facebook, which can't install it.", "Tap ⋯ or ⋮ and choose “Open in browser” (Safari or Chrome), or copy the link below.", "In the browser, tap “Install app” again."],
    ins_android_chrome: ["Tap ⋮ in the top right of Chrome.", "Tap “Install app” or “Add to Home screen”.", "Tap “Install”."],
    ins_android_firefox: ["Tap ⋮ in Firefox.", "Tap “Install” or “Add to Home screen”.", "Confirm."],
    ins_mac_safari: ["Click File in the menu bar, or the Share button in Safari's toolbar.", "Choose “Add to Dock”.", "Click “Add”. Needs macOS Sonoma (Safari 17) or later."],
    ins_desktop_chrome: ["Click the install icon (screen with an arrow) at the right end of the address bar.", "Or click ⋮ → “Cast, save, and share” → “Install page as app” (Edge: ⋯ → Apps → Install this site as an app).", "Click “Install”."],
    ins_desktop_firefox: ["Firefox on desktop can't install web apps yet.", "Copy the link and open it in Chrome, Edge or Safari.", "Then click “Install app” again."],
  },
};
const ICONX = {
  dots: "M5 12h.01 M12 12h.01 M19 12h.01",
  image: "M3 5h18v14H3z M3 16l5-5 4 4 3-3 6 6 M8.5 9.5h.01",
  lock: "M6 11h12v10H6z M8 11V7a4 4 0 0 1 8 0v4",
  mail: "M3 6h18v12H3z M3 7l9 7 9-7",
  expand: "M4 9V4h5 M20 9V4h-5 M4 15v5h5 M20 15v5h-5",
  download: "M12 3v12 M7 10l5 5 5-5 M4 19h16",
  copy: "M8 8h11v13H8z M5 16V3h11",
  phonedev: "M8 2h8a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z M11 18h2",
};
function detectPlatform() {
  if (!hasWindow) return { kind: "desktop_chrome", standalone: false };
  const ua = navigator.userAgent || "";
  const ios = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const android = /Android/i.test(ua);
  const inApp = /\bLine\/|FBAN|FBAV|FB_IAB|Instagram|Messenger|MicroMessenger|TikTok|Twitter|Snapchat/i.test(ua);
  const firefox = /Firefox|FxiOS/.test(ua);
  const safari = /Safari/.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|EdgA|Edg\/|OPR|OPiOS|SamsungBrowser|YaBrowser|GSA\//.test(ua);
  const mm = (q) => (window.matchMedia ? window.matchMedia(q).matches : false);
  const standalone = window.navigator.standalone === true || mm("(display-mode: standalone)") || mm("(display-mode: fullscreen)") || mm("(display-mode: minimal-ui)") || mm("(display-mode: window-controls-overlay)") || String(document.referrer).startsWith("android-app://");
  let kind;
  if (inApp) kind = "inapp";
  else if (ios) kind = safari ? "ios_safari" : /CriOS|EdgiOS/.test(ua) ? "ios_chrome" : "ios_other";
  else if (android) kind = firefox ? "android_firefox" : "android_chrome";
  else if (/Macintosh/.test(ua) && safari) kind = "mac_safari";
  else if (firefox) kind = "desktop_firefox";
  else kind = "desktop_chrome";
  return { kind, standalone };
}
/* Catch Chrome's install event even if it fires before React mounts */
if (hasWindow && !window.__ccInstallHooked) {
  window.__ccInstallHooked = true;
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); window.__ccInstallEvt = e; window.dispatchEvent(new Event("cc-installable")); });
}


/* ─── profile, trust & reviews text ─────────────────────────────────────── */
const UI_TXT = {
  hi: {
    profile: "प्रोफ़ाइल",
    edit_photo: "प्रोफ़ाइल फ़ोटो बदलें",
    upload_photo: "फ़ोटो अपलोड करें",
    use_logo: "ApnaCircle लोगो लगाएँ",
    use_initials: "नाम के अक्षर दिखाएँ",
    remove_photo: "फ़ोटो हटाएँ",
    profile_saved: "प्रोफ़ाइल सेव हो गई",
    your_city: "आपका शहर",
    set_city: "शहर सेट करें",
    city_hint: "प्रोफ़ाइल पर शहर और राज्य दिखाने के लिए ZIP डालें",
    member_since: "{d} से सदस्य",
    trust: "भरोसा",
    verified_phone: "फ़ोन सत्यापित",
    verified_email: "ईमेल सत्यापित",
    not_verified: "फ़ोन सत्यापित नहीं",
    reviews: "रिव्यू",
    review_one: "रिव्यू",
    no_reviews: "अभी कोई रिव्यू नहीं",
    new_member: "नए सदस्य",
    see_reviews: "सभी रिव्यू देखें",
    write_review: "रिव्यू लिखें",
    rate_seller: "बेचने वाले को रेटिंग दें",
    rate_buyer: "खरीदार को रेटिंग दें",
    your_rating: "आपकी रेटिंग",
    review_ph: "अनुभव कैसा रहा? क्या वे समय पर आए, क्या सामान बताए अनुसार था?",
    review_send: "रिव्यू पोस्ट करें",
    review_thanks: "रिव्यू के लिए धन्यवाद",
    review_update: "रिव्यू अपडेट हो गया",
    review_need_deal: "एक-दूसरे को मैसेज करने के बाद ही रिव्यू दे सकते हैं",
    review_self: "आप खुद को रिव्यू नहीं दे सकते",
    review_fail: "रिव्यू पोस्ट नहीं हो सका। फिर से कोशिश करें।",
    review_as_buyer: "खरीदार के रूप में",
    review_as_seller: "बेचने वाले के रूप में",
    reviews_about: "{n} के रिव्यू",
    deals_done: "{n} सौदे पूरे",
    listings_count: "{n} लिस्टिंग",
    photo_of: "फ़ोटो {a} / {b}",
    login_to_review: "रिव्यू देने के लिए लॉग इन करें",
    trust_tip: "मिलने से पहले स्टार और सत्यापन ज़रूर देखें",
    view_profile: "प्रोफ़ाइल देखें",
    nav_carry: "सामान भेजें",
    per_kg: " / किलो",
    per_kg_btn: "प्रति किलो",
    kg: "किलो",
    kg_n: "{n} किलो",
    carry_h: "सामान भेजें · भारत ⇄ अमेरिका",
    carry_sub: "जल्द सफ़र कर रहे हैं और बैग में जगह बची है? अपनी तारीखें पोस्ट करें। घर कुछ भेजना है? राज्य और तारीख से खोजें, फिर यात्री को मैसेज करें।",
    carry_post: "अपनी यात्रा पोस्ट करें",
    carry_how: ["दिशा और अपने पास का राज्य चुनें", "तारीख, बचे किलो और प्रति किलो कीमत देखें", "मैसेज में बात तय करें, उड़ान से पहले सामान दें"],
    dir_all: "सभी",
    dir_us_in: "अमेरिका → भारत",
    dir_in_us: "भारत → अमेरिका",
    zones: "ज़ोन",
    zone_all: "सभी राज्य",
    carry_search_ph: "शहर, राज्य, एयरपोर्ट या नाम खोजें…",
    trips_n: "{n} आने वाली यात्राएँ",
    route: "रूट",
    no_trips: "इस दायरे में अभी कोई यात्रा नहीं",
    no_trips_b: "जल्द सफ़र कर रहे हैं? अपनी यात्रा पोस्ट करें ताकि आसपास के लोग पूछ सकें।",
    depart: "रवानगी",
    return_d: "वापसी",
    one_way: "सिर्फ़ एक तरफ़",
    space: "जगह",
    price_kg: "कीमत",
    traveler: "यात्री",
    contact: "संपर्क",
    trip_full: "भर गया",
    today: "आज उड़ान",
    tomorrow: "कल उड़ान",
    days_left: "{n} दिन बाद",
    verified_short: "फ़ोन सत्यापित",
    carry_new_h: "बैग में जगह पोस्ट करें",
    f_dir: "दिशा",
    f_us_zip: "अमेरिका का ZIP कोड",
    f_us_zip_h: "5 अंकों का ZIP, इससे आपका शहर और राज्य दिखेगा",
    f_province: "भारत में शहर",
    f_depart: "रवानगी की तारीख",
    f_return: "वापसी की तारीख",
    optional: "(वैकल्पिक)",
    f_kg: "कितने किलो की जगह",
    f_price_kg: "कीमत",
    f_accepts: "क्या-क्या ले जा सकते हैं",
    f_airline: "एयरलाइन",
    f_note: "नोट्स",
    f_note_ph: "जैसे 10 तारीख तक Edison में दे दें, या मुझे कूरियर कर दें",
    carry_publish: "यात्रा पोस्ट करें",
    err_province: "भारत में एयरपोर्ट चुनें",
    err_depart: "रवानगी की तारीख चुनें (आज या बाद की)",
    err_return: "वापसी रवानगी के बाद होनी चाहिए",
    err_kg: "0.5 से 100 किलो के बीच लिखें",
    err_contact: "WhatsApp या फ़ोन नंबर जोड़ें",
    err_state: "ZIP कोड नहीं पहचाना गया",
    err_dir: "दिशा चुनें",
    err_required: "जानकारी अधूरी है",
    acc_docs: "दस्तावेज़",
    acc_clothes: "कपड़े",
    acc_snacks: "नमकीन और सूखा खाना",
    acc_cosmetics: "कॉस्मेटिक्स",
    acc_supplements: "विटामिन और सप्लीमेंट",
    acc_small: "छोटा सामान",
    acc_gifts: "उपहार",
    safety_carry: "यात्री ध्यान दें: लेने से पहले हर सामान खोलकर जाँचें। सील बंद पैकेट, अनजान सामान, दवाइयाँ, या भारतीय और अमेरिकी कस्टम में मना चीज़ें कभी न ले जाएँ। कीमत और वज़न पहले चैट में तय करें।",
    foot_tag: "आपका लोकल भारतीय समुदाय, एक ही जगह। सामान खरीदें-बेचें, कमरे, नौकरी, टिफ़िन और सेवाएँ ढूँढें, और आसपास के लोगों से जुड़ें।",
    trust_enc: "एन्क्रिप्टेड कनेक्शन",
    trust_sell: "हम आपका डेटा कभी नहीं बेचते",
    rights: "सर्वाधिकार सुरक्षित।",
    serving: "पूरे अमेरिका के भारतीय समुदायों के लिए",
    f_market: "बाज़ार",
    f_browse: "लिस्टिंग देखें",
    f_carry: "सामान भेजें",
    f_post: "लिस्टिंग पोस्ट करें",
    f_community: "समुदाय",
    f_account: "आपका खाता",
    f_support: "सहायता से संपर्क",
    f_legal: "कानूनी जानकारी",
    updated: "आख़िरी अपडेट",
    at_glance: "संक्षेप में",
    on_this_page: "इस पेज पर",
    legal_nav: "कानूनी जानकारी",
    questions_h: "कोई सवाल?",
    questions_b: "हमारी टीम मदद के लिए तैयार है। बस हमें ईमेल करें।",
    contact_us: "संपर्क करें",
    read_next: "आगे पढ़ें",
    nav_rooms: "समुदाय",
    admin_rooms_link: "समुदाय की समीक्षा",
    from: "कहाँ से",
    to: "कहाँ तक",
    f_airport: "भारत का अंतरराष्ट्रीय एयरपोर्ट",
    f_airport_ph: "एयरपोर्ट चुनें",
    f_airport_h: "सिर्फ़ अंतरराष्ट्रीय एयरपोर्ट",
    err_airport: "कृपया भारत का एयरपोर्ट चुनें",
    secure_h: "संपर्क सुरक्षित ApnaCircle चैट में ही रहता है",
    secure_b: "लोग आपसे सिर्फ़ ऐप की चैट से संपर्क करते हैं। आपका फ़ोन नंबर और निजी जानकारी लिस्टिंग पर कभी नहीं दिखती, और हर कनेक्शन HTTPS से एन्क्रिप्टेड है।",
    privacy: "प्राइवेसी पॉलिसी",
    terms: "उपयोग की शर्तें",
    effective: "लागू",
    and_w: "और",
    agree_pre: "खाता बनाकर आप हमारी इन बातों से सहमत होते हैं:",
    av_title: "प्रोफ़ाइल फ़ोटो",
    av_change: "प्रोफ़ाइल फ़ोटो बदलें",
    av_hint: "सही जगह के लिए खींचें। ज़ूम के लिए स्लाइडर इस्तेमाल करें।",
    av_zoom: "ज़ूम",
    av_save: "यही फ़ोटो लगाएँ",
    av_other: "दूसरी फ़ोटो चुनें",
    av_remove: "फ़ोटो हटाएँ",
    loading: "लोड हो रहा है…",
    load_older: "पुराने मैसेज दिखाएँ",
    typing: "टाइप कर रहे हैं…",
    st_sending: "भेज रहे हैं",
    st_sent: "भेजा गया, अभी पढ़ा नहीं",
    st_read: "पढ़ लिया",
    st_failed: "नहीं भेजा गया। फिर से कोशिश के लिए दबाएँ।",
    st_rate: "बहुत तेज़ी से भेज रहे हैं। थोड़ा रुककर फिर दबाएँ।",
    discard_msg: "हटाएँ",
    reconnecting: "फिर से जुड़ रहे हैं… ऑनलाइन होते ही मैसेज चले जाएँगे",
    conn_online: "जुड़े हुए हैं। मैसेज तुरंत पहुँचते हैं।",
    conn_offline: "लाइव कनेक्शन बंद है। मैसेज सामान्य कनेक्शन से भेजे जा रहे हैं।",
    api_down: "सर्वर से संपर्क नहीं हो पा रहा। फिर से कोशिश करें।",
    retry: "फिर से कोशिश करें",
    notif_h: "आवाज़ें",
    sound_msg: "नए मैसेज पर आवाज़",
    sound_post: "फ़ॉलो की गई कैटेगरी में नई पोस्ट पर आवाज़",
    interests: "आपकी फ़ॉलो की गई कैटेगरी",
    interests_hint: "वे कैटेगरी चुनें जिनकी नई पोस्ट की सूचना चाहिए",
    test_sound: "सुनें",
    vibrate: "मैसेज पर वाइब्रेट (फ़ोन)",
    new_post: "{c} में नया: {t}",
    new_msg: "{n} का नया मैसेज",
    on: "चालू",
    off: "बंद",
    chat_fail: "चैट शुरू नहीं हो सकी। फिर से कोशिश करें।",
    listing_gone: "यह लिस्टिंग हटा दी गई है"
  },
  en: {
    profile: "Profile",
    edit_photo: "Change profile photo",
    upload_photo: "Upload a photo",
    use_logo: "Use ApnaCircle logo",
    use_initials: "Use initials",
    remove_photo: "Remove photo",
    profile_saved: "Profile saved",
    your_city: "Your city",
    set_city: "Set city",
    city_hint: "Enter a ZIP to show your city and state on your profile",
    member_since: "Member since {d}",
    trust: "Trust",
    verified_phone: "Phone verified",
    verified_email: "Email verified",
    not_verified: "Phone not verified",
    reviews: "reviews",
    review_one: "review",
    no_reviews: "No reviews yet",
    new_member: "New member",
    see_reviews: "See all reviews",
    write_review: "Write a review",
    rate_seller: "Rate the seller",
    rate_buyer: "Rate the buyer",
    your_rating: "Your rating",
    review_ph: "How did it go? Were they on time, was the item as described?",
    review_send: "Post review",
    review_thanks: "Thanks for the review",
    review_update: "Review updated",
    review_need_deal: "You can review after you've messaged each other",
    review_self: "You can't review yourself",
    review_fail: "Couldn't post the review. Try again.",
    review_as_buyer: "as a buyer",
    review_as_seller: "as a seller",
    reviews_about: "Reviews for {n}",
    deals_done: "{n} completed deals",
    listings_count: "{n} listings",
    photo_of: "Photo {a} of {b}",
    login_to_review: "Log in to review",
    trust_tip: "Check the stars and verification before you meet",
    view_profile: "View profile",
    nav_carry: "Luggage",
    per_kg: " / kg",
    per_kg_btn: "Per kg",
    kg: "kg",
    kg_n: "{n} kg",
    carry_h: "Luggage space · India ⇄ US",
    carry_sub: "Flying soon with spare kilos? Post your dates. Want to send something home? Search by state and date, then message the traveller.",
    carry_post: "Post your trip",
    carry_how: ["Pick a direction and the state near you", "Check dates, kilos left and price per kg", "Message to agree, hand over before the flight"],
    dir_all: "All",
    dir_us_in: "US → India",
    dir_in_us: "India → US",
    zones: "Zones",
    zone_all: "All states",
    carry_search_ph: "Search city, state, airport or name…",
    trips_n: "{n} upcoming trips",
    route: "Route",
    no_trips: "No trips in this range yet",
    no_trips_b: "Flying soon? Post your trip so people near you can ask.",
    depart: "Departs",
    return_d: "Returns",
    one_way: "One way",
    space: "Space",
    price_kg: "Price",
    traveler: "Traveller",
    contact: "Contact",
    trip_full: "Full",
    today: "Flies today",
    tomorrow: "Flies tomorrow",
    days_left: "In {n} days",
    verified_short: "Verified phone",
    carry_new_h: "Post luggage space",
    f_dir: "Direction",
    f_us_zip: "US ZIP code",
    f_us_zip_h: "5-digit ZIP, shows your city and state",
    f_province: "City in India",
    f_depart: "Departure date",
    f_return: "Return date",
    optional: "(optional)",
    f_kg: "Kilos available",
    f_price_kg: "Price",
    f_accepts: "Accepts",
    f_airline: "Airline",
    f_note: "Notes",
    f_note_ph: "e.g. drop off by the 10th in Edison, or mail it to me",
    carry_publish: "Post trip",
    err_province: "Choose an airport in India",
    err_depart: "Pick a departure date (today or later)",
    err_return: "Return must be after departure",
    err_kg: "Enter 0.5–100 kg",
    err_contact: "Add a WhatsApp or phone number",
    err_state: "ZIP code not recognised",
    err_dir: "Pick a direction",
    err_required: "Missing details",
    acc_docs: "Documents",
    acc_clothes: "Clothes",
    acc_snacks: "Snacks & dry food",
    acc_cosmetics: "Cosmetics",
    acc_supplements: "Vitamins & supplements",
    acc_small: "Small items",
    acc_gifts: "Gifts",
    safety_carry: "Travellers: open and check every item before accepting it. Never carry sealed packages, anything you can't identify, medicines, or items banned by Indian or US customs. Agree price and weight in chat first.",
    foot_tag: "Your local Indian community, all in one place. Buy and sell, find rooms, jobs, tiffin and services, and connect with people near you.",
    trust_enc: "Encrypted connections",
    trust_sell: "We never sell your data",
    rights: "All rights reserved.",
    serving: "Serving Indian communities across the U.S.",
    f_market: "Marketplace",
    f_browse: "Browse listings",
    f_carry: "Luggage space",
    f_post: "Post a listing",
    f_community: "Community",
    f_account: "Your account",
    f_support: "Contact support",
    f_legal: "Legal",
    updated: "Last updated",
    at_glance: "At a glance",
    on_this_page: "On this page",
    legal_nav: "Legal",
    questions_h: "Questions?",
    questions_b: "Our team is happy to help. Just send us an email.",
    contact_us: "Contact us",
    read_next: "Read next",
    nav_rooms: "Community",
    admin_rooms_link: "Community review",
    from: "From",
    to: "To",
    f_airport: "International airport in India",
    f_airport_ph: "Choose an airport",
    f_airport_h: "International airports only",
    err_airport: "Please choose an airport in India",
    secure_h: "Contact stays in secure ApnaCircle chat",
    secure_b: "People reach you only through in-app chat. Your phone number and personal details are never shown on your listing, and every connection is encrypted with HTTPS.",
    privacy: "Privacy Policy",
    terms: "Terms of Use",
    effective: "Effective",
    and_w: "and",
    agree_pre: "By creating an account, you agree to our",
    av_title: "Profile photo",
    av_change: "Change profile photo",
    av_hint: "Drag to position. Use the slider to zoom.",
    av_zoom: "Zoom",
    av_save: "Use this photo",
    av_other: "Choose another",
    av_remove: "Remove photo",
    loading: "Loading…",
    load_older: "Show earlier messages",
    typing: "typing…",
    st_sending: "Sending",
    st_sent: "Sent, not read yet",
    st_read: "Read",
    st_failed: "Not sent. Tap to try again.",
    st_rate: "Sending too fast. Wait a moment, then tap to try again.",
    discard_msg: "Delete",
    reconnecting: "Reconnecting… messages will go out when you're back online",
    conn_online: "Connected. Messages arrive instantly.",
    conn_offline: "Live connection is off. Messages are sent over the normal connection.",
    api_down: "Can't reach the server. Try again.",
    retry: "Try again",
    notif_h: "Sounds",
    sound_msg: "Sound for new messages",
    sound_post: "Sound for new posts in categories you follow",
    interests: "Categories you follow",
    interests_hint: "Pick the categories you want to hear about when someone posts",
    test_sound: "Play",
    vibrate: "Vibrate for messages (phones)",
    new_post: "New in {c}: {t}",
    new_msg: "New message from {n}",
    on: "On",
    off: "Off",
    chat_fail: "Couldn't start the chat. Try again.",
    listing_gone: "This listing was removed"
  },
};

/* ─── stars & avatars ───────────────────────────────────────────────────── */
const STAR_D = "M12 3.2l2.65 5.6 6 .85-4.35 4.2 1.05 6.05L12 17.05 6.65 19.9l1.05-6.05L3.35 9.65l6-.85z";
function Stars({ v, cls = "" }) {
  const r = Math.round(Number(v) || 0);
  return (
    <span className={"stars " + cls} aria-label={`${(Number(v) || 0).toFixed(1)} / 5`}>
      {[0, 1, 2, 3, 4].map((i) => <svg key={i} viewBox="0 0 24 24" className={i < r ? "on" : ""} aria-hidden="true"><path d={STAR_D} /></svg>)}
    </span>
  );
}
function StarPick({ value, onPick, t }) {
  return (
    <div className="starpick" role="radiogroup" aria-label={t("your_rating")}>
      {[1, 2, 3, 4, 5].map((v) => (
        <button key={v} role="radio" aria-checked={value === v} aria-label={String(v)} className={v <= value ? "on" : ""} onClick={() => onPick(v)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d={STAR_D} /></svg>
        </button>
      ))}
    </div>
  );
}
/* Profile picture: uploaded photo, ApnaCircle logo, or initials */
/* Square crop with drag-to-position and zoom; exports a 512 px JPEG (sharp on
   high-density screens, ~40-110 KB). */
function AvatarEditor({ app, file, busy, hasPhoto, onCancel, onPick, onRemove, onSave }) {
  const { t } = app;
  const VIEW = 280, OUT = 512;
  const [img, setImg] = useState(null), [zoom, setZoom] = useState(1), [pos, setPos] = useState({ x: 0, y: 0 }), [err, setErr] = useState("");
  const drag = useRef(null);
  useEffect(() => {
    let live = true;
    const url = URL.createObjectURL(file), im = new Image();
    im.onload = () => { if (live) { setImg(im); setZoom(1); setPos({ x: 0, y: 0 }); setErr(""); } };
    im.onerror = () => { if (live) setErr("photo"); };   // e.g. HEIC on a browser that can't read it
    im.src = url;
    return () => { live = false; setTimeout(() => URL.revokeObjectURL(url), 0); };
  }, [file]);
  // scale so the photo always covers the square, then keep it covering while dragging
  const base = img ? Math.max(VIEW / img.naturalWidth, VIEW / img.naturalHeight) : 1;
  const w = img ? img.naturalWidth * base * zoom : 0, h = img ? img.naturalHeight * base * zoom : 0;
  const clamp = (p, z = zoom) => {
    const ww = img.naturalWidth * base * z, hh = img.naturalHeight * base * z;
    const mx = (ww - VIEW) / 2, my = (hh - VIEW) / 2;
    return { x: Math.max(-mx, Math.min(mx, p.x)), y: Math.max(-my, Math.min(my, p.y)) };
  };
  const down = (e) => { e.currentTarget.setPointerCapture(e.pointerId); drag.current = { sx: e.clientX, sy: e.clientY, p: pos }; };
  const move = (e) => { if (!drag.current || !img) return; const d = drag.current; setPos(clamp({ x: d.p.x + e.clientX - d.sx, y: d.p.y + e.clientY - d.sy })); };
  const up = () => { drag.current = null; };
  const setZ = (z) => { setZoom(z); setPos((p) => clamp(p, z)); };
  const wheel = (e) => { if (img) setZ(Math.max(1, Math.min(3, zoom - e.deltaY * 0.002))); };
  const exportIt = () => {
    const c = document.createElement("canvas"); c.width = OUT; c.height = OUT;
    const k = OUT / VIEW, g = c.getContext("2d");
    g.fillStyle = "#fff"; g.fillRect(0, 0, OUT, OUT);
    g.imageSmoothingQuality = "high";
    g.drawImage(img, (VIEW / 2 - w / 2 + pos.x) * k, (VIEW / 2 - h / 2 + pos.y) * k, w * k, h * k);
    let q = 0.9, out = c.toDataURL("image/jpeg", q);
    while (out.length > 150000 && q > 0.5) { q -= 0.08; out = c.toDataURL("image/jpeg", q); }
    onSave(out);
  };
  return (
    <div className="scrim" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="sheet aved" role="dialog" aria-modal="true" aria-label={t("av_title")}>
        <div className="hd"><h2>{t("av_title")}</h2><button className="ibtn" onClick={onCancel} aria-label={t("close")}><Ico n="close" /></button></div>
        {err ? <div className="auth-error">{t("err_photo")}</div> : (
          <div className="avstage" style={{ width: VIEW, height: VIEW }} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onWheel={wheel}>
            {img ? <img src={img.src} alt="" draggable={false}
              style={{ width: w, height: h, transform: `translate(${VIEW / 2 - w / 2 + pos.x}px, ${VIEW / 2 - h / 2 + pos.y}px)` }} /> : <div className="spin" />}
            <span className="avmask" aria-hidden="true" />
          </div>
        )}
        <p className="avhint">{t("av_hint")}</p>
        <label className="avzoom"><Ico n="search" /><input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => setZ(Number(e.target.value))} aria-label={t("av_zoom")} /></label>
        <div className="avacts">
          <button className="btn pri" disabled={!img || busy} onClick={exportIt}>{busy ? t("loading") : t("av_save")}</button>
          <button className="btn" disabled={busy} onClick={onPick}>{t("av_other")}</button>
          {hasPhoto && <button className="btn danger" disabled={busy} onClick={onRemove}>{t("av_remove")}</button>}
        </div>
      </div>
    </div>
  );
}
function Avatar({ src, useLogo, name, size = 44, cls = "" }) {
  const st = { width: size, height: size, fontSize: Math.round(size * 0.36) };
  const img = safeImg(src);
  if (img) return <span className={"av ph " + cls} style={st}><img src={img} alt="" loading="lazy" /></span>;
  if (useLogo) return <span className={"av logo " + cls} style={st}><img src={EMBLEM_LIGHT} alt="" className="em-l" /><img src={EMBLEM_DARK} alt="" className="em-d" /></span>;
  return <span className={"av " + cls} style={st}>{initials(name)}</span>;
}
const shortDate = (ts, lang) => new Date(ts || Date.now()).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-US", { month: "short", year: "numeric" });

/* ─── routing (hash based, so it works inside any router) ───────────────── */
function parseRoute() {
  if (!hasWindow) return { r: "home" };
  // "?…" after the route (e.g. ?invite=1, ?social=ok) is read by the page, not part of the id
  const h = (window.location.hash || "#/").slice(1).split("?")[0].split("/").filter(Boolean);
  const r = h[0] || "home";
  if (r.startsWith("verify")) return { r: "verify" };
  if (r === "join") return { r: "join" };
  if (r === "care") return h[1] === "new" ? { r: "carenew" } : { r: "care" };
  if (r === "item" && h[1]) return { r: "item", id: decodeURIComponent(h[1]) };
  if (r === "chat" && h[1]) return { r: "chat", id: decodeURIComponent(h[1]) };
  if (r === "carry") return h[1] === "new" ? { r: "carrynew" } : h[1] === "companion" ? { r: "carry", companion: true } : { r: "carry" };
  if (r === "need" && h[1]) {
    if (h[1] === "companion") return { r: "carry", companion: true };
    if (h[1] === "eldercare") return { r: "care" };
    if (h[1] === "send") return { r: "carry" };
    return { r: h[1] === "safety" ? "safety" : "need", id: h[1] };
  }
  if (r === "rooms") return h[1] === "new" ? { r: "roomnew" } : h[1] === "review" ? { r: "roomsadmin" } : { r: "rooms" };
  if (r === "room" && h[1]) return { r: h[2] === "manage" ? "roommanage" : "room", id: decodeURIComponent(h[1]) };
  if (["post", "inbox", "saved", "me", "login", "register", "privacy", "terms"].includes(r)) return { r };
  return { r: "home" };
}

/* ─── small UI atoms ─────────────────────────────────────────────────────── */
const Ico = ({ n, className = "i", style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" aria-hidden="true"><path d={ICON[n] || ICONX[n] || ICON.tag} /></svg>
);
let GID = 0;
const subIcon = (c, s) => CAT[c]?.subs?.find((v) => v[0] === s)?.[3] || CAT[c]?.icon || "tag";
function Cover({ x }) {
  const idRef = useRef(null);
  if (!idRef.current) idRef.current = "ccg" + ++GID;
  const g = idRef.current, c = CAT[x.cat] || CAT.market, [a, b] = c.grad;
  return (
    <svg className="cv" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs><linearGradient id={g} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={a} /><stop offset="1" stopColor={b} /></linearGradient></defs>
      <rect width="400" height="400" fill={`url(#${g})`} />
      <circle cx="330" cy="70" r="120" fill="#fff" fillOpacity=".05" />
      <path d="M-30 345 Q200 205 430 345" stroke="#E4C375" strokeOpacity=".7" strokeWidth="13" fill="none" />
      <path d="M30 392 Q200 292 370 392" stroke="#fff" strokeOpacity=".22" strokeWidth="10" fill="none" />
      <g transform="translate(146 104) scale(4.5)" fill="none" stroke="#fff" strokeOpacity=".94" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
        <path d={ICON[subIcon(x.cat, x.sub)]} />
      </g>
    </svg>
  );
}
const Thumb = ({ x, i = 0 }) => (x.photos && x.photos[i]
  ? <><span className="thbg" style={{ backgroundImage: `url(${x.photos[i]})` }} aria-hidden="true" /><img className="thimg" src={x.photos[i]} alt="" loading="lazy" /></>
  : <Cover x={x} />);

/* ─── formatting helpers bound to language + location ───────────────────── */
function makeFmt(lang, loc) {
  const t = (k, v) => tr(lang, k, v);
  const L = (o, f) => (lang === "en" && o[f + "_en"] ? o[f + "_en"] : o[f]);
  const catName = (id) => (CAT[id] ? CAT[id][lang] : "");
  const subName = (c, s) => { const x = CAT[c]?.subs?.find((v) => v[0] === s); return x ? (lang === "en" ? x[2] : x[1]) : ""; };
  const stateName = (st) => (STATE[st] ? STATE[st][lang] : st);
  const priceText = (x) => {
    if (x.unit === "free") return t("free");
    if (x.unit === "tier") return "$".repeat(Math.max(1, Math.min(4, x.price || 1)));
    if (x.unit === "ask" || x.price == null) return t("ask");
    const s = "$" + Number(x.price).toLocaleString("en-US", { maximumFractionDigits: 2 });
    return s + (x.unit === "month" ? t("per_month") : x.unit === "hour" ? t("per_hour") : x.unit === "kg" ? t("per_kg") : "");
  };
  const ago = (ts) => {
    const m = Math.max(0, Math.floor((Date.now() - ts) / 60000));
    if (m < 1) return t("just_now");
    if (m < 60) return t("m_ago", { n: m });
    const h = Math.floor(m / 60); if (h < 24) return t("h_ago", { n: h });
    const d = Math.floor(h / 24); if (d < 14) return t("d_ago", { n: d });
    return new Date(ts).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-US", { day: "numeric", month: "short" });
  };
  const clock = (ts) => new Date(ts).toLocaleTimeString(lang === "hi" ? "hi-IN" : "en-US", { hour: "numeric", minute: "2-digit" });
  const place = (x) => (x.city ? `${x.city}, ${x.state}` : x.state || "");
  const dist = (x) => (loc.mode !== "zip" || x.lat == null ? null : miles(loc.lat, loc.lng, x.lat, x.lng));
  const distText = (d) => (d == null ? "" : d < 1 ? "< 1 " + t("mi") : Math.round(d).toLocaleString() + " " + t("mi"));
  const locLabel = () => {
    if (loc.mode === "zip") return loc.radius ? t("within", { p: `${loc.city} ${loc.zip}`, r: loc.radius }) : t("near", { p: `${loc.city} ${loc.zip}` });
    if (loc.mode === "state") return stateName(loc.state);
    return t("loc_all");
  };
  return { t, L, catName, subName, stateName, priceText, ago, clock, place, dist, distText, locLabel };
}
const initials = (n) => { const s = String(n || "").replace(/^(mr\.?|mrs\.?|ms\.?|dr\.?|shri|smt\.?|श्री|श्रीमती|डॉ\.?)\s+/i, "").trim(); return (s[0] || "?").toUpperCase(); };
/* WhatsApp click-to-chat. A 10-digit number is treated as U.S. (+1); Indian
   numbers should be entered with +91. */
const waLink = (n) => {
  const d = String(n || "").replace(/\D/g, "");
  if (d.length < 10 || d.length > 15) return "";
  return "https://wa.me/" + (d.length === 10 ? "1" + d : d);
};
const telHref = (p) => "tel:" + String(p || "").replace(/[^\d+]/g, "");
const validLoc = (l) => (l && ["all", "state", "zip"].includes(l.mode) ? l : { mode: "all" });

/* ─── photo compression ─────────────────────────────────────────────────── */
function compress(file, max = 1600, cap = 420000) {
  return new Promise((res, rej) => {
    if (!file || !/^image\//.test(file.type)) return rej(new Error("type"));
    const url = URL.createObjectURL(file), img = new Image();
    img.onload = () => {
      try {
        const s = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
        const c = document.createElement("canvas"); c.width = Math.round(img.naturalWidth * s); c.height = Math.round(img.naturalHeight * s);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        let q = 0.84, out = c.toDataURL("image/jpeg", q);
        while (out.length > cap && q > 0.3) { q -= 0.1; out = c.toDataURL("image/jpeg", q); }
        URL.revokeObjectURL(url); res(out);
      } catch (e) { URL.revokeObjectURL(url); rej(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); rej(new Error("load")); };
    img.src = url;
  });
}

/* Cover thumbnail for the feed (~560 px): the grid loads this, the listing page loads full photos. */
function makeThumb(src, max = 560, cap = 70000) {
  return new Promise((res) => {
    if (!src) return res("");
    const img = new Image();
    img.onload = () => {
      try {
        const s = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
        const c = document.createElement("canvas"); c.width = Math.round(img.naturalWidth * s); c.height = Math.round(img.naturalHeight * s);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        let q = 0.8, out = c.toDataURL("image/jpeg", q);
        while (out.length > cap && q > 0.4) { q -= 0.1; out = c.toDataURL("image/jpeg", q); }
        res(out);
      } catch (e) { res(""); }
    };
    img.onerror = () => res("");
    img.src = src;
  });
}

/* ============================================================================
   ROOT
   ========================================================================== */
export default function ApnaCircleMarketplace() {
  Store.init();
  const [lang, setLangS] = useState(() => (LS.get("ac_lang", "en") === "hi" ? "hi" : "en"));
  const [metro, setMetroS] = useState(() => { const m = LS.get("ac_metro", ""); Store.metro = m; return m; });
  const setMetro = (m) => { setMetroS(m); LS.set("ac_metro", m); Store.metro = m; Store.sync(); };
  const [theme, setThemeS] = useState(() => { const v = LS.get("ac_theme", "light"); return ["auto", "light", "dark"].includes(v) ? v : "light"; });
  const [sysDark, setSysDark] = useState(() => hasWindow && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches);
  const [loc, setLocS] = useState(() => validLoc(LS.get("ac_loc", { mode: "all" })));
  const [cat, setCat] = useState("all");
  const [sub, setSub] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("new");
  const [show, setShow] = useState(30);
  const [saved, setSavedS] = useState(() => new Set(LS.get("ac_saved", [])));
  const [name, setNameS] = useState(() => LS.get("ac_name", ""));
  const [route, setRoute] = useState(parseRoute);
  const [inboxTab, setInboxTab] = useState("buying");
  const [sheet, setSheet] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const plat = useMemo(detectPlatform, []);
  const [standalone, setStandalone] = useState(plat.standalone);
  const [installEvt, setInstallEvt] = useState(() => (hasWindow ? window.__ccInstallEvt || null : null));
  const [installNotice, setInstallNotice] = useState(() => LS.get("ac_install_notice_v3", true));
  const [, bump] = useReducer((x) => x + 1, 0);
  const navCount = useRef(0), homeScroll = useRef(0), prevRoute = useRef(route), toastTimer = useRef(0);
  const routeRef = useRef(route), langRef = useRef(lang), baseTitle = useRef("ApnaCircle");
  routeRef.current = route; langRef.current = lang;

  const F = useMemo(() => makeFmt(lang, loc), [lang, loc]);
  const { t } = F;
  const dark = theme === "dark" || (theme === "auto" && sysDark);

  /* effects */
  useEffect(() => Store.subscribe(bump), []);
  const [pending, setPending] = useState({ total: 0, byRoom: {} });
  const refreshPending = useCallback(() => {
    if (!user) { setPending({ total: 0, byRoom: {} }); return; }
    api("/rooms/pending/").then((d) => setPending({ total: d.total || 0, byRoom: Object.fromEntries((d.rooms || []).map((r) => [r.roomId, r.count])) })).catch(() => {});
  }, [user]);
  useEffect(() => {
    refreshPending();
    const tm = setInterval(() => { if (document.visibilityState === "visible") refreshPending(); }, 90000);
    const onLive = (e) => {
      const m = e.detail || {};
      if (m.type === "room_request") { toast(nt({ lang: langRef.current }, "room_req", { n: m.name || "", g: m.roomName || "" })); refreshPending(); }
    };
    window.addEventListener("ac-live", onLive);
    return () => { clearInterval(tm); window.removeEventListener("ac-live", onLive); };
  }, [refreshPending]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onVerify = () => { toast(nt({ lang: langRef.current }, "v_needed")); go("/verify"); };
    window.addEventListener("ac-verify", onVerify);
    return () => window.removeEventListener("ac-verify", onVerify);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  /* notification sounds: new message, or a new post in a topic this person follows */
  useEffect(() => {
    Store.onUser = (u) => setUser(u);
    if (Chat) Chat.onIncoming = (m, th) => {
      const r = routeRef.current;
      const watching = r.r === "chat" && r.id === m.thread && document.visibilityState === "visible" && document.hasFocus();
      if (watching) return;
      Sound.play("message");
      if (r.r !== "chat") {
        const name = (th && (th.sellerId === m.from ? th.sellerName : th.buyerName)) || "";
        toast(tr(langRef.current, "new_msg", { n: name || tr(langRef.current, "member") }));
      }
    };
    Store.onNewListing = (x) => {
      const prefs = Sound.getPrefs();
      if (!prefs.interests.includes(x.cat)) return;
      if (Sound.play("post")) {
        const l = langRef.current;
        toast(tr(l, "new_post", { c: CAT[x.cat] ? CAT[x.cat][l] : "", t: x.title }));
      }
    };
    return () => { if (Chat) Chat.onIncoming = null; Store.onNewListing = null; Store.onUser = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const unreadTotal = Chat ? Chat.unreadTotal() : 0;
  useEffect(() => { document.title = (unreadTotal ? `(${unreadTotal}) ` : "") + baseTitle.current; }, [unreadTotal, route]);
  useEffect(() => {
    const stop = Store.startAutoRefresh();
    return () => { if (typeof stop === "function") stop(); };
  }, []);
  useEffect(() => {
    let live = true;
    api("/auth/me/").then((d) => {
      if (!live) return;
      const u = d && d.authenticated ? d.user : null;
      setUser(u); Store.setUser(u);
      if (u && u.displayName && !LS.get("ac_name", "")) { setNameS(u.displayName); LS.set("ac_name", u.displayName); }
    }).catch(() => { if (live) { setUser(null); Store.setUser(null); } }).finally(() => { if (live) setAuthReady(true); });
    return () => { live = false; };
  }, []);
  /* PWA: service worker, manifest links, install events */
  useEffect(() => {
    const addLink = (rel, href, extra = {}) => {
      if (document.querySelector(`link[rel="${rel}"]`)) return;
      const l = document.createElement("link"); l.rel = rel; l.href = href; Object.assign(l, extra); document.head.appendChild(l);
    };
    addLink("apple-touch-icon", "/apple-touch-icon.png");
    const addMeta = (name, content) => { if (!document.querySelector(`meta[name="${name}"]`)) { const m = document.createElement("meta"); m.name = name; m.content = content; document.head.appendChild(m); } };
    addMeta("apple-mobile-web-app-capable", "yes");
    addMeta("mobile-web-app-capable", "yes");
    addMeta("apple-mobile-web-app-title", "ApnaCircle");
    addMeta("apple-mobile-web-app-status-bar-style", "default");
    addMeta("theme-color", "#13294B");
    const onReady = () => setInstallEvt(window.__ccInstallEvt || null);
    const onInstalled = () => { window.__ccInstallEvt = null; setInstallEvt(null); setStandalone(true); toast(tr(lang, "install_done")); };
    window.addEventListener("cc-installable", onReady);
    window.addEventListener("appinstalled", onInstalled);
    const mq = window.matchMedia ? window.matchMedia("(display-mode: standalone)") : null;
    const onMode = (e) => setStandalone(e.matches || window.navigator.standalone === true);
    if (mq) mq.addEventListener ? mq.addEventListener("change", onMode) : mq.addListener(onMode);
    return () => {
      window.removeEventListener("cc-installable", onReady); window.removeEventListener("appinstalled", onInstalled);
      if (mq) mq.removeEventListener ? mq.removeEventListener("change", onMode) : mq.removeListener(onMode);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onHash = () => { navCount.current++; setSheet(null); setRoute(parseRoute()); };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  useEffect(() => {
    if (!authReady) return;
    const protectedRoutes = new Set(["carenew", "verify", "item", "post", "carrynew", "roomnew", "roommanage", "roomsadmin", "inbox", "chat", "saved", "me"]);
    if (!user && protectedRoutes.has(route.r)) {
      LS.set("ac_after_auth", window.location.hash.slice(1) || "/");
      window.location.hash = "/login";
    }
  }, [authReady, user, route]);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)"), f = (e) => setSysDark(e.matches);
    mq.addEventListener ? mq.addEventListener("change", f) : mq.addListener(f);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", f) : mq.removeListener(f));
  }, []);
  useEffect(() => {
    if (document.getElementById("cc-font")) return;
    const l = document.createElement("link"); l.id = "cc-font"; l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Mukta:wght@400;500;600;700&display=swap";
    document.head.appendChild(l);
  }, []);
  useEffect(() => {
    let m = document.querySelector('meta[name="viewport"]');
    if (m && !/viewport-fit=cover/.test(m.content)) m.content = m.content + ", viewport-fit=cover";
  }, []);
  useEffect(() => {
    const prev = prevRoute.current; prevRoute.current = route;
    if (route.r === "home" && prev.r !== "home") window.scrollTo(0, homeScroll.current || 0);
    else if (prev.r !== route.r || prev.id !== route.id) window.scrollTo(0, 0);
    const x = route.r === "item" ? Store.get(route.id) : null;
    baseTitle.current = x ? `${F.L(x, "title")} — ApnaCircle` : "ApnaCircle — " + t("tagline");
  }, [route]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!sheet) return; const f = (e) => { if (e.key === "Escape") setSheet(null); }; window.addEventListener("keydown", f); document.body.style.overflow = "hidden"; return () => { window.removeEventListener("keydown", f); document.body.style.overflow = ""; }; }, [sheet]);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  /* actions */
  const go = useCallback((path) => {
    if (route.r === "home") homeScroll.current = window.scrollY;
    if (window.location.hash === "#" + path) setRoute(parseRoute()); else window.location.hash = path;
  }, [route.r]);
  const back = (to = "/") => { if (navCount.current > 0) window.history.back(); else go(to); };
  const toast = (m) => { setToastMsg(m); clearTimeout(toastTimer.current); toastTimer.current = setTimeout(() => setToastMsg(null), 2600); };
  const setLang = (v) => { setLangS(v); LS.set("ac_lang", v); };
  const setTheme = (v) => { setThemeS(v); LS.set("ac_theme", v); };
  const setLoc = (v) => {
    setLocS(v); LS.set("ac_loc", v); setShow(30);
    if (v.mode === "zip" && sort === "new") setSort("near");
    if (v.mode !== "zip" && sort === "near") setSort("new");
  };
  const setName = (v) => { setNameS(v); LS.set("ac_name", v); };
  const toggleSave = (e, id) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!user) { LS.set("ac_after_auth", "/item/" + encodeURIComponent(id)); go("/login"); return; }
    const n = new Set(saved); const on = !n.has(id);
    on ? n.add(id) : n.delete(id);
    setSavedS(n); LS.set("ac_saved", [...n]);
    if (on) toast(t("saved_btn"));
  };
  const requireAuth = (target) => {
    if (user) { go(target); return true; }
    LS.set("ac_after_auth", target); go("/login"); return false;
  };
  const setSessionUser = (u) => {
    setUser(u); Store.setUser(u);
    if (u && u.displayName) { setNameS(u.displayName); LS.set("ac_name", u.displayName); }
  };
  const finishAuth = (u) => {
    setSessionUser(u);
    const target = LS.get("ac_after_auth", "/"); LS.del("ac_after_auth");
    go(target && target.startsWith("/") ? target : "/");
  };
  const logoutUser = async () => {
    try { await api("/auth/logout/", { method: "POST", body: "{}" }); } catch (e) {}
    setUser(null); Store.setUser(null);
    go("/");
  };
  const [opening, setOpening] = useState(false);
  const openChat = async (listing) => {
    if (!user) { requireAuth("/item/" + encodeURIComponent(listing.id)); return; }
    if (listing.ownerId === Store.me) { toast(t("own_listing")); return; }
    if (opening) return;
    setOpening(true);
    try {
      const id = await Chat.start(listing.id);
      go("/chat/" + encodeURIComponent(id));
    } catch (e) {
      toast(e && e.message === "listing_not_found" ? t("listing_gone") : t("chat_fail"));
    } finally { setOpening(false); }
  };
  const installApp = async () => {
    const evt = installEvt || (hasWindow ? window.__ccInstallEvt : null);
    if (evt && typeof evt.prompt === "function") {
      try {
        await evt.prompt();
        const choice = await evt.userChoice;
        window.__ccInstallEvt = null; setInstallEvt(null);
        if (choice && choice.outcome === "accepted") { setInstallNotice(false); LS.set("ac_install_notice_v3", false); }
        return;
      } catch (e) { window.__ccInstallEvt = null; setInstallEvt(null); }
    }
    setSheet({ type: "install" });
  };
  const dismissInstallNotice = () => { setInstallNotice(false); LS.set("ac_install_notice_v3", false); };
  const onSearch = (v) => {
    setQ(v.slice(0, 80)); setShow(30);
    if (route.r !== "home") { homeScroll.current = 0; go("/"); }
  };

  const app = { F, t, lang, loc, cat, sub, q, sort, show, saved, name, route, inboxTab, dark, theme, user, authReady,
    setCat, setSub, setQ, setSort, setShow, setLoc, setLang, setTheme, setName, setInboxTab, setSheet,
    metro, setMetro, pendingTotal: pending.total, pendingByRoom: pending.byRoom, refreshPending,
    go, back, toast, toggleSave, openChat, opening, setSessionUser, onSearch, requireAuth, finishAuth, logoutUser,
    plat, canInstall: !standalone, oneTap: !!installEvt, installNotice, installApp, dismissInstallNotice };

  let body;
  const needsAuth = ["carenew", "verify", "item", "post", "carrynew", "roomnew", "roommanage", "roomsadmin", "inbox", "chat", "saved", "me"].includes(route.r);
  if (!authReady && needsAuth) body = <main><div className="auth-loading">Loading…</div></main>;
  else if (route.r === "login" || route.r === "register") body = <AuthView app={app} mode={route.r} />;
  else if (needsAuth && !user) body = <AuthView app={app} mode="login" />;
  else if (route.r === "item") body = <ItemView app={app} />;
  else if (route.r === "post") body = <PostView app={app} />;
  else if (route.r === "carry") body = <CarryView key={route.companion ? "c" : "a"} app={app} companion={!!route.companion} />;
  else if (route.r === "need") body = <NeedView app={app} need={route.id} />;
  else if (route.r === "verify") body = <VerifyView key={hasWindow ? window.location.hash : "v"} app={app} />;
  else if (route.r === "join") body = <JoinView app={app} />;
  else if (route.r === "care") body = <CareView app={app} />;
  else if (route.r === "carenew") body = <CarePostView app={app} />;
  else if (route.r === "safety") body = <SafetyView app={app} />;
  else if (route.r === "rooms") body = <RoomsView app={app} />;
  else if (route.r === "roomnew") body = <RoomCreate app={app} />;
  else if (route.r === "room") body = <RoomView app={app} id={route.id} />;
  else if (route.r === "roommanage") body = <RoomManage app={app} id={route.id} />;
  else if (route.r === "roomsadmin") body = <RoomsAdmin app={app} />;
  else if (route.r === "privacy" || route.r === "terms") body = <LegalView app={app} kind={route.r} />;
  else if (route.r === "carrynew") body = <CarryPostView app={app} />;
  else if (route.r === "inbox" || route.r === "chat") body = <InboxView app={app} />;
  else if (route.r === "saved") body = <SavedView app={app} />;
  else if (route.r === "me") body = <MeView app={app} />;
  else body = <HomeView app={app} />;

  return (
    <div className="cc" data-theme={theme === "auto" ? undefined : theme} data-r={route.r} lang={lang}>
      <style>{CSS}</style>
      <Header app={app} />
      {app.canInstall && installNotice && route.r === "home" && <InstallNotice app={app} />}
      {body}
      {["home", "carry", "rooms", "saved", "me", "privacy", "terms", "need", "safety", "join", "care"].includes(route.r) && <SiteFoot app={app} />}
      <TabBar app={app} />
      {sheet && (
        <div className="scrim" onClick={(e) => { if (e.target === e.currentTarget) setSheet(null); }}>
          <div className="sheet" role="dialog" aria-modal="true">
            {sheet.type === "loc" && <LocSheet app={app} />}
            {sheet.type === "report" && <ReportSheet app={app} listing={sheet.listing} />}
            {sheet.type === "confirm" && <ConfirmSheet app={app} {...sheet} />}
            {sheet.type === "install" && <InstallSheet app={app} />}
            {sheet.type === "review" && <ReviewSheet app={app} {...sheet} />}
            {sheet.type === "reviews" && <ReviewsSheet app={app} {...sheet} />}
          </div>
        </div>
      )}
      {toastMsg && <div className="toast" role="status">{toastMsg}</div>}
    </div>
  );
}

/* ─── header & tab bar ──────────────────────────────────────────────────── */
function SearchBox({ app, id, cls = "search" }) {
  const { t, q, onSearch } = app;
  return (
    <div className={cls}>
      <Ico n="search" />
      <label className="sr" htmlFor={id}>{t("search_ph")}</label>
      <input id={id} type="search" enterKeyHint="search" autoComplete="off" placeholder={t("search_ph")} value={q}
        onChange={(e) => onSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} />
      {q && <button className="clr" onClick={() => app.setQ("")} aria-label={t("clear_search")}><Ico n="close" /></button>}
    </div>
  );
}
function Header({ app }) {
  const { t, route, lang, dark, user } = app, n = Store.unread(), r = route.r;
  const accountLabel = user ? (user.displayName || user.email || t("nav_me")) : (lang === "hi" ? "लॉग इन" : "Login");
  const prof = Store.profile();
  return (
    <header className="top">
      <div className="top-in">
        <a className="brand" href="#/" aria-label={"ApnaCircle " + t("nav_home")}>
          <img className="em-l" src={EMBLEM_LIGHT} alt="" /><img className="em-d" src={EMBLEM_DARK} alt="" />
          <span className="wm"><b><i className="wa">Apna</i><i className="wc">Circle</i></b><span>अपना सर्कल</span></span>
        </a>
        <SearchBox app={app} id="ccq1" cls="search d" />
        <nav className="dnav" aria-label="main">
          <a href="#/" className={r === "home" ? "on" : ""}><Ico n="home" />{t("nav_home")}</a>
          <a href="#/inbox" className={r === "inbox" || r === "chat" ? "on" : ""}><Ico n="chat" />{t("nav_inbox")}{n > 0 && <span className="dot">{n}</span>}</a>
          <a href="#/rooms" className={["rooms", "roomnew", "room", "roommanage", "roomsadmin"].includes(r) ? "on" : ""}><Ico n="people" />{t("nav_rooms")}{app.pendingTotal > 0 && <b className="nbadge">{app.pendingTotal}</b>}</a>
          <a href="#/carry" className={r === "carry" || r === "carrynew" ? "on" : ""}><Ico n="plane" />{t("nav_carry")}</a>
          <a href={user ? "#/me" : "#/login"} className={(r === "me" || r === "login" || r === "register" ? "on " : "") + "account-link"}>
            {user ? <Avatar src={prof.avatar} useLogo={prof.useLogo} name={user.displayName || user.initials} size={30} cls="hdr" /> : <Ico n="user" />}
            <span className="user-short">{accountLabel}</span>
          </a>
        </nav>
        <div className="tools">
          <button className="lang" onClick={() => app.setLang(lang === "hi" ? "en" : "hi")} aria-label="Language / भाषा">
            <span className={lang === "hi" ? "on" : ""}>हिंदी</span><span className={lang === "en" ? "on" : ""}>EN</span>
          </button>
          <button className="ibtn" onClick={() => app.setTheme(dark ? "light" : "dark")} aria-label={t("theme_toggle")}><Ico n={dark ? "sun" : "moonui"} /></button>
          <a className="ibtn m-saved" href="#/saved" aria-label={t("nav_saved")}><Ico n="heart" /></a>
          {user && <a className="mobile-avatar" href="#/me" aria-label={accountLabel}><Avatar src={prof.avatar} useLogo={prof.useLogo} name={user.displayName || user.initials} size={32} /></a>}
          <a className="btn-post" href="#/post"><Ico n="plus" />{t("nav_post")}</a>
        </div>
      </div>
      <div className="m-search"><SearchBox app={app} id="ccq2" /></div>
    </header>
  );
}
function TabBar({ app }) {
  const { t, route, user } = app, n = Store.unread(), r = route.r;
  const A = ({ h, k, ic, on }) => (
    <a href={h} className={on ? "on" : ""}><Ico n={ic} /><span>{t(k)}</span>{k === "nav_inbox" && n > 0 && <span className="dot">{n}</span>}{k === "nav_rooms" && app.pendingTotal > 0 && <span className="dot">{app.pendingTotal}</span>}</a>
  );
  return (
    <nav className="tabbar" aria-label="tabs">
      <A h="#/" k="nav_home" ic="home" on={r === "home"} />
      <A h="#/rooms" k="nav_rooms" ic="people" on={["rooms", "roomnew", "room", "roommanage", "roomsadmin"].includes(r)} />
      <a href="#/post" aria-label={t("nav_post")}><span className="postfab"><Ico n="plus" /></span><span>{t("nav_post")}</span></a>
      <A h="#/inbox" k="nav_inbox" ic="chat" on={r === "inbox" || r === "chat"} />
      <A h={user ? "#/me" : "#/login"} k="nav_me" ic="user" on={r === "me" || r === "login" || r === "register"} />
    </nav>
  );
}
function MBar({ app, title, to = "/", children }) {
  return (
    <div className="mbar">
      <button className="ibtn" onClick={() => app.back(to)} aria-label={app.t("back")}><Ico n="back" /></button>
      <div className="mt">{title}</div>{children}
    </div>
  );
}

/* ─── cards & home ──────────────────────────────────────────────────────── */
function Card({ app, x }) {
  const { F, t, saved, toggleSave } = app, d = F.dist(x), sv = saved.has(x.id);
  const fresh = Date.now() - x.createdAt < 6 * H && x.status !== "sold";
  return (
    <a className="card" href={"#/item/" + encodeURIComponent(x.id)} onClick={(e) => { if (!app.user) { e.preventDefault(); app.requireAuth("/item/" + encodeURIComponent(x.id)); } }}>
      <div className="ph">
        <Thumb x={x} />
        {x.isExample ? <span className="badge ex">{nt(app, "example")}</span> : x.status === "sold" ? <span className="badge sold">{t("sold")}</span> : fresh ? <span className="badge new">{t("new_badge")}</span> : null}
        <button className={"heart" + (sv ? " on" : "")} onClick={(e) => toggleSave(e, x.id)} aria-pressed={sv} aria-label={t(sv ? "saved_btn" : "save")}><Ico n="heart" /></button>
      </div>
      <div className="cbody">
        <div className="price">{F.priceText(x)}</div>
        <div className="ctitle">{F.L(x, "title")}</div>
        {x.meta && <MetaBadges app={app} x={x} />}
        <div className="cmeta"><span>{F.place(x)}</span><span>{d != null ? F.distText(d) : F.ago(x.createdAt)}</span></div>
      </div>
    </a>
  );
}
function useResults(app) {
  const { F, cat, sub, q, sort, loc } = app;
  const qq = q.toLowerCase().trim(), terms = qq ? qq.split(/\s+/) : [];
  let list = Store.all().filter((x) => x.cat !== "carry" && (x.status !== "sold" || x.ownerId === Store.me));
  if (app.metro) list = list.filter((x) => x.metro === app.metro);
  if (cat !== "all") list = list.filter((x) => x.cat === cat);
  if (sub) list = list.filter((x) => x.sub === sub);
  if (loc.mode === "state") list = list.filter((x) => x.state === loc.state);
  if (loc.mode === "zip" && loc.radius) list = list.filter((x) => { const d = F.dist(x); return d != null && d <= loc.radius; });
  if (terms.length) list = list.filter((x) => {
    const hay = [x.title, x.title_en, x.desc, x.desc_en, x.city, x.state, x.zip, x.sellerName, CAT[x.cat].th, CAT[x.cat].en,
      ...(CAT[x.cat].subs.find((s) => s[0] === x.sub) || []).slice(1, 3)].join(" ").toLowerCase();
    return terms.every((w) => hay.includes(w));
  });
  const pv = (x) => (x.unit === "free" ? 0 : x.unit === "ask" || x.unit === "tier" || x.price == null ? null : x.price);
  const s = sort === "near" && loc.mode !== "zip" ? "new" : sort;
  return list.sort((a, b) => {
    if (s === "near") return (F.dist(a) ?? 1e9) - (F.dist(b) ?? 1e9);
    if (s === "low" || s === "high") {
      const A = pv(a), B = pv(b);
      if (A == null && B == null) return b.createdAt - a.createdAt;
      if (A == null) return 1; if (B == null) return -1;
      return s === "low" ? A - B : B - A;
    }
    return b.createdAt - a.createdAt;
  });
}
function HomeView({ app }) {
  const { t, F, cat, sub, q, sort, show, loc, lang } = app;
  const list = useResults(app), shown = list.slice(0, show), c = CAT[cat];
  const sorts = [["new", "sort_new"], ["near", "sort_near"], ["low", "sort_low"], ["high", "sort_high"]].filter((s) => s[0] !== "near" || loc.mode === "zip");
  return (
    <main>
      <VerifyBanner app={app} />
      <CityBar app={app} />
      {!q && cat === "all" && <NeedsHub app={app} />}
      {!q && cat === "all" && <JoinBanner app={app} />}
      <div className="locbar">
        <button className="pill loc" onClick={() => app.setSheet({ type: "loc" })}><Ico n="pin" /><span className="t">{F.locLabel()}</span><Ico n="down" /></button>
        <label className="pill"><Ico n="sort" /><select value={sort} aria-label="sort" onChange={(e) => { app.setSort(e.target.value); app.setShow(30); }}>
          {sorts.map((s) => <option key={s[0]} value={s[0]}>{t(s[1])}</option>)}</select></label>
        <span className="count">{t("results", { n: list.length.toLocaleString() })}</span>
      </div>
      <div className="tabs" role="tablist" aria-label={t("categories")}>
        {CATS.map((k) => (
          <button key={k.id} className={"tab" + (cat === k.id ? " on" : "") + (k.id === "carry" ? " carrytab" : "")} aria-pressed={cat === k.id} onClick={() => { if (k.id === "carry") { app.go("/carry"); return; } app.setCat(k.id); app.setSub(""); app.setShow(30); }}>
            <span className="ic"><Ico n={k.icon} /></span><span className="lb">{k[lang]}</span>
          </button>
        ))}
      </div>
      {c.subs && cat !== "carry" && (
        <div className="subs" role="group" aria-label={F.catName(cat)}>
          <button className={"chip" + (!sub ? " on" : "")} onClick={() => { app.setSub(""); app.setShow(30); }}>{t("all")}</button>
          {c.subs.map((s) => <button key={s[0]} className={"chip" + (sub === s[0] ? " on" : "")} onClick={() => { app.setSub(s[0]); app.setShow(30); }}>{lang === "en" ? s[2] : s[1]}</button>)}
        </div>
      )}
      {!q && cat === "all" && list.length > 0 && <HomeSections app={app} list={list} />}
      {!list.length && !Store.initialSynced ? (
        Store.error
          ? <div className="empty"><div className="eic"><Ico n="clock" /></div><h3>{t("api_down")}</h3><p />
              <div className="acts"><button className="btn pri" onClick={() => Store.sync()}>{t("retry")}</button></div></div>
          : <div className="grid" style={{ marginTop: 14 }} aria-busy="true">{Array.from({ length: 10 }, (_, i) => <div className="card skel" key={i}><div className="ph" /><div className="cbody"><i /><i /><i /></div></div>)}</div>
      ) : !list.length ? (
        <div className="empty">
          <div className="eic"><Ico n={q.trim() ? "search" : "pin"} /></div>
          <h3>{q.trim() ? t("empty_q", { q: q.trim() }) : t("empty_t")}</h3>
          <p>{q.trim() ? t("empty_qb") : t("empty_b")}</p>
          <div className="acts">
            {loc.mode !== "all" && <button className="btn pri" onClick={() => app.setLoc({ mode: "all" })}>{t("widen")}</button>}
            {q.trim() ? <button className="btn" onClick={() => app.setQ("")}>{t("clear_search")}</button>
              : <a className={"btn" + (loc.mode === "all" ? " pri" : "")} href="#/post">{t("post_first")}</a>}
          </div>
        </div>
      ) : (
        <>
          <div className="grid" style={{ marginTop: 14 }}>{shown.map((x) => <Card key={x.id} app={app} x={x} />)}</div>
          {(list.length > shown.length || Store.hasMoreListings) && <div className="more"><button className="btn" disabled={Store.loadingMore} onClick={() => { app.setShow(show + 30); if (Store.hasMoreListings) Store.loadMore(); }}>{Store.loadingMore ? t("loading") : t("load_more")}</button></div>}
        </>
      )}
    </main>
  );
}

/* ─── listing detail ────────────────────────────────────────────────────── */
function Gallery({ x, alt, t, sv, onSave, onShare }) {
  const track = useRef(null); const [i, setI] = useState(0);
  const [open, setOpen] = useState(-1);          // index shown full screen, -1 = closed
  const [ratio, setRatio] = useState(4 / 3);      // frame follows the first photo's shape
  const photos = (x.photos || []).filter(Boolean);
  const n = Math.max(1, photos.length);
  const to = (k) => { const el = track.current; if (el) el.scrollTo({ left: el.clientWidth * k, behavior: "smooth" }); };
  const onKey = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); to(Math.max(0, i - 1)); }
    if (e.key === "ArrowRight") { e.preventDefault(); to(Math.min(n - 1, i + 1)); }
    if (e.key === "Enter" && photos.length) setOpen(i);
  };
  const onFirstLoad = (e) => {
    const im = e.currentTarget;
    if (im.naturalWidth && im.naturalHeight) setRatio(Math.min(1.6, Math.max(0.8, im.naturalWidth / im.naturalHeight)));
  };
  return (
    <div className="galwrap">
      <div className="gal" tabIndex={0} onKeyDown={onKey} aria-label={t("photo_of", { a: i + 1, b: n })}>
        <div className="track" ref={track} onScroll={(e) => { const el = e.currentTarget; setI(Math.round(el.scrollLeft / Math.max(1, el.clientWidth))); }}>
          {photos.length ? photos.map((p, k) => (
            <div className="slide" key={k} role="button" tabIndex={-1} style={{ aspectRatio: String(ratio) }} onClick={() => setOpen(k)} aria-label={t("photo_of", { a: k + 1, b: n })}>
              <span className="blurbg" style={{ backgroundImage: `url(${p})` }} aria-hidden="true" />
              <img src={p} alt={`${alt} ${k + 1}`} onLoad={k === 0 ? onFirstLoad : undefined} />
            </div>
          )) : <div className="slide cover"><Cover x={x} /></div>}
        </div>
        <div className="galtags">
          {x.status === "sold" && <span className="gtag sold">{t("sold")}</span>}
          {x.cond && <span className="gtag">{t("cond_" + x.cond)}</span>}
        </div>
        <div className="galtools">
          <button className={"rnd" + (sv ? " on" : "")} onClick={onSave} aria-pressed={!!sv} aria-label={t(sv ? "saved_btn" : "save")}><Ico n="heart" /></button>
          <button className="rnd" onClick={onShare} aria-label={t("share")}><Ico n="share" /></button>
        </div>
        {photos.length > 0 && <span className="expand" aria-hidden="true"><Ico n="expand" /></span>}
        {n > 1 && <>
          <button className="nav p" onClick={() => to(Math.max(0, i - 1))} aria-label={t("prev")}><Ico n="back" /></button>
          <button className="nav n" onClick={() => to(Math.min(n - 1, i + 1))} aria-label={t("nextimg")}><Ico n="right" /></button>
          <span className="counter">{i + 1}/{n}</span>
        </>}
      </div>
      {n > 1 && <div className="thumbs">{photos.map((p, k) => (
        <button key={k} className={k === i ? "on" : ""} onClick={() => to(k)} aria-label={t("photo_of", { a: k + 1, b: n })}><img src={p} alt="" /></button>
      ))}</div>}
      {open >= 0 && <Lightbox photos={photos} start={open} alt={alt} t={t} onClose={(k) => { setOpen(-1); if (k !== i) setTimeout(() => to(k), 0); }} />}
    </div>
  );
}

/* Full-screen photo viewer: every photo at its full size and true shape,
   swipe / arrows / keyboard to move, pinch to zoom on phones, Esc or ✕ to close. */
function Lightbox({ photos, start, alt, t, onClose }) {
  const track = useRef(null); const [i, setI] = useState(start);
  const n = photos.length;
  const to = (k, smooth = true) => { const el = track.current; if (el) el.scrollTo({ left: el.clientWidth * k, behavior: smooth ? "smooth" : "auto" }); };
  useEffect(() => {
    to(start, false);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose(iRef.current);
      if (e.key === "ArrowLeft") to(Math.max(0, iRef.current - 1));
      if (e.key === "ArrowRight") to(Math.min(n - 1, iRef.current + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const iRef = useRef(start); iRef.current = i;
  return (
    <div className="pviewer" role="dialog" aria-modal="true" aria-label={alt}>
      <div className="lbtrack" ref={track} onScroll={(e) => { const el = e.currentTarget; setI(Math.round(el.scrollLeft / Math.max(1, el.clientWidth))); }}>
        {photos.map((p, k) => (
          <div className="lbslide" key={k} onClick={(e) => { if (e.target === e.currentTarget) onClose(i); }}>
            <img src={p} alt={`${alt} ${k + 1}`} />
          </div>
        ))}
      </div>
      <div className="lbtop">
        <span className="lbcount">{n > 1 ? `${i + 1} / ${n}` : ""}</span>
        <button className="lbclose" onClick={() => onClose(i)} aria-label={t("close")}><Ico n="close" /></button>
      </div>
      {n > 1 && <>
        <button className="lbnav p" disabled={i === 0} onClick={() => to(i - 1)} aria-label={t("prev")}><Ico n="back" /></button>
        <button className="lbnav n" disabled={i === n - 1} onClick={() => to(i + 1)} aria-label={t("nextimg")}><Ico n="right" /></button>
        <div className="lbthumbs">{photos.map((p, k) => (
          <button key={k} className={k === i ? "on" : ""} onClick={() => to(k)} aria-label={t("photo_of", { a: k + 1, b: n })}><img src={p} alt="" /></button>
        ))}</div>
      </>}
    </div>
  );
}

/* ─── seller / buyer trust card ─────────────────────────────────────────── */
function TrustCard({ app, personKey, name, avatar, useLogo, city, since, verified, listingId, role, founding }) {
  const { t, lang } = app;
  const { avg, count } = Store.rating(personKey);
  const myProfile = personKey && personKey === Store.me ? Store.profile() : null;
  const pic = avatar || (myProfile && myProfile.avatar) || null;
  const logo = useLogo || (myProfile && myProfile.useLogo) || false;
  const place = city || (myProfile ? [myProfile.city, myProfile.state].filter(Boolean).join(", ") : "");
  const canReview = !!app.user && personKey && personKey !== Store.me && Store.dealtWith(personKey);
  const mineAlready = canReview && Store.myReviewFor(personKey);
  return (
    <div className="trust">
      <Avatar src={pic} useLogo={logo} name={name} size={52} />
      <div className="tinfo">
        <b className="tname">{name || "—"}{founding && <FoundingMark app={app} />}</b>
        <div className="trow">
          {count ? <><Stars v={avg} /><small>{avg.toFixed(1)} · {count} {t(count === 1 ? "review_one" : "reviews")}</small></>
            : <small className="muted">{t("no_reviews")}</small>}
        </div>
        <div className="tmeta">
          {place && <span><Ico n="pin" />{place}</span>}
          {since ? <span><Ico n="clock" />{t("member_since", { d: shortDate(since, lang) })}</span> : null}
          {verified && <span className="ok"><Ico n="shield" />{t("verified_phone")}</span>}
        </div>
      </div>
      <div className="tacts">
        {count > 0 && <button className="btn sm" onClick={() => app.setSheet({ type: "reviews", personKey, name })}>{t("see_reviews")}</button>}
        {canReview && <button className="btn sm gold" onClick={() => app.setSheet({ type: "review", about: personKey, aboutName: name, listingId, role })}>
          <Ico n="sparkle" />{mineAlready ? t("write_review") : t(role === "buyer" ? "rate_buyer" : "rate_seller")}</button>}
      </div>
    </div>
  );
}

function ItemView({ app }) {
  const { t, F, route, saved, toggleSave, loc } = app;
  useEffect(() => { Store.fetchDetail(route.id); }, [route.id]);
  const x = Store.get(route.id);
  if (!x && Store.detail[route.id] === "loading") return (
    <main><MBar app={app} title="" /><div className="empty"><div className="spin" aria-hidden="true" /><p>{t("loading")}</p></div></main>
  );
  if (!x && Store.detail[route.id] === "error") return (
    <main><MBar app={app} title="" /><div className="empty"><div className="eic"><Ico n="clock" /></div><h3>{t("api_down")}</h3><p />
      <div className="acts"><button className="btn pri" onClick={() => Store.fetchDetail(route.id)}>{t("retry")}</button></div></div></main>
  );
  if (!x) return (
    <main><MBar app={app} title="" />
      <div className="empty"><div className="eic"><Ico n="search" /></div><h3>{t("not_found")}</h3><p />
        <div className="acts"><a className="btn pri" href="#/">{t("back_home")}</a></div></div></main>
  );
  const sv = saved.has(x.id), mine = x.ownerId === Store.me, d = F.dist(x);
  const share = async () => {
    const url = window.location.href;
    try { if (navigator.share) { await navigator.share({ title: F.L(x, "title"), url }); return; } } catch (e) { if (e && e.name === "AbortError") return; }
    try { await navigator.clipboard.writeText(url); app.toast(t("copied")); } catch (e) { app.toast(url); }
  };
  const primary = x.isExample
    ? <a className="btn gold" href="#/join"><Ico n="plus" />{nt(app, "example_cta")}</a>
    : mine
    ? <a className="btn pri" href="#/me">{t("own_listing")}</a>
    : <button className="btn pri" disabled={x.status === "sold" || app.opening} onClick={() => app.openChat(x)}><Ico n="chat" />{app.opening ? t("loading") : t("msg_seller")}</button>;
  const contact = x.cat === "carry" || x.isExample ? null : <>
    {x.line && waLink(x.line) && <a className="btn line" href={waLink(x.line)} target="_blank" rel="noopener noreferrer">WhatsApp</a>}
    {x.phone && <a className="btn" href={telHref(x.phone)}><Ico n="phone" />{t("call")}</a>}
  </>;
  const rel = Store.all().filter((y) => y.id !== x.id && y.cat === x.cat && y.status !== "sold")
    .sort((a, b) => loc.mode === "zip" ? (F.dist(a) ?? 1e9) - (F.dist(b) ?? 1e9) : ((a.sub === x.sub ? 0 : 1) - (b.sub === x.sub ? 0 : 1) || b.createdAt - a.createdAt))
    .slice(0, 5);
  const goCat = (e, c) => { e.preventDefault(); app.setCat(c); app.setSub(""); app.go("/"); };
  return (
    <main>
      <MBar app={app} title={F.catName(x.cat)}>
        <button className="ibtn" onClick={(e) => toggleSave(e, x.id)} aria-pressed={sv} aria-label={t(sv ? "saved_btn" : "save")} style={sv ? { color: "var(--gold-ink)" } : undefined}><Ico n="heart" /></button>
        <button className="ibtn" onClick={share} aria-label={t("share")}><Ico n="share" /></button>
      </MBar>
      <nav className="crumbs" aria-label="breadcrumb">
        <a href="#/" onClick={(e) => goCat(e, "all")}>{t("nav_home")}</a><Ico n="right" />
        <a href="#/" onClick={(e) => goCat(e, x.cat)}>{F.catName(x.cat)}</a><Ico n="right" /><span>{F.subName(x.cat, x.sub)}</span>
      </nav>
      <div className="detail">
        <div className="galcol">
          {x.cat === "carry" && !x.photos.length ? null : <Gallery x={x} alt={F.L(x, "title")} t={t} sv={sv} onSave={(e) => toggleSave(e, x.id)} onShare={share} />}
          {(x.cat !== "carry" || x.desc) && <section className="box desc">
            <h3>{t("details")}</h3>
            <p>{F.L(x, "desc")}</p>
          </section>}
        </div>
        <div className="info">
          <div className="cat">{F.catName(x.cat)}, {F.subName(x.cat, x.sub)}</div>
          <h1>{F.L(x, "title")}</h1>
          <div className="bigprice">{F.priceText(x)}{x.neg && <small>{t("obo")}</small>}
            {x.status === "sold" && <small style={{ color: "var(--danger)", background: "color-mix(in srgb,var(--danger) 12%,transparent)" }}>{t("sold")}</small>}</div>
          {x.isExample && <div className="exbanner"><Ico n="flag" /><span>{nt(app, "example_b")}</span></div>}
          {x.trip && <TripPanel app={app} x={x} />}
          {META_CATS.includes(x.cat) && <MetaPanel app={app} x={x} />}
          <div className="facts">
            <span><Ico n="pin" />{F.place(x)}{d != null ? ` (${F.distText(d)})` : ""}</span>
            <span><Ico n="clock" />{t("posted", { t: F.ago(x.createdAt) })}</span>
            {x.cond && <span><Ico n="sparkle" />{t("cond_" + x.cond)}</span>}
          </div>
          <div className="acts desk">{primary}{contact}
            <button className="btn" onClick={(e) => toggleSave(e, x.id)} aria-pressed={sv}><Ico n="heart" />{t(sv ? "saved_btn" : "save")}</button>
            <button className="btn" onClick={share} aria-label={t("share")}><Ico n="share" /></button>
          </div>
          <div className="box trustbox"><h3>{t("seller")}</h3>
            <SellerBadges app={app} x={x} />
            <TrustCard app={app} founding={!!x.sellerFounding} personKey={x.ownerId} name={x.sellerName} avatar={x.sellerAvatar} useLogo={x.sellerLogo}
              city={x.sellerCity || F.place(x)} since={x.sellerSince} verified={x.sellerVerified}
              listingId={x.id} role="seller" />
            {!app.user && <p className="hint" style={{ margin: "10px 0 0" }}>{t("trust_tip")}</p>}
          </div>
          <div className="box safety"><h3><Ico n="shield" />{t("safety_t")}</h3><p>{t("safety_" + (CAT[x.cat].safety || "market"))}</p></div>
          {!mine && <button className="linkish" onClick={() => app.setSheet({ type: "report", listing: x })}><Ico n="flag" />{t("report")}</button>}
        </div>
      </div>
      {rel.length > 0 && <><div className="sec-h"><h2>{t("related")}</h2></div><div className="grid">{rel.map((y) => <Card key={y.id} app={app} x={y} />)}</div></>}
      <div className="actbar" role="region" aria-label={t("msg_seller")}>{primary}{contact}</div>
    </main>
  );
}

/* ─── post flow ─────────────────────────────────────────────────────────── */
const newDraft = (loc, name) => ({ step: 0, photos: [], cat: "", sub: "", title: "", desc: "", cond: "good", price: "", unit: "fixed", neg: false, meta: {},
  zip: loc.mode === "zip" ? loc.zip : "", name: name || "", line: "", phone: "" });
function PostView({ app }) {
  const { t, F, loc, lang } = app;
  const [D, setD] = useState(() => {
    const d = LS.get("ac_draft", null);
    if (d && typeof d === "object") return { ...newDraft(loc, app.name), ...d, photos: (d.photos || []).map(safeImg).filter(Boolean) };
    return newDraft(loc, app.name);
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const steps = t("steps");
  useEffect(() => { if (!LS.set("ac_draft", D)) LS.set("ac_draft", { ...D, photos: [] }); }, [D]);
  const up = (patch) => { setD((d) => ({ ...d, ...patch })); setErr(""); };
  const priced = ["fixed", "month", "hour"].includes(D.unit);
  const units = D.cat === "housing" ? ["month", "fixed", "ask"] : D.cat === "jobs" ? ["hour", "month", "fixed", "ask"] : ["fixed", "free", "ask"];
  useEffect(() => { if (D.step === 3 && !units.includes(D.unit)) up({ unit: units[0] }); }); // eslint-disable-line
  useEffect(() => { if (D.step === 4 && !D.name && app.name) up({ name: app.name }); }, [D.step]); // eslint-disable-line
  const z = zipInfo(D.zip);

  const validate = () => {
    if (D.step === 1 && (!D.cat || !D.sub)) return t("pick_topic");
    if (D.step === 2) { if (D.title.trim().length < 5) return t("err_title"); if (D.desc.trim().length < 10) return t("err_desc"); const de = detailsError(app, D); if (de) return de; }
    if (D.step === 3) {
      if (priced) { const p = parseFloat(String(D.price).replace(/[,$\s]/g, "")); if (!isFinite(p) || p < 0 || p > 99999999) return t("err_price"); }
      if (!z) return t("err_zip");
    }
    if (D.step === 4 && !D.name.trim()) return t("err_name");
    return "";
  };
  const publish = async () => {
    if (busy) return;
    const de = detailsError(app, D); if (de) { setErr(de); return; }
    setBusy(true);
    const data = { cat: D.cat, sub: D.sub, title: D.title.trim().slice(0, 90), desc: D.desc.trim().slice(0, 3000),
      price: priced ? Math.round(parseFloat(String(D.price).replace(/[,$\s]/g, "")) * 100) / 100 : D.unit === "free" ? 0 : null,
      unit: D.unit, neg: priced && !!D.neg, cond: D.cat === "marketplace" ? D.cond : null, zip: D.zip, city: z?.city, state: z?.state,
      photos: D.photos, sellerName: D.name.trim().slice(0, 40), line: D.line.trim().replace(/^@/, "").slice(0, 40), phone: D.phone.trim().slice(0, 24) };
    if (META_CATS.includes(D.cat)) data.meta = D.meta || {};
    try {
      if (D.photos[0]) data.thumb = await makeThumb(D.photos[0]);
      if (JSON.stringify(data).length > 4200000) throw new Error("big");
      const id = await Store.addListing(data);
      app.setName(data.sellerName); LS.del("ac_draft");
      app.toast(t("published")); window.location.replace("#/item/" + encodeURIComponent(id));
    } catch (e) {
      setBusy(false);
      if (e && e.status === 401) { app.requireAuth("/post"); return; }
      setErr(e && /^event_/.test(e.message || "") ? nt(app, "err_" + e.message) : e && e.message === "big" ? t("err_big") : e && e.code === "network_error" ? t("api_down") : t("err_save"));
    }
  };
  const next = () => { const e = validate(); if (e) { setErr(e); return; } if (D.step === 4) publish(); else { up({ step: D.step + 1 }); window.scrollTo(0, 0); } };
  const addPhotos = async (files) => {
    setErr(t("photo_proc"));
    const list = Array.from(files || []).slice(0, 6 - D.photos.length), outp = [];
    let bad = false;
    for (const f of list) { try { outp.push(await compress(f)); } catch (e) { bad = true; } }
    setD((d) => {
      const photos = [...d.photos, ...outp].slice(0, 6);
      let big = false;
      while (photos.reduce((a, p) => a + p.length, 0) > 3600000 && photos.length) { photos.pop(); big = true; }
      setErr(big ? t("err_big") : bad ? t("err_photo") : "");
      return { ...d, photos };
    });
  };
  const fake = { ...D, price: parseFloat(D.price), createdAt: Date.now(), city: z?.city, state: z?.state };
  if (!isFinite(fake.price)) fake.price = null;

  return (
    <main><div className="wrap-narrow">
      <MBar app={app} title={t("post_h")} />
      <div className="steps" aria-hidden="true">{steps.map((_, i) => <i key={i} className={i <= D.step ? "on" : ""} />)}</div>
      <div className="stepname"><div>{t("step_of", { a: D.step + 1, b: steps.length })}<b>{steps[D.step]}</b></div>
        <button className="btn sm" onClick={() => { LS.del("ac_draft"); setD(newDraft(loc, app.name)); setErr(""); }}>{t("discard")}</button></div>

      <div className="card-pad">
        {D.step === 0 && <>
          <p className="hint" style={{ margin: "0 0 14px" }}>{t("photos_hint")}</p>
          <div className="pgrid">
            {D.photos.map((p, i) => (
              <div className="pslot" key={i}><img src={p} alt="" />
                <button className="x" onClick={() => up({ photos: D.photos.filter((_, k) => k !== i) })} aria-label={t("remove")}><Ico n="close" /></button>
                {i === 0 && <span className="cvtag">{t("cover")}</span>}</div>
            ))}
            {D.photos.length < 6 && (
              <label className="padd" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.currentTarget.querySelector("input").click(); } }}>
                <Ico n="camera" /><span>{t("add_photo")}</span>
                <input type="file" accept="image/*" multiple className="sr" onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
              </label>
            )}
          </div>
          <p className="hint" style={{ marginTop: 14 }}>{t("photos_skip")}</p>
        </>}

        {D.step === 1 && <>
          <div className="field"><span>{t("pick_topic")}</span><div className="topics">
            {CATS.filter((c) => c.id !== "all").map((c) => (
              <button key={c.id} className={"topic" + (D.cat === c.id ? " on" : "")} onClick={() => { if (c.id === "carry") { app.go("/carry/new"); return; } up({ cat: c.id, sub: "", meta: {} }); }}><span className="ic"><Ico n={c.icon} /></span>{c[lang]}</button>
            ))}</div></div>
          {CAT[D.cat] && <div className="field" style={{ margin: 0 }}><span>{t("pick_sub")}</span><div className="seg">
            {CAT[D.cat].subs.map((s) => <button key={s[0]} className={D.sub === s[0] ? "on" : ""} onClick={() => up({ sub: s[0] })}>{lang === "en" ? s[2] : s[1]}</button>)}
          </div></div>}
        </>}

        {D.step === 2 && <>
          <label className="field"><span>{t("f_title")}</span><input className="inp" maxLength={90} value={D.title} placeholder={t("f_title_ph")} onChange={(e) => up({ title: e.target.value })} /></label>
          <label className="field"><span>{t("f_desc")}</span><textarea className="inp" maxLength={3000} value={D.desc} placeholder={t("f_desc_ph")} onChange={(e) => up({ desc: e.target.value })} /></label>
          <DetailsFields app={app} D={D} up={up} />
          {D.cat === "marketplace" && <div className="field" style={{ margin: 0 }}><span>{t("f_cond")}</span><div className="seg">
            {["new", "likenew", "good", "fair"].map((c) => <button key={c} className={D.cond === c ? "on" : ""} onClick={() => up({ cond: c })}>{t("cond_" + c)}</button>)}
          </div></div>}
        </>}

        {D.step === 3 && <>
          <div className="field"><span>{t("f_unit")}</span><div className="seg">
            {units.map((u) => <button key={u} className={D.unit === u ? "on" : ""} onClick={() => up({ unit: u })}>{t("unit_" + u)}</button>)}</div></div>
          {priced && <>
            <label className="field"><span>{t("f_price")}</span><input className="inp" inputMode="decimal" maxLength={10} value={D.price} placeholder="0" onChange={(e) => up({ price: e.target.value.replace(/[^\d.,]/g, "") })} /></label>
            <label className="check"><input type="checkbox" checked={D.neg} onChange={(e) => up({ neg: e.target.checked })} />{t("f_neg")}</label>
          </>}
          <label className="field"><span>{t("f_zip")}</span>
            <input className="inp" inputMode="numeric" maxLength={5} value={D.zip} placeholder={t("loc_zip_ph")} onChange={(e) => up({ zip: e.target.value.replace(/\D/g, "").slice(0, 5) })} />
            <div className={"hint " + (z ? "ok" : D.zip.length === 5 ? "err" : "")}>{z ? `${z.city}, ${z.state}` : D.zip.length === 5 ? t("loc_zip_bad") : ""}</div></label>
        </>}

        {D.step === 4 && <>
          <label className="field"><span>{t("f_name")}</span><input className="inp" maxLength={40} value={D.name} placeholder={t("f_name_ph")} onChange={(e) => up({ name: e.target.value })} /></label>
          <div className="row2">
            <label className="field"><span>{t("f_line")}</span><input className="inp" maxLength={20} inputMode="tel" autoCapitalize="off" placeholder="+91 98xxx xxxxx / (469) xxx-xxxx" value={D.line} onChange={(e) => up({ line: e.target.value })} /></label>
            <label className="field"><span>{t("f_phone")}</span><input className="inp" type="tel" maxLength={24} value={D.phone} onChange={(e) => up({ phone: e.target.value })} /></label>
          </div>
          <p className="hint" style={{ margin: "-4px 0 18px" }}>{t("contact_hint")}</p>
          <div className="field" style={{ margin: 0 }}><span>{t("preview")}</span>
            <div className="review"><div className="ph"><Thumb x={fake} /></div>
              <dl><dt>{t("f_title")}</dt><dd><b>{D.title}</b></dd>
                <dt>{t("f_price")}</dt><dd>{F.priceText(fake)}{D.neg && priced ? ` (${t("obo")})` : ""}</dd>
                <dt>{t("categories")}</dt><dd>{F.catName(D.cat)}, {F.subName(D.cat, D.sub)}</dd>
                <dt>{t("f_zip")}</dt><dd>{z ? `${z.city}, ${z.state} ${z.zip}` : D.zip}</dd></dl></div></div>
        </>}
      </div>
      <div className="hint err" role="alert">{err}</div>
      <div className="pfoot">
        {D.step > 0 ? <button className="btn" onClick={() => { up({ step: D.step - 1 }); }}><Ico n="back" />{t("back")}</button> : <span />}
        <button className="btn pri" disabled={busy} onClick={next}>{busy ? t("publishing") : D.step === 4 ? t("publish") : t("next")}</button>
      </div>
    </div></main>
  );
}

/* ─── "Most needed": roommates, tiffin, visa jobs, flight companion, events, scam alerts ─── */
const NT = {
  en: {
    hub_h: "Most needed in our community", hub_sub: "What Indians in the U.S. look for every day",
    need_all: "Any", f_diet_pref: "Food at home", diet_any: "Any", diet_veg: "Veg only", diet_nonveg: "Non-veg OK",
    f_gender: "Looking for", g_any: "Anyone", g_female: "Female", g_male: "Male", g_family: "Family",
    f_move_in: "Move-in date", f_lease: "Lease (months)", lease_flex: "Flexible", f_near: "Near (university or company)", f_near_ph: "e.g. UT Dallas, Toyota HQ Plano",
    f_furnished: "Furnished", f_langs: "Languages spoken", langs_hint: "Helps people find someone who speaks their language",
    f_visa: "Visa sponsorship", f_job_type: "Job type", jt_full_time: "Full-time", jt_part_time: "Part-time", jt_contract: "Contract", jt_internship: "Internship",
    f_workplace: "Workplace", wp_onsite: "On-site", wp_hybrid: "Hybrid", wp_remote: "Remote", f_pay: "Pay", f_pay_ph: "e.g. $45/hr, $95k/yr",
    job_warn: "Never pay for a job, training, or your own H-1B filing. Report any listing that asks you for money.",
    f_diets: "Food type", f_zones: "Delivery area", f_zones_ph: "e.g. Irving, Coppell, Las Colinas", f_weekly: "Weekly price (USD)", f_meals: "Meals per week", f_trial: "Trial meal available",
    f_start: "Starts", f_end: "Ends", f_venue: "Venue", f_venue_ph: "e.g. Karya Siddhi Hanuman Temple", f_address: "Address", f_festival: "Festival", fest_none: "Not a festival event",
    err_event_start: "Add the date and time of the event", err_event_end: "The end must be after the start", err_event_venue: "Add the venue",
    move_in_on: "Move in {d}", lease_n: "{n}-month lease", near_x: "Near {x}", furnished: "Furnished", trial: "Trial meal",
    meals_n: "{n} meals/week", delivers: "Delivers to {x}", at_venue: "At {x}",
    companion: "Can accompany parents", companion_b: "I can help an elderly or first-time flyer on my flight (boarding, transit, immigration forms)",
    f_flight: "Flight number", f_flight_ph: "e.g. AI 105", companion_h: "Flight companion for parents",
    companion_sub: "Parents flying from India alone? Find a traveller on the same route who can help with boarding, transit and forms. Message them first, meet on video, and share the flight details only once you're comfortable.",
    companion_only: "Companions only", need_companion: "Need a companion? Post the route in Community, or message a traveller below.",
    fest_h: "Festival calendar", fest_sub: "Festivals from every region. Dates follow the lunar calendar, so each event shows its own date.",
    upcoming: "Upcoming events", no_events: "No upcoming events here yet", post_event: "Post an event", post_here: "Post in {c}",
    safety_h: "Scam alerts", safety_sub: "Scams that often target Indians in the U.S., and how to stay safe.", how_safe: "How to stay safe",
    report_h: "Report a scam", report_b: "If you lost money or were threatened, also contact your local police.",
    see_all: "See all", results_n: "{n} results",
  },
  hi: {
    hub_h: "हमारे समुदाय में सबसे ज़्यादा ज़रूरी", hub_sub: "अमेरिका में भारतीय रोज़ जो ढूँढते हैं",
    need_all: "कोई भी", f_diet_pref: "घर में खाना", diet_any: "कोई भी", diet_veg: "सिर्फ़ शाकाहारी", diet_nonveg: "मांसाहारी चलेगा",
    f_gender: "किसकी तलाश", g_any: "कोई भी", g_female: "महिला", g_male: "पुरुष", g_family: "परिवार",
    f_move_in: "आने की तारीख", f_lease: "लीज़ (महीने)", lease_flex: "लचीला", f_near: "किसके पास (यूनिवर्सिटी या कंपनी)", f_near_ph: "जैसे UT Dallas, Toyota HQ Plano",
    f_furnished: "फ़र्निश्ड", f_langs: "बोली जाने वाली भाषाएँ", langs_hint: "इससे लोग अपनी भाषा बोलने वाले को ढूँढ पाते हैं",
    f_visa: "वीज़ा स्पॉन्सरशिप", f_job_type: "नौकरी का प्रकार", jt_full_time: "फ़ुल-टाइम", jt_part_time: "पार्ट-टाइम", jt_contract: "कॉन्ट्रैक्ट", jt_internship: "इंटर्नशिप",
    f_workplace: "काम की जगह", wp_onsite: "ऑफ़िस से", wp_hybrid: "हाइब्रिड", wp_remote: "रिमोट", f_pay: "वेतन", f_pay_ph: "जैसे $45/घंटा, $95k/साल",
    job_warn: "नौकरी, ट्रेनिंग या अपनी H-1B फ़ाइलिंग के लिए कभी पैसे न दें। पैसे माँगने वाली हर लिस्टिंग की रिपोर्ट करें।",
    f_diets: "खाने का प्रकार", f_zones: "डिलीवरी का इलाका", f_zones_ph: "जैसे Irving, Coppell, Las Colinas", f_weekly: "साप्ताहिक कीमत (USD)", f_meals: "हफ़्ते में कितने भोजन", f_trial: "ट्रायल मील उपलब्ध",
    f_start: "शुरू", f_end: "खत्म", f_venue: "जगह", f_venue_ph: "जैसे कार्य सिद्धि हनुमान मंदिर", f_address: "पता", f_festival: "त्योहार", fest_none: "त्योहार का इवेंट नहीं",
    err_event_start: "इवेंट की तारीख और समय जोड़ें", err_event_end: "खत्म होने का समय शुरू के बाद होना चाहिए", err_event_venue: "जगह जोड़ें",
    move_in_on: "{d} से रह सकते हैं", lease_n: "{n} महीने की लीज़", near_x: "{x} के पास", furnished: "फ़र्निश्ड", trial: "ट्रायल मील",
    meals_n: "हफ़्ते में {n} भोजन", delivers: "{x} में डिलीवरी", at_venue: "{x} में",
    companion: "माता-पिता के साथ चल सकते हैं", companion_b: "मैं अपनी फ़्लाइट में किसी बुज़ुर्ग या पहली बार उड़ने वाले की मदद कर सकता/सकती हूँ (बोर्डिंग, ट्रांज़िट, इमिग्रेशन फ़ॉर्म)",
    f_flight: "फ़्लाइट नंबर", f_flight_ph: "जैसे AI 105", companion_h: "माता-पिता के लिए यात्रा साथी",
    companion_sub: "माता-पिता भारत से अकेले आ रहे हैं? उसी रूट पर ऐसा यात्री ढूँढें जो बोर्डिंग, ट्रांज़िट और फ़ॉर्म में मदद कर सके। पहले मैसेज करें, वीडियो पर मिलें, और भरोसा होने पर ही फ़्लाइट की जानकारी साझा करें।",
    companion_only: "सिर्फ़ यात्रा साथी", need_companion: "साथी चाहिए? समुदाय में अपना रूट पोस्ट करें, या नीचे किसी यात्री को मैसेज करें।",
    fest_h: "त्योहारों का कैलेंडर", fest_sub: "हर क्षेत्र के त्योहार। तारीखें चाँद के कैलेंडर से बदलती हैं, इसलिए हर इवेंट पर उसकी अपनी तारीख दिखती है।",
    upcoming: "आने वाले इवेंट्स", no_events: "यहाँ अभी कोई आने वाला इवेंट नहीं", post_event: "इवेंट पोस्ट करें", post_here: "{c} में पोस्ट करें",
    safety_h: "धोखाधड़ी से सावधान", safety_sub: "अमेरिका में भारतीयों के साथ होने वाले आम धोखे, और उनसे कैसे बचें।", how_safe: "कैसे बचें",
    report_h: "धोखे की रिपोर्ट करें", report_b: "अगर पैसे गए हैं या धमकी मिली है, तो अपनी लोकल पुलिस से भी संपर्क करें।",
    see_all: "सब देखें", results_n: "{n} नतीजे",
  },
};
const nt = (app, k, vars) => {
  let s = NT[app.lang === "en" ? "en" : "hi"][k] ?? k;
  if (vars) Object.keys(vars).forEach((v) => { s = s.replace("{" + v + "}", vars[v]); });
  return s;
};
const META_CATS = ["housing", "jobs", "food", "events", "eldercare"];
const optLabel = (arr, id, lang) => { const o = arr.find((a) => a[0] === id); return o ? (lang === "en" ? o[1] : o[2]) : id; };
const langName = (code) => (LANG_OPTS.find((l) => l[0] === code) || [code, code])[1];
const fmtEventTime = (iso, lang) => {
  const d = new Date(String(iso || ""));
  return isNaN(d) ? "" : d.toLocaleString(lang === "hi" ? "hi-IN" : "en-US", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
};

function Chips({ opts, value, onChange, multi, lang, cls = "" }) {
  const on = (id) => (multi ? (value || []).includes(id) : value === id);
  const click = (id) => (multi ? onChange(on(id) ? value.filter((v) => v !== id) : [...(value || []), id]) : onChange(id));
  return <div className={"chips " + cls}>{opts.map(([id, en, hi]) => (
    <button key={id} type="button" className={"chip" + (on(id) ? " on" : "")} aria-pressed={on(id)} onClick={() => click(id)}>{lang === "en" ? en : hi}</button>
  ))}</div>;
}
function LangChips({ value, onChange }) {
  return <div className="chips langchips">{LANG_OPTS.map(([code, native, en]) => {
    const on = (value || []).includes(code);
    return <button key={code} type="button" className={"chip" + (on ? " on" : "")} aria-pressed={on} title={en}
      onClick={() => onChange(on ? value.filter((v) => v !== code) : [...(value || []), code].slice(0, 6))}>{native}</button>;
  })}</div>;
}

/* Extra fields in the post form, by category */
function DetailsFields({ app, D, up }) {
  const { lang } = app, m = D.meta || {};
  const upM = (patch) => up({ meta: { ...m, ...patch } });
  const langs = <div className="field"><span>{nt(app, "f_langs")} <em>{app.t("optional")}</em></span><LangChips value={m.langs} onChange={(v) => upM({ langs: v })} /><small className="zipok">{nt(app, "langs_hint")}</small></div>;
  if (D.cat === "housing") return <div className="detailbox">
    <div className="field"><span>{nt(app, "f_diet_pref")}</span><Chips lang={lang} value={m.diet || "any"} onChange={(v) => upM({ diet: v })} opts={[["any", "Any", "कोई भी"], ["veg", "Veg only", "सिर्फ़ शाकाहारी"], ["nonveg", "Non-veg OK", "मांसाहारी चलेगा"]]} /></div>
    <div className="field"><span>{nt(app, "f_gender")}</span><Chips lang={lang} value={m.gender || "any"} onChange={(v) => upM({ gender: v })} opts={[["any", "Anyone", "कोई भी"], ["female", "Female", "महिला"], ["male", "Male", "पुरुष"], ["family", "Family", "परिवार"]]} /></div>
    <div className="two">
      <label className="field"><span>{nt(app, "f_move_in")}</span><input className="inp" type="date" value={m.move_in || ""} onChange={(e) => upM({ move_in: e.target.value })} /></label>
      <label className="field"><span>{nt(app, "f_lease")}</span><input className="inp" inputMode="numeric" maxLength={2} placeholder={nt(app, "lease_flex")} value={m.lease_months ?? ""} onChange={(e) => upM({ lease_months: e.target.value.replace(/\D/g, "") })} /></label>
    </div>
    <label className="field"><span>{nt(app, "f_near")}</span><input className="inp" maxLength={80} placeholder={nt(app, "f_near_ph")} value={m.near || ""} onChange={(e) => upM({ near: e.target.value })} /></label>
    <label className="check"><input type="checkbox" checked={!!m.furnished} onChange={(e) => upM({ furnished: e.target.checked })} />{nt(app, "f_furnished")}</label>
    {langs}
  </div>;
  if (D.cat === "jobs") return <div className="detailbox">
    <div className="field"><span>{nt(app, "f_visa")}</span><Chips multi lang={lang} value={m.visa || []} onChange={(v) => upM({ visa: v })} opts={VISAS} /></div>
    <div className="field"><span>{nt(app, "f_job_type")}</span><Chips lang={lang} value={m.job_type || "full_time"} onChange={(v) => upM({ job_type: v })} opts={["full_time", "part_time", "contract", "internship"].map((k) => [k, NT.en["jt_" + k], NT.hi["jt_" + k]])} /></div>
    <div className="field"><span>{nt(app, "f_workplace")}</span><Chips lang={lang} value={m.workplace || "onsite"} onChange={(v) => upM({ workplace: v })} opts={["onsite", "hybrid", "remote"].map((k) => [k, NT.en["wp_" + k], NT.hi["wp_" + k]])} /></div>
    <label className="field"><span>{nt(app, "f_pay")} <em>{app.t("optional")}</em></span><input className="inp" maxLength={40} placeholder={nt(app, "f_pay_ph")} value={m.pay || ""} onChange={(e) => upM({ pay: e.target.value })} /></label>
    {langs}
    <div className="warnbox"><Ico n="shield" /><span>{nt(app, "job_warn")}</span></div>
  </div>;
  if (D.cat === "food") return <div className="detailbox">
    <div className="field"><span>{nt(app, "f_diets")}</span><Chips multi lang={lang} value={m.diets || []} onChange={(v) => upM({ diets: v })} opts={DIETS} /></div>
    <label className="field"><span>{nt(app, "f_zones")}</span><input className="inp" maxLength={160} placeholder={nt(app, "f_zones_ph")} value={m.zones || ""} onChange={(e) => upM({ zones: e.target.value })} /></label>
    <div className="two">
      <label className="field"><span>{nt(app, "f_weekly")}</span><input className="inp" inputMode="decimal" maxLength={6} value={m.weekly_price ?? ""} onChange={(e) => upM({ weekly_price: e.target.value.replace(/[^\d.]/g, "") })} /></label>
      <label className="field"><span>{nt(app, "f_meals")}</span><input className="inp" inputMode="numeric" maxLength={2} value={m.meals_per_week ?? ""} onChange={(e) => upM({ meals_per_week: e.target.value.replace(/\D/g, "") })} /></label>
    </div>
    <label className="check"><input type="checkbox" checked={!!m.trial} onChange={(e) => upM({ trial: e.target.checked })} />{nt(app, "f_trial")}</label>
    {langs}
  </div>;
  if (D.cat === "events") return <div className="detailbox">
    <div className="two">
      <label className="field"><span>{nt(app, "f_start")}</span><input className="inp" type="datetime-local" value={m.start || ""} onChange={(e) => upM({ start: e.target.value })} /></label>
      <label className="field"><span>{nt(app, "f_end")} <em>{app.t("optional")}</em></span><input className="inp" type="datetime-local" value={m.end || ""} onChange={(e) => upM({ end: e.target.value })} /></label>
    </div>
    <label className="field"><span>{nt(app, "f_venue")}</span><input className="inp" maxLength={80} placeholder={nt(app, "f_venue_ph")} value={m.venue || ""} onChange={(e) => upM({ venue: e.target.value })} /></label>
    <label className="field"><span>{nt(app, "f_address")} <em>{app.t("optional")}</em></span><input className="inp" maxLength={140} value={m.address || ""} onChange={(e) => upM({ address: e.target.value })} /></label>
    <label className="field"><span>{nt(app, "f_festival")}</span><select className="inp" value={m.festival || ""} onChange={(e) => upM({ festival: e.target.value || null })}>
      <option value="">{nt(app, "fest_none")}</option>
      {FESTIVALS.map((f) => <option key={f[0]} value={f[0]}>{(lang === "en" ? f[1] : f[2]) + (f[3] !== f[1] && f[3] !== f[2] ? " · " + f[3] : "")}</option>)}
    </select></label>
    {langs}
  </div>;
  return null;
}
function detailsError(app, D) {
  if (D.cat !== "events") return "";
  const m = D.meta || {};
  if (!m.start) return nt(app, "err_event_start");
  if (m.end && m.end < m.start) return nt(app, "err_event_end");
  if (!m.venue || m.venue.trim().length < 2) return nt(app, "err_event_venue");
  return "";
}

/* Small badges on listing cards */
function MetaBadges({ app, x }) {
  const { lang } = app, m = x.meta || {}, b = [];
  if (x.cat === "housing") {
    if (m.diet === "veg") b.push(nt(app, "diet_veg"));
    if (m.gender && m.gender !== "any") b.push(nt(app, "g_" + m.gender));
    if (m.near) b.push(nt(app, "near_x", { x: m.near }));
  } else if (x.cat === "jobs") {
    (m.visa || []).filter((v) => v !== "none").slice(0, 2).forEach((v) => b.push(optLabel(VISAS, v, lang)));
    if (m.workplace === "remote") b.push(nt(app, "wp_remote"));
  } else if (x.cat === "food") {
    (m.diets || []).slice(0, 3).forEach((d) => b.push(optLabel(DIETS, d, lang)));
  } else if (x.cat === "eldercare") {
    b.push(nt(app, m.kind === "offer" ? "care_offer" : "care_need"));
    if (m.start) b.push(fmtDay(m.start, lang) + (m.end && m.end !== m.start ? " → " + fmtDay(m.end, lang) : ""));
  } else if (x.cat === "events") {
    if (m.start) b.push(fmtEventTime(m.start, lang));
    if (m.festival) b.push(optLabel(FESTIVALS.map((f) => [f[0], f[1], f[2]]), m.festival, lang));
  }
  if (!b.length) return null;
  return <div className="mbadges">{b.slice(0, 3).map((s, i) => <span key={i} className={"mb" + (x.cat === "jobs" && i === 0 ? " visa" : "")}>{s}</span>)}</div>;
}
/* Details panel on the listing page */
function MetaPanel({ app, x }) {
  const { lang } = app, m = x.meta || {}, rows = [];
  const add = (label, value) => { if (value) rows.push([label, value]); };
  if (x.cat === "housing") {
    add(nt(app, "f_diet_pref"), m.diet && nt(app, "diet_" + m.diet));
    add(nt(app, "f_gender"), m.gender && nt(app, "g_" + m.gender));
    add(nt(app, "f_move_in"), m.move_in && tripDate(m.move_in, lang, { day: "numeric", month: "long", year: "numeric" }));
    add(nt(app, "f_lease"), m.lease_months ? nt(app, "lease_n", { n: m.lease_months }) : m.lease_months === 0 ? nt(app, "lease_flex") : "");
    add(nt(app, "f_near"), m.near); add(nt(app, "f_furnished"), m.furnished ? "✓" : "");
  } else if (x.cat === "jobs") {
    add(nt(app, "f_visa"), (m.visa || []).map((v) => optLabel(VISAS, v, lang)).join(" · "));
    add(nt(app, "f_job_type"), m.job_type && nt(app, "jt_" + m.job_type)); add(nt(app, "f_workplace"), m.workplace && nt(app, "wp_" + m.workplace)); add(nt(app, "f_pay"), m.pay);
  } else if (x.cat === "food") {
    add(nt(app, "f_diets"), (m.diets || []).map((d) => optLabel(DIETS, d, lang)).join(" · "));
    add(nt(app, "f_zones"), m.zones); add(nt(app, "f_weekly"), m.weekly_price != null ? "$" + m.weekly_price : "");
    add(nt(app, "f_meals"), m.meals_per_week); add(nt(app, "f_trial"), m.trial ? "✓" : "");
  } else if (x.cat === "events") {
    add(nt(app, "f_start"), fmtEventTime(m.start, lang)); add(nt(app, "f_end"), m.end && fmtEventTime(m.end, lang));
    add(nt(app, "f_venue"), m.venue); add(nt(app, "f_address"), m.address);
    add(nt(app, "f_festival"), m.festival && optLabel(FESTIVALS.map((f) => [f[0], f[1], f[2]]), m.festival, lang));
  }
  if (x.cat === "eldercare") {
    add(nt(app, "care_kind"), nt(app, m.kind === "offer" ? "care_offer" : "care_need"));
    add(nt(app, "care_dates"), m.start && fmtDay(m.start, lang) + (m.end && m.end !== m.start ? " → " + fmtDay(m.end, lang) : ""));
    add(nt(app, "care_days"), (m.days || []).map((d) => optLabel(WEEKDAYS, d, lang)).join(" · "));
    add(nt(app, "care_hours"), m.hours); add(nt(app, "care_tasks"), (m.tasks || []).map((k) => nt(app, "task_" + k)).join(" · "));
    add(nt(app, "care_pay"), m.pay && nt(app, "pay_" + m.pay));
  }
  add(nt(app, "f_langs"), (m.langs || []).map(langName).join(" · "));
  if (!rows.length && x.cat !== "jobs") return null;
  return <section className="metapanel">
    <dl>{rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
    {x.cat === "jobs" && <div className="warnbox"><Ico n="shield" /><span>{nt(app, "job_warn")}</span></div>}
    {x.cat === "eldercare" && <div className="warnbox"><Ico n="shield" /><span>{nt(app, "care_safety")}</span></div>}
  </section>;
}

/* Home hub */
function NeedsHub({ app }) {
  const { lang } = app;
  return <section className="needs" aria-label={nt(app, "hub_h")}>
    <div className="needs-h"><h2>{nt(app, "hub_h")}</h2><span>{nt(app, "hub_sub")}</span></div>
    <div className="needs-row">{NEEDS.map(([key, icon, en, hi, den, dhi, g1, g2]) => (
      <a key={key} className="need" href={"#/need/" + key} style={{ "--g1": g1, "--g2": g2 }}>
        <span className="need-ic"><Ico n={icon} /></span>
        <b>{lang === "en" ? en : hi}</b><small>{lang === "en" ? den : dhi}</small>
      </a>
    ))}</div>
  </section>;
}
const NEED_CFG = {
  roommates: { cat: "housing", filters: [["diet", [["veg", "Veg only", "सिर्फ़ शाकाहारी"], ["nonveg", "Non-veg OK", "मांसाहारी चलेगा"]]], ["gender", [["female", "Female", "महिला"], ["male", "Male", "पुरुष"], ["family", "Family", "परिवार"]]]] },
  tiffin: { cat: "food", filters: [["diet", DIETS]] },
  jobs: { cat: "jobs", filters: [["visa", [["h1b", "H-1B", "H-1B"], ["opt", "OPT / STEM OPT / CPT", "OPT / STEM OPT / CPT"], ["gc", "Green card", "ग्रीन कार्ड"], ["clearance", "Clearance / citizens", "क्लीयरेंस / नागरिक"]]]] },
  events: { cat: "events", upcoming: true, filters: [] },
};
function NeedView({ app, need }) {
  const { lang } = app;
  const cfg = NEED_CFG[need], meta = NEEDS.find((n) => n[0] === need);
  const [f, setF] = useState({}), [lng, setLng] = useState(""), [rows, setRows] = useState(null), [err, setErr] = useState(false);
  useEffect(() => { setF({}); setLng(""); }, [need]);
  useEffect(() => {
    if (!cfg) return;
    let live = true; setRows(null);
    const qs = new URLSearchParams({ cat: cfg.cat, limit: "60" });
    if (app.metro) qs.set("metro", app.metro);
    if (cfg.upcoming) qs.set("upcoming", "1");
    Object.entries(f).forEach(([k, v]) => v && qs.set(k, v));
    if (lng) qs.set("lang", lng);
    api("/listings/?" + qs.toString()).then((d) => {
      if (!live) return;
      Store._merge(d.listings || []); Store.emit();
      setRows((d.listings || []).map((r) => Store.get(r.id) || normListing(r.id, r)).filter(Boolean)); setErr(false);
    }).catch(() => live && setErr(true));
    return () => { live = false; };
  }, [need, f, lng, app.metro]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cfg || !meta) return <main><div className="empty"><h3>{app.t("not_found")}</h3></div></main>;
  const [, icon, en, hi, den, dhi, g1, g2] = meta;
  const post = () => {
    if (!app.user) { app.requireAuth("/post"); return; }
    LS.set("ac_draft", { ...newDraft(app.loc, app.name), cat: cfg.cat, sub: "", step: 0 });
    app.go("/post");
  };
  return <main className="needpage">
    <MBar app={app} title={lang === "en" ? en : hi} to="/" />
    <section className="needhero" style={{ "--g1": g1, "--g2": g2 }}>
      <span className="need-ic big"><Ico n={icon} /></span>
      <div><h1>{lang === "en" ? en : hi}</h1><p>{lang === "en" ? den : dhi}</p></div>
      <button className="btn gold" onClick={post}><Ico n="plus" />{cfg.cat === "events" ? nt(app, "post_event") : nt(app, "post_here", { c: CAT[cfg.cat][lang] })}</button>
    </section>
    {cfg.filters.map(([key, opts]) => (
      <div className="needfilter" key={key}>
        <button className={"chip" + (!f[key] ? " on" : "")} onClick={() => setF({ ...f, [key]: "" })}>{nt(app, "need_all")}</button>
        {opts.map(([id, oen, ohi]) => <button key={id} className={"chip" + (f[key] === id ? " on" : "")} aria-pressed={f[key] === id} onClick={() => setF({ ...f, [key]: f[key] === id ? "" : id })}>{lang === "en" ? oen : ohi}</button>)}
      </div>
    ))}
    {need === "events" && <FestivalStrip app={app} value={f.festival || ""} onChange={(v) => setF({ ...f, festival: v })} />}
    <div className="needfilter langs">
      <span className="nf-l">{nt(app, "f_langs")}</span>
      <button className={"chip" + (!lng ? " on" : "")} onClick={() => setLng("")}>{nt(app, "need_all")}</button>
      {LANG_OPTS.slice(0, 10).map(([c, native]) => <button key={c} className={"chip" + (lng === c ? " on" : "")} onClick={() => setLng(lng === c ? "" : c)}>{native}</button>)}
    </div>
    {err ? <div className="empty"><h3>{app.t("api_down")}</h3></div>
      : rows === null ? <div className="spin" />
      : !rows.length ? <div className="empty"><div className="eic"><Ico n={icon} /></div><h3>{need === "events" ? nt(app, "no_events") : app.t("empty_t")}</h3>
          <div className="acts"><button className="btn pri" onClick={post}>{need === "events" ? nt(app, "post_event") : app.t("post_first")}</button></div></div>
      : <><div className="ccount">{nt(app, "results_n", { n: rows.length })}</div><div className="grid">{rows.map((x) => <Card key={x.id} app={app} x={x} />)}</div></>}
    {cfg.cat === "jobs" && <div className="warnbox wide"><Ico n="shield" /><span>{nt(app, "job_warn")}</span> <a href="#/need/safety">{nt(app, "safety_h")} →</a></div>}
  </main>;
}
function FestivalStrip({ app, value, onChange }) {
  const { lang } = app;
  return <section className="fest">
    <div className="fest-h"><h3>{nt(app, "fest_h")}</h3><small>{nt(app, "fest_sub")}</small></div>
    <div className="fest-row">{FESTIVALS.map(([id, en, hi, native, ren, rhi, men, mhi]) => (
      <button key={id} className={"festcard" + (value === id ? " on" : "")} aria-pressed={value === id} onClick={() => onChange(value === id ? "" : id)}>
        <b>{lang === "en" ? en : hi}</b>{native !== en && native !== hi && <span className="native">{native}</span>}
        <small>{lang === "en" ? ren : rhi}</small><small className="mo">{lang === "en" ? men : mhi}</small>
      </button>
    ))}</div>
  </section>;
}
function SafetyView({ app }) {
  const { lang } = app, L = lang === "en" ? "en" : "hi";
  return <main className="needpage"><div className="wrap-narrow">
    <MBar app={app} title={nt(app, "safety_h")} to="/" />
    <section className="needhero" style={{ "--g1": "#8C1D1D", "--g2": "#C0392B" }}>
      <span className="need-ic big"><Ico n="shield" /></span><div><h1>{nt(app, "safety_h")}</h1><p>{nt(app, "safety_sub")}</p></div>
    </section>
    <div className="scams">{SCAMS[L].map(([h, what, how], i) => (
      <article className="scam" key={i}><h3><span>{i + 1}</span>{h}</h3><p>{what}</p><div className="how"><b>{nt(app, "how_safe")}</b><p>{how}</p></div></article>
    ))}</div>
    <section className="reportbox"><h3>{nt(app, "report_h")}</h3>
      <ul>{REPORT_LINKS.map(([label, href]) => <li key={href}><a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">{label}</a></li>)}</ul>
      <p>{nt(app, "report_b")}</p></section>
  </div></main>;
}

/* ─── launch: city bar, home sections, verification, Founding Members ─── */
Object.assign(NT.en, {
  city_h: "Your city", city_all: "All cities", wave2: "Opening soon", example: "Example",
  example_b: "This is an example to show how ApnaCircle works. It isn't a real listing.", example_cta: "Post the first real one",
  sec_rooms: "Rooms & roommates", sec_jobs: "Jobs near you", sec_food: "Food & tiffin", sec_events: "Events coming up", sec_biz: "Local businesses",
  sec_fresh: "Fresh listings", sec_groups: "Popular community groups", see_all: "See all",
  verify_h: "Verify your account", verify_sub: "Browse freely. To post, message, or create a group, complete these three steps. It keeps fake accounts out.",
  v_email: "Email", v_phone: "Phone", v_social: "Social account", v_done: "Verified", v_send: "Send code", v_code: "Enter the 6-digit code", v_check: "Verify",
  v_social_b: "Connect one account you already use. We only read your name and email, and one social account can verify only one ApnaCircle account.",
  v_connect: "Continue with {p}", v_unlink: "Remove", v_none: "Social verification isn't set up on this server yet.",
  v_ok: "Social account verified", v_taken: "That social account already verifies another ApnaCircle account.", v_err: "Couldn't verify. Please try again.",
  v_cancel: "Verification was cancelled.", v_all_done: "You're verified. Welcome to the community!", v_needed: "Please verify your account to continue.",
  v_banner: "Verify your account to post and message", v_wait: "Please wait {n} seconds before asking for another code.", clearance: "Security clearance / citizens only",
  join_h: "Become a Founding Member", join_sub: "ApnaCircle is a free, verified community for Indians in the U.S., built by us to help each other. The first 100 people to post a real listing in each city get a Founding Member badge.",
  join_real: "{n} real listings", join_left: "{n} Founding badges left", join_post: "Post a listing", join_invite: "Invite friends",
  join_wa: "Share on WhatsApp", join_copy: "Copy link", join_copied: "Link copied", founding: "Founding Member · {c}", fm_title: "Founding Member", fm_body: "One of ApnaCircle’s early members",
  join_msg: "Moving to a new city, looking for a veg roommate, or need someone to fly with your parents from India? Join ApnaCircle, a free, verified community for Indians in the U.S. Chat safely without sharing your number. Be a Founding Member in your city:",
  join_banner: "Founding Member badge: limited time only. Claim yours now.", verified_by: "Verified: {x}",
});
Object.assign(NT.hi, {
  city_h: "आपका शहर", city_all: "सभी शहर", wave2: "जल्द शुरू", example: "उदाहरण",
  example_b: "यह ApnaCircle कैसे काम करता है यह दिखाने के लिए एक उदाहरण है। यह असली लिस्टिंग नहीं है।", example_cta: "पहली असली लिस्टिंग पोस्ट करें",
  sec_rooms: "कमरे और रूममेट", sec_jobs: "आसपास की नौकरियाँ", sec_food: "खाना और टिफ़िन", sec_events: "आने वाले इवेंट्स", sec_biz: "लोकल बिज़नेस",
  sec_fresh: "नई लिस्टिंग", sec_groups: "लोकप्रिय कम्युनिटी ग्रुप", see_all: "सब देखें",
  verify_h: "अपना खाता सत्यापित करें", verify_sub: "देखना मुफ़्त और खुला है। पोस्ट, मैसेज या ग्रुप बनाने के लिए ये तीन कदम पूरे करें। इससे नकली खाते दूर रहते हैं।",
  v_email: "ईमेल", v_phone: "फ़ोन", v_social: "सोशल अकाउंट", v_done: "सत्यापित", v_send: "कोड भेजें", v_code: "6 अंकों का कोड डालें", v_check: "सत्यापित करें",
  v_social_b: "जो अकाउंट आप पहले से इस्तेमाल करते हैं उसे जोड़ें। हम सिर्फ़ नाम और ईमेल पढ़ते हैं, और एक सोशल अकाउंट से सिर्फ़ एक ApnaCircle खाता सत्यापित होता है।",
  v_connect: "{p} से आगे बढ़ें", v_unlink: "हटाएँ", v_none: "इस सर्वर पर सोशल सत्यापन अभी सेट नहीं है।",
  v_ok: "सोशल अकाउंट सत्यापित हो गया", v_taken: "यह सोशल अकाउंट पहले से किसी दूसरे ApnaCircle खाते को सत्यापित करता है।", v_err: "सत्यापन नहीं हो सका। फिर से कोशिश करें।",
  v_cancel: "सत्यापन रद्द कर दिया गया।", v_all_done: "आप सत्यापित हैं। समुदाय में आपका स्वागत है!", v_needed: "आगे बढ़ने के लिए कृपया अपना खाता सत्यापित करें।",
  v_banner: "पोस्ट और मैसेज करने के लिए खाता सत्यापित करें", v_wait: "नया कोड माँगने से पहले {n} सेकंड रुकें।", clearance: "सिक्योरिटी क्लीयरेंस / सिर्फ़ नागरिक",
  join_h: "Founding Member बनें", join_sub: "ApnaCircle अमेरिका में भारतीयों का मुफ़्त और सत्यापित समुदाय है, जिसे हमने एक-दूसरे की मदद के लिए बनाया है। हर शहर में असली लिस्टिंग पोस्ट करने वाले पहले 100 लोगों को Founding Member बैज मिलता है।",
  join_real: "{n} असली लिस्टिंग", join_left: "{n} Founding बैज बाकी", join_post: "लिस्टिंग पोस्ट करें", join_invite: "दोस्तों को बुलाएँ",
  join_wa: "WhatsApp पर शेयर करें", join_copy: "लिंक कॉपी करें", join_copied: "लिंक कॉपी हो गया", founding: "Founding Member · {c}", fm_title: "Founding Member", fm_body: "ApnaCircle के शुरुआती सदस्यों में से एक",
  join_msg: "नए शहर में आ रहे हैं, शाकाहारी रूममेट चाहिए, या माता-पिता के साथ भारत से उड़ान में कोई साथी? ApnaCircle से जुड़ें: अमेरिका में भारतीयों का मुफ़्त और सत्यापित समुदाय। नंबर साझा किए बिना सुरक्षित चैट करें। अपने शहर के Founding Member बनें:",
  join_banner: "Founding Member बैज: सिर्फ़ सीमित समय के लिए। अभी पाएँ।", verified_by: "सत्यापित: {x}",
});
const PROVIDER_LABEL = { google: "Google", facebook: "Facebook", linkedin: "LinkedIn" };

function CityBar({ app }) {
  const { lang, metro } = app;
  return <div className="citybar" role="group" aria-label={nt(app, "city_h")}>
    <span className="cb-l"><Ico n="pin" />{nt(app, "city_h")}</span>
    <button className={"chip" + (!metro ? " on" : "")} onClick={() => app.setMetro("")}>{nt(app, "city_all")}</button>
    {METROS.map(([id, en, hi]) => (
      <button key={id} className={"chip city" + (metro === id ? " on" : "")} aria-pressed={metro === id} onClick={() => app.setMetro(metro === id ? "" : id)}>
        {lang === "en" ? en : hi}
      </button>
    ))}
  </div>;
}
function HomeSections({ app, list }) {
  const { lang } = app;
  const [groups, setGroups] = useState([]);
  useEffect(() => { api("/rooms/").then((d) => setGroups((d.rooms || []).slice(0, 6))).catch(() => {}); }, []);
  const byCat = (c) => list.filter((x) => x.cat === c);
  const events = byCat("events").filter((x) => x.meta && x.meta.start && new Date(x.meta.start) > Date.now() - 6 * H).sort((a, b) => a.meta.start.localeCompare(b.meta.start));
  const rows = [["sec_rooms", byCat("housing"), "#/need/roommates"], ["sec_jobs", byCat("jobs"), "#/need/jobs"], ["sec_food", byCat("food"), "#/need/tiffin"],
    ["sec_events", events, "#/need/events"], ["sec_biz", byCat("businesses"), null]];
  return <div className="hsections">
    {rows.filter(([, xs]) => xs.length).map(([k, xs, href]) => (
      <section className="hsec" key={k}>
        <div className="hsec-h"><h2>{nt(app, k)}</h2>{href ? <a href={href}>{nt(app, "see_all")} →</a>
          : <button className="linkish" onClick={() => { app.setCat("businesses"); app.setSub(""); }}>{nt(app, "see_all")} →</button>}</div>
        <div className="hsec-row">{xs.slice(0, 10).map((x) => <Card key={x.id} app={app} x={x} />)}</div>
      </section>
    ))}
    {groups.length > 0 && <section className="hsec">
      <div className="hsec-h"><h2>{nt(app, "sec_groups")}</h2><a href="#/rooms">{nt(app, "see_all")} →</a></div>
      <div className="hsec-row groups">{groups.map((r) => <RoomCard key={r.id} app={app} room={r} />)}</div>
    </section>}
    <div className="hsec-h fresh"><h2>{nt(app, "sec_fresh")}</h2></div>
    {lang && null}
  </div>;
}
function JoinBanner({ app }) {
  return <a className="joinbanner" href="#/join"><Ico n="sparkle" /><span>{nt(app, "join_banner")}</span><Ico n="right" /></a>;
}
function VerifyBanner({ app }) {
  if (!app.user || !(app.user.missingSteps || []).length) return null;
  return <a className="verifybanner" href="#/verify"><Ico n="shield" /><span>{nt(app, "v_banner")}</span><Ico n="right" /></a>;
}
/* ✦ after a member's name. Hover or keyboard focus shows the note; on touch screens a tap toggles it. */
function FoundingMark({ app }) {
  const [open, setOpen] = useState(false), id = useRef("fm" + Math.random().toString(36).slice(2, 8)).current, box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);
  return <span className={"fmark" + (open ? " open" : "")} ref={box}>
    <button type="button" className="fmark-btn" aria-label={nt(app, "fm_title")} aria-describedby={id} aria-expanded={open}
      onClick={(e) => { e.preventDefault(); setOpen(!open); }} onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}>✦</button>
    <span className="fmark-tip" role="tooltip" id={id}><b>{nt(app, "fm_title")}</b><span>{nt(app, "fm_body")}</span></span>
  </span>;
}
function SellerBadges({ app, x }) {
  const out = [];
  if (x.sellerSocial && x.sellerSocial.length) out.push(<span key="s" className="sbadge social"><Ico n="check" />{nt(app, "verified_by", { x: x.sellerSocial.map((p) => PROVIDER_LABEL[p] || p).join(" · ") })}</span>);
  return out.length ? <div className="sbadges">{out}</div> : null;
}
function VerifyView({ app }) {
  const { t } = app, u = app.user;
  const [prov, setProv] = useState(null), [code, setCode] = useState({ email: "", phone: "" }), [sent, setSent] = useState({}), [busy, setBusy] = useState(""), [msg, setMsg] = useState(null);
  const refresh = useCallback(async () => {
    try { const d = await api("/auth/me/"); if (d.user) app.setSessionUser(d.user); } catch (e) { /* offline */ }
    try { setProv(await api("/auth/social/")); } catch (e) { setProv({ providers: [], linked: [] }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    refresh();
    const q = new URLSearchParams((window.location.hash.split("?")[1]) || "");
    const r = q.get("social");
    if (r) setMsg({ ok: r === "ok", text: nt(app, { ok: "v_ok", taken: "v_taken", cancelled: "v_cancel" }[r] || "v_err") });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!u) return <main><div className="empty"><h3>{t("loading")}</h3></div></main>;
  const miss = u.missingSteps || [];
  const send = async (kind) => {
    setBusy(kind); setMsg(null);
    try { const d = await api(`/auth/send-${kind}-otp/`, { method: "POST", body: "{}" }); setSent({ ...sent, [kind]: d.devOtp || true }); }
    catch (e) { setMsg({ ok: false, text: e.status === 429 ? nt(app, "v_wait", { n: (e.data && e.data.retryAfter) || 60 }) : nt(app, "v_err") }); }
    setBusy("");
  };
  const check = async (kind) => {
    setBusy(kind); setMsg(null);
    try { await api(`/auth/verify-${kind}/`, { method: "POST", body: JSON.stringify({ code: code[kind] }) }); await refresh(); }
    catch (e) { setMsg({ ok: false, text: nt(app, "v_err") }); }
    setBusy("");
  };
  const unlink = async (p) => { try { await api(`/auth/social/${p}/`, { method: "DELETE" }); await refresh(); } catch (e) { /* ignore */ } };
  const done = (k) => (k === "email" ? u.emailVerified : k === "phone" ? u.phoneVerified : (u.social || []).length > 0);
  const Step = ({ k, n, children }) => <section className={"vstep" + (done(k) ? " done" : "")}>
    <div className="vstep-h"><span className="vnum">{done(k) ? <Ico n="check" /> : n}</span><b>{nt(app, "v_" + k)}</b>{done(k) && <span className="vok">{nt(app, "v_done")}</span>}</div>
    {!done(k) && <div className="vbody">{children}</div>}
  </section>;
  const otp = (k) => <>
    {!sent[k] ? <button className="btn sm pri" disabled={busy === k} onClick={() => send(k)}>{nt(app, "v_send")}</button> : <div className="votp">
      <input className="inp" inputMode="numeric" maxLength={6} placeholder={nt(app, "v_code")} value={code[k]} onChange={(e) => setCode({ ...code, [k]: e.target.value.replace(/\D/g, "") })} />
      <button className="btn sm pri" disabled={busy === k || code[k].length !== 6} onClick={() => check(k)}>{nt(app, "v_check")}</button>
      {typeof sent[k] === "string" && <small className="muted">DEV: {sent[k]}</small>}
    </div>}
  </>;
  return <main><div className="wrap-narrow verifypage">
    <MBar app={app} title={nt(app, "verify_h")} to="/me" />
    <section className="needhero" style={{ "--g1": "#0B1F3A", "--g2": "#1E7B34" }}>
      <span className="need-ic big"><Ico n="shield" /></span><div><h1>{nt(app, "verify_h")}</h1><p>{nt(app, "verify_sub")}</p></div>
    </section>
    {msg && <div className={"vmsg" + (msg.ok ? " ok" : "")} role="status">{msg.text}</div>}
    {!miss.length && <div className="vmsg ok">{nt(app, "v_all_done")}</div>}
    <Step k="email" n={1}>{otp("email")}</Step>
    <Step k="phone" n={2}>{otp("phone")}</Step>
    <section className={"vstep" + (done("social") ? " done" : "")}>
      <div className="vstep-h"><span className="vnum">{done("social") ? <Ico n="check" /> : 3}</span><b>{nt(app, "v_social")}</b>{done("social") && <span className="vok">{nt(app, "v_done")}</span>}</div>
      <div className="vbody">
        <p className="muted">{nt(app, "v_social_b")}</p>
        {(u.social || []).map((p) => <div className="vlinked" key={p}><Ico n="check" /><b>{PROVIDER_LABEL[p] || p}</b><button className="linkish" onClick={() => unlink(p)}>{nt(app, "v_unlink")}</button></div>)}
        {prov && !prov.providers.length && !(u.social || []).length && <p className="muted">{nt(app, "v_none")}</p>}
        <div className="vsocial">{prov && prov.providers.filter((p) => !(u.social || []).includes(p.id)).map((p) => (
          <a key={p.id} className={"btn social " + p.id} href={`/api/auth/social/${p.id}/start/`}>{nt(app, "v_connect", { p: p.label })}</a>
        ))}</div>
      </div>
    </section>
  </div></main>;
}
function JoinView({ app }) {
  const { lang } = app, L = lang === "en" ? 3 : 4;
  const [stats, setStats] = useState(null), [share, setShare] = useState(false);
  useEffect(() => { api("/metros/").then(setStats).catch(() => setStats({ metros: [], foundingOpen: true })); }, []);
  const open = !stats || stats.foundingOpen !== false;
  const post = () => (app.user ? app.go("/post") : app.requireAuth("/post"));
  return <main className="joinpage">
    <MBar app={app} title={nt(app, "join_h")} to="/" />
    <section className="joinhero">
      <div>
        <FoundingBadge app={app} open={open} big />
        <h1>{nt(app, "join_h")}</h1>
        <p className="jstrong">{open ? nt(app, "founding_strong") : nt(app, "founding_closed")}</p>
        {open && <p>{nt(app, "founding_perk")}</p>}
        <div className="jacts">{open && <button className="btn gold" onClick={post}><Ico n="plus" />{nt(app, "join_post")}</button>}
          <button className="btn ghost" onClick={() => setShare(true)}><Ico n="people" />{nt(app, "join_invite")}</button></div>
      </div>
    </section>
    <section className="promises">{PROMISES.map((p) => <div className="promise" key={p[1]}><span className="lic"><Ico n={p[0]} /></span><b>{lang === "en" ? p[1] : p[2]}</b><p>{p[L]}</p></div>)}</section>
    <section className="jcities"><h2>{nt(app, "city_h")}</h2>
      {!stats ? <div className="spin" /> : <div className="jcity-grid">{METROS.map(([id, en, hi, , areas]) => {
        const st = (stats.metros || []).find((m) => m.id === id) || { listings: 0 };
        return <button key={id} className="jcity" onClick={() => { app.setMetro(id); app.go("/"); }}>
          <b>{lang === "en" ? en : hi}</b><small>{areas}</small>
          <span className="jstat">{nt(app, "join_real", { n: st.listings })}</span>
        </button>;
      })}</div>}
    </section>
    {share && <InviteSheet app={app} title={nt(app, "invite_h")} sub={nt(app, "invite_join_sub")} url={inviteUrl("/i/", app)} onClose={() => setShare(false)} />}
  </main>;
}

/* ─── elder care calendar · invites · founding badge ─── */
Object.assign(NT.en, {
  care_start_d: "From", care_end_d: "Until", care_h: "Elder care", care_sub: "Help for our parents and grandparents. Offer the days you're free, or post the dates your family needs a hand: rides, doctor visits, company, meals.",
  care_post: "Post on the care calendar", care_how: ["Pick Need help or Offer help", "Set the dates, days and what's needed", "Message to agree the details and meet first"],
  care_all: "All", care_need: "Need help", care_offer: "Offer help", care_tasks: "Help with", care_empty: "Nothing on the calendar here yet",
  care_empty_b: "Be the first: offer a few free days, or post the dates your family needs help.", care_dates: "Dates", care_days: "Days",
  care_hours: "Hours", care_hours_ph: "e.g. 10am–2pm", care_pay: "Arrangement", pay_volunteer: "Volunteer", pay_paid: "Paid", pay_discuss: "Discuss",
  care_note: "Details", care_note_ph: "Who needs help, languages, mobility, anything important. Don't share medical records or addresses here.",
  care_new_h: "Post on the care calendar", care_kind: "I want to", care_publish: "Post to calendar",
  task_companionship: "Company & conversation", task_rides: "Rides", task_doctor_visits: "Doctor visits", task_meals: "Meals",
  task_errands: "Groceries & errands", task_overnight: "Overnight stay", task_language: "Language help", task_tech_help: "Phone & video calls",
  err_care_start: "Choose a start date (today or later)", err_care_end: "End must be after the start (up to 6 months)", err_care_tasks: "Pick at least one kind of help",
  care_safety: "Meet on video first, then in person with family present. Never share bank details or give access to money. For medical care or paid caregiving, check licences and references. ApnaCircle doesn't screen helpers.",
  invite: "Invite", invite_h: "Invite people", invite_sub: "Share this group with friends and family.", invite_join_sub: "Invite friends to ApnaCircle.",
  copy_msg: "Copy message", copied_msg: "Message copied", msg_lang: "Message language", lang_en: "English", lang_hien: "हिंदी + English",
  more_share: "More…", invited_banner: "You've been invited to this group.", private_note: "Private group: the admin approves each request.",
  room_req: "{n} wants to join {g}", requests_n: "{n} requests",
  founding_limited: "Limited time", founding_h: "Founding Member", founding_strong: "This badge is only available for a limited time. Post your first real listing now. Once the window closes, the Founding Member badge is gone for good.",
  founding_closed: "The Founding Member window has closed. Thank you to everyone who helped build ApnaCircle.",
  founding_perk: "Your badge shows on your profile and every listing you post, forever.",
});
Object.assign(NT.hi, {
  care_start_d: "कब से", care_end_d: "कब तक", care_h: "बुज़ुर्गों की देखभाल", care_sub: "हमारे माता-पिता और दादा-दादी के लिए मदद। जिन दिनों आप खाली हैं वो बताएँ, या जिन तारीखों पर परिवार को मदद चाहिए वो पोस्ट करें: राइड, डॉक्टर के पास जाना, साथ बैठना, खाना।",
  care_post: "देखभाल कैलेंडर पर पोस्ट करें", care_how: ["‘मदद चाहिए’ या ‘मदद कर सकते हैं’ चुनें", "तारीखें, दिन और ज़रूरत बताएँ", "मैसेज में बात तय करें और पहले मिल लें"],
  care_all: "सभी", care_need: "मदद चाहिए", care_offer: "मदद कर सकते हैं", care_tasks: "किस काम में", care_empty: "यहाँ कैलेंडर पर अभी कुछ नहीं",
  care_empty_b: "पहल करें: अपने खाली दिन बताएँ, या जिन तारीखों पर परिवार को मदद चाहिए वो पोस्ट करें।", care_dates: "तारीखें", care_days: "दिन",
  care_hours: "समय", care_hours_ph: "जैसे सुबह 10 से दोपहर 2", care_pay: "व्यवस्था", pay_volunteer: "स्वयंसेवा", pay_paid: "पेड", pay_discuss: "बात करके तय",
  care_note: "विवरण", care_note_ph: "किसे मदद चाहिए, भाषा, चलने-फिरने की स्थिति, कोई ज़रूरी बात। मेडिकल रिकॉर्ड या पता यहाँ न लिखें।",
  care_new_h: "देखभाल कैलेंडर पर पोस्ट करें", care_kind: "मैं चाहता/चाहती हूँ", care_publish: "कैलेंडर पर पोस्ट करें",
  task_companionship: "साथ और बातचीत", task_rides: "राइड", task_doctor_visits: "डॉक्टर के पास जाना", task_meals: "खाना",
  task_errands: "किराना और छोटे काम", task_overnight: "रात को रुकना", task_language: "भाषा में मदद", task_tech_help: "फ़ोन और वीडियो कॉल",
  err_care_start: "शुरू की तारीख चुनें (आज या बाद की)", err_care_end: "खत्म की तारीख शुरू के बाद हो (6 महीने तक)", err_care_tasks: "कम से कम एक तरह की मदद चुनें",
  care_safety: "पहले वीडियो पर मिलें, फिर परिवार की मौजूदगी में आमने-सामने। बैंक की जानकारी कभी न दें, पैसों तक पहुँच न दें। मेडिकल या पेड देखभाल के लिए लाइसेंस और रेफ़रेंस जाँचें। ApnaCircle मदद करने वालों की जाँच नहीं करता।",
  invite: "बुलाएँ", invite_h: "लोगों को बुलाएँ", invite_sub: "यह ग्रुप दोस्तों और परिवार के साथ शेयर करें।", invite_join_sub: "दोस्तों को ApnaCircle पर बुलाएँ।",
  copy_msg: "मैसेज कॉपी करें", copied_msg: "मैसेज कॉपी हो गया", msg_lang: "मैसेज की भाषा", lang_en: "English", lang_hien: "हिंदी + English",
  more_share: "और…", invited_banner: "आपको इस ग्रुप में बुलाया गया है।", private_note: "प्राइवेट ग्रुप: हर अनुरोध को एडमिन मंज़ूर करते हैं।",
  room_req: "{n} {g} से जुड़ना चाहते हैं", requests_n: "{n} अनुरोध",
  founding_limited: "सीमित समय", founding_h: "Founding Member", founding_strong: "यह बैज सिर्फ़ सीमित समय के लिए है। अभी अपनी पहली असली लिस्टिंग पोस्ट करें। समय खत्म होने के बाद Founding Member बैज फिर कभी नहीं मिलेगा।",
  founding_closed: "Founding Member का समय खत्म हो गया है। ApnaCircle बनाने में साथ देने वाले सभी का धन्यवाद।",
  founding_perk: "आपका बैज आपकी प्रोफ़ाइल और आपकी हर लिस्टिंग पर हमेशा दिखेगा।",
});
const CARE_TASKS = ["companionship", "rides", "doctor_visits", "meals", "errands", "overnight", "language", "tech_help"];
const WEEKDAYS = [["mon", "Mon", "सोम"], ["tue", "Tue", "मंगल"], ["wed", "Wed", "बुध"], ["thu", "Thu", "गुरु"], ["fri", "Fri", "शुक्र"], ["sat", "Sat", "शनि"], ["sun", "Sun", "रवि"]];
const fmtDay = (iso, lang) => tripDate(iso, lang, { day: "numeric", month: "short" });

/* ── invite messages (clean, trustworthy; honest about what members get) ── */
function inviteMessage(app, { group, url, privateGroup, lang }) {
  const who = (app.user && (app.user.firstName || (app.user.displayName || "").split(" ")[0])) || "";
  const en = [
    "Hi! 👋", "",
    group ? `${who ? who + " invited you" : "You're invited"} to join “${group}” on ApnaCircle 🇮🇳🇺🇸` : `${who ? who + " invited you" : "You're invited"} to join ApnaCircle 🇮🇳🇺🇸`,
    "",
    "ApnaCircle is a free, verified community for Indians in the U.S. Find rooms and roommates, tiffin, jobs with visa sponsorship, events, and trusted help from people near you.",
    "✅ Every member verifies their email and phone", "🔒 Chat privately without sharing your number",
    "",
    "🎖️ Founding Members get a special badge on their profile, for a limited time only.",
    privateGroup ? "\nThis is a private group, so the admin will approve your request." : "",
    "", `👉 Join here: ${url}`, "", "ApnaCircle: our community, together.",
  ].join("\n").replace(/\n{3,}/g, "\n\n");
  if (lang !== "hien") return en;
  const hi = [
    "नमस्ते! 🙏", "",
    group ? `${who ? who + " ने आपको" : "आपको"} ApnaCircle पर “${group}” ग्रुप में बुलाया है 🇮🇳🇺🇸` : `${who ? who + " ने आपको" : "आपको"} ApnaCircle पर बुलाया है 🇮🇳🇺🇸`,
    "",
    "ApnaCircle अमेरिका में भारतीयों का मुफ़्त और सत्यापित समुदाय है। कमरे और रूममेट, टिफ़िन, वीज़ा स्पॉन्सरशिप वाली नौकरियाँ, इवेंट्स, और आसपास के लोगों से भरोसेमंद मदद, सब एक जगह।",
    "✅ हर सदस्य अपना ईमेल और फ़ोन सत्यापित करता है", "🔒 अपना नंबर दिए बिना निजी चैट",
    "", "🎖️ Founding Members को प्रोफ़ाइल पर खास बैज मिलता है, सिर्फ़ सीमित समय के लिए।",
    privateGroup ? "\nयह प्राइवेट ग्रुप है, एडमिन आपके अनुरोध को मंज़ूर करेंगे।" : "",
    "", `👉 यहाँ जुड़ें: ${url}`,
  ].join("\n").replace(/\n{3,}/g, "\n\n");
  return hi + "\n\n— — —\n\n" + en;
}
const SHARE_ICONS = {
  whatsapp: "M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm5.5 14.1c-.2.7-1.4 1.3-2 1.4-.5.1-1.2.1-3.8-.9-3.2-1.3-5.2-4.5-5.4-4.7-.2-.2-1.3-1.7-1.3-3.3s.8-2.3 1.1-2.7c.3-.3.6-.4.8-.4h.6c.2 0 .4 0 .6.5l.9 2.1c.1.2.1.4 0 .6l-.4.6-.4.5c-.1.1-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.2 1 2.2 1.4 2.5 1.5.3.1.5.1.7-.1l.9-1.1c.2-.3.4-.2.7-.1l2 .9c.3.1.5.2.6.3.1.2.1.8-.1 1.5z",
  facebook: "M14 8h3V4h-3c-2.8 0-4.5 1.8-4.5 4.6V11H7v4h2.5v9h4v-9h3l.5-4h-3.5V8.8c0-.5.3-.8.5-.8z",
  telegram: "M21.5 3.6 2.9 10.8c-1.3.5-1.2 1.3-.2 1.6l4.8 1.5 1.8 5.6c.2.7.4.9.9.9.4 0 .6-.2.9-.5l2.3-2.2 4.8 3.5c.9.5 1.5.2 1.7-.8l3.1-14.8c.3-1.3-.5-1.9-1.5-1.5zM9.3 14.4l8.7-5.5c.4-.3.8-.1.5.2l-7.2 6.5-.3 3.1-1.7-4.3z",
};
function InviteSheet({ app, title, sub, url, group, privateGroup, onClose }) {
  const [lang, setLang] = useState(app.lang === "hi" ? "hien" : "en");
  const text = inviteMessage(app, { group, url, privateGroup, lang });
  const links = [
    ["whatsapp", "WhatsApp", "https://wa.me/?text=" + encodeURIComponent(text)],
    ["facebook", "Facebook", "https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(url)],
    ["telegram", "Telegram", "https://t.me/share/url?url=" + encodeURIComponent(url) + "&text=" + encodeURIComponent(text.replace(url, "").trim())],
  ];
  const copy = async () => { try { await navigator.clipboard.writeText(text); app.toast(nt(app, "copied_msg")); } catch (e) { /* ignore */ } };
  const native = async () => { try { await navigator.share({ title: "ApnaCircle", text, url }); } catch (e) { /* cancelled */ } };
  return (
    <div className="scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sheet invite" role="dialog" aria-modal="true" aria-label={title}>
        <div className="hd"><h2>{title}</h2><button className="ibtn" onClick={onClose} aria-label={app.t("close")}><Ico n="close" /></button></div>
        <p className="muted">{sub}</p>
        {privateGroup && <p className="invnote"><Ico n="lock" />{nt(app, "private_note")}</p>}
        <div className="shares">{links.map(([id, label, href]) => (
          <a key={id} className={"share " + id} href={href} target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d={SHARE_ICONS[id]} fill="currentColor" /></svg><span>{label}</span></a>
        ))}
          {typeof navigator !== "undefined" && navigator.share && <button className="share more" onClick={native}><Ico n="share" /><span>{nt(app, "more_share")}</span></button>}
        </div>
        <div className="invlang"><span>{nt(app, "msg_lang")}</span>
          <button className={"chip" + (lang === "en" ? " on" : "")} onClick={() => setLang("en")}>{nt(app, "lang_en")}</button>
          <button className={"chip" + (lang === "hien" ? " on" : "")} onClick={() => setLang("hien")}>{nt(app, "lang_hien")}</button></div>
        <pre className="invmsg">{text}</pre>
        <div className="invacts"><button className="btn pri" onClick={copy}><Ico n="copy" />{nt(app, "copy_msg")}</button></div>
      </div>
    </div>
  );
}
const firstName = (app) => (app.user && (app.user.firstName || (app.user.displayName || "").split(" ")[0])) || "";
const inviteUrl = (path, app) => window.location.origin + path + (firstName(app) ? (path.includes("?") ? "&" : "?") + "from=" + encodeURIComponent(firstName(app)) : "");

function FoundingBadge({ app, open = true, big = false }) {
  return <div className={"fbadge" + (big ? " big" : "") + (open ? "" : " closed")}>
    <span className="fb-medal"><Ico n="sparkle" /></span>
    <span className="fb-text"><b>{nt(app, "founding_h")}</b>{open && <i>{nt(app, "founding_limited")}</i>}</span>
  </div>;
}

/* ── elder care calendar ── */
function CareView({ app }) {
  const { lang, t } = app;
  const [kind, setKind] = useState(""), [task, setTask] = useState(""), [lng, setLng] = useState(""), [rows, setRows] = useState(null), [err, setErr] = useState(false);
  useEffect(() => {
    let live = true; setRows(null);
    const qs = new URLSearchParams({ cat: "eldercare" });
    if (kind) qs.set("kind", kind); if (task) qs.set("task", task); if (lng) qs.set("lang", lng); if (app.metro) qs.set("metro", app.metro);
    api("/listings/?" + qs.toString()).then((d) => {
      if (!live) return; Store._merge(d.listings || []); Store.emit();
      setRows((d.listings || []).map((r) => Store.get(r.id) || normListing(r.id, r)).filter(Boolean)); setErr(false);
    }).catch(() => live && setErr(true));
    return () => { live = false; };
  }, [kind, task, lng, app.metro]);
  const post = () => (app.user ? app.go("/care/new") : app.requireAuth("/care/new"));
  const groups = [];
  (rows || []).forEach((x) => { const k = ((x.meta && x.meta.start) || "").slice(0, 7); let g = groups.find((y) => y.k === k); if (!g) { g = { k, xs: [] }; groups.push(g); } g.xs.push(x); });
  return <main className="carepage">
    <MBar app={app} title={nt(app, "care_h")} to="/" />
    <section className="needhero" style={{ "--g1": "#6B2D5C", "--g2": "#B0558F" }}>
      <span className="need-ic big"><Ico n="heart" /></span><div><h1>{nt(app, "care_h")}</h1><p>{nt(app, "care_sub")}</p></div>
      <button className="btn gold" onClick={post}><Ico n="plus" />{nt(app, "care_post")}</button>
    </section>
    <ol className="chow">{NT[lang === "en" ? "en" : "hi"].care_how.map((s, i) => <li key={i}><span>{i + 1}</span>{s}</li>)}</ol>
    <div className="needfilter">
      {[["", "care_all"], ["need", "care_need"], ["offer", "care_offer"]].map(([k, label]) => <button key={k || "all"} className={"chip" + (kind === k ? " on" : "")} onClick={() => setKind(k)}>{nt(app, label)}</button>)}
    </div>
    <div className="needfilter"><span className="nf-l">{nt(app, "care_tasks")}</span>
      {CARE_TASKS.map((k) => <button key={k} className={"chip" + (task === k ? " on" : "")} onClick={() => setTask(task === k ? "" : k)}>{nt(app, "task_" + k)}</button>)}</div>
    <div className="needfilter langs"><span className="nf-l">{nt(app, "f_langs")}</span>
      {LANG_OPTS.slice(0, 10).map(([c, native]) => <button key={c} className={"chip" + (lng === c ? " on" : "")} onClick={() => setLng(lng === c ? "" : c)}>{native}</button>)}</div>
    {err ? <div className="empty"><h3>{t("api_down")}</h3></div> : rows === null ? <div className="spin" />
      : !rows.length ? <div className="empty"><div className="eic"><Ico n="heart" /></div><h3>{nt(app, "care_empty")}</h3><p>{nt(app, "care_empty_b")}</p>
          <div className="acts"><button className="btn pri" onClick={post}>{nt(app, "care_post")}</button></div></div>
      : groups.map((g) => <section className="cmonth" key={g.k}>
          <h3>{g.k ? tripDate(g.k + "-01", lang, { month: "long", year: "numeric" }) : ""}</h3>
          {g.xs.map((x) => <CareRow key={x.id} app={app} x={x} />)}
        </section>)}
    <div className="warnbox wide"><Ico n="shield" /><span>{nt(app, "care_safety")}</span></div>
  </main>;
}
function CareRow({ app, x }) {
  const { lang } = app, m = x.meta || {}, mine = x.ownerId === Store.me;
  const days = (m.days || []).map((d) => optLabel(WEEKDAYS, d, lang)).join(" · ");
  return <article className={"carerow " + (m.kind || "need")}>
    <div className="cdate"><b>{fmtDay(m.start, lang)}</b>{m.end && m.end !== m.start && <><span>→</span><b>{fmtDay(m.end, lang)}</b></>}</div>
    <div className="cmain">
      <span className={"ckind " + m.kind}>{nt(app, m.kind === "offer" ? "care_offer" : "care_need")}</span>
      <a className="ctitle2" href={"#/item/" + encodeURIComponent(x.id)}>{app.F.L(x, "title")}</a>
      <div className="mbadges">{(m.tasks || []).slice(0, 4).map((k) => <span key={k} className="mb">{nt(app, "task_" + k)}</span>)}</div>
      <small className="muted">{[days, m.hours, m.pay && nt(app, "pay_" + m.pay), (m.langs || []).map(langName).join(" · "), app.F.place(x)].filter(Boolean).join(" · ")}</small>
    </div>
    <div className="cwho"><Avatar src={x.sellerAvatar} name={x.sellerName} size={34} /><span>{x.sellerName}</span>
      {!mine && <button className="btn sm pri" onClick={() => app.openChat(x)}><Ico n="chat" />{app.t("msg_seller")}</button>}</div>
  </article>;
}
function CarePostView({ app }) {
  const { lang, t } = app;
  const today = new Date().toISOString().slice(0, 10);
  const [D, setD] = useState({ kind: "need", start: "", end: "", days: [], hours: "", tasks: [], pay: "discuss", langs: [], note: "", zip: (app.loc && app.loc.zip) || "" });
  const [err, setErr] = useState(""), [busy, setBusy] = useState(false);
  const up = (p) => { setD((d) => ({ ...d, ...p })); setErr(""); };
  const z = zipInfo(D.zip);
  const submit = async () => {
    if (!D.start || D.start < today) return setErr(nt(app, "err_care_start"));
    if (D.end && D.end < D.start) return setErr(nt(app, "err_care_end"));
    if (!D.tasks.length) return setErr(nt(app, "err_care_tasks"));
    if (!z) return setErr(t("err_zip"));
    if (D.note.trim().length < 10) return setErr(t("err_desc"));
    setBusy(true);
    const taskText = D.tasks.slice(0, 2).map((k) => NT.en["task_" + k].toLowerCase()).join(" and ");
    const title = (D.kind === "offer" ? "Offering help: " : "Help needed: ") + taskText + (z.city ? ", " + z.city : "");
    try {
      const r = await api("/listings/", { method: "POST", body: JSON.stringify({ cat: "eldercare", sub: D.kind === "offer" ? "offer_help" : "need_help", title: title.slice(0, 90),
        desc: D.note.trim(), price: null, unit: "ask", zip: D.zip, sellerName: app.name || "",
        meta: { kind: D.kind, start: D.start, end: D.end || D.start, days: D.days, hours: D.hours, tasks: D.tasks, pay: D.pay, langs: D.langs } }) });
      Store._merge([r]); Store.emit(); app.toast(t("published")); window.location.replace("#/care");
    } catch (e) { setBusy(false); setErr(/^care_/.test(e.message || "") ? nt(app, "err_" + e.message) : t("err_save")); }
  };
  return <main><div className="wrap-narrow">
    <MBar app={app} title={nt(app, "care_new_h")} to="/care" />
    <h1 className="pagetitle">{nt(app, "care_new_h")}</h1>
    <div className="card-pad cform">
      <div className="field"><span>{nt(app, "care_kind")}</span><div className="seg">
        {[["need", "care_need"], ["offer", "care_offer"]].map(([k, l]) => <button key={k} className={D.kind === k ? "on" : ""} onClick={() => up({ kind: k })}>{nt(app, l)}</button>)}</div></div>
      <div className="two">
        <label className="field"><span>{nt(app, "care_start_d")}</span><input className="inp" type="date" min={today} value={D.start} onChange={(e) => up({ start: e.target.value })} /></label>
        <label className="field"><span>{nt(app, "care_end_d")} <em>{t("optional")}</em></span><input className="inp" type="date" min={D.start || today} value={D.end} onChange={(e) => up({ end: e.target.value })} /></label>
      </div>
      <div className="field"><span>{nt(app, "care_days")} <em>{t("optional")}</em></span><Chips multi lang={lang} value={D.days} onChange={(v) => up({ days: v })} opts={WEEKDAYS} /></div>
      <label className="field"><span>{nt(app, "care_hours")} <em>{t("optional")}</em></span><input className="inp" maxLength={40} placeholder={nt(app, "care_hours_ph")} value={D.hours} onChange={(e) => up({ hours: e.target.value })} /></label>
      <div className="field"><span>{nt(app, "care_tasks")}</span><Chips multi lang={lang} value={D.tasks} onChange={(v) => up({ tasks: v })} opts={CARE_TASKS.map((k) => [k, NT.en["task_" + k], NT.hi["task_" + k]])} /></div>
      <div className="field"><span>{nt(app, "care_pay")}</span><Chips lang={lang} value={D.pay} onChange={(v) => up({ pay: v })} opts={["volunteer", "paid", "discuss"].map((k) => [k, NT.en["pay_" + k], NT.hi["pay_" + k]])} /></div>
      <div className="field"><span>{nt(app, "f_langs")} <em>{t("optional")}</em></span><LangChips value={D.langs} onChange={(v) => up({ langs: v })} /></div>
      <label className="field"><span>{t("f_us_zip")}</span><input className="inp" inputMode="numeric" maxLength={5} value={D.zip} placeholder="75024" onChange={(e) => up({ zip: e.target.value.replace(/\D/g, "") })} />
        <small className={"zipok" + (z ? " ok" : "")}>{z ? `${z.city ? z.city + ", " : ""}${z.state}` : t("f_us_zip_h")}</small></label>
      <label className="field"><span>{nt(app, "care_note")}</span><textarea className="inp" rows={4} maxLength={1500} placeholder={nt(app, "care_note_ph")} value={D.note} onChange={(e) => up({ note: e.target.value })} /></label>
      <div className="warnbox"><Ico n="shield" /><span>{nt(app, "care_safety")}</span></div>
      {err && <div className="err" role="alert">{err}</div>}
      <button className="btn pri wide" disabled={busy} onClick={submit}>{busy ? t("loading") : nt(app, "care_publish")}</button>
    </div>
  </div></main>;
}

/* ─── luggage space: US ⇄ India ───────────────────────────────── */
const ACCEPTS = ["docs", "clothes", "snacks", "cosmetics", "supplements", "small", "gifts"];
const regionOf = (st) => (REGIONS.find((r) => r[3].includes(st)) || [])[0] || "";
const isoToday = () => new Date().toLocaleDateString("en-CA");
const tripDate = (iso, lang, opts) => {
  const d = new Date(String(iso || "") + "T12:00:00");
  return isNaN(d) ? "" : d.toLocaleDateString(lang === "hi" ? "hi-IN" : "en-US", opts);
};
const daysUntil = (iso) => {
  const d = Date.parse(String(iso || "") + "T00:00:00"), t0 = new Date(); t0.setHours(0, 0, 0, 0);
  return isNaN(d) ? null : Math.round((d - t0.getTime()) / 864e5);
};
const airportOf = (code) => AIRPORTS.find((a) => a[0] === code) || null;
/* short: "Mumbai (BOM)" for routes; long: "Mumbai · Chhatrapati Shivaji Maharaj Intl (BOM)" for the picker */
function airportLabel(code, lang, long = false) {
  const a = airportOf(code);
  if (!a) return "";
  const [c, nHi, nEn, cityHi, cityEn] = a;
  const [name, city] = lang === "en" ? [nEn, cityEn] : [nHi, cityHi];
  return long && name !== city ? `${city} · ${name} (${c})` : `${city} (${c})`;
}
function tripEnds(x, F, lang) {
  const tp = x.trip, us = [tp.city || x.city, tp.state].filter(Boolean).join(", ");
  const usLabel = us || F.stateName(tp.state);
  const thLabel = (tp.airport && airportLabel(tp.airport, lang)) || tp.province;
  return tp.dir === "us_in" ? [usLabel, thLabel] : [thLabel, usLabel];
}
function WhenBadge({ iso, t }) {
  const n = daysUntil(iso);
  if (n == null) return null;
  const label = n <= 0 ? t("today") : n === 1 ? t("tomorrow") : t("days_left", { n });
  return <span className={"when" + (n <= 3 ? " soon" : "")}>{label}</span>;
}
function TripPanel({ app, x }) {
  const { t, lang } = app, tp = x.trip, [from, to] = tripEnds(x, app.F, lang);
  const long = { weekday: "short", day: "numeric", month: "short", year: "numeric" };
  return (
    <section className="trip">
      <div className="troute"><b>{from}</b><span className="tarrow" aria-hidden="true"><Ico n="plane" /></span><b>{to}</b></div>
      <div className="tgrid">
        <div><small>{t("depart")}</small><strong>{tripDate(tp.depart, lang, long)}</strong><WhenBadge iso={tp.depart} t={t} /></div>
        <div><small>{t("return_d")}</small><strong>{tp.ret ? tripDate(tp.ret, lang, long) : t("one_way")}</strong></div>
        <div><small>{t("space")}</small><strong>{t("kg_n", { n: tp.kg })}</strong></div>
        <div><small>{t("price_kg")}</small><strong>{app.F.priceText(x)}</strong></div>
      </div>
      {tp.accepts.length > 0 && <div className="accepts"><small>{t("f_accepts")}</small>
        <div>{tp.accepts.map((a) => <span key={a} className="chip sm">{t("acc_" + a)}</span>)}</div></div>}
      {tp.airline && <p className="muted" style={{ margin: "10px 0 0" }}><Ico n="plane" /> {tp.airline}{tp.flight ? " · " + tp.flight : ""}</p>}
      {tp.companion && <div className="compbox"><Ico n="heart" /><div><b>{nt(app, "companion")}</b><small>{nt(app, "companion_b")}</small></div></div>}
      {tp.langs && tp.langs.length > 0 && <p className="muted" style={{ margin: "8px 0 0" }}>{nt(app, "f_langs")}: {tp.langs.map(langName).join(" · ")}</p>}
    </section>
  );
}
function TripRow({ app, x }) {
  const { t, lang, F } = app, tp = x.trip, [from, to] = tripEnds(x, F, lang);
  const mine = x.ownerId === Store.me, full = x.status === "sold";
  const open = () => app.go("/item/" + encodeURIComponent(x.id));
  const stop = (e) => e.stopPropagation();
  return (
    <div className={"trow" + (full ? " full" : "")} role="row" tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === "Enter") open(); }}>
      <div className="tdate" role="cell">
        <b>{tripDate(tp.depart, lang, { day: "numeric" })}</b>
        <span>{tripDate(tp.depart, lang, { month: "short" })}</span>
        <small>{tripDate(tp.depart, lang, { weekday: "short" })}</small>
      </div>
      <div className="troutec" role="cell">
        <span className={"dirtag " + tp.dir}>{t("dir_" + tp.dir)}</span>{tp.companion && <span className="dirtag comp"><Ico n="heart" />{nt(app, "companion")}</span>}
        <div className="tline"><b>{from}</b><Ico n="right" /><b>{to}</b></div>
        <WhenBadge iso={tp.depart} t={t} />
      </div>
      <div className="tcell" role="cell"><small>{t("return_d")}</small>{tp.ret ? tripDate(tp.ret, lang, { day: "numeric", month: "short" }) : t("one_way")}</div>
      <div className="tcell" role="cell"><small>{t("space")}</small><b className="kg">{full ? t("trip_full") : t("kg_n", { n: tp.kg })}</b></div>
      <div className="tcell" role="cell"><small>{t("price_kg")}</small><b>{F.priceText(x)}</b></div>
      <div className="tuser" role="cell">
        <Avatar src={x.sellerAvatar} name={x.sellerName} size={34} />
        <span><b>{x.sellerName}</b>{x.sellerVerified && <small className="ok"><Ico n="check" />{t("verified_short")}</small>}</span>
      </div>
      <div className="tacts" role="cell" onClick={stop}>
        {mine ? <a className="btn sm" href={"#/item/" + encodeURIComponent(x.id)}>{t("own_listing")}</a> : <>
          <button className="btn sm pri" disabled={full || app.opening} onClick={() => app.openChat(x)}><Ico n="chat" />{t("contact")}</button>
        </>}
      </div>
    </div>
  );
}
function CarryView({ app, companion = false }) {
  const { t, lang, F } = app;
  const [dir, setDir] = useState(companion ? "in_us" : "all"), [region, setRegion] = useState(""), [st, setSt] = useState(""), [q, setQ] = useState("");
  const [comp, setComp] = useState(companion), [lng, setLng] = useState("");
  useEffect(() => { Store.loadTrips(); }, []);
  const all = Store.trips();
  const byDir = all.filter((x) => (dir === "all" || x.trip.dir === dir) && (!comp || x.trip.companion) && (!lng || (x.trip.langs || []).includes(lng)));
  const terms = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const matchQ = (x) => {
    if (!terms.length) return true;
    const tp = x.trip, ap = airportOf(tp.airport) || [], hay = [tp.city, tp.province, tp.state, F.stateName(tp.state), STATE[tp.state]?.[2], x.city, x.sellerName, x.desc, tp.airline, ...ap].join(" ").toLowerCase();
    return terms.every((w) => hay.includes(w));
  };
  const searched = byDir.filter(matchQ);
  const count = (fn) => searched.filter(fn).length;
  const list = searched.filter((x) => (!region || regionOf(x.trip.state) === region) && (!st || x.trip.state === st));
  const statesInRegion = region ? [...new Set(searched.filter((x) => regionOf(x.trip.state) === region).map((x) => x.trip.state))].sort() : [];
  const months = [];
  list.forEach((x) => {
    const k = x.trip.depart.slice(0, 7);
    if (!months.length || months[months.length - 1].k !== k) months.push({ k, rows: [] });
    months[months.length - 1].rows.push(x);
  });
  return (
    <main className="carry">
      <section className="chero">
        <div>
          <h1>{comp ? nt(app, "companion_h") : t("carry_h")}</h1>
          <p>{comp ? nt(app, "companion_sub") : t("carry_sub")}</p>
        </div>
        <a className="btn gold" href="#/carry/new"><Ico n="plus" />{t("carry_post")}</a>
      </section>
      <ol className="chow">{t("carry_how").map((s, i) => <li key={i}><span>{i + 1}</span>{s}</li>)}</ol>

      <div className="ctools">
        <div className="seg dirseg" role="tablist">
          {["all", "us_in", "in_us"].map((k) => <button key={k} role="tab" aria-selected={dir === k} className={dir === k ? "on" : ""} onClick={() => setDir(k)}>{t("dir_" + k)}</button>)}
        </div>
        <label className="csearch"><Ico n="search" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("carry_search_ph")} aria-label={t("carry_search_ph")} />
          {q && <button onClick={() => setQ("")} aria-label={t("clear_search")}><Ico n="close" /></button>}</label>
      </div>
      <div className="needfilter langs">
        <button className={"chip comp" + (comp ? " on" : "")} aria-pressed={comp} onClick={() => setComp(!comp)}><Ico n="heart" />{nt(app, "companion_only")}</button>
        <span className="nf-l">{nt(app, "f_langs")}</span>
        {LANG_OPTS.slice(0, 10).map(([c, native]) => <button key={c} className={"chip" + (lng === c ? " on" : "")} onClick={() => setLng(lng === c ? "" : c)}>{native}</button>)}
      </div>
      {comp && <p className="muted" style={{ margin: "0 2px 10px" }}>{nt(app, "need_companion")}</p>}

      <div className="zones" role="group" aria-label={t("zones")}>
        <button className={"zone" + (!region ? " on" : "")} onClick={() => { setRegion(""); setSt(""); }}>{t("zone_all")}<i>{searched.length}</i></button>
        {REGIONS.map((r) => (
          <button key={r[0]} className={"zone" + (region === r[0] ? " on" : "")} onClick={() => { setRegion(region === r[0] ? "" : r[0]); setSt(""); }}>
            {lang === "en" ? r[2] : r[1]}<i>{count((x) => regionOf(x.trip.state) === r[0])}</i></button>
        ))}
      </div>
      {region && statesInRegion.length > 0 && <div className="zones states">
        {statesInRegion.map((s2) => <button key={s2} className={"chip" + (st === s2 ? " on" : "")} onClick={() => setSt(st === s2 ? "" : s2)}>
          {F.stateName(s2)} <b>{count((x) => x.trip.state === s2)}</b></button>)}
      </div>}

      <div className="ccount">{Store.tripsLoading && !all.length ? t("loading") : t("trips_n", { n: list.length })}</div>

      {!all.length && Store.tripsLoading ? <div className="spin" aria-label={t("loading")} />
        : Store.tripsError && !all.length ? <div className="empty"><h3>{t("api_down")}</h3><p />
            <div className="acts"><button className="btn pri" onClick={() => Store.loadTrips()}>{t("retry")}</button></div></div>
        : !list.length ? <div className="empty"><div className="eic"><Ico n="plane" /></div><h3>{t("no_trips")}</h3><p>{t("no_trips_b")}</p>
            <div className="acts"><a className="btn pri" href="#/carry/new">{t("carry_post")}</a>
              {(region || q || dir !== "all") && <button className="btn" onClick={() => { setRegion(""); setSt(""); setQ(""); setDir("all"); }}>{t("widen")}</button>}</div></div>
        : <div className="sched" role="table" aria-label={t("carry_h")}>
            <div className="thead" role="row">
              <span role="columnheader">{t("depart")}</span><span role="columnheader">{t("route")}</span><span role="columnheader">{t("return_d")}</span>
              <span role="columnheader">{t("space")}</span><span role="columnheader">{t("price_kg")}</span><span role="columnheader">{t("traveler")}</span><span role="columnheader" />
            </div>
            {months.map((m) => <React.Fragment key={m.k}>
              <div className="tmonth" role="row"><span role="cell">{tripDate(m.k + "-15", lang, { month: "long", year: "numeric" })}</span></div>
              {m.rows.map((x) => <TripRow key={x.id} app={app} x={x} />)}
            </React.Fragment>)}
          </div>}
      <aside className="safety carrysafe"><h3><Ico n="shield" />{t("safety_t")}</h3><p>{t("safety_carry")}</p></aside>
    </main>
  );
}
function CarryPostView({ app }) {
  const { t, lang, loc } = app;
  const prof = Store.profile();
  const [D, setD] = useState(() => ({
    dir: "us_in", zip: prof.zip || (loc.mode === "zip" ? loc.zip : "") || "", airport: "", depart: "", ret: "", kg: "",
    priceMode: "kg", price: "", accepts: ["docs", "clothes", "snacks"], airline: "", note: "", companion: false, flight: "", langs: [],
    name: app.name || "",
  }));
  const [err, setErr] = useState(""), [busy, setBusy] = useState(false);
  const up = (p) => { setD((d) => ({ ...d, ...p })); setErr(""); };
  const z = zipInfo(D.zip);
  const today = isoToday();
  const validate = () => {
    if (!z) return t("err_zip");
    if (!airportOf(D.airport)) return t("err_airport");
    if (!D.depart || D.depart < today) return t("err_depart");
    if (D.ret && D.ret < D.depart) return t("err_return");
    const kg = parseFloat(D.kg); if (!isFinite(kg) || kg < 0.5 || kg > 100) return t("err_kg");
    if (D.priceMode === "kg") { const p = parseFloat(D.price); if (!isFinite(p) || p < 0 || p > 1000) return t("err_price"); }
    if (!D.name.trim()) return t("err_name");
    return "";
  };
  const publish = async () => {
    const e = validate(); if (e) { setErr(e); return; }
    if (busy) return; setBusy(true);
    const ap = airportOf(D.airport), province = ap[5], thSide = airportLabel(D.airport, lang), us = z.city ? `${z.city}, ${z.state}` : z.state;
    const title = D.dir === "us_in" ? `${us} → ${thSide}` : `${thSide} → ${us}`;
    try {
      const id = await Store.addListing({
        cat: "carry", sub: D.dir, title: title.slice(0, 90), desc: D.note.trim().slice(0, 3000),
        price: D.priceMode === "kg" ? Math.round(parseFloat(D.price) * 100) / 100 : null, unit: D.priceMode === "kg" ? "kg" : "ask",
        zip: D.zip, sellerName: D.name.trim().slice(0, 40), line: "", phone: "",
        trip: { dir: D.dir, state: z.state, city: z.city || "", province, airport: D.airport, depart: D.depart, return: D.ret || null,
          kg: parseFloat(D.kg), accepts: D.accepts, airline: D.airline.trim(),
          companion: !!D.companion, flight: D.flight.trim(), langs: D.langs },
      });
      app.setName(D.name.trim()); app.toast(t("published"));
      window.location.replace("#/item/" + encodeURIComponent(id));
    } catch (e2) {
      setBusy(false);
      if (e2 && e2.status === 401) { app.requireAuth("/carry/new"); return; }
      const code = e2 && e2.message;
      setErr(code && /^trip_/.test(code) ? t("err_" + code.replace("trip_", "")) : e2 && e2.code === "network_error" ? t("api_down") : t("err_save"));
    }
  };
  const toggle = (a) => up({ accepts: D.accepts.includes(a) ? D.accepts.filter((v) => v !== a) : [...D.accepts, a] });
  return (
    <main><div className="wrap-narrow">
      <MBar app={app} title={t("carry_new_h")} to="/carry" />
      <h1 className="pagetitle">{t("carry_new_h")}</h1>
      <div className="card-pad cform">
        <div className="field"><span>{t("f_dir")}</span><div className="seg">
          {["us_in", "in_us"].map((k) => <button key={k} className={D.dir === k ? "on" : ""} onClick={() => up({ dir: k })}>{t("dir_" + k)}</button>)}</div></div>
        {/* From → To follows the direction: US → Thailand, or Thailand → US */}
        <div className="two route2">
          {(D.dir === "us_in" ? ["us", "in"] : ["in", "us"]).map((side, i) => side === "us" ? (
            <label className="field" key="us"><span><b className="ft">{t(i === 0 ? "from" : "to")}</b>{t("f_us_zip")}</span>
              <input className="inp" inputMode="numeric" maxLength={5} value={D.zip} placeholder="93301" onChange={(e) => up({ zip: e.target.value.replace(/\D/g, "") })} />
              <small className={"zipok" + (z ? " ok" : "")}>{z ? `${z.city ? z.city + ", " : ""}${z.state}` : t("f_us_zip_h")}</small></label>
          ) : (
            <label className="field" key="in"><span><b className="ft">{t(i === 0 ? "from" : "to")}</b>{t("f_airport")}</span>
              <select className="inp" value={D.airport} onChange={(e) => up({ airport: e.target.value })}>
                <option value="">{t("f_airport_ph")}</option>
                {AIRPORTS.map((ap) => <option key={ap[0]} value={ap[0]}>{airportLabel(ap[0], lang, true)}</option>)}
              </select>
              <small className="zipok">{t("f_airport_h")}</small></label>
          ))}
        </div>
        <div className="two">
          <label className="field"><span>{t("f_depart")}</span><input className="inp" type="date" min={today} value={D.depart} onChange={(e) => up({ depart: e.target.value })} /></label>
          <label className="field"><span>{t("f_return")} <em>{t("optional")}</em></span><input className="inp" type="date" min={D.depart || today} value={D.ret} onChange={(e) => up({ ret: e.target.value })} /></label>
        </div>
        <div className="two">
          <label className="field"><span>{t("f_kg")}</span><div className="suffix"><input className="inp" inputMode="decimal" maxLength={5} value={D.kg} placeholder="10" onChange={(e) => up({ kg: e.target.value.replace(/[^\d.]/g, "") })} /><i>{t("kg")}</i></div></label>
          <div className="field"><span>{t("f_price_kg")}</span>
            <div className="seg sm">{["kg", "ask"].map((k) => <button key={k} className={D.priceMode === k ? "on" : ""} onClick={() => up({ priceMode: k })}>{t(k === "kg" ? "per_kg_btn" : "ask")}</button>)}</div>
            {D.priceMode === "kg" && <div className="suffix" style={{ marginTop: 8 }}><i className="pre">$</i><input className="inp" inputMode="decimal" maxLength={6} value={D.price} placeholder="10" onChange={(e) => up({ price: e.target.value.replace(/[^\d.]/g, "") })} /><i>{t("per_kg")}</i></div>}
          </div>
        </div>
        <div className="field"><span>{t("f_accepts")}</span><div className="chips">
          {ACCEPTS.map((a) => <button key={a} className={"chip" + (D.accepts.includes(a) ? " on" : "")} aria-pressed={D.accepts.includes(a)} onClick={() => toggle(a)}>{t("acc_" + a)}</button>)}</div></div>
        <div className="two">
          <label className="field"><span>{t("f_airline")} <em>{t("optional")}</em></span><input className="inp" maxLength={40} value={D.airline} placeholder="Air India, Emirates, Qatar…" onChange={(e) => up({ airline: e.target.value })} /></label>
          <label className="field"><span>{nt(app, "f_flight")} <em>{t("optional")}</em></span><input className="inp" maxLength={12} value={D.flight} placeholder={nt(app, "f_flight_ph")} onChange={(e) => up({ flight: e.target.value.toUpperCase() })} /></label>
        </div>
        <label className="compcheck"><input type="checkbox" checked={D.companion} onChange={(e) => up({ companion: e.target.checked })} />
          <span><b>{nt(app, "companion")}</b><small>{nt(app, "companion_b")}</small></span></label>
        <div className="field"><span>{nt(app, "f_langs")} <em>{t("optional")}</em></span><LangChips value={D.langs} onChange={(v) => up({ langs: v })} /></div>
        <label className="field"><span>{t("f_note")} <em>{t("optional")}</em></span><textarea className="inp" maxLength={1500} value={D.note} placeholder={t("f_note_ph")} onChange={(e) => up({ note: e.target.value })} /></label>
        <h3 className="subh">{t("contact")}</h3>
        <label className="field"><span>{t("f_name")}</span><input className="inp" maxLength={40} value={D.name} onChange={(e) => up({ name: e.target.value })} /></label>
        <div className="securebox" role="note">
          <span className="sic" aria-hidden="true"><Ico n="shield" /></span>
          <div><b>{t("secure_h")}</b><p>{t("secure_b")}</p></div>
        </div>
        <aside className="safety carrysafe"><h3><Ico n="shield" />{t("safety_t")}</h3><p>{t("safety_carry")}</p></aside>
        {err && <div className="err" role="alert">{err}</div>}
        <button className="btn pri wide" disabled={busy} onClick={publish}>{busy ? t("loading") : t("carry_publish")}</button>
      </div>
    </div></main>
  );
}

/* ─── community groups (forum) ──────────────────────────────────────────
   Forum-style rooms: posts + comments. Anyone signed in can create a room
   today; ID verification (Stripe Identity) becomes a requirement next.
   Server rules live in backend/rooms/views.py. */
const RT = {
  hi: {
    nav: "समुदाय",
    created: "आपका ग्रुप तैयार है",
    h: "समुदाय",
    sub: "आपके शहर के भारतीयों के लोकल ग्रुप। सवाल पूछें, सुझाव साझा करें, और एक-दूसरे की मदद करें।",
    create: "ग्रुप बनाएँ",
    search_ph: "ग्रुप, शहर या विषय खोजें…",
    all_states: "सभी राज्य",
    mine: "आपके ग्रुप",
    discover: "सभी ग्रुप",
    none: "यहाँ अभी कोई ग्रुप नहीं",
    none_b: "पहला ग्रुप बनाएँ ताकि आपके शहर के भारतीय एक-दूसरे से जुड़ सकें।",
    members: "{n} सदस्य",
    posts: "{n} पोस्ट",
    join: "जुड़ें",
    joined: "जुड़ चुके हैं",
    pending: "अनुरोध भेजा",
    ask_join: "जुड़ने का अनुरोध करें",
    leave: "ग्रुप छोड़ें",
    manage: "ग्रुप मैनेज करें",
    approval: "जुड़ने के लिए मंज़ूरी ज़रूरी",
    public: "सार्वजनिक",
    owner: "मालिक",
    mod: "मॉडरेटर",
    member: "सदस्य",
    all_us: "पूरा अमेरिका",
    new_h: "कम्युनिटी ग्रुप बनाएँ",
    f_name: "ग्रुप का नाम",
    f_name_ph: "जैसे Dallas Desi Community, Edison पेरेंट्स सर्कल",
    f_desc: "छोटा विवरण",
    f_desc_ph: "यह ग्रुप किस बारे में है?",
    f_where: "इलाका",
    f_city: "शहर",
    f_join: "कौन जुड़ सकता है",
    pub_b: "कोई भी तुरंत जुड़ सकता है",
    appr_b: "पहले मॉडरेटर मंज़ूरी देंगे",
    rules: "ग्रुप के नियम",
    rules_note: "हमने बुनियादी नियम पहले से जोड़ दिए हैं। चाहें तो बदल सकते हैं।",
    create_btn: "ग्रुप बनाएँ",
    err_name: "ग्रुप का नाम कम से कम 3 अक्षर का हो",
    err_taken: "यह नाम पहले से है। कोई दूसरा नाम आज़माएँ।",
    err_limit: "आप ज़्यादा से ज़्यादा {n} ग्रुप बना सकते हैं",
    in_review: "यह नाम आधिकारिक लगता है, इसलिए खोज में दिखने से पहले ApnaCircle टीम इसकी समीक्षा करेगी।",
    disabled: "इस ग्रुप को ApnaCircle टीम ने रोक दिया है।",
    composer: "कोई सवाल पूछें या ग्रुप के साथ कुछ साझा करें…",
    add_photo: "फ़ोटो",
    post_btn: "पोस्ट करें",
    join_to_post: "पोस्ट और कमेंट करने के लिए ग्रुप से जुड़ें",
    pending_note: "अनुरोध भेज दिया गया। मॉडरेटर इसकी समीक्षा करेंगे।",
    members_only: "यह ग्रुप सिर्फ़ सदस्यों को दिखता है",
    muted: "आपको कुछ समय के लिए पोस्ट करने से रोका गया है",
    polite_h: "क्या इसे थोड़ा बेहतर शब्दों में कहना चाहेंगे?",
    polite_b: "आपके मैसेज में ऐसे शब्द हैं जो दूसरों को बुरे लग सकते हैं। हमारा समुदाय बातचीत को सम्मानजनक रखता है।",
    polite_edit: "मैसेज बदलें",
    polite_send: "फिर भी पोस्ट करें",
    pinned: "पिन किया",
    pin: "पिन करें",
    unpin: "पिन हटाएँ",
    hide: "छिपाएँ",
    restore: "वापस दिखाएँ",
    remove: "हटाएँ",
    report: "रिपोर्ट करें",
    reported: "रिपोर्ट भेज दी गई। समुदाय को सुरक्षित रखने में मदद के लिए धन्यवाद।",
    held: "मॉडरेटर की समीक्षा का इंतज़ार",
    hidden: "रिपोर्ट के बाद छिपाया गया",
    comments: "{n} कमेंट",
    comment: "कमेंट करें",
    comment_ph: "कमेंट लिखें…",
    send: "भेजें",
    load_more: "और देखें",
    empty_feed: "अभी कोई पोस्ट नहीं। पहली बातचीत शुरू करें।",
    confirm_del: "यह पोस्ट हटाएँ?",
    confirm_room_del: "यह ग्रुप हमेशा के लिए हटाएँ? सदस्य और पोस्ट अब नहीं दिखेंगे।",
    tab_settings: "सेटिंग्स",
    tab_members: "सदस्य",
    tab_requests: "अनुरोध",
    tab_banned: "प्रतिबंधित",
    save: "सेव करें",
    saved: "सेव हो गया",
    approve: "मंज़ूर करें",
    reject: "अस्वीकार करें",
    make_mod: "मॉडरेटर बनाएँ",
    remove_mod: "मॉडरेटर से हटाएँ",
    mute24: "24 घंटे रोकें",
    unmute: "रोक हटाएँ",
    kick: "ग्रुप से निकालें",
    ban: "प्रतिबंधित करें",
    unban: "प्रतिबंध हटाएँ",
    no_requests: "कोई नया अनुरोध नहीं",
    no_banned: "कोई प्रतिबंधित नहीं",
    delete_room: "ग्रुप हटाएँ",
    admin_h: "समुदाय की समीक्षा",
    admin_rooms: "समीक्षा के इंतज़ार में / रुके हुए ग्रुप",
    admin_posts: "छिपी या रोकी गई पोस्ट",
    enable: "चालू करें",
    disable: "ग्रुप रोकें",
    queue_empty: "समीक्षा के लिए कुछ नहीं",
    status_review: "समीक्षा में",
    status_disabled: "रुका हुआ",
    open_room: "ग्रुप खोलें",
    strikes: "{n} असभ्य शब्द",
    default_rules: "1. विनम्र रहें और सम्मान दें। सभ्य भाषा का इस्तेमाल करें।\n2. विषय से हटकर बिक्री या विज्ञापन नहीं (बेचने के लिए बाज़ार का इस्तेमाल करें)।\n3. धोखाधड़ी, परेशान करना या दूसरों की निजी जानकारी साझा करना मना है।\n4. पैसे लेकर वीज़ा, ग्रीन कार्ड या नौकरी की गारंटी देना मना है।\n5. नियम तोड़ने वाली पोस्ट की रिपोर्ट करें।"
  },
  en: {
    nav: "Community",
    created: "Your group is ready",
    h: "Community",
    sub: "Local groups for Indians in your city. Ask questions, share recommendations, and help each other.",
    create: "Create a group",
    search_ph: "Search groups, cities, or topics…",
    all_states: "All states",
    mine: "Your groups",
    discover: "All groups",
    none: "No groups here yet",
    none_b: "Start the first group so Indians in your city can connect.",
    members: "{n} members",
    posts: "{n} posts",
    join: "Join",
    joined: "Joined",
    pending: "Requested",
    ask_join: "Request to join",
    leave: "Leave group",
    manage: "Manage group",
    approval: "Approval to join",
    public: "Public",
    owner: "Owner",
    mod: "Moderator",
    member: "Member",
    all_us: "All of the U.S.",
    new_h: "Create a community group",
    f_name: "Group name",
    f_name_ph: "e.g. Dallas Desi Community, Parents Circle Edison",
    f_desc: "Short description",
    f_desc_ph: "What is this group for?",
    f_where: "Area",
    f_city: "City",
    f_join: "Who can join",
    pub_b: "Anyone can join instantly",
    appr_b: "Moderators approve first",
    rules: "Group rules",
    rules_note: "We've added basic rules for you. Edit them if you like.",
    create_btn: "Create group",
    err_name: "Group name needs at least 3 characters",
    err_taken: "That name is taken. Try another.",
    err_limit: "You can create up to {n} groups",
    in_review: "This name looks official, so the ApnaCircle team will review it before it appears in search.",
    disabled: "This group was paused by the ApnaCircle team.",
    composer: "Ask a question or share something with the group…",
    add_photo: "Photo",
    post_btn: "Post",
    join_to_post: "Join the group to post and comment",
    pending_note: "Request sent. A moderator will review it.",
    members_only: "This group is visible to members only",
    muted: "You're temporarily paused from posting",
    polite_h: "Would you like to rephrase that a little?",
    polite_b: "Your message includes words that may upset others. Our community keeps conversations respectful.",
    polite_edit: "Edit message",
    polite_send: "Post anyway",
    pinned: "Pinned",
    pin: "Pin",
    unpin: "Unpin",
    hide: "Hide",
    restore: "Restore",
    remove: "Delete",
    report: "Report",
    reported: "Report sent. Thanks for helping keep the community safe.",
    held: "Waiting for moderator review",
    hidden: "Hidden after reports",
    comments: "{n} comments",
    comment: "Comment",
    comment_ph: "Write a comment…",
    send: "Send",
    load_more: "Load more",
    empty_feed: "No posts yet. Start the first conversation.",
    confirm_del: "Delete this post?",
    confirm_room_del: "Delete this group permanently? Members and posts will no longer be shown.",
    tab_settings: "Settings",
    tab_members: "Members",
    tab_requests: "Requests",
    tab_banned: "Banned",
    save: "Save",
    saved: "Saved",
    approve: "Approve",
    reject: "Decline",
    make_mod: "Make moderator",
    remove_mod: "Remove moderator",
    mute24: "Pause 24 h",
    unmute: "Unpause",
    kick: "Remove from group",
    ban: "Ban",
    unban: "Unban",
    no_requests: "No new requests",
    no_banned: "No one is banned",
    delete_room: "Delete group",
    admin_h: "Community review",
    admin_rooms: "Groups waiting for review / paused",
    admin_posts: "Hidden or held posts",
    enable: "Enable",
    disable: "Pause group",
    queue_empty: "Nothing to review",
    status_review: "In review",
    status_disabled: "Paused",
    open_room: "Open group",
    strikes: "{n} impolite words",
    default_rules: "1. Be kind and respectful. Use polite language.\n2. No off-topic selling or ads (use the Marketplace to sell).\n3. No scams, harassment, or sharing other people's private information.\n4. No visa, green card, or job guarantees for money.\n5. Please report posts that break these rules."
  },
};
const rt = (app, k, vars) => {
  let s = (RT[app.lang === "en" ? "en" : "hi"][k]) ?? k;
  if (vars) Object.keys(vars).forEach((v) => { s = s.replace("{" + v + "}", vars[v]); });
  return s;
};
/* Same short list as backend/rooms/polite.py: a gentle hint, never a block. */
const RUDE_WORDS = ["चूतिया", "मादरचोद", "बहनचोद", "भोसड़ी", "हरामी", "कमीना", "कुत्ते", "गांडू", "भड़वा", "रंडी", "साला कुत्ता",
  "chutiya", "madarchod", "behenchod", "bhenchod", "bhosdi", "harami", "kamina", "gandu", "bhadwa", "randi", "mc ", "bc ",
  "fuck", "shit", "bitch", "asshole", "bastard", "cunt", "dickhead", "motherfucker"];
const rudeIn = (text) => { const t = (" " + String(text || "") + " ").replace(/kaminari/gi, " ").toLowerCase(); return RUDE_WORDS.filter((w) => t.includes(w)); };
const ROOM_GRADS = [["#13294B", "#2A5C8F"], ["#7A3E12", "#C9772F"], ["#1E5B4A", "#3E9C7E"], ["#5B2A6E", "#9B5CB4"], ["#8C6A22", "#C9A24D"], ["#23405E", "#4F87B8"]];
const roomGrad = (name) => { let h = 0; for (const ch of String(name)) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return ROOM_GRADS[h % ROOM_GRADS.length]; };
function RoomAvatar({ room, size = 48 }) {
  const [a, b] = roomGrad(room.name);
  return <span className="ravatar" style={{ width: size, height: size, fontSize: size * 0.42, background: `linear-gradient(135deg,${a},${b})` }} aria-hidden="true">{initials(room.name)}</span>;
}
const roomPlace = (room, app) => [room.city, room.state ? app.F.stateName(room.state) : ""].filter(Boolean).join(", ") || rt(app, "all_us");
const roomRoleLabel = (app, role) => (role === "owner" || role === "admin" ? rt(app, "owner") : role === "mod" ? rt(app, "mod") : "");

function RoomsView({ app }) {
  const { F } = app;
  const [data, setData] = useState(null), [mine, setMine] = useState([]), [q, setQ] = useState(""), [st, setSt] = useState(""), [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    const tm = setTimeout(async () => {
      try {
        const d = await api(`/rooms/?q=${encodeURIComponent(q)}&state=${st}`);
        if (live) { setData(d); setErr(false); }
      } catch (e) { if (live) setErr(true); }
    }, q ? 250 : 0);
    return () => { live = false; clearTimeout(tm); };
  }, [q, st]);
  useEffect(() => { if (app.user) api("/rooms/?mine=1").then((d) => setMine(d.rooms || [])).catch(() => {}); }, [app.user]);
  const list = data ? data.rooms : [];
  const create = () => (app.user ? app.go("/rooms/new") : app.requireAuth("/rooms/new"));
  return (
    <main className="roomspage">
      <section className="rhero">
        <div><h1>{rt(app, "h")}</h1><p>{rt(app, "sub")}</p></div>
        <button className="btn gold" onClick={create}><Ico n="plus" />{rt(app, "create")}</button>
      </section>
      <div className="ctools">
        <label className="csearch"><Ico n="search" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={rt(app, "search_ph")} aria-label={rt(app, "search_ph")} /></label>
        <select className="inp rstate" value={st} onChange={(e) => setSt(e.target.value)} aria-label={rt(app, "f_where")}>
          <option value="">{rt(app, "all_states")}</option>
          {STATES.map((s) => <option key={s[0]} value={s[0]}>{F.stateName(s[0])}</option>)}
        </select>
      </div>
      {mine.length > 0 && !q && <>
        <h2 className="rsec">{rt(app, "mine")}</h2>
        <div className="rmine">{mine.map((r) => (
          <a key={r.id} className="rmini" href={"#/room/" + r.id}><RoomAvatar room={r} size={40} />
            <span><b>{r.name}</b><small>{r.status !== "active" ? rt(app, "status_" + r.status) : app.pendingByRoom[r.id] > 0 ? <em className="reqs">{nt(app, "requests_n", { n: app.pendingByRoom[r.id] })}</em> : roomRoleLabel(app, r.myRole) || rt(app, "members", { n: r.members })}</small></span></a>
        ))}</div>
      </>}
      <h2 className="rsec">{rt(app, "discover")}</h2>
      {err ? <div className="empty"><h3>{app.t("api_down")}</h3></div>
        : !data ? <div className="spin" aria-label={app.t("loading")} />
        : !list.length ? <div className="empty"><div className="eic"><Ico n="people" /></div><h3>{rt(app, "none")}</h3><p>{rt(app, "none_b")}</p>
            <div className="acts"><button className="btn pri" onClick={create}>{rt(app, "create")}</button></div></div>
        : <div className="rgrid">{list.map((r) => <RoomCard key={r.id} app={app} room={r} />)}</div>}
    </main>
  );
}
function RoomCard({ app, room }) {
  const { F } = app;
  return (
    <a className="rcard" href={"#/room/" + room.id}>
      <div className="rcard-top"><RoomAvatar room={room} />
        <div className="rcard-h"><b>{room.name}</b><small><Ico n="pin" />{roomPlace(room, app)}</small></div></div>
      {room.description && <p>{room.description}</p>}
      <div className="rcard-meta">
        <span>{rt(app, "members", { n: room.members })}</span><span>{rt(app, "posts", { n: room.posts })}</span>
        {room.visibility === "approval" && <span className="rbadge">{rt(app, "approval")}</span>}
        {room.myRole && ["owner", "mod", "member", "admin"].includes(room.myRole) && <span className="rbadge ok">{rt(app, "joined")}</span>}
        <span className="rtime">{F.ago(room.lastActivity)}</span>
      </div>
    </a>
  );
}
function RoomCreate({ app }) {
  const { F, lang } = app;
  const [D, setD] = useState({ name: "", description: "", state: app.loc && app.loc.state ? app.loc.state : "", city: "", visibility: "public", rules: RT[lang === "en" ? "en" : "hi"].default_rules });
  const [err, setErr] = useState(""), [busy, setBusy] = useState(false), [showRules, setShowRules] = useState(false);
  const up = (p) => { setD((d) => ({ ...d, ...p })); setErr(""); };
  const submit = async () => {
    if (D.name.trim().length < 3) { setErr(rt(app, "err_name")); return; }
    setBusy(true);
    try {
      const r = await api("/rooms/", { method: "POST", body: JSON.stringify(D) });
      app.toast(r.status === "review" ? rt(app, "in_review") : rt(app, "created"));
      window.location.replace("#/room/" + r.id);
    } catch (e) {
      setBusy(false);
      if (e.status === 401) { app.requireAuth("/rooms/new"); return; }
      const c = e.message;
      setErr(c === "name_taken" ? rt(app, "err_taken") : c === "room_limit" ? rt(app, "err_limit", { n: (e.data && e.data.limit) || 3 }) : c === "name_required" ? rt(app, "err_name") : app.t("err_save"));
    }
  };
  return (
    <main><div className="wrap-narrow">
      <MBar app={app} title={rt(app, "new_h")} to="/rooms" />
      <h1 className="pagetitle">{rt(app, "new_h")}</h1>
      <div className="card-pad cform">
        <label className="field"><span>{rt(app, "f_name")}</span><input className="inp" maxLength={60} value={D.name} placeholder={rt(app, "f_name_ph")} onChange={(e) => up({ name: e.target.value })} autoFocus /></label>
        <label className="field"><span>{rt(app, "f_desc")} <em>{app.t("optional")}</em></span><input className="inp" maxLength={500} value={D.description} placeholder={rt(app, "f_desc_ph")} onChange={(e) => up({ description: e.target.value })} /></label>
        <div className="two">
          <label className="field"><span>{rt(app, "f_where")}</span>
            <select className="inp" value={D.state} onChange={(e) => up({ state: e.target.value })}>
              <option value="">{rt(app, "all_us")}</option>
              {STATES.map((s) => <option key={s[0]} value={s[0]}>{F.stateName(s[0])}</option>)}
            </select></label>
          <label className="field"><span>{rt(app, "f_city")} <em>{app.t("optional")}</em></span><input className="inp" maxLength={60} value={D.city} placeholder="Los Angeles" onChange={(e) => up({ city: e.target.value })} /></label>
        </div>
        <div className="field"><span>{rt(app, "f_join")}</span>
          <div className="rvis">
            {[["public", "pub_b"], ["approval", "appr_b"]].map(([k, b]) => (
              <button key={k} className={"rvopt" + (D.visibility === k ? " on" : "")} aria-pressed={D.visibility === k} onClick={() => up({ visibility: k })}>
                <b>{rt(app, k)}</b><small>{rt(app, b)}</small></button>
            ))}
          </div></div>
        <div className="field"><button className="linkish rrules-t" onClick={() => setShowRules(!showRules)}>{rt(app, "rules")} {showRules ? "▴" : "▾"}</button>
          <small className="zipok">{rt(app, "rules_note")}</small>
          {showRules && <textarea className="inp" rows={6} maxLength={3000} value={D.rules} onChange={(e) => up({ rules: e.target.value })} />}</div>
        {err && <div className="err" role="alert">{err}</div>}
        <button className="btn pri wide" disabled={busy} onClick={submit}>{busy ? app.t("loading") : rt(app, "create_btn")}</button>
      </div>
    </div></main>
  );
}
function PoliteSheet({ app, words, onEdit, onSend }) {
  return (
    <div className="scrim" onClick={onEdit}>
      <div className="sheet polite" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <span className="pic" aria-hidden="true">🙏</span>
        <h3>{rt(app, "polite_h")}</h3>
        <p>{rt(app, "polite_b")}</p>
        <div className="pwords">{words.map((w) => <span key={w}>{w}</span>)}</div>
        <div className="pacts"><button className="btn pri" onClick={onEdit}>{rt(app, "polite_edit")}</button><button className="btn" onClick={onSend}>{rt(app, "polite_send")}</button></div>
      </div>
    </div>
  );
}
function RoomView({ app, id }) {
  const [room, setRoom] = useState(null), [missing, setMissing] = useState(false);
  const [posts, setPosts] = useState([]), [more, setMore] = useState(false), [feedErr, setFeedErr] = useState(""), [loading, setLoading] = useState(true);
  const [text, setText] = useState(""), [photos, setPhotos] = useState([]), [busy, setBusy] = useState(false), [polite, setPolite] = useState(null), [showRules, setShowRules] = useState(false);
  const [viewer, setViewer] = useState(null), [invite, setInvite] = useState(false);
  const invited = hasWindow && /[?&]invite=1/.test(window.location.hash);
  const loadRoom = useCallback(async () => {
    try { setRoom(await api(`/rooms/${encodeURIComponent(id)}/`)); } catch (e) { if (e.status === 404) setMissing(true); }
  }, [id]);
  const loadFeed = useCallback(async (before) => {
    try {
      const d = await api(`/rooms/${encodeURIComponent(id)}/posts/${before ? "?before=" + before : ""}`);
      setPosts((p) => (before ? [...p, ...d.posts] : d.posts)); setMore(d.hasMore); setFeedErr("");
    } catch (e) { setFeedErr(e.message === "members_only" ? "members_only" : "down"); }
    setLoading(false);
  }, [id]);
  useEffect(() => { setRoom(null); setMissing(false); setPosts([]); setLoading(true); loadRoom(); loadFeed(); }, [id, loadRoom, loadFeed]);
  if (missing) return <main><MBar app={app} title="" to="/rooms" /><div className="empty"><h3>{app.t("not_found")}</h3><div className="acts"><a className="btn" href="#/rooms">{rt(app, "h")}</a></div></div></main>;
  if (!room) return <main><div className="spin" aria-label={app.t("loading")} /></main>;
  const role = room.myRole, member = ["admin", "owner", "mod", "member"].includes(role), mod = ["admin", "owner", "mod"].includes(role);
  const join = async () => {
    if (!app.user) { app.requireAuth("/room/" + id); return; }
    try { await api(`/rooms/${id}/join/`, { method: "POST" }); await loadRoom(); loadFeed(); } catch (e) { app.toast(e.message === "banned" ? app.t("err_save") : app.t("err_save")); }
  };
  const leave = async () => { try { await api(`/rooms/${id}/leave/`, { method: "POST" }); loadRoom(); } catch (e) { app.toast(app.t("err_save")); } };
  const addPhotos = async (files) => {
    const out = [...photos];
    for (const f of [...files].slice(0, 4 - out.length)) { try { out.push(await compress(f)); } catch (e) { /* skip */ } }
    setPhotos(out);
  };
  const send = async (confirmPolite) => {
    if (!text.trim() && !photos.length) return;
    const words = rudeIn(text);
    if (words.length && !confirmPolite) { setPolite(words); return; }
    setPolite(null); setBusy(true);
    try {
      const p = await api(`/rooms/${id}/posts/`, { method: "POST", body: JSON.stringify({ text, photos, confirmPolite: !!confirmPolite }) });
      setPosts((ps) => { const pinned = ps.filter((x) => x.pinned); return [...pinned, p, ...ps.filter((x) => !x.pinned)]; });
      setText(""); setPhotos([]);
      if (p.status === "held") app.toast(rt(app, "held"));
    } catch (e) {
      if (e.message === "impolite") setPolite((e.data && e.data.words) || []);
      else app.toast(e.message === "muted" ? rt(app, "muted") : e.status === 429 ? app.t("st_rate") : app.t("err_save"));
    }
    setBusy(false);
  };
  const updatePost = (p) => setPosts((ps) => ps.map((x) => (x.id === p.id ? { ...x, ...p } : x)));
  const dropPost = (pid) => setPosts((ps) => ps.filter((x) => x.id !== pid));
  return (
    <main className="roompage">
      <MBar app={app} title={room.name} to="/rooms" />
      <section className="rhead">
        <div className="rhead-top">
          <RoomAvatar room={room} size={64} />
          <div className="rhead-h">
            <h1>{room.name}</h1>
            <p><Ico n="pin" />{roomPlace(room, app)} · {rt(app, "members", { n: room.members })} · {rt(app, room.visibility)}</p>
          </div>
          <div className="rhead-acts">
            {["admin", "owner", "mod", "member"].includes(role) && room.status === "active" && <button className="btn sm" onClick={() => setInvite(true)}><Ico n="people" />{nt(app, "invite")}</button>}
            {mod && <a className="btn sm" href={"#/room/" + id + "/manage"}><Ico n="wrench" />{rt(app, "manage")}{app.pendingByRoom[id] > 0 && <b className="nbadge">{app.pendingByRoom[id]}</b>}</a>}
            {!role && room.status === "active" && <button className="btn sm pri" onClick={join}>{rt(app, room.visibility === "approval" ? "ask_join" : "join")}</button>}
            {role === "pending" && <span className="rbadge">{rt(app, "pending")}</span>}
            {role === "member" && <button className="btn sm" onClick={leave}>{rt(app, "leave")}</button>}
          </div>
        </div>
        {invited && !role && room.status === "active" && <div className="invbanner"><Ico n="people" /><span>{nt(app, "invited_banner")}{room.visibility === "approval" ? " " + nt(app, "private_note") : ""}</span>
          <button className="btn sm pri" onClick={join}>{rt(app, room.visibility === "approval" ? "ask_join" : "join")}</button></div>}
        {room.description && <p className="rdesc">{room.description}</p>}
        {room.status === "review" && <div className="rnote">{rt(app, "in_review")}</div>}
        {room.status === "disabled" && <div className="rnote bad">{rt(app, "disabled")}{room.statusNote ? ` ${room.statusNote}` : ""}</div>}
        {room.rules && <div className="rrules"><button className="linkish" onClick={() => setShowRules(!showRules)}><Ico n="shield" />{rt(app, "rules")} {showRules ? "▴" : "▾"}</button>
          {showRules && <p>{room.rules}</p>}</div>}
      </section>

      {member && room.status === "active" ? (
        <section className="rcompose">
          <textarea className="inp" rows={text ? 3 : 2} maxLength={5000} value={text} placeholder={rt(app, "composer")} onChange={(e) => setText(e.target.value)} />
          {photos.length > 0 && <div className="rphotos-edit">{photos.map((p, i) => (
            <span key={i}><img src={p} alt="" /><button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} aria-label={app.t("del")}><Ico n="close" /></button></span>
          ))}</div>}
          <div className="rcompose-bar">
            <label className={"btn sm" + (photos.length >= 4 ? " disabled" : "")}><Ico n="image" />{rt(app, "add_photo")}
              <input type="file" accept="image/*" multiple hidden disabled={photos.length >= 4} onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} /></label>
            <button className="btn sm pri" disabled={busy || (!text.trim() && !photos.length)} onClick={() => send(false)}>{busy ? app.t("loading") : rt(app, "post_btn")}</button>
          </div>
        </section>
      ) : room.status === "active" && (
        <div className="rjoin">{role === "pending" ? rt(app, "pending_note") : <>{rt(app, "join_to_post")} <button className="btn sm pri" onClick={join}>{rt(app, room.visibility === "approval" ? "ask_join" : "join")}</button></>}</div>
      )}

      <section className="rfeed">
        {loading ? <div className="spin" /> : feedErr === "members_only" ? <div className="empty"><div className="eic"><Ico n="lock" /></div><h3>{rt(app, "members_only")}</h3></div>
          : feedErr ? <div className="empty"><h3>{app.t("api_down")}</h3></div>
          : !posts.length ? <div className="empty"><div className="eic"><Ico n="chat" /></div><h3>{rt(app, "empty_feed")}</h3></div>
          : posts.map((p) => <RoomPost key={p.id} app={app} room={room} p={p} mod={mod} member={member} onChange={updatePost} onDrop={dropPost} onPhoto={(i) => setViewer({ photos: p.photos, start: i })} />)}
        {more && <div className="more"><button className="btn" onClick={() => loadFeed(posts.filter((x) => !x.pinned).slice(-1)[0]?.id)}>{rt(app, "load_more")}</button></div>}
      </section>
      {polite && <PoliteSheet app={app} words={polite} onEdit={() => setPolite(null)} onSend={() => send(true)} />}
      {viewer && <Lightbox photos={viewer.photos.map(safeImg)} start={viewer.start} alt={room.name} t={app.t} onClose={() => setViewer(null)} />}
      {invite && <InviteSheet app={app} title={nt(app, "invite_h")} sub={nt(app, "invite_sub")} group={room.name} privateGroup={room.visibility === "approval"}
        url={inviteUrl("/g/" + room.id + "/", app)} onClose={() => setInvite(false)} />}
    </main>
  );
}
function RoomPost({ app, room, p, mod, member, onChange, onDrop, onPhoto }) {
  const { F } = app;
  const [open, setOpen] = useState(false), [menu, setMenu] = useState(false);
  const act = async (patch) => { try { onChange(await api(`/rooms/posts/${p.id}/`, { method: "PATCH", body: JSON.stringify(patch) })); } catch (e) { app.toast(app.t("err_save")); } setMenu(false); };
  const del = async () => { if (!window.confirm(rt(app, "confirm_del"))) return; try { await api(`/rooms/posts/${p.id}/`, { method: "DELETE" }); onDrop(p.id); } catch (e) { app.toast(app.t("err_save")); } };
  const report = async () => { try { await api("/rooms/report/", { method: "POST", body: JSON.stringify({ kind: "post", id: p.id }) }); app.toast(rt(app, "reported")); } catch (e) { app.toast(app.t("err_save")); } setMenu(false); };
  const isOwner = p.authorId === room.ownerId;
  const photos = p.photos.map(safeImg).filter(Boolean);
  return (
    <article className={"rpost" + (p.status !== "visible" ? " dim" : "")}>
      <header>
        <Avatar src={p.authorAvatar} name={p.authorName} size={38} />
        <div className="rpost-h"><b>{p.authorName}{isOwner && <span className="rrole">{rt(app, "owner")}</span>}</b>
          <small>{F.ago(p.createdAt)}{p.pinned && <> · <span className="rpin">{rt(app, "pinned")}</span></>}</small></div>
        {app.user && <div className="rmenu">
          <button className="ibtn" onClick={() => setMenu(!menu)} aria-label="menu" aria-expanded={menu}><Ico n="dots" /></button>
          {menu && <div className="rmenu-pop" role="menu">
            {mod && <button onClick={() => act({ pinned: !p.pinned })}>{rt(app, p.pinned ? "unpin" : "pin")}</button>}
            {mod && p.status === "visible" && <button onClick={() => act({ status: "hidden" })}>{rt(app, "hide")}</button>}
            {mod && p.status !== "visible" && <button onClick={() => act({ status: "visible" })}>{rt(app, "restore")}</button>}
            {(p.mine || mod) && <button className="danger" onClick={del}>{rt(app, "remove")}</button>}
            {!p.mine && <button onClick={report}>{rt(app, "report")}</button>}
          </div>}
        </div>}
      </header>
      {p.status !== "visible" && <div className="rstatus">{rt(app, p.status === "held" ? "held" : "hidden")}</div>}
      {p.text && <p className="rtext">{p.text}</p>}
      {photos.length > 0 && <div className={"rpics n" + photos.length}>{photos.map((src, i) => (
        <button key={i} onClick={() => onPhoto(i)} aria-label={app.t("photo_of", { a: i + 1, b: photos.length })}><img src={src} alt="" loading="lazy" /></button>
      ))}</div>}
      <footer><button className="linkish" onClick={() => setOpen(!open)}><Ico n="chat" />{p.comments ? rt(app, "comments", { n: p.comments }) : rt(app, "comment")}</button></footer>
      {open && <RoomComments app={app} p={p} mod={mod} member={member} onCount={(d) => onChange({ id: p.id, comments: Math.max(0, p.comments + d) })} />}
    </article>
  );
}
function RoomComments({ app, p, mod, member, onCount }) {
  const { F } = app;
  const [list, setList] = useState(null), [text, setText] = useState(""), [polite, setPolite] = useState(null), [busy, setBusy] = useState(false);
  useEffect(() => { api(`/rooms/posts/${p.id}/comments/`).then((d) => setList(d.comments)).catch(() => setList([])); }, [p.id]);
  const send = async (confirmPolite) => {
    if (!text.trim()) return;
    const words = rudeIn(text);
    if (words.length && !confirmPolite) { setPolite(words); return; }
    setPolite(null); setBusy(true);
    try {
      const c = await api(`/rooms/posts/${p.id}/comments/`, { method: "POST", body: JSON.stringify({ text, confirmPolite: !!confirmPolite }) });
      setList((l) => [...(l || []), c]); setText(""); onCount(1);
    } catch (e) { if (e.message === "impolite") setPolite((e.data && e.data.words) || []); else app.toast(e.message === "muted" ? rt(app, "muted") : app.t("err_save")); }
    setBusy(false);
  };
  const del = async (c) => { try { await api(`/rooms/comments/${c.id}/`, { method: "DELETE" }); setList((l) => l.filter((x) => x.id !== c.id)); onCount(-1); } catch (e) { app.toast(app.t("err_save")); } };
  return (
    <div className="rcomments">
      {list === null ? <div className="spin sm" /> : list.map((c) => (
        <div className="rcomment" key={c.id}>
          <Avatar src={c.authorAvatar} name={c.authorName} size={28} />
          <div><b>{c.authorName}</b> <small>{F.ago(c.createdAt)}</small><p>{c.text}</p></div>
          {(c.mine || mod) && <button className="ibtn sm" onClick={() => del(c)} aria-label={rt(app, "remove")}><Ico n="trash" /></button>}
        </div>
      ))}
      {member && <div className="rcomment-in">
        <input className="inp" maxLength={1500} value={text} placeholder={rt(app, "comment_ph")} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) send(false); }} />
        <button className="btn sm pri" disabled={busy || !text.trim()} onClick={() => send(false)}>{rt(app, "send")}</button>
      </div>}
      {polite && <PoliteSheet app={app} words={polite} onEdit={() => setPolite(null)} onSend={() => send(true)} />}
    </div>
  );
}
function RoomManage({ app, id }) {
  const { F } = app;
  const [room, setRoom] = useState(null), [tab, setTab] = useState(() => (app.pendingByRoom[id] > 0 ? "requests" : "members")), [list, setList] = useState(null), [form, setForm] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api(`/rooms/${id}/`); setRoom(r); setForm({ name: r.name, description: r.description, rules: r.rules, city: r.city, state: r.state, visibility: r.visibility }); }
    catch (e) { setRoom(false); }
  }, [id]);
  const loadList = useCallback(async () => {
    setList(null);
    const q = tab === "requests" ? "?state=pending" : tab === "banned" ? "?state=banned" : "";
    try { setList((await api(`/rooms/${id}/members/${q}`)).members); } catch (e) { setList([]); }
  }, [id, tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab !== "settings") loadList(); }, [tab, loadList]);
  useEffect(() => { if (tab === "requests") app.refreshPending(); }, [list]); // eslint-disable-line react-hooks/exhaustive-deps
  if (room === false) return <main><div className="empty"><h3>{app.t("not_found")}</h3></div></main>;
  if (!room || !form) return <main><div className="spin" /></main>;
  const boss = room.myRole === "owner" || room.myRole === "admin", admin = room.myRole === "admin";
  if (!["owner", "admin", "mod"].includes(room.myRole)) return <main><div className="empty"><h3>{app.t("not_found")}</h3></div></main>;
  const act = async (m, action, extra) => {
    try { await api(`/rooms/${id}/members/${m.userId.split(":")[1]}/`, { method: "POST", body: JSON.stringify({ action, ...extra }) }); loadList(); load(); }
    catch (e) { app.toast(app.t("err_save")); }
  };
  const save = async (patch) => {
    try { const r = await api(`/rooms/${id}/`, { method: "PATCH", body: JSON.stringify(patch || form) }); setRoom(r); app.toast(rt(app, "saved")); }
    catch (e) { app.toast(e.message === "name_taken" ? rt(app, "err_taken") : app.t("err_save")); }
  };
  const delRoom = async () => {
    if (!window.confirm(rt(app, "confirm_room_del"))) return;
    try { await api(`/rooms/${id}/`, { method: "DELETE" }); window.location.replace("#/rooms"); } catch (e) { app.toast(app.t("err_save")); }
  };
  const up = (p) => setForm((f) => ({ ...f, ...p }));
  return (
    <main><div className="wrap-narrow rmanage">
      <MBar app={app} title={rt(app, "manage")} to={"/room/" + id} />
      <div className="rmanage-h"><RoomAvatar room={room} size={44} /><div><h1>{room.name}</h1><a href={"#/room/" + id} className="linkish">{rt(app, "open_room")}</a></div></div>
      <div className="segtabs" role="tablist">
        {["members", "requests", "banned", ...(boss ? ["settings"] : [])].map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{rt(app, "tab_" + k)}</button>
        ))}
      </div>
      {tab === "settings" ? (
        <div className="card-pad cform" style={{ marginTop: 14 }}>
          {admin && <div className="radmin">
            <b>ApnaCircle</b>
            <span className={"rbadge" + (room.status === "active" ? " ok" : "")}>{room.status === "active" ? rt(app, "enable") : rt(app, "status_" + room.status)}</span>
            {room.status !== "active" ? <button className="btn sm pri" onClick={() => save({ status: "active" })}>{rt(app, "enable")}</button>
              : <button className="btn sm" onClick={() => save({ status: "disabled" })}>{rt(app, "disable")}</button>}
          </div>}
          <label className="field"><span>{rt(app, "f_name")}</span><input className="inp" maxLength={60} value={form.name} onChange={(e) => up({ name: e.target.value })} /></label>
          <label className="field"><span>{rt(app, "f_desc")}</span><input className="inp" maxLength={500} value={form.description} onChange={(e) => up({ description: e.target.value })} /></label>
          <div className="two">
            <label className="field"><span>{rt(app, "f_where")}</span><select className="inp" value={form.state} onChange={(e) => up({ state: e.target.value })}>
              <option value="">{rt(app, "all_us")}</option>{STATES.map((s) => <option key={s[0]} value={s[0]}>{F.stateName(s[0])}</option>)}</select></label>
            <label className="field"><span>{rt(app, "f_city")}</span><input className="inp" maxLength={60} value={form.city} onChange={(e) => up({ city: e.target.value })} /></label>
          </div>
          <div className="field"><span>{rt(app, "f_join")}</span><div className="rvis">
            {[["public", "pub_b"], ["approval", "appr_b"]].map(([k, b]) => (
              <button key={k} className={"rvopt" + (form.visibility === k ? " on" : "")} onClick={() => up({ visibility: k })}><b>{rt(app, k)}</b><small>{rt(app, b)}</small></button>))}
          </div></div>
          <label className="field"><span>{rt(app, "rules")}</span><textarea className="inp" rows={6} maxLength={3000} value={form.rules} onChange={(e) => up({ rules: e.target.value })} /></label>
          <button className="btn pri wide" onClick={() => save()}>{rt(app, "save")}</button>
          <button className="btn danger wide" onClick={delRoom} style={{ marginTop: 10 }}><Ico n="trash" />{rt(app, "delete_room")}</button>
        </div>
      ) : (
        <div className="rmembers">
          {list === null ? <div className="spin" /> : !list.length ? <div className="empty"><p>{rt(app, tab === "requests" ? "no_requests" : "no_banned")}</p></div>
            : list.map((m) => (
              <div className="rmrow" key={m.userId}>
                <Avatar src={m.avatar} name={m.name} size={38} />
                <div className="rmrow-h"><b>{m.name}</b>
                  <small>{roomRoleLabel(app, m.role) || rt(app, "member")}{m.muted ? " · " + rt(app, "mute24") : ""}{m.strikes ? " · " + rt(app, "strikes", { n: m.strikes }) : ""}</small></div>
                <div className="rmrow-a">
                  {tab === "requests" && <><button className="btn sm pri" onClick={() => act(m, "approve")}>{rt(app, "approve")}</button><button className="btn sm" onClick={() => act(m, "reject")}>{rt(app, "reject")}</button></>}
                  {tab === "banned" && <button className="btn sm" onClick={() => act(m, "unban")}>{rt(app, "unban")}</button>}
                  {tab === "members" && m.role !== "owner" && (boss || m.role === "member") && m.userId !== Store.me && <select className="inp sm" value="" aria-label={rt(app, "manage")}
                    onChange={(e) => { const v = e.target.value; if (v === "mute") act(m, "mute", { hours: 24 }); else if (v) act(m, v); }}>
                    <option value="">⋯</option>
                    {boss && m.role === "member" && <option value="make_mod">{rt(app, "make_mod")}</option>}
                    {boss && m.role === "mod" && <option value="remove_mod">{rt(app, "remove_mod")}</option>}
                    {m.muted ? <option value="unmute">{rt(app, "unmute")}</option> : <option value="mute">{rt(app, "mute24")}</option>}
                    <option value="remove">{rt(app, "kick")}</option>
                    <option value="ban">{rt(app, "ban")}</option>
                  </select>}
                </div>
              </div>
            ))}
        </div>
      )}
    </div></main>
  );
}
function RoomsAdmin({ app }) {
  const { F } = app;
  const [q, setQ] = useState(null);
  const load = useCallback(() => api("/rooms/review/").then(setQ).catch(() => setQ({ rooms: [], posts: [], denied: true })), []);
  useEffect(() => { load(); }, [load]);
  if (!app.user || !app.user.isAdmin) return <main><div className="empty"><h3>{app.t("not_found")}</h3></div></main>;
  const roomAct = async (r, patch, method = "PATCH") => { try { await api(`/rooms/${r.id}/`, { method, body: method === "PATCH" ? JSON.stringify(patch) : undefined }); load(); } catch (e) { app.toast(app.t("err_save")); } };
  const postAct = async (p, patch, method = "PATCH") => { try { await api(`/rooms/posts/${p.id}/`, { method, body: method === "PATCH" ? JSON.stringify(patch) : undefined }); load(); } catch (e) { app.toast(app.t("err_save")); } };
  return (
    <main><div className="wrap-narrow">
      <MBar app={app} title={rt(app, "admin_h")} to="/me" />
      <h1 className="pagetitle">{rt(app, "admin_h")}</h1>
      {!q ? <div className="spin" /> : <>
        <h2 className="rsec">{rt(app, "admin_rooms")}</h2>
        {!q.rooms.length ? <p className="muted">{rt(app, "queue_empty")}</p> : q.rooms.map((r) => (
          <div className="rmrow" key={r.id}><RoomAvatar room={r} size={40} />
            <div className="rmrow-h"><b><a href={"#/room/" + r.id}>{r.name}</a></b><small>{rt(app, "status_" + r.status)} · {r.ownerName} · {F.ago(r.createdAt)}</small></div>
            <div className="rmrow-a"><button className="btn sm pri" onClick={() => roomAct(r, { status: "active" })}>{rt(app, "enable")}</button>
              {r.status !== "disabled" && <button className="btn sm" onClick={() => roomAct(r, { status: "disabled" })}>{rt(app, "disable")}</button>}
              <button className="btn sm danger" onClick={() => window.confirm(rt(app, "confirm_room_del")) && roomAct(r, null, "DELETE")}><Ico n="trash" /></button></div>
          </div>))}
        <h2 className="rsec">{rt(app, "admin_posts")}</h2>
        {!q.posts.length ? <p className="muted">{rt(app, "queue_empty")}</p> : q.posts.map((p) => (
          <div className="rmrow top" key={p.id}><Avatar src={p.authorAvatar} name={p.authorName} size={38} />
            <div className="rmrow-h"><b>{p.authorName}</b><small><a href={"#/room/" + p.roomId}>{p.roomName}</a> · {rt(app, p.status === "held" ? "held" : "hidden")}{p.reports ? ` · ${p.reports} ${rt(app, "report")}` : ""}</small><p className="rtext sm">{p.text}</p></div>
            <div className="rmrow-a"><button className="btn sm pri" onClick={() => postAct(p, { status: "visible" })}>{rt(app, "restore")}</button>
              <button className="btn sm danger" onClick={() => postAct(p, null, "DELETE")}>{rt(app, "remove")}</button></div>
          </div>))}
      </>}
    </div></main>
  );
}

/* ─── legal pages & footer ─────────────────────────────────────────────── */
function LegalView({ app, kind }) {
  const { lang, t } = app;
  const L = lang === "en" ? "en" : "hi", doc = LEGAL[kind][L], other = kind === "privacy" ? "terms" : "privacy";
  const fill = (p, key) => p.split("{email}").flatMap((part, i) => (i ? [<a key={key + "m" + i} href={"mailto:" + SUPPORT_EMAIL}>{SUPPORT_EMAIL}</a>, part] : [part]));
  const [active, setActive] = useState(0);
  useEffect(() => { window.scrollTo(0, 0); setActive(0); }, [kind]);
  useEffect(() => {
    const els = [...document.querySelectorAll(".lsec")];
    if (!("IntersectionObserver" in window) || !els.length) return undefined;
    const io = new IntersectionObserver((ents) => {
      ents.forEach((e) => { if (e.isIntersecting) setActive(Number(e.target.dataset.i)); });
    }, { rootMargin: "-20% 0px -70% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [kind, lang]);
  const jump = (i) => { const el = document.getElementById(`lsec-${i}`); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
  return (
    <main className="legalpage">
      <MBar app={app} title={doc.title} to="/me" />
      <header className="lhero">
        <div className="lhero-in">
          <nav className="lswitch" aria-label={t("legal_nav")}>
            {["privacy", "terms"].map((k) => <a key={k} href={"#/" + k} className={k === kind ? "on" : ""} aria-current={k === kind ? "page" : undefined}>{LEGAL[k][L].title}</a>)}
          </nav>
          <h1>{doc.title}</h1>
          <p className="ldate">{t("updated")} {tripDate(EFFECTIVE, lang, { day: "numeric", month: "long", year: "numeric" })}</p>
          <p className="lintro">{doc.intro[0]}</p>
        </div>
      </header>
      <section className="lglance" aria-label={t("at_glance")}>
        <h2>{t("at_glance")}</h2>
        <div className="lgrid">
          {doc.glance.map(([ic, h, p], i) => (
            <div className="lcard" key={i}><span className="lic"><Ico n={ic} /></span><b>{h}</b><p>{p}</p></div>
          ))}
        </div>
      </section>
      <div className="lbody">
        <aside className="ltoc" aria-label={t("on_this_page")}>
          <small>{t("on_this_page")}</small>
          <ol>{doc.sections.map(([h], i) => (
            <li key={i}><button className={active === i ? "on" : ""} onClick={() => jump(i)}>{h}</button></li>
          ))}</ol>
        </aside>
        <article className="ldoc">
          {doc.intro.slice(1).map((p, i) => <p className="lead" key={i}>{p}</p>)}
          {doc.sections.map(([h, ps], i) => (
            <section className="lsec" id={`lsec-${i}`} data-i={i} key={i}>
              <h2><span>{String(i + 1).padStart(2, "0")}</span>{h}</h2>
              {ps.length > 1 ? <ul>{ps.map((p, j) => <li key={j}>{fill(p, i + "-" + j)}</li>)}</ul> : <p>{fill(ps[0], i + "-0")}</p>}
            </section>
          ))}
          <div className="lcontact">
            <span className="lic"><Ico n="mail" /></span>
            <div><b>{t("questions_h")}</b><p>{t("questions_b")}</p></div>
            <a className="btn pri" href={"mailto:" + SUPPORT_EMAIL}>{t("contact_us")}</a>
          </div>
          <a className="lnext" href={"#/" + other}><small>{t("read_next")}</small><b>{LEGAL[other][L].title}</b><Ico n="right" /></a>
        </article>
      </div>
    </main>
  );
}
function SiteFoot({ app }) {
  const { t, lang } = app;
  const cols = [
    [t("f_market"), [["#/", t("f_browse")], ["#/carry", t("f_carry")], ["#/post", t("f_post")], ["#/saved", t("nav_saved")]]],
    [t("f_community"), [["#/join", "Founding Members"], ["#/verify", nt(app, "verify_h")], ["#/inbox", t("nav_inbox")], ["#/me", t("f_account")], ["mailto:" + SUPPORT_EMAIL, t("f_support")]]],
    [t("f_legal"), [["#/privacy", t("privacy")], ["#/terms", t("terms")]]],
  ];
  return (
    <footer className="sitefoot" lang={lang === "en" ? "en" : "hi"}>
      <div className="sf-in">
        <div className="sf-brand">
          <a href="#/" className="sf-logo" aria-label="ApnaCircle"><img className="em-l" src={EMBLEM_LIGHT} alt="" /><img className="em-d" src={EMBLEM_DARK} alt="" /><span><b><i className="wa">Apna</i><i className="wc">Circle</i></b><small>अपना सर्कल</small></span></a>
          <p>{t("foot_tag")}</p>
          <ul className="sf-trust">
            <li><Ico n="lock" />{t("trust_enc")}</li>
            <li><Ico n="shield" />{t("trust_sell")}</li>
          </ul>
        </div>
        {cols.map(([h, links]) => (
          <nav className="sf-col" key={h} aria-label={h}>
            <h3>{h}</h3>
            <ul>{links.map(([href, label]) => <li key={href}><a href={href}>{label}</a></li>)}</ul>
          </nav>
        ))}
      </div>
      <div className="sf-bar">
        <span>© {new Date().getFullYear()} ApnaCircle. {t("rights")}</span>
        <span>{t("serving")}</span>
      </div>
    </footer>
  );
}

/* ─── inbox & chat ──────────────────────────────────────────────────────── */
/* WhatsApp-style status: clock = sending, grey ✓✓ = stored but not read, coloured ✓✓ = read */
function Ticks({ m, th, t }) {
  if (m.status === "sending") return <span className="tick sending" title={t("st_sending")} aria-label={t("st_sending")}><svg viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" /><path d="M8 4.8V8l2.2 1.4" /></svg></span>;
  if (m.status === "failed") return <span className="tick failed" title={t("st_failed")} aria-label={t("st_failed")}><svg viewBox="0 0 16 16"><circle cx="8" cy="8" r="6.2" /><path d="M8 4.6v4.2M8 11.2v.01" /></svg></span>;
  const read = m.id && th.peerReadId >= m.id;
  const label = t(read ? "st_read" : "st_sent");
  return <span className={"tick " + (read ? "read" : "sent")} title={label} aria-label={label}><svg viewBox="0 0 20 14"><path d="M1.5 7.5l3.6 3.6L12.4 3.8" /><path d="M7.6 11.1l.2.2 8.9-8.9" /></svg></span>;
}
function peerName(th, t) {
  return th.sellerId === Store.me ? (th.buyerName || t("buyer")) : (th.sellerName || th.peerName || "—");
}
function ThreadRow({ app, th, active }) {
  const { F, t } = app, x = Store.listingFor(th);
  const un = th.unread > 0;
  const mineLast = th.lastBy === Store.me;
  return (
    <a className={"th" + (active ? " on" : "") + (un ? " unread" : "")} href={"#/chat/" + encodeURIComponent(th.id)}>
      <span className="tph">{x && <Thumb x={x} />}<Avatar src={th.peerAvatar} name={peerName(th, t)} size={24} cls="thav" /></span>
      <span className="tb">
        <span className="tt"><b>{peerName(th, t)}</b><small>{th.lastAt ? F.ago(th.lastAt) : ""}</small></span>
        <span className="ts">{x ? F.L(x, "title") : "—"}</span>
        <span className="tl">
          {Chat.isTyping(th.id) ? <span className="tdots" role="status" aria-label={t("typing")}><i /><i /><i /></span> : <>
            {mineLast && th.lastMessageId > 0 && <Ticks m={{ id: th.lastMessageId, status: "sent" }} th={th} t={t} />}
            {mineLast ? t("you_p") : ""}{th.lastText || ""}</>}
        </span>
      </span>
      {un && <span className="ucount" aria-label={`${th.unread} unread`}>{th.unread > 99 ? "99+" : th.unread}</span>}
    </a>
  );
}
function ChatPane({ app, th }) {
  const { t, F } = app, x = Store.listingFor(th), selling = th.sellerId === Store.me;
  const [text, setText] = useState("");
  const box = useRef(null), inp = useRef(null), stick = useRef(true), lastTyping = useRef(0);
  const b = Chat.messages(th.id);
  const msgs = b.list;
  const lastKey = msgs.length ? (msgs[msgs.length - 1].id || msgs[msgs.length - 1].clientId) : "";
  const typing = Chat.isTyping(th.id);

  useEffect(() => { Chat.setVisibleThread(th.id); return () => Chat.setVisibleThread(null); }, [th.id]);
  useEffect(() => { if (b.loaded) Chat.markRead(th.id); }, [th.id, lastKey, b.loaded]); // eslint-disable-line
  useEffect(() => { const el = box.current; if (el && stick.current) el.scrollTop = el.scrollHeight; }, [lastKey, b.loaded, th.id, typing]);
  /* Phones: when the keyboard opens, keep the header, the history and the input
     all inside the visible area (like WhatsApp) instead of letting the page slide up. */
  useEffect(() => {
    const vv = window.visualViewport;
    const el = box.current && box.current.closest(".inbox");
    if (!vv || !el || !window.matchMedia("(max-width: 859px)").matches) return undefined;
    const fit = () => {
      el.style.height = vv.height + "px";
      el.style.top = vv.offsetTop + "px";
      el.style.bottom = "auto";
      const m = box.current;
      if (m && stick.current) m.scrollTop = m.scrollHeight;
    };
    fit();
    vv.addEventListener("resize", fit); vv.addEventListener("scroll", fit);
    return () => { vv.removeEventListener("resize", fit); vv.removeEventListener("scroll", fit); el.style.height = ""; el.style.top = ""; el.style.bottom = ""; };
  }, [th.id]);
  useEffect(() => { if (inp.current && window.matchMedia && window.matchMedia("(min-width: 860px)").matches) inp.current.focus(); }, [th.id]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (el.scrollTop < 60 && b.hasMore && !b.loading) {
      const h = el.scrollHeight;
      Chat.loadOlder(th.id).then(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight - h; });
    }
  };
  const send = (v) => {
    if (!v.trim()) return;
    stick.current = true;
    Chat.send(th.id, v);
    setText(""); if (inp.current) inp.current.style.height = "";
  };
  const onType = (e) => {
    setText(e.target.value);
    e.target.style.height = "auto"; e.target.style.height = Math.min(140, e.target.scrollHeight) + "px";
    const now = Date.now();
    if (now - lastTyping.current > 2500 && e.target.value) { lastTyping.current = now; Chat.typingPing(th.id); }
  };
  let lastT = 0, lastFrom = null;
  return (
    <>
      <div className="ch">
        <button className="ibtn" data-act="back" onClick={() => app.back("/inbox")} aria-label={t("back")}><Ico n="back" /></button>
        <a className="tph" href={x ? "#/item/" + encodeURIComponent(x.id) : "#/inbox"} aria-label={t("view_listing")}>{x && <Thumb x={x} />}</a>
        <Avatar src={th.peerAvatar} name={peerName(th, t)} size={36} cls="chav" />
        <div className="mid">
          <b>{peerName(th, t)}</b>
          <small>{x ? `${F.priceText(x)}, ${F.L(x, "title")}` : ""}</small>
        </div>
        <ChatTrust app={app} th={th} selling={selling} />
        {x && !selling && x.phone && <a className="ibtn" href={telHref(x.phone)} aria-label={t("call")}><Ico n="phone" /></a>}
      </div>
      {Chat.isOffline() && <div className="connbar" role="status">{t("reconnecting")}</div>}
      <div className="msgs" ref={box} onScroll={onScroll} aria-live="polite">
        {b.hasMore && <button className="older" onClick={() => Chat.loadOlder(th.id)} disabled={b.loading}>{b.loading ? t("loading") : t("load_older")}</button>}
        {!b.loaded && b.loading && <div className="spin" aria-label={t("loading")} />}
        {b.error && !b.loaded && <div className="sysnote">{t("api_down")} <button className="linkish" onClick={() => Chat.load(th.id)}>{t("retry")}</button></div>}
        {msgs.map((m) => {
          const gap = (m.at || 0) - lastT > 30 * 60e3; lastT = m.at || 0;
          const mine = m.from === Store.me, grouped = !gap && lastFrom === m.from; lastFrom = m.from;
          return (<React.Fragment key={m.clientId || m.id}>
            {gap && <div className="btime">{F.ago(m.at)}, {F.clock(m.at)}</div>}
            <div className={"bub" + (mine ? " me" : "") + (grouped ? " grp" : "") + (m.status === "failed" ? " failed" : "")}>
              <span className="btxt">{String(m.text || "")}</span>
              <span className="bmeta">{F.clock(m.at)}{mine && <Ticks m={m} th={th} t={t} />}</span>
            </div>
            {m.status === "failed" && <div className="bfail">
              <span>{t(m.error === "rate" ? "st_rate" : "st_failed")}</span>
              <button onClick={() => Chat.retry(m.clientId)}>{t("retry")}</button>
              <button onClick={() => Chat.discard(m.clientId)}>{t("discard_msg")}</button>
            </div>}
          </React.Fragment>);
        })}
        {typing && <div className="bub typingbub" role="status" aria-label={t("typing")}><i /><i /><i /></div>}
      </div>
      {b.loaded && !msgs.length && th.buyerId === Store.me && (
        <div className="quick">{t("quick").map((s) => <button key={s} className="chip" onClick={() => send(s)}>{s}</button>)}</div>
      )}
      <div className="compose">
        <label className="sr" htmlFor="cccin">{t("chat_ph")}</label>
        <textarea id="cccin" ref={inp} rows={1} maxLength={2000} placeholder={t("chat_ph")} value={text} enterKeyHint="send"
          onChange={onType}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(text); } }} />
        <button className="sendb" onMouseDown={(e) => e.preventDefault()} onClick={() => send(text)} disabled={!text.trim()} aria-label={t("send")}><Ico n="send" /></button>
      </div>
    </>
  );
}
function ChatTrust({ app, th, selling }) {
  const { t } = app;
  const other = selling ? th.buyerId : th.sellerId;
  if (!other || other === Store.me) return null;
  const { avg, count } = Store.rating(other);
  const name = peerName(th, t);
  return (
    <div className="chattrust">
      {count ? <button className="ministars" onClick={() => app.setSheet({ type: "reviews", personKey: other, name })} aria-label={t("see_reviews")}>
        <Stars v={avg} /><small>{avg.toFixed(1)}</small></button> : <small className="muted">{t("no_reviews")}</small>}
      {th.lastMessageId > 0 && <button className="btn sm gold" onClick={() => app.setSheet({ type: "review", about: other, aboutName: name, listingId: th.listingId, role: selling ? "buyer" : "seller" })}>
        <Ico n="sparkle" />{t(selling ? "rate_buyer" : "rate_seller")}</button>}
    </div>
  );
}
function InboxView({ app }) {
  const { t, route, inboxTab } = app;
  const all = Store.threads();
  const th = route.r === "chat" ? Chat.thread(route.id) : null;
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setMissing(false);
    if (route.r === "chat" && !Chat.thread(route.id)) Chat.fetchThread(route.id).then((ok) => { if (!ok) setMissing(true); });
  }, [route.r, route.id]);
  const tab = th ? (th.sellerId === Store.me ? "selling" : "buying") : inboxTab;
  useEffect(() => { if (th && tab !== inboxTab) app.setInboxTab(tab); }); // eslint-disable-line
  const mine = tab === "selling" ? all.filter((x) => x.sellerId === Store.me) : all.filter((x) => x.buyerId === Store.me);
  const unreadIn = (k) => all.filter((x) => (k === "selling" ? x.sellerId : x.buyerId) === Store.me && x.unread > 0).length;
  return (
    <main><div className="inbox">
      <section className="ilist">
        <div className="hd"><h1>{t("inbox_h")}</h1>
          <div className="segtabs" role="tablist">
            {["buying", "selling"].map((k) => <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => app.setInboxTab(k)}>
              {t("tab_" + k)}{unreadIn(k) > 0 && <span className="segdot">{unreadIn(k)}</span>}</button>)}
          </div></div>
        <div className="threads">
          {!Chat.threadsLoaded && !all.length ? <div className="spin" aria-label={t("loading")} />
            : mine.length ? mine.map((x) => <ThreadRow key={x.id} app={app} th={x} active={route.r === "chat" && route.id === x.id} />)
            : <div className="empty" style={{ padding: "40px 20px" }}><div className="eic"><Ico n="chat" /></div><h3>{t("inbox_empty")}</h3>
              <p>{tab === "selling" ? t("inbox_empty_s") : t("inbox_empty_b")}</p></div>}
        </div>
      </section>
      <section className="chat">
        {th ? <ChatPane key={th.id} app={app} th={th} />
          : route.r === "chat" ? <div className="chat-empty"><div>{missing ? <>{t("not_found")}<div style={{ marginTop: 12 }}><a className="btn" href="#/inbox">{t("inbox_h")}</a></div></> : <div className="spin" aria-label={t("loading")} />}</div></div>
          : <div className="chat-empty"><div><div className="empty" style={{ padding: 0 }}><div className="eic"><Ico n="chat" /></div></div>{t("pick_chat")}</div></div>}
      </section>
    </div></main>
  );
}

/* ─── authentication ────────────────────────────────────────────────────── */
function AuthView({ app, mode }) {
  const isRegister = mode === "register";
  const en = app.lang === "en";
  const C = en ? {
    login: "Log in", register: "Create account", email: "Email address", password: "Password",
    confirm: "Confirm password", first: "First name", last: "Last name", phone: "Phone number",
    noAccount: "New to ApnaCircle?", haveAccount: "Already have an account?", make: "Create an account", back: "Back to marketplace",
    loginIntro: "Log in to view listings, save items, post, and message sellers.",
    regIntro: "Create your free ApnaCircle account.", mismatch: "Passwords do not match.",
    invalid: "Email or password is incorrect.", exists: "This email is already registered.", phoneExists: "This phone number is already registered.",
    weak: "Please use a stronger password (at least 12 characters).", problem: "Something went wrong. Please try again.",
    welcome: "You’re signed in.", creating: "Creating account…", signing: "Signing in…",
    phoneBad: "Enter a valid U.S. phone number.", nameBad: "First and last name are required.",
    pwHint: "Use 12+ characters with uppercase, lowercase, a number, and a special character.",
    pwLen: "12+ characters", pwUpper: "Uppercase letter", pwLower: "Lowercase letter", pwNumber: "Number", pwSpecial: "Special character", pwMatch: "Passwords match", pwNoMatch: "Passwords do not match", showPw: "Show password", hidePw: "Hide password", usPhone: "U.S. phone numbers only"
  } : {
    login: "लॉग इन", register: "साइन अप", email: "ईमेल", password: "पासवर्ड",
    confirm: "पासवर्ड दोबारा लिखें", first: "पहला नाम", last: "उपनाम", phone: "फ़ोन नंबर",
    noAccount: "खाता नहीं है?", haveAccount: "पहले से खाता है?", make: "खाता बनाएँ", back: "बाज़ार पर लौटें",
    loginIntro: "लिस्टिंग देखने, सेव करने, पोस्ट करने और बेचने वालों को मैसेज करने के लिए लॉग इन करें",
    regIntro: "मुफ़्त ApnaCircle खाता बनाएँ", mismatch: "पासवर्ड मेल नहीं खाते",
    invalid: "ईमेल या पासवर्ड गलत है", exists: "इस ईमेल से पहले से खाता है", phoneExists: "इस फ़ोन नंबर से पहले से खाता है",
    weak: "कृपया ज़्यादा सुरक्षित पासवर्ड रखें (कम से कम 12 अक्षर)", problem: "कुछ गड़बड़ हो गई। फिर से कोशिश करें",
    welcome: "लॉग इन हो गया", creating: "खाता बन रहा है…", signing: "लॉग इन हो रहा है…",
    phoneBad: "कृपया सही अमेरिकी फ़ोन नंबर डालें", nameBad: "कृपया पहला नाम और उपनाम लिखें",
    pwHint: "12 या ज़्यादा अक्षर रखें, जिसमें बड़ा अक्षर, छोटा अक्षर, अंक और विशेष चिह्न हो",
    pwLen: "12+ अक्षर", pwUpper: "बड़ा अक्षर", pwLower: "छोटा अक्षर", pwNumber: "अंक", pwSpecial: "विशेष चिह्न", pwMatch: "पासवर्ड मेल खाते हैं", pwNoMatch: "पासवर्ड मेल नहीं खाते", showPw: "पासवर्ड दिखाएँ", hidePw: "पासवर्ड छिपाएँ", usPhone: "सिर्फ़ अमेरिकी फ़ोन नंबर"
  };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const passwordChecks = {
    length: password.length >= 12,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
  const passwordStrong = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = !!confirm && password === confirm;
  const formatUsPhone = (value) => {
    let digits = String(value || "").replace(/\D/g, "");
    if (digits.length > 10 && digits.startsWith("1")) digits = digits.slice(1);
    digits = digits.slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  };
  const usPhoneValid = (() => {
    const d = phone.replace(/\D/g, "");
    const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
    return /^[2-9]\d{2}[2-9]\d{6}$/.test(ten);
  })();

  const errorText = (e2) => {
    const d = (e2 && e2.data) || {}, codeName = d.error;
    if (codeName === "invalid_credentials") return C.invalid;
    if (codeName === "email_in_use") return C.exists;
    if (codeName === "phone_in_use") return C.phoneExists;
    if (codeName === "valid_phone_required") return C.phoneBad;
    if (codeName === "name_required") return C.nameBad;
    if (codeName === "weak_password") return (d.messages || []).join(" ") || C.weak;
    return C.problem;
  };

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (isRegister && !usPhoneValid) { setErr(C.phoneBad); return; }
    if (isRegister && !passwordStrong) { setErr(C.weak); return; }
    if (isRegister && password !== confirm) { setErr(C.mismatch); return; }
    setBusy(true);
    try {
      const data = await api(isRegister ? "/auth/register/" : "/auth/login/", {
        method: "POST",
        body: JSON.stringify(isRegister ? { email, password, firstName, lastName, phone } : { email, password })
      });
      app.toast(C.welcome);
      app.finishAuth(data.user);
    } catch (e2) {
      setErr(errorText(e2));
    } finally { setBusy(false); }
  };

  if (app.user) return (
    <main className="auth-page"><section className="auth-card">
      <div className="auth-mark"><span className="user-avatar big">{app.user.initials || "U"}</span></div>
      <h1>{app.user.displayName}</h1>
      <p>{app.user.email}</p>
      <div className="auth-actions"><a className="btn pri" href="#/me">{app.t("nav_me")}</a><a className="btn" href="#/">{C.back}</a></div>
    </section></main>
  );

  return (
    <main className="auth-page">
      <section className="auth-card">
        <a className="auth-brand" href="#/"><img src={LOGO_FULL} alt="ApnaCircle" /></a>
        <h1>{isRegister ? C.register : C.login}</h1>
        <p className="auth-intro">{isRegister ? C.regIntro : C.loginIntro}</p>
        <form onSubmit={submit}>
          {isRegister && <div className="auth-name-grid">
            <label className="field"><span>{C.first}</span><input className="inp" autoComplete="given-name" maxLength={150} required value={firstName} onChange={(e) => setFirstName(e.target.value)} /></label>
            <label className="field"><span>{C.last}</span><input className="inp" autoComplete="family-name" maxLength={150} required value={lastName} onChange={(e) => setLastName(e.target.value)} /></label>
          </div>}
          <label className="field"><span>{C.email}</span><input className="inp" type="email" inputMode="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          {isRegister && <label className="field"><span>{C.phone}</span><div className="phone-input-wrap"><span className="phone-prefix"></span><input className="inp phone-us-input" type="tel" inputMode="numeric" autoComplete="tel-national" placeholder="(555) 123-4567" maxLength={14} required value={phone} onChange={(e) => setPhone(formatUsPhone(e.target.value))} /></div><small className="field-help">{C.usPhone}</small></label>}
          <label className="field"><span>{C.password}</span><div className="password-wrap"><input className="inp password-input" type={showPassword ? "text" : "password"} autoComplete={isRegister ? "new-password" : "current-password"} minLength={isRegister ? 12 : 1} required value={password} onChange={(e) => setPassword(e.target.value)} /><button className="password-eye" type="button" aria-label={showPassword ? C.hidePw : C.showPw} title={showPassword ? C.hidePw : C.showPw} onClick={() => setShowPassword((v) => !v)}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="2.7"/></svg></button></div></label>
          {isRegister && <div className="password-rules" aria-live="polite"><div className={passwordChecks.length ? "ok" : ""}>✓ {C.pwLen}</div><div className={passwordChecks.upper ? "ok" : ""}>✓ {C.pwUpper}</div><div className={passwordChecks.lower ? "ok" : ""}>✓ {C.pwLower}</div><div className={passwordChecks.number ? "ok" : ""}>✓ {C.pwNumber}</div><div className={passwordChecks.special ? "ok" : ""}>✓ {C.pwSpecial}</div></div>}
          {isRegister && <label className="field"><span>{C.confirm}</span><div className="password-wrap"><input className={("inp password-input " + (confirm ? (passwordsMatch ? "match" : "no-match") : "")).trim()} type={showConfirm ? "text" : "password"} autoComplete="new-password" minLength={12} required value={confirm} onChange={(e) => setConfirm(e.target.value)} /><button className="password-eye" type="button" aria-label={showConfirm ? C.hidePw : C.showPw} title={showConfirm ? C.hidePw : C.showPw} onClick={() => setShowConfirm((v) => !v)}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="2.7"/></svg></button></div>{confirm && <small className={passwordsMatch ? "password-match ok" : "password-match bad"}>{passwordsMatch ? C.pwMatch : C.pwNoMatch}</small>}</label>}
          {err && <div className="auth-error" role="alert">{err}</div>}
          <button className="btn pri block auth-submit" type="submit" disabled={busy || (isRegister && (!passwordStrong || !passwordsMatch || !usPhoneValid))}>{busy ? (isRegister ? C.creating : C.signing) : (isRegister ? C.register : C.login)}</button>
          {isRegister && <p className="agree">{app.t("agree_pre")} <a href="#/terms">{app.t("terms")}</a> {app.t("and_w")} <a href="#/privacy">{app.t("privacy")}</a></p>}
        </form>
        <div className="auth-switch">
          <span>{isRegister ? C.haveAccount : C.noAccount}</span>
          <a href={isRegister ? "#/login" : "#/register"}>{isRegister ? C.login : C.make}</a>
        </div>
        <a className="auth-back" href="#/"><Ico n="back" />{C.back}</a>
      </section>
    </main>
  );
}

/* ─── saved & account ───────────────────────────────────────────────────── */
function SavedView({ app }) {
  const { t, saved } = app;
  const list = [...saved].map((id) => Store.get(id)).filter(Boolean);
  useEffect(() => { [...saved].forEach((id) => { if (!Store.get(id) && !Store.detail[id]) Store.fetchDetail(id); }); }, [saved]);
  return (
    <main>
      <div className="sec-h"><h2 style={{ fontSize: 23 }}>{t("saved_h")}</h2><p>{list.length ? t("results", { n: list.length }) : ""}</p></div>
      {list.length ? <div className="grid">{list.map((x) => <Card key={x.id} app={app} x={x} />)}</div>
        : <div className="empty"><div className="eic"><Ico n="heart" /></div><h3>{t("saved_empty")}</h3><p>{t("saved_empty_b")}</p>
          <div className="acts"><a className="btn pri" href="#/">{t("nav_home")}</a></div></div>}
    </main>
  );
}
function MeView({ app }) {
  const { t, F, lang, theme, saved } = app;
  const mine = [...Store.mine].sort((a, b) => b.createdAt - a.createdAt);
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneSent, setPhoneSent] = useState(false);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneErr, setPhoneErr] = useState("");
  const [devPhoneOtp, setDevPhoneOtp] = useState("");
  const Seg = ({ cur, opts, on }) => <div className="seg">{opts.map(([v, l]) => <button key={v} className={cur === v ? "on" : ""} onClick={() => on(v)}>{l}</button>)}</div>;
  const setStatus = async (x, st) => { try { await Store.updateListing(x.id, { status: st }); app.toast(t(st === "sold" ? "marked_sold" : "relisted")); } catch (e) { app.toast(t("save_fail")); } };
  const del = (x) => app.setSheet({ type: "confirm", title: t("confirm_del"), body: t("confirm_del_b"), ok: t("del"),
    onOk: async () => { try { await Store.deleteListing(x.id); if (saved.has(x.id)) app.toggleSave(null, x.id); app.toast(t("deleted")); } catch (e) { app.toast(t("save_fail")); } } });
  const sendPhoneOtp = async () => {
    setPhoneErr(""); setPhoneBusy(true);
    try {
      const d = await api("/auth/send-phone-otp/", { method: "POST", body: "{}" });
      if (d.alreadyVerified) { app.toast(lang === "hi" ? "फ़ोन पहले से सत्यापित है" : "Phone already verified"); return; }
      setPhoneSent(true); setDevPhoneOtp(d.devOtp || "");
      app.toast(lang === "hi" ? "सत्यापन कोड भेज दिया" : "Verification code sent");
    } catch (e) { setPhoneErr(lang === "hi" ? "कोड नहीं भेजा जा सका। SMS सेटिंग जाँचें" : "Could not send SMS. Check SMS configuration."); }
    finally { setPhoneBusy(false); }
  };
  const verifyPhone = async () => {
    setPhoneErr(""); setPhoneBusy(true);
    try {
      const d = await api("/auth/verify-phone/", { method: "POST", body: JSON.stringify({ code: phoneCode }) });
      app.setSessionUser(d.user); setPhoneSent(false); setPhoneCode(""); setDevPhoneOtp("");
      app.toast(lang === "hi" ? "फ़ोन सत्यापित हो गया" : "Phone verified");
    } catch (e) { setPhoneErr(lang === "hi" ? "कोड गलत है या उसकी समय-सीमा खत्म हो गई" : "Code is incorrect or expired."); }
    finally { setPhoneBusy(false); }
  };
  return (
    <main><div className="me-grid">
      <section>
        <div className="sec-h" style={{ marginTop: 0 }}><h2 style={{ fontSize: 23 }}>{t("my_listings")}</h2></div>
        <div className="mine">
          {mine.length ? mine.map((x) => (
            <div className="mrow" key={x.id}>
              <a className="tph" href={"#/item/" + encodeURIComponent(x.id)}><Thumb x={x} /></a>
              <div className="mb"><b>{F.L(x, "title")}</b><small>{F.priceText(x)}, {F.place(x)}{x.status === "sold" ? ", " + t("sold") : ""}</small></div>
              <div className="ma">
                <button className="btn sm" onClick={() => setStatus(x, x.status === "sold" ? "active" : "sold")}>{t(x.status === "sold" ? "relist" : "mark_sold")}</button>
                <button className="btn sm danger" onClick={() => del(x)} aria-label={t("del")}><Ico n="trash" /></button>
              </div>
            </div>
          )) : <div className="empty" style={{ padding: "30px 10px" }}><div className="eic"><Ico n="tag" /></div><h3>{t("no_mine")}</h3><p />
            <div className="acts"><a className="btn pri" href="#/post">{t("post_first")}</a><a className="btn" href="#/">{t("nav_home")}</a></div></div>}
        </div>
      </section>
      <aside>
        <div className="setlist">
          {app.user && <ProfileCard app={app} />}
          {app.user && <div className="setrow"><span>{lang === "hi" ? "पहला नाम" : "First name"}</span><b>{app.user.firstName || "—"}</b></div>}
          {app.user && <div className="setrow"><span>{lang === "hi" ? "उपनाम" : "Last name"}</span><b>{app.user.lastName || "—"}</b></div>}
          {app.user && <div className="setrow"><span>{lang === "hi" ? "ईमेल" : "Email"}</span><b className="acct-email">{app.user.email}</b></div>}
          {app.user && <div className="setrow phone-row"><span>{lang === "hi" ? "फ़ोन" : "Phone"}</span><div className="verify-value"><b>{app.user.phone || "—"}</b><small className={app.user.phoneVerified ? "verified ok" : "verified"}>{app.user.phoneVerified ? (lang === "hi" ? "✓ सत्यापित" : "✓ Verified") : (lang === "hi" ? "सत्यापन ज़रूरी" : "Verification required")}</small>{!app.user.phoneVerified && !phoneSent && <button className="btn sm" disabled={phoneBusy} onClick={sendPhoneOtp}>{phoneBusy ? "…" : (lang === "hi" ? "फ़ोन सत्यापित करें" : "Verify phone")}</button>}</div></div>}
          {app.user && !app.user.phoneVerified && phoneSent && <div className="phone-verify-box">
            <label className="field"><span>{lang === "hi" ? "6 अंकों का SMS कोड" : "6-digit SMS code"}</span><input className="inp otp-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={phoneCode} onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))} /></label>
            {devPhoneOtp && <div className="dev-otp"><b>{lang === "hi" ? "लोकल टेस्ट कोड" : "Local test code"}:</b> {devPhoneOtp}</div>}
            {phoneErr && <div className="auth-error">{phoneErr}</div>}
            <div className="verify-actions"><button className="btn pri sm" disabled={phoneBusy || phoneCode.length !== 6} onClick={verifyPhone}>{lang === "hi" ? "सत्यापित करें" : "Verify"}</button><button className="btn sm" disabled={phoneBusy} onClick={sendPhoneOtp}>{lang === "hi" ? "फिर भेजें" : "Resend"}</button></div>
          </div>}
          <NotifySettings app={app} />
          <div className="setrow"><span>{t("language")}</span><Seg cur={lang} on={app.setLang} opts={[["hi", "हिंदी"], ["en", "English"]]} /></div>
          <div className="setrow"><span>{t("theme")}</span><Seg cur={theme} on={app.setTheme} opts={[["auto", t("th_auto")], ["light", t("th_light")], ["dark", t("th_dark")]]} /></div>
          {app.canInstall && <div className="setrow"><span>{t("install_app")}</span><button className="btn sm gold" onClick={app.installApp}><Ico n="download" />{t("install_app")}</button></div>}
          <div className="setrow"><span>{t("area")}</span><button className="btn sm" onClick={() => app.setSheet({ type: "loc" })}><Ico n="pin" />{F.locLabel()}</button></div>
          <div className="setrow"><span>{lang === "hi" ? "बाज़ार" : "Marketplace"}</span><a className="btn sm" href="#/"><Ico n="home" />{t("nav_home")}</a></div>
          {app.user && <div className="setrow"><span>{lang === "hi" ? "खाता" : "Account"}</span><button className="btn sm danger" onClick={app.logoutUser}>{lang === "hi" ? "लॉग आउट" : "Log out"}</button></div>}
        </div>
        <div className="setlist about" style={{ marginTop: 16 }}>
          <img src={LOGO_FULL} alt="ApnaCircle" className="em-l" /><img src={EMBLEM_DARK} alt="ApnaCircle" className="em-d" style={{ width: 120 }} />
          <p>{t("about")}</p>
          <div className="legallinks"><a href="#/privacy">{t("privacy")}</a><a href="#/terms">{t("terms")}</a>{app.user && app.user.isAdmin && <a href="#/rooms/review">{t("admin_rooms_link")}</a>}</div>
          <div className="status"><i className={Chat && Chat.status === "online" ? "on" : "off"} />{t(Chat && Chat.status === "online" ? "conn_online" : "conn_offline")}</div>
        </div>
      </aside>
    </div></main>
  );
}

/* ─── notification sounds ───────────────────────────────────────────────── */
function Switch({ on, onChange, label }) {
  return <button className={"switch" + (on ? " on" : "")} role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)}><i /></button>;
}
function NotifySettings({ app }) {
  const { t, lang } = app;
  const [p, setP] = useState(Sound.getPrefs);
  const up = (patch) => setP(Sound.setPrefs(patch));
  const toggleCat = (id) => up({ interests: p.interests.includes(id) ? p.interests.filter((x) => x !== id) : [...p.interests, id], post: true });
  return (
    <div className="notify">
      <h3>{t("notif_h")}</h3>
      <div className="setrow"><span>{t("sound_msg")}</span>
        <div className="srow"><button className="btn sm" onClick={() => Sound.play("message", { force: true })}>{t("test_sound")}</button>
          <Switch on={p.msg} label={t("sound_msg")} onChange={(v) => { up({ msg: v }); if (v) Sound.play("message", { force: true }); }} /></div></div>
      <div className="setrow"><span>{t("vibrate")}</span><Switch on={p.vibrate} label={t("vibrate")} onChange={(v) => up({ vibrate: v })} /></div>
      <div className="setrow"><span>{t("sound_post")}</span>
        <div className="srow"><button className="btn sm" onClick={() => Sound.play("post", { force: true })}>{t("test_sound")}</button>
          <Switch on={p.post} label={t("sound_post")} onChange={(v) => { up({ post: v }); if (v) Sound.play("post", { force: true }); }} /></div></div>
      {p.post && <div className="interests">
        <small>{t("interests_hint")}</small>
        <div className="chips">{CATS.filter((c) => c.id !== "all").map((c) => (
          <button key={c.id} className={"chip" + (p.interests.includes(c.id) ? " on" : "")} aria-pressed={p.interests.includes(c.id)} onClick={() => toggleCat(c.id)}>{c[lang]}</button>
        ))}</div>
      </div>}
    </div>
  );
}

/* ─── profile card, reviews ─────────────────────────────────────────────── */
function ProfileCard({ app }) {
  const { t } = app;
  const [p, setP] = useState(() => Store.profile());
  const [zip, setZip] = useState(() => Store.profile().zip || "");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const file = useRef(null);
  const user = app.user || {};
  const name = user.displayName || app.name || "—";
  const { avg, count } = Store.rating(Store.me);
  const mine = Store.mine;
  const z = zipInfo(zip);
  const save = async (patch) => {
    setBusy(true); setErr("");
    try { const next = await Store.saveProfile(patch); setP(next); app.toast(t("profile_saved")); }
    catch (e) { setErr(t("save_fail")); }
    finally { setBusy(false); }
  };
  const [crop, setCrop] = useState(null);   // image file being cropped
  const onFile = (f) => {
    if (!f) return;
    if (!/^image\//.test(f.type)) { setErr(t("err_photo")); return; }
    setErr(""); setCrop(f);
  };
  const place = [p.city, p.state].filter(Boolean).join(", ");
  return (
    <div className="profcard">
      <div className="phead">
        <button className="avedit" onClick={() => file.current && file.current.click()} disabled={busy} aria-label={t("av_change")}>
          <Avatar src={p.avatar} useLogo={p.useLogo} name={name} size={72} />
          <span className="avcam" aria-hidden="true"><Ico n="camera" /></span>
        </button>
        <div className="pinfo">
          <b>{name}</b>
          <div className="trow">
            {count ? <><Stars v={avg} /><small>{avg.toFixed(1)} · {count} {t(count === 1 ? "review_one" : "reviews")}</small></>
              : <small className="muted">{t("new_member")}</small>}
          </div>
          <div className="tmeta">
            {place ? <span><Ico n="pin" />{place}</span> : <span className="muted"><Ico n="pin" />{t("your_city")}</span>}
            <span><Ico n="tag" />{t("listings_count", { n: mine.length })}</span>
            {user.phoneVerified ? <span className="ok"><Ico n="shield" />{t("verified_phone")}</span> : <span className="muted"><Ico n="shield" />{t("not_verified")}</span>}
          </div>
        </div>
        <button className="btn sm" onClick={() => setEditing(!editing)} aria-expanded={editing}>{t("change")}</button>
      </div>
      {editing && (
        <div className="pedit">
          <div className="field"><span>{t("edit_photo")}</span>
            <div className="seg">
              <button disabled={busy} onClick={() => file.current && file.current.click()}><Ico n="camera" />{t("upload_photo")}</button>
              <button className={p.useLogo && !p.avatar ? "on" : ""} disabled={busy} onClick={() => save({ avatar: null, useLogo: true })}>{t("use_logo")}</button>
              <button className={!p.useLogo && !p.avatar ? "on" : ""} disabled={busy} onClick={() => save({ avatar: null, useLogo: false })}>{t("use_initials")}</button>
            </div>
          </div>
          <label className="field"><span>{t("your_city")}</span>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="inp" inputMode="numeric" maxLength={5} placeholder={t("loc_zip_ph")} value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))} />
              <button className="btn" disabled={busy || !z} onClick={() => save({ zip: z.zip, city: z.city, state: z.state })} aria-label={t("set_city")}><Ico n="check" /></button>
            </div>
            <div className={"hint " + (z ? "ok" : zip.length === 5 ? "err" : "")}>{z ? `${z.city}, ${z.state}` : zip.length === 5 ? t("loc_zip_bad") : t("city_hint")}</div>
          </label>
          {app.loc.mode === "zip" && <button className="btn sm" disabled={busy} onClick={() => { setZip(app.loc.zip); save({ zip: app.loc.zip, city: app.loc.city, state: app.loc.state }); }}><Ico n="pin" />{app.loc.city}, {app.loc.state}</button>}
          {err && <div className="auth-error">{err}</div>}
        </div>
      )}
      {count > 0 && <button className="btn sm block" style={{ marginTop: 10 }} onClick={() => app.setSheet({ type: "reviews", personKey: Store.me, name })}>{t("see_reviews")}</button>}
      <input ref={file} type="file" accept="image/*" className="sr" onChange={(e) => { onFile(e.target.files && e.target.files[0]); e.target.value = ""; }} />
      {crop && <AvatarEditor app={app} file={crop} busy={busy} hasPhoto={!!p.avatar}
        onCancel={() => setCrop(null)}
        onPick={() => file.current && file.current.click()}
        onRemove={async () => { setCrop(null); await save({ avatar: null, useLogo: false }); }}
        onSave={async (data) => { await save({ avatar: data, useLogo: false }); setCrop(null); }} />}
    </div>
  );
}
function ReviewList({ app, personKey }) {
  const { t, lang } = app;
  const list = Store.reviewsFor(personKey);
  if (!list.length) return <p className="hint" style={{ margin: 0 }}>{t("no_reviews")}</p>;
  return (
    <div className="rlist">
      {list.slice(0, 30).map((r, i) => (
        <div className="ritem" key={r.id || i}>
          <div className="rtop"><Avatar name={r.byName || "?"} size={34} /><div><b>{r.byName || t("member")}</b>
            <small>{shortDate(r.at, lang)} · {t(r.role === "buyer" ? "review_as_seller" : "review_as_buyer")}</small></div>
            <Stars v={r.rating} /></div>
          {r.text && <p>{r.text}</p>}
        </div>
      ))}
    </div>
  );
}
function ReviewsSheet({ app, personKey, name }) {
  const { t } = app;
  const { avg, count } = Store.rating(personKey);
  return (
    <>
      <SheetHead app={app} title={t("reviews_about", { n: name || "—" })} />
      {count > 0 && <div className="rsum"><b>{avg.toFixed(1)}</b><Stars v={avg} /><small>{count} {t(count === 1 ? "review_one" : "reviews")}</small></div>}
      <ReviewList app={app} personKey={personKey} />
    </>
  );
}
function ReviewSheet({ app, about, aboutName, listingId, role }) {
  const { t } = app;
  const existing = Store.myReviewFor(about);
  const [rating, setRating] = useState(existing ? existing.rating : 0);
  const [text, setText] = useState(existing ? existing.text || "" : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const blocked = !app.user ? t("login_to_review") : about === Store.me ? t("review_self") : !Store.dealtWith(about) ? t("review_need_deal") : "";
  const submit = async () => {
    if (!rating || busy) return;
    setBusy(true); setErr("");
    try {
      await Store.addReview({ about, aboutName, byName: (app.user && app.user.displayName) || app.name || "", listingId, rating, text: text.trim(), role });
      app.setSheet(null); app.toast(t(existing ? "review_update" : "review_thanks"));
    } catch (e) { setErr(t("review_fail")); setBusy(false); }
  };
  return (
    <>
      <SheetHead app={app} title={t(role === "buyer" ? "rate_buyer" : "rate_seller")} />
      <div className="in-hero"><Avatar name={aboutName} size={42} /><div><b>{aboutName || "—"}</b><span>{t(role === "buyer" ? "review_as_seller" : "review_as_buyer")}</span></div></div>
      {blocked ? <>
        <p style={{ color: "var(--ink2)", margin: "0 0 18px" }}>{blocked}</p>
        <button className="btn pri block" onClick={() => app.setSheet(null)}>{t("close")}</button>
      </> : <>
        <div className="field"><span>{t("your_rating")}</span><StarPick value={rating} onPick={setRating} t={t} /></div>
        <label className="field"><span>{t("write_review")}</span>
          <textarea className="inp" maxLength={600} placeholder={t("review_ph")} value={text} onChange={(e) => setText(e.target.value)} /></label>
        {err && <div className="auth-error">{err}</div>}
        <button className="btn pri block" disabled={!rating || busy} onClick={submit}>{busy ? "…" : t("review_send")}</button>
      </>}
    </>
  );
}

/* ─── sheets ────────────────────────────────────────────────────────────── */
function SheetHead({ app, title }) {
  return <div className="hd"><h2>{title}</h2><button className="ibtn" onClick={() => app.setSheet(null)} aria-label={app.t("close")}><Ico n="close" /></button></div>;
}
function LocSheet({ app }) {
  const { t, F, loc, lang } = app;
  const [zip, setZip] = useState(loc.mode === "zip" ? loc.zip : "");
  const [radius, setRadius] = useState(loc.mode === "zip" && loc.radius != null ? loc.radius : 50);
  const [st, setSt] = useState(loc.mode === "state" ? loc.state : "");
  const [gps, setGps] = useState(""); const [busy, setBusy] = useState(false);
  const z = zipInfo(zip);
  const useGps = () => {
    if (!navigator.geolocation) { setGps(t("gps_fail")); return; }
    setBusy(true); setGps("");
    navigator.geolocation.getCurrentPosition((p) => { setBusy(false); const n = nearestZip(p.coords.latitude, p.coords.longitude); if (n) { setZip(n.zip); setSt(""); } },
      () => { setBusy(false); setGps(t("gps_fail")); }, { timeout: 9000, maximumAge: 600000 });
  };
  const apply = () => {
    if (zip && !z) return;
    if (z) app.setLoc({ mode: "zip", zip: z.zip, lat: z.lat, lng: z.lng, city: z.city, state: z.state, radius });
    else if (st) app.setLoc({ mode: "state", state: st });
    else app.setLoc({ mode: "all" });
    app.setSheet(null);
    if (app.route.r !== "home") app.go("/");
  };
  return (
    <>
      <SheetHead app={app} title={t("loc_title")} />
      <label className="field"><span>{t("loc_zip")}</span>
        <input className="inp" inputMode="numeric" maxLength={5} autoComplete="postal-code" value={zip} placeholder={t("loc_zip_ph")}
          onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 5); setZip(v); if (v) setSt(""); setGps(""); }} />
        <div className={"hint " + (gps ? "err" : zip.length === 5 ? (z ? "ok" : "err") : "")}>
          {gps || (zip.length === 5 ? (z ? `${z.city}, ${F.stateName(z.state)}` : t("loc_zip_bad")) : "")}</div></label>
      <div className="field"><span>{t("loc_radius")}</span><div className="seg">
        {[10, 25, 50, 100, 250, 0].map((r) => <button key={r} className={radius === r ? "on" : ""} onClick={() => setRadius(r)}>{r ? `${r} ${t("mi")}` : t("any_dist")}</button>)}
      </div></div>
      <button className="btn block" style={{ marginBottom: 16 }} disabled={busy} onClick={useGps}><Ico n="gps" />{busy ? t("gps_wait") : t("loc_gps")}</button>
      <div className="or">{t("loc_or")}</div>
      <label className="field"><span>{t("loc_state")}</span>
        <select className="inp" value={st} onChange={(e) => { setSt(e.target.value); if (e.target.value) setZip(""); }}>
          <option value="">{t("all_states")}</option>
          {STATES.map((s) => <option key={s[0]} value={s[0]}>{lang === "en" ? s[2] : `${s[1]} (${s[2]})`}</option>)}
        </select></label>
      <button className="btn pri block" onClick={apply}>{t("loc_apply")}</button>
    </>
  );
}
function ReportSheet({ app, listing }) {
  const { t } = app; const [pick, setPick] = useState(-1);
  const send = async () => { try { await Store.report(listing, I18N.en.report_r[pick]); app.setSheet(null); app.toast(t("reported")); } catch (e) { app.toast(t("save_fail")); } };
  return (
    <>
      <SheetHead app={app} title={t("report_t")} />
      <div className="seg" style={{ flexDirection: "column" }}>
        {t("report_r").map((r, i) => (
          <button key={i} className={pick === i ? "on" : ""} onClick={() => setPick(i)}
            style={{ justifyContent: "flex-start", textAlign: "left", height: "auto", minHeight: 44, borderRadius: 12, padding: "10px 14px" }}>{r}</button>
        ))}
      </div>
      <button className="btn pri block" style={{ marginTop: 16 }} disabled={pick < 0} onClick={send}>{t("report_send")}</button>
    </>
  );
}
function ConfirmSheet({ app, title, body, ok, onOk }) {
  return (
    <>
      <SheetHead app={app} title={title} />
      <p style={{ color: "var(--ink2)", margin: "0 0 18px" }}>{body}</p>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button className="btn" onClick={() => app.setSheet(null)}>{app.t("cancel")}</button>
        <button className="btn pri" style={{ background: "var(--danger)", borderColor: "var(--danger)", color: "#fff" }} onClick={async () => { app.setSheet(null); await onOk(); }}>{ok}</button>
      </div>
    </>
  );
}


/* ─── install banner & guide ────────────────────────────────────────────── */
function InstallNotice({ app }) {
  const { t } = app;
  return (
    <div className="install-wrap">
      <aside className="install-notice" aria-label={t("install_app")}>
        <img src={EMBLEM_LIGHT} alt="" className="em-l" /><img src={EMBLEM_DARK} alt="" className="em-d" />
        <div className="in-txt"><strong>{t("install_app")}</strong><span>{t("install_benefit")}</span></div>
        <button className="btn-install" onClick={app.installApp}><Ico n="download" />{t("install_app")}</button>
        <button className="notice-close" onClick={app.dismissInstallNotice} aria-label={t("close")}><Ico n="close" /></button>
      </aside>
    </div>
  );
}
function InstallSheet({ app }) {
  const { t, lang, plat } = app;
  const steps = INSTALL_TXT[lang]["ins_" + plat.kind] || INSTALL_TXT[lang].ins_desktop_chrome;
  const needsCopy = ["inapp", "ios_other", "desktop_firefox"].includes(plat.kind);
  const copy = async () => {
    const url = window.location.origin + window.location.pathname;
    try { await navigator.clipboard.writeText(url); app.toast(t("install_copied")); }
    catch (e) { window.prompt(t("install_copy"), url); }
  };
  return (
    <>
      <SheetHead app={app} title={t("install_title")} />
      <div className="in-hero"><img src={EMBLEM_LIGHT} alt="" className="em-l" /><img src={EMBLEM_DARK} alt="" className="em-d" />
        <div><b>ApnaCircle</b><span>{t("install_benefit")}</span></div></div>
      <ol className="isteps">{steps.map((s, i) => <li key={i}><span className="n">{i + 1}</span><span>{s}</span></li>)}</ol>
      {needsCopy && <button className="btn block" style={{ marginBottom: 10 }} onClick={copy}><Ico n="copy" />{t("install_copy")}</button>}
      <p className="hint" style={{ margin: "0 0 16px" }}>{t("install_hint")}</p>
      <button className="btn pri block" onClick={() => app.setSheet(null)}>{t("close")}</button>
    </>
  );
}

/* ─── styles (scoped to .cc) ────────────────────────────────────────────── */
const CSS = `
.cc{
  --bg:#F3F5F9; --panel:#FFFFFF; --panel2:#EBEFF6; --panel3:#E1E7F1;
  --ink:#0E1C33; --ink2:#46566F; --ink3:#7E8BA1; --line:#DCE2EC;
  --navy:#13294B; --gold:#C9A24D; --gold-ink:#8C6A22; --gold-soft:#F4EAD2;
  --primary:#13294B; --on-primary:#FFFFFF; --primary-soft:#E3E9F3;
  --danger:#B4332B; --ok:#1E7A4F; --line-green:#1DA851;
  --scrim:rgba(8,16,30,.46); --shadow:0 10px 34px rgba(14,28,51,.14);
  --r-lg:22px; --r-md:14px; --r-sm:10px;
  --font:"Mukta","Noto Sans Devanagari","Nirmala UI","Kohinoor Devanagari","Segoe UI",system-ui,-apple-system,sans-serif;
  color-scheme:light;
  box-sizing:border-box;
  padding-top:env(safe-area-inset-top,0px);
  padding-bottom:env(safe-area-inset-bottom,0px);
}
@media (prefers-color-scheme:dark){
  .cc:not([data-theme="light"]){
    --bg:#08111F; --panel:#0F1B2F; --panel2:#15233A; --panel3:#1C2D48;
    --ink:#ECF0F6; --ink2:#AAB6C9; --ink3:#72819A; --line:rgba(255,255,255,.09);
    --gold-ink:#E2C072; --gold-soft:rgba(201,162,77,.16);
    --primary:#D6B25E; --on-primary:#0B1628; --primary-soft:rgba(214,178,94,.14);
    --danger:#F0877E; --ok:#5FD39A; --scrim:rgba(0,0,0,.62); --shadow:0 12px 40px rgba(0,0,0,.5);
    color-scheme:dark;
  }
}
.cc[data-theme="dark"]{
  --bg:#08111F; --panel:#0F1B2F; --panel2:#15233A; --panel3:#1C2D48;
  --ink:#ECF0F6; --ink2:#AAB6C9; --ink3:#72819A; --line:rgba(255,255,255,.09);
  --gold-ink:#E2C072; --gold-soft:rgba(201,162,77,.16);
  --primary:#D6B25E; --on-primary:#0B1628; --primary-soft:rgba(214,178,94,.14);
  --danger:#F0877E; --ok:#5FD39A; --scrim:rgba(0,0,0,.62); --shadow:0 12px 40px rgba(0,0,0,.5);
  color-scheme:dark;
}
:where(.cc) *,:where(.cc) *::before,:where(.cc) *::after{box-sizing:inherit}
.cc{min-height:100vh;margin:0;background:var(--bg);color:var(--ink);font-family:var(--font);font-size:15px;line-height:1.55;-webkit-font-smoothing:antialiased;overflow-x:clip;-webkit-text-size-adjust:100%}
:where(.cc) :where(button,input,select,textarea){font:inherit;color:inherit}
:where(.cc) button{cursor:pointer;background:none;border:0;padding:0}
:where(.cc) a{color:inherit;text-decoration:none}
:where(.cc) img{max-width:100%;display:block}
svg.i{width:22px;height:22px;flex:none;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}
:where(.cc) :focus-visible{outline:2.5px solid var(--gold);outline-offset:2px;border-radius:8px}
.sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
@media (prefers-reduced-motion:reduce){.cc *{animation:none!important;transition:none!important;scroll-behavior:auto!important}}

/* brand emblem swaps with theme */
.cc .em-d{display:none}
@media (prefers-color-scheme:dark){.cc:not([data-theme="light"]) .em-l{display:none}.cc:not([data-theme="light"]) .em-d{display:block}}
.cc[data-theme="dark"] .em-l{display:none} .cc[data-theme="dark"] .em-d{display:block}

/* ---------- top bar ---------- */
.top{position:sticky;top:env(safe-area-inset-top,0px);z-index:40;background:color-mix(in srgb,var(--bg) 88%,transparent);backdrop-filter:saturate(1.4) blur(14px);-webkit-backdrop-filter:saturate(1.4) blur(14px);border-bottom:1px solid var(--line)}
.top-in{max-width:1240px;margin:0 auto;padding:10px 16px;display:flex;align-items:center;gap:14px}
.brand{display:flex;align-items:center;gap:10px;flex:none}
.brand img{height:34px;width:auto}
.wm{display:flex;flex-direction:column;line-height:1}
.wm b{font-weight:700;font-size:20px;letter-spacing:-.2px;color:var(--ink)}
.wm span{font-size:12px;color:var(--gold-ink);font-weight:600;margin-top:3px;letter-spacing:.5px}
.search{flex:1;min-width:0;position:relative}
.search input{width:100%;height:44px;border-radius:999px;border:1px solid var(--line);background:var(--panel);padding:0 16px 0 44px;outline:none;transition:border-color .15s}
.search input:focus{border-color:var(--gold)}
.search input::placeholder{color:var(--ink3)}
.search svg{position:absolute;left:15px;top:11px;color:var(--ink3)}
.search .clr{position:absolute;right:6px;top:6px;width:32px;height:32px;border-radius:50%;display:grid;place-items:center;color:var(--ink3)}
.tools{display:flex;align-items:center;gap:6px;flex:none}
.ibtn{width:40px;height:40px;border-radius:12px;display:grid;place-items:center;color:var(--ink2);position:relative}
.ibtn:hover{background:var(--panel2);color:var(--ink)}
.lang{height:34px;padding:0 4px;border-radius:999px;border:1px solid var(--line);display:flex;align-items:center;gap:2px;font-size:12.5px;font-weight:600;background:var(--panel)}
.lang span{padding:4px 9px;border-radius:999px;color:var(--ink3)}
.lang span.on{background:var(--primary);color:var(--on-primary)}
.dnav{display:flex;align-items:center;gap:4px}
.dnav a{display:flex;align-items:center;gap:7px;height:40px;padding:0 12px;border-radius:12px;color:var(--ink2);font-weight:500;position:relative}
.dnav a:hover{background:var(--panel2);color:var(--ink)}
.dnav a.on{color:var(--ink);background:var(--panel2)}
.account-link{max-width:190px}.user-short{max-width:112px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.user-avatar{width:30px;height:30px;border-radius:50%;display:inline-grid;place-items:center;flex:none;background:var(--navy);color:#fff;font-size:12px;font-weight:800;letter-spacing:.03em;box-shadow:inset 0 0 0 1px rgba(255,255,255,.2)}
.user-avatar.big{width:48px;height:48px;font-size:17px}.profile-avatar{width:48px;height:48px;font-size:17px}
.mobile-avatar{display:none;width:36px;height:36px;border-radius:50%;place-items:center;background:var(--navy);color:#fff;font-size:12px;font-weight:800}
.btn-post{display:inline-flex;align-items:center;gap:6px;height:40px;padding:0 16px;border-radius:999px;background:var(--gold);color:#13294B;font-weight:700}
.btn-post:hover{filter:brightness(1.05)}
.dot{position:absolute;top:6px;right:6px;min-width:17px;height:17px;padding:0 4px;border-radius:999px;background:var(--danger);color:#fff;font-size:10.5px;font-weight:700;display:grid;place-items:center;line-height:1}
.m-search{display:none}

/* ---------- layout ---------- */
.cc main{max-width:1240px;margin:0 auto;padding:0 16px 40px}
.locbar{display:flex;align-items:center;gap:10px;padding:16px 0 4px;flex-wrap:wrap}
.pill{display:inline-flex;align-items:center;gap:7px;height:38px;padding:0 14px;border-radius:999px;background:var(--panel);border:1px solid var(--line);font-weight:500;font-size:14px;white-space:nowrap;max-width:100%}
.pill svg{width:18px;height:18px}
.pill.loc{color:var(--ink)}
.pill.loc svg:first-child{color:var(--gold-ink)}
.pill .t{overflow:hidden;text-overflow:ellipsis}
.pill select{appearance:none;-webkit-appearance:none;background:none;border:0;outline:none;font-weight:500;padding-right:4px;cursor:pointer}
.count{margin-left:auto;color:var(--ink3);font-size:13.5px}

/* topic tabs */
.tabs{display:flex;gap:4px;overflow-x:auto;scrollbar-width:none;padding:14px 0 6px;margin:0 -16px;padding-left:16px;padding-right:16px;scroll-snap-type:x proximity}
.tabs::-webkit-scrollbar{display:none}
.tab{flex:none;width:84px;display:flex;flex-direction:column;align-items:center;gap:7px;padding:4px 2px 8px;border-radius:14px;scroll-snap-align:start;color:var(--ink2)}
.tab .ic{width:52px;height:52px;border-radius:17px;display:grid;place-items:center;background:var(--panel);border:1px solid var(--line);transition:background .15s,color .15s,border-color .15s}
.tab .ic svg{width:25px;height:25px}
.tab .lb{font-size:12.5px;line-height:1.25;text-align:center;font-weight:500;min-height:2.5em}
.tab:hover .ic{border-color:var(--gold)}
.tab.on{color:var(--ink)}
.tab.on .ic{background:var(--primary);color:var(--on-primary);border-color:var(--primary)}
.tab.on .lb{font-weight:700}
.subs{display:flex;gap:8px;overflow-x:auto;scrollbar-width:none;padding:6px 16px 4px;margin:0 -16px}
.subs::-webkit-scrollbar{display:none}
.chip{flex:none;height:34px;padding:0 14px;border-radius:999px;border:1px solid var(--line);background:var(--panel);font-size:13.5px;font-weight:500;color:var(--ink2)}
.chip.on{background:var(--primary-soft);border-color:var(--primary);color:var(--ink);font-weight:600}
.sec-h{display:flex;align-items:baseline;justify-content:space-between;gap:12px;margin:18px 0 12px}
.sec-h h2{margin:0;font-size:19px;font-weight:700;letter-spacing:-.1px}
.sec-h p{margin:0;color:var(--ink3);font-size:13.5px}

/* listing grid */
.grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:18px 12px}
@media (min-width:640px){.grid{grid-template-columns:repeat(3,minmax(0,1fr));gap:24px 16px}}
@media (min-width:960px){.grid{grid-template-columns:repeat(4,minmax(0,1fr))}}
@media (min-width:1200px){.grid{grid-template-columns:repeat(5,minmax(0,1fr))}}
.card{display:block;position:relative;min-width:0}
.ph{position:relative;aspect-ratio:1/1;border-radius:var(--r-md);overflow:hidden;background:var(--panel2)}
.ph img,.ph svg.cv{width:100%;height:100%;object-fit:cover;display:block}
.card:hover .ph img,.card:hover .ph svg.cv{transform:scale(1.03)}
.ph img,.ph svg.cv{transition:transform .35s ease}
.heart{position:absolute;top:8px;right:8px;width:36px;height:36px;border-radius:50%;display:grid;place-items:center;background:rgba(10,18,32,.38);color:#fff;backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.heart svg{width:19px;height:19px}
.heart.on{color:#fff;background:rgba(201,162,77,.92)}
.heart.on svg{fill:currentColor}
.badge{position:absolute;left:8px;top:8px;height:24px;padding:0 9px;border-radius:999px;font-size:11.5px;font-weight:700;display:flex;align-items:center;gap:4px}
.badge.sold{background:#0E1C33;color:#fff}
.badge.new{background:var(--gold);color:#13294B}
.cbody{padding:9px 2px 0}
.price{font-weight:700;font-size:16.5px;letter-spacing:-.1px;color:var(--ink)}
.ctitle{font-size:14px;color:var(--ink2);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;line-height:1.4;margin-top:1px}
.cmeta{font-size:12.5px;color:var(--ink3);margin-top:4px;display:flex;gap:8px;justify-content:space-between;white-space:nowrap}
.cmeta span:first-child{overflow:hidden;text-overflow:ellipsis}
.more{display:flex;justify-content:center;margin:28px 0 8px}
.btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;height:46px;padding:0 20px;border-radius:999px;font-weight:600;border:1px solid var(--line);background:var(--panel);color:var(--ink);white-space:nowrap}
.btn:hover{border-color:var(--ink3)}
.btn.pri{background:var(--primary);color:var(--on-primary);border-color:var(--primary)}
.btn.pri:hover{filter:brightness(1.08)}
.btn.gold{background:var(--gold);color:#13294B;border-color:var(--gold)}
.btn.line{background:var(--line-green);border-color:var(--line-green);color:#fff}
.btn.danger{color:var(--danger)}
.btn.sm{height:36px;padding:0 14px;font-size:13.5px}
.btn.block{width:100%}
.btn[disabled]{opacity:.45;pointer-events:none}
.empty{text-align:center;padding:56px 20px 40px;max-width:420px;margin:0 auto}
.empty .eic{width:68px;height:68px;border-radius:22px;background:var(--panel2);display:grid;place-items:center;margin:0 auto 16px;color:var(--gold-ink)}
.empty .eic svg{width:30px;height:30px}
.empty h3{margin:0 0 6px;font-size:18px}
.empty p{margin:0 0 20px;color:var(--ink2)}
.empty .acts{display:flex;gap:10px;justify-content:center;flex-wrap:wrap}

/* ---------- authentication ---------- */
.auth-page{min-height:calc(100vh - 150px);display:grid;place-items:center;padding:42px 16px 90px}
.auth-card{width:min(100%,460px);background:var(--panel);border:1px solid var(--line);border-radius:24px;padding:30px;box-shadow:var(--shadow)}
.auth-brand{display:block;width:170px;margin:0 auto 24px}.auth-brand img{display:block;width:100%;height:auto}
.auth-mark{width:64px;height:64px;border-radius:20px;background:var(--panel2);display:grid;place-items:center;margin:0 auto 18px}.auth-mark img{max-width:44px;max-height:44px}
.auth-card h1{text-align:center;font-size:27px;line-height:1.25;margin:0 0 8px}.auth-intro,.auth-card>p{text-align:center;color:var(--ink2);margin:0 auto 24px;max-width:370px;line-height:1.55}
.auth-submit{margin-top:4px}.auth-error{margin:-2px 0 14px;padding:10px 12px;border-radius:10px;background:color-mix(in srgb,var(--danger) 10%,transparent);color:var(--danger);font-size:13.5px;line-height:1.45}
.auth-switch{display:flex;justify-content:center;gap:7px;flex-wrap:wrap;margin-top:18px;font-size:14px;color:var(--ink2)}.auth-switch a{font-weight:700;color:var(--gold-ink)}
.auth-back{display:flex;align-items:center;justify-content:center;gap:6px;margin-top:18px;color:var(--ink3);font-size:13.5px}.auth-back svg{width:16px;height:16px}
.auth-actions{display:flex;gap:10px;justify-content:center;flex-wrap:wrap}.auth-loading{display:grid;place-items:center;min-height:55vh;color:var(--ink3)}
.auth-name-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}.password-wrap{position:relative}.password-input{padding-right:48px}.password-eye{position:absolute;right:6px;top:50%;transform:translateY(-50%);width:38px;height:38px;border:0;border-radius:10px;background:transparent;color:var(--ink3);display:grid;place-items:center;cursor:pointer}.password-eye:hover{background:var(--panel2);color:var(--ink)}.password-eye svg{width:21px;height:21px;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}.password-rules{display:grid;grid-template-columns:1fr 1fr;gap:5px 12px;margin:-4px 0 14px;font-size:12px;color:var(--ink3)}.password-rules div.ok{color:var(--ok);font-weight:600}.password-match{display:block;margin-top:5px;font-size:12px}.password-match.ok{color:var(--ok)}.password-match.bad{color:var(--danger)}.password-input.match{border-color:var(--ok)}.password-input.no-match{border-color:var(--danger)}.phone-input-wrap{position:relative}.phone-prefix{position:absolute;left:13px;top:50%;transform:translateY(-50%);z-index:1;font-weight:700;color:var(--ink2);pointer-events:none}.phone-us-input{padding-left:42px}.field-help{display:block;margin-top:5px;color:var(--ink3);font-size:12px}
.otp-input{font-size:24px;letter-spacing:.3em;text-align:center;font-weight:700}.dev-otp{margin:8px 0 14px;padding:10px 12px;border-radius:10px;background:var(--panel2);font-size:13px;color:var(--ink2)}
.auth-linkbtn{border:0;background:none;color:var(--gold-ink);font-weight:700;display:block;margin:14px auto 0;cursor:pointer}.auth-linkbtn:disabled{opacity:.5;cursor:default}
.profile-head{display:flex;align-items:center;gap:12px;padding:14px 0 16px}.profile-head div{min-width:0}.profile-head b,.profile-head small{display:block}.profile-head small{color:var(--ink3);margin-top:3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.verify-value{text-align:right;display:flex;flex-direction:column;align-items:flex-end;gap:4px;min-width:0}.verified{font-size:12px;color:var(--danger)}.verified.ok{color:#23845b}.phone-row{align-items:flex-start}.phone-verify-box{padding:12px 0 16px;border-top:1px solid var(--line)}.verify-actions{display:flex;gap:8px}.phone-verify-box .field{margin-bottom:8px}
.acct-email{font-size:13px;max-width:220px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
@media(max-width:560px){.auth-page{padding:18px 12px 100px;place-items:start center}.auth-card{border-radius:18px;padding:24px 18px}.auth-card h1{font-size:24px}.auth-name-grid{grid-template-columns:1fr}}

/* ---------- detail ---------- */
.mbar{display:none}
.crumbs{display:flex;align-items:center;gap:6px;padding:16px 0 8px;color:var(--ink3);font-size:13.5px}
.crumbs a:hover{color:var(--ink)}
.detail{display:grid;grid-template-columns:minmax(0,1.3fr) minmax(0,1fr);gap:36px;align-items:start;padding-bottom:20px}
.gal{position:relative;border-radius:var(--r-lg);overflow:hidden;background:var(--panel2)}
.gal .track{display:flex;overflow-x:auto;scroll-snap-type:x mandatory;scrollbar-width:none}
.gal .track::-webkit-scrollbar{display:none}
.gal .slide{flex:none;width:100%;aspect-ratio:4/3;cursor:zoom-in;padding:0;border:0;scroll-snap-align:center;position:relative;overflow:hidden;display:grid;place-items:center}
.gal .slide .blurbg{position:absolute;inset:-24px;background-size:cover;background-position:center;filter:blur(26px) saturate(.9);opacity:.28}
.gal .slide img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain}
.gal .slide.cover svg.cv{width:100%;height:100%;object-fit:cover}
.gal .nav{position:absolute;top:50%;transform:translateY(-50%);width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,.9);color:#0E1C33;display:grid;place-items:center;box-shadow:var(--shadow)}
.gal .nav.p{left:12px}.gal .nav.n{right:12px}
.gal .dots{position:absolute;bottom:12px;left:0;right:0;display:flex;justify-content:center;gap:6px}
.gal .dots i{width:7px;height:7px;border-radius:50%;background:rgba(255,255,255,.5)}
.gal .dots i.on{background:#fff;width:18px;border-radius:4px}
.thumbs{display:flex;gap:8px;margin-top:10px;overflow-x:auto}
.thumbs button{width:64px;height:64px;border-radius:10px;overflow:hidden;flex:none;border:2px solid transparent;opacity:.7}
.thumbs button.on{border-color:var(--gold);opacity:1}
.thumbs img,.thumbs svg.cv{width:100%;height:100%;object-fit:cover}
.info{position:sticky;top:84px}
.info .cat{font-size:13px;color:var(--gold-ink);font-weight:600;margin-bottom:4px}
.info h1{font-size:25px;line-height:1.3;margin:0 0 6px;letter-spacing:-.2px;font-weight:700}
.info .bigprice{font-size:27px;font-weight:700;letter-spacing:-.3px;display:flex;align-items:baseline;gap:10px;flex-wrap:wrap}
.info .bigprice small{font-size:13px;font-weight:600;color:var(--ok);background:color-mix(in srgb,var(--ok) 12%,transparent);padding:2px 9px;border-radius:999px}
.facts{display:flex;flex-wrap:wrap;gap:8px 18px;color:var(--ink2);font-size:14px;margin:12px 0 18px}
.facts span{display:inline-flex;align-items:center;gap:6px}
.facts svg{width:17px;height:17px;color:var(--ink3)}
.acts{display:flex;gap:10px;flex-wrap:wrap}
.acts .btn.pri{flex:1 1 180px}
.box{background:var(--panel);border:1px solid var(--line);border-radius:var(--r-md);padding:16px 18px;margin-top:18px}
.box h3{margin:0 0 8px;font-size:15.5px;font-weight:700}
.box p{margin:0;color:var(--ink2);white-space:pre-wrap;overflow-wrap:anywhere}
.seller{display:flex;align-items:center;gap:12px}
.av{width:44px;height:44px;border-radius:50%;display:grid;place-items:center;font-weight:700;background:var(--gold-soft);color:var(--gold-ink);flex:none;font-size:15px}
.seller b{display:block}
.seller small{color:var(--ink3)}
.safety{background:var(--gold-soft);border-color:transparent}
.safety h3{display:flex;align-items:center;gap:8px;color:var(--gold-ink)}
.safety h3 svg{width:19px;height:19px}
.safety p{color:var(--ink)}
.linkish{color:var(--ink3);font-size:13.5px;display:inline-flex;align-items:center;gap:6px;margin-top:14px}
.linkish:hover{color:var(--danger)}
.note{font-size:13px;color:var(--ink3);margin-top:10px}

/* ---------- sheet / dialog ---------- */
.scrim{position:fixed;inset:0;background:var(--scrim);z-index:80;display:flex;align-items:center;justify-content:center;padding:20px;animation:fade .18s ease}
.sheet{width:100%;max-width:460px;max-height:min(88vh,760px);overflow:auto;background:var(--panel);border-radius:var(--r-lg);box-shadow:var(--shadow);padding:20px 20px 22px;animation:rise .22s cubic-bezier(.2,.8,.2,1)}
.sheet h2{margin:0;font-size:19px}
.sheet .hd{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px}
@keyframes fade{from{opacity:0}}
@keyframes rise{from{transform:translateY(24px);opacity:0}}
.field{display:block;margin-bottom:14px}
.field>span{display:block;font-size:13.5px;font-weight:600;margin-bottom:6px;color:var(--ink2)}
.inp{width:100%;height:48px;border-radius:var(--r-sm);border:1px solid var(--line);background:var(--bg);padding:0 14px;outline:none}
textarea.inp{height:auto;min-height:130px;padding:12px 14px;resize:vertical;line-height:1.55}
.inp:focus{border-color:var(--gold)}
select.inp{appearance:none;-webkit-appearance:none;background-image:linear-gradient(45deg,transparent 50%,var(--ink3) 50%),linear-gradient(135deg,var(--ink3) 50%,transparent 50%);background-position:calc(100% - 20px) 21px,calc(100% - 15px) 21px;background-size:5px 5px;background-repeat:no-repeat;padding-right:36px}
.hint{font-size:13px;color:var(--ink3);margin-top:6px}
.hint.ok{color:var(--ok)} .hint.err{color:var(--danger)}
.seg{display:flex;flex-wrap:wrap;gap:8px}
.seg button{height:38px;padding:0 14px;border-radius:999px;border:1px solid var(--line);background:var(--panel);font-size:13.5px;font-weight:500}
.seg button.on{background:var(--primary);color:var(--on-primary);border-color:var(--primary)}
.or{display:flex;align-items:center;gap:10px;color:var(--ink3);font-size:13px;margin:4px 0 14px}
.or::before,.or::after{content:"";flex:1;height:1px;background:var(--line)}
.row2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.check{display:flex;align-items:center;gap:10px;font-weight:500;margin:4px 0 14px;cursor:pointer}
.check input{width:20px;height:20px;accent-color:var(--navy)}

/* ---------- post ---------- */
.wrap-narrow{max-width:720px;margin:0 auto}
.steps{display:flex;gap:6px;margin:18px 0 6px}
.steps i{flex:1;height:5px;border-radius:3px;background:var(--panel3)}
.steps i.on{background:var(--gold)}
.stepname{display:flex;justify-content:space-between;color:var(--ink3);font-size:13px;margin-bottom:14px}
.stepname b{color:var(--ink);font-size:21px;font-weight:700;display:block;margin-top:2px}
.card-pad{background:var(--panel);border:1px solid var(--line);border-radius:var(--r-lg);padding:20px}
.pgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
@media (min-width:560px){.pgrid{grid-template-columns:repeat(4,1fr)}}
.pslot{position:relative;aspect-ratio:1/1;border-radius:12px;overflow:hidden;background:var(--panel2)}
.pslot img{width:100%;height:100%;object-fit:cover}
.pslot .x{position:absolute;top:6px;right:6px;width:28px;height:28px;border-radius:50%;background:rgba(10,18,32,.6);color:#fff;display:grid;place-items:center}
.pslot .x svg{width:16px;height:16px}
.pslot .cvtag{position:absolute;left:6px;bottom:6px;font-size:11px;font-weight:700;background:var(--gold);color:#13294B;padding:2px 7px;border-radius:999px}
.padd{aspect-ratio:1/1;border-radius:12px;border:1.5px dashed var(--ink3);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;color:var(--ink2);font-size:13px;font-weight:600}
.padd:hover{border-color:var(--gold);color:var(--ink)}
.topics{display:grid;grid-template-columns:repeat(2,1fr);gap:10px}
@media (min-width:560px){.topics{grid-template-columns:repeat(3,1fr)}}
.topic{display:flex;align-items:center;gap:10px;padding:12px;border-radius:14px;border:1px solid var(--line);background:var(--panel);text-align:left;font-weight:600;font-size:14px;line-height:1.3}
.topic .ic{width:38px;height:38px;border-radius:12px;display:grid;place-items:center;background:var(--panel2);flex:none}
.topic.on{border-color:var(--primary);background:var(--primary-soft)}
.topic.on .ic{background:var(--primary);color:var(--on-primary)}
.pfoot{position:sticky;bottom:0;display:flex;gap:10px;justify-content:space-between;padding:14px 0 calc(14px + env(safe-area-inset-bottom,0px));background:linear-gradient(to top,var(--bg) 70%,transparent);margin-top:12px}
.pfoot .btn.pri{min-width:150px}
.review{display:grid;grid-template-columns:140px 1fr;gap:16px;align-items:start}
.review .ph{border-radius:12px}
.review dl{margin:0;display:grid;grid-template-columns:auto 1fr;gap:6px 14px;font-size:14px}
.review dt{color:var(--ink3)} .review dd{margin:0;overflow-wrap:anywhere}

/* ---------- inbox ---------- */
.inbox{display:grid;grid-template-columns:360px minmax(0,1fr);gap:0;border:1px solid var(--line);border-radius:var(--r-lg);overflow:hidden;background:var(--panel);height:calc(100vh - 150px);height:calc(100dvh - 150px);min-height:480px;margin-top:18px}
.ilist{border-right:1px solid var(--line);display:flex;flex-direction:column;min-height:0}
.ilist .hd{padding:16px 16px 10px}
.ilist h1{margin:0 0 10px;font-size:21px}
.segtabs{display:flex;background:var(--panel2);border-radius:12px;padding:3px}
.segtabs button{flex:1;height:36px;border-radius:10px;font-weight:600;font-size:14px;color:var(--ink2)}
.segtabs button.on{background:var(--panel);color:var(--ink);box-shadow:0 1px 3px rgba(0,0,0,.12)}
.threads{overflow:auto;flex:1}
.th{display:flex;gap:12px;align-items:center;padding:12px 16px;border-top:1px solid var(--line)}
.th:hover{background:var(--panel2)}
.th.on{background:var(--primary-soft)}
.th .tph{width:52px;height:52px;border-radius:12px;overflow:hidden;flex:none;background:var(--panel2)}
.th .tph img,.th .tph svg.cv{width:100%;height:100%;object-fit:cover}
.th .tb{min-width:0;flex:1}
.th .tt{display:flex;justify-content:space-between;gap:8px}
.th .tt b{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:14.5px}
.th .tt small{color:var(--ink3);font-size:12px;flex:none}
.th .ts{font-size:13px;color:var(--ink3);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.th .tl{font-size:13.5px;color:var(--ink2);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.th.unread .tl{color:var(--ink);font-weight:600}
.th .ud{width:9px;height:9px;border-radius:50%;background:var(--gold);flex:none}
.chat{display:flex;flex-direction:column;min-height:0;min-width:0}
.chat .ch{display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid var(--line)}
.chat .ch .tph{width:44px;height:44px;border-radius:10px;overflow:hidden;flex:none}
.chat .ch .tph svg.cv,.chat .ch .tph img{width:100%;height:100%;object-fit:cover}
.chat .ch b{display:block;font-size:14.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.chat .ch small{color:var(--ink3);display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tph{display:block}
.chat .ch .mid{min-width:0;flex:1}
.msgs{flex:1;overflow:auto;padding:18px 16px;display:flex;flex-direction:column;gap:6px}
.bub{max-width:min(78%,560px);padding:7px 10px 6px 12px;border-radius:18px;background:var(--panel2);align-self:flex-start;white-space:pre-wrap;overflow-wrap:anywhere;font-size:14.5px}
.bub.me{align-self:flex-end;background:var(--primary);color:var(--on-primary);border-bottom-right-radius:6px}
.bub:not(.me){border-bottom-left-radius:6px}
.btime{font-size:11.5px;color:var(--ink3);align-self:center;margin:10px 0 4px}
.sysnote{align-self:center;font-size:12.5px;color:var(--ink3);background:var(--panel2);padding:6px 12px;border-radius:999px;text-align:center;margin-bottom:8px}
.quick{display:flex;gap:8px;overflow-x:auto;padding:0 16px 10px;scrollbar-width:none}
.quick::-webkit-scrollbar{display:none}
.compose{display:flex;gap:8px;padding:10px 12px;border-top:1px solid var(--line);align-items:flex-end}
.compose textarea{flex:1;min-height:44px;max-height:120px;border-radius:22px;border:1px solid var(--line);background:var(--bg);padding:11px 16px;resize:none;outline:none;line-height:1.4}
.compose textarea:focus{border-color:var(--gold)}
.sendb{width:44px;height:44px;border-radius:50%;background:var(--primary);color:var(--on-primary);display:grid;place-items:center;flex:none}
.sendb[disabled]{opacity:.4}
.chat-empty{flex:1;display:grid;place-items:center;color:var(--ink3);text-align:center;padding:20px}

/* ---------- me ---------- */
.me-grid{display:grid;grid-template-columns:minmax(0,1fr) 340px;gap:28px;align-items:start;margin-top:18px}
.me-grid>*{min-width:0}
.setrow{flex-wrap:wrap}
.setrow .btn{max-width:100%;overflow:hidden;text-overflow:ellipsis}
.mine{display:flex;flex-direction:column;gap:10px}
.mrow{display:flex;gap:12px;align-items:center;padding:10px;border:1px solid var(--line);background:var(--panel);border-radius:var(--r-md)}
.mrow .tph{width:64px;height:64px;border-radius:10px;overflow:hidden;flex:none}
.mrow .tph img,.mrow .tph svg.cv{width:100%;height:100%;object-fit:cover}
.mrow .mb{flex:1;min-width:0}
.mrow .mb b{display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.mrow .mb small{color:var(--ink3)}
.mrow .ma{display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end}
.setlist{background:var(--panel);border:1px solid var(--line);border-radius:var(--r-lg);padding:6px 18px}
.setrow{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 0;border-top:1px solid var(--line)}
.setrow:first-child{border-top:0}
.setrow>span{font-weight:600}
.about{text-align:center;padding:22px 18px}
.about img{width:170px;margin:0 auto 10px}
.about p{color:var(--ink2);margin:0;font-size:14px}
.status{display:flex;align-items:center;gap:8px;font-size:13px;color:var(--ink3);margin-top:12px;justify-content:center}
.status i{width:8px;height:8px;border-radius:50%;background:var(--ok)}
.status i.off{background:var(--ink3)}

/* ---------- toast ---------- */
.toast{position:fixed;left:50%;bottom:calc(92px + env(safe-area-inset-bottom,0px));transform:translateX(-50%);background:#0E1C33;color:#fff;padding:11px 18px;border-radius:999px;font-size:14px;font-weight:500;z-index:120;box-shadow:var(--shadow);max-width:calc(100vw - 32px);text-align:center;animation:rise .2s ease}
.cc[data-theme="dark"] .toast{background:#ECF0F6;color:#0E1C33}
@media (prefers-color-scheme:dark){.cc:not([data-theme="light"]) .toast{background:#ECF0F6;color:#0E1C33}}

/* ---------- mobile ---------- */
.tabbar{display:none}
@media (max-width:859px){
  .dnav,.top .search.d,.tools .btn-post{display:none}
  .mobile-avatar{display:grid}
  .top-in{padding:8px 14px 6px}
  .brand img{height:30px}
  .wm b{font-size:18px}
  .tools{margin-left:auto}
  .m-search{display:block;padding:0 14px 10px;max-width:1240px;margin:0 auto}
  main{padding:0 14px calc(96px + env(safe-area-inset-bottom,0px))}
  .locbar{padding-top:12px}
  .count{flex-basis:100%;margin-left:2px;order:3}
  .tabs{margin:0 -14px;padding-left:14px;padding-right:14px}
  .subs{margin:0 -14px;padding-left:14px;padding-right:14px}
  .tabbar{display:flex;position:fixed;left:0;right:0;bottom:0;z-index:50;background:color-mix(in srgb,var(--panel) 94%,transparent);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);border-top:1px solid var(--line);padding:6px 6px env(safe-area-inset-bottom,0px)}
  .tabbar a{flex:1;display:flex;flex-direction:column;align-items:center;gap:2px;padding:6px 0 8px;font-size:11px;font-weight:600;color:var(--ink3);position:relative}
  .tabbar a.on{color:var(--ink)}
  .tabbar a.on svg{color:var(--gold-ink)}
  .tabbar .postfab{width:50px;height:50px;border-radius:17px;background:var(--gold);color:#13294B;display:grid;place-items:center;margin-top:-20px;box-shadow:0 6px 18px rgba(201,162,77,.45)}
  .tabbar .postfab svg{width:26px;height:26px;stroke-width:2.3}
  .tabbar .dot{top:2px;right:calc(50% - 22px)}
  .cc[data-r="item"] .top,.cc[data-r="chat"] .top,.cc[data-r="post"] .top,
  .cc[data-r="item"] .tabbar,.cc[data-r="chat"] .tabbar,.cc[data-r="post"] .tabbar{display:none}
  .cc[data-r="chat"] main,.cc[data-r="post"] main{padding-bottom:0}
  .cc[data-r="item"] main{padding-bottom:calc(88px + env(safe-area-inset-bottom,0px))}
  .mbar{display:flex;align-items:center;gap:8px;position:sticky;top:env(safe-area-inset-top,0px);z-index:40;background:color-mix(in srgb,var(--bg) 90%,transparent);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);margin:0 -14px;padding:8px 10px;border-bottom:1px solid var(--line)}
  .mbar .mt{flex:1;min-width:0;font-weight:700;font-size:16px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .crumbs{display:none}
  .detail{display:flex;flex-direction:column;gap:16px}
  .galcol{display:contents}
  .galwrap{order:1}
  .info{order:2}
  .box.desc{order:3;margin-top:0}
  .gal{margin:0 -14px;border-radius:0}
  .thumbs{padding-bottom:2px}
  .thumbs button{width:54px;height:54px}
  .gal .nav{display:none}
  .info{position:static}
  .info h1{font-size:21px}
  .info .bigprice{font-size:24px}
  .info .acts.desk{display:none}
  .actbar{position:fixed;left:0;right:0;bottom:0;display:flex;gap:8px;margin:0;padding:10px 14px calc(10px + env(safe-area-inset-bottom,0px));background:color-mix(in srgb,var(--panel) 96%,transparent);border-top:1px solid var(--line);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);z-index:30}
  .actbar .btn.pri{flex:1}
  .inbox{grid-template-columns:1fr;height:auto;min-height:0;border:0;border-radius:0;background:none;margin:0 -14px}
  .inbox .ilist{border-right:0}
  .cc[data-r="chat"] .ilist{display:none}
  .cc[data-r="inbox"] .chat{display:none}
  .cc[data-r="chat"] .inbox{position:fixed;inset:0;top:env(safe-area-inset-top,0px);bottom:env(safe-area-inset-bottom,0px);margin:0;background:var(--bg);z-index:60}
  .cc[data-r="chat"] .chat{height:100%}
  .ilist .hd{padding:14px 14px 10px}
  .me-grid{grid-template-columns:1fr}
  .mrow{flex-wrap:wrap}
  .mrow .ma{flex-basis:100%}
  .review{grid-template-columns:96px 1fr}
  .scrim{align-items:flex-end;padding:0}
  .sheet{max-width:none;border-radius:var(--r-lg) var(--r-lg) 0 0;padding-bottom:calc(22px + env(safe-area-inset-bottom,0px))}
  .pfoot{margin:12px -14px 0;padding-left:14px;padding-right:14px}
}
@media (min-width:860px){.actbar{display:none}.m-search{display:none}.chat .ch [data-act="back"]{display:none}}

/* ---------- install ---------- */
.install-wrap{max-width:1240px;margin:0 auto;padding:12px 16px 0}
.install-notice{display:flex;align-items:center;gap:12px;padding:10px 10px 10px 14px;border-radius:var(--r-md);background:var(--gold-soft);border:1px solid color-mix(in srgb,var(--gold) 45%,transparent)}
.install-notice img{height:30px;width:auto;flex:none}
.install-notice .in-txt{flex:1;min-width:0;display:flex;flex-direction:column;line-height:1.35}
.install-notice strong{font-size:14.5px}
.install-notice .in-txt span{font-size:13px;color:var(--ink2)}
.btn-install{display:inline-flex;align-items:center;gap:6px;height:38px;padding:0 14px;border-radius:999px;background:var(--primary);color:var(--on-primary);font-weight:600;font-size:13.5px;white-space:nowrap;flex:none}
.btn-install svg{width:18px;height:18px}
.notice-close{width:34px;height:34px;border-radius:50%;display:grid;place-items:center;color:var(--ink2);flex:none}
.notice-close:hover{background:color-mix(in srgb,var(--ink) 8%,transparent)}
.in-hero{display:flex;align-items:center;gap:14px;padding:14px;border-radius:var(--r-md);background:var(--panel2);margin-bottom:16px}
.in-hero img{height:42px;width:auto}
.in-hero b{display:block}
.in-hero span{font-size:13.5px;color:var(--ink2)}
.isteps{list-style:none;margin:0 0 16px;padding:0;display:flex;flex-direction:column;gap:12px}
.isteps li{display:flex;gap:12px;align-items:flex-start;line-height:1.5}
.isteps .n{width:28px;height:28px;border-radius:50%;background:var(--primary);color:var(--on-primary);display:grid;place-items:center;font-weight:700;font-size:13.5px;flex:none;margin-top:-1px}
@media (max-width:859px){
  .install-wrap{padding:10px 14px 0}
  .install-notice .in-txt span{display:none}
  .btn-install{height:36px;padding:0 12px}
}
@media (max-width:380px){.install-notice img{display:none}}
/* ---------- gallery, trust, profile, reviews ---------- */
.galcol{display:flex;flex-direction:column;min-width:0}
.gal{background:var(--panel2);outline:1px solid var(--line);outline-offset:-1px}
.gal:focus-visible{outline:2.5px solid var(--gold)}
.gal .counter{position:absolute;right:12px;bottom:12px;background:rgba(10,18,32,.62);color:#fff;font-size:12px;font-weight:600;padding:3px 10px;border-radius:999px;backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.galtags{position:absolute;left:12px;top:12px;display:flex;gap:6px;flex-wrap:wrap;max-width:60%}
.gtag{height:26px;padding:0 11px;border-radius:8px;background:rgba(14,28,51,.78);color:#fff;font-size:11.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;display:flex;align-items:center;backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.gtag.sold{background:var(--danger)}
.galtools{position:absolute;right:12px;top:12px;display:flex;gap:8px}
.galtools .rnd{width:40px;height:40px;border-radius:50%;display:grid;place-items:center;background:rgba(14,28,51,.5);color:#fff;backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px)}
.galtools .rnd:hover{background:rgba(14,28,51,.72)}
.galtools .rnd.on{background:var(--gold);color:#13294B}
.galtools .rnd.on svg{fill:currentColor}
.thumbs{padding-top:2px;scrollbar-width:none}
.thumbs::-webkit-scrollbar{display:none}
.thumbs button{background:var(--panel2)}
.box.desc h3{font-size:12.5px;letter-spacing:.09em;text-transform:uppercase;color:var(--ink3);border-bottom:1px solid var(--line);padding-bottom:10px;margin-bottom:12px}
.box.desc p{font-size:15px;line-height:1.7}
.stars{display:inline-flex;gap:2px;flex:none}
.stars svg{width:15px;height:15px;fill:none;stroke:var(--gold);stroke-width:1.6;stroke-linejoin:round}
.stars svg.on{fill:var(--gold)}
.starpick{display:flex;gap:6px}
.starpick button{width:46px;height:46px;border-radius:12px;display:grid;place-items:center;border:1px solid var(--line);background:var(--panel)}
.starpick svg{width:26px;height:26px;fill:none;stroke:var(--gold);stroke-width:1.5;stroke-linejoin:round}
.starpick button.on svg{fill:var(--gold)}
.av{border-radius:50%;display:inline-grid;place-items:center;flex:none;background:var(--gold-soft);color:var(--gold-ink);font-weight:700;overflow:hidden}
.av.ph img,.av.logo img{width:100%;height:100%;object-fit:cover}
.av.logo{background:var(--panel2);padding:14%}
.av.logo img{object-fit:contain}
.av.hdr{box-shadow:inset 0 0 0 1px var(--line)}
.trust{display:flex;gap:12px;align-items:flex-start;flex-wrap:wrap}
.trust .tinfo{flex:1;min-width:150px}
.trust .tinfo b{display:block;font-size:15.5px}
.trow{display:flex;align-items:center;gap:8px;margin:3px 0 6px;flex-wrap:wrap}
.trow small{color:var(--ink2);font-size:13px}
.trow small.muted,.tmeta .muted{color:var(--ink3)}
.tmeta{display:flex;flex-direction:column;gap:4px;font-size:13px;color:var(--ink2)}
.tmeta span{display:inline-flex;align-items:center;gap:6px}
.tmeta svg{width:15px;height:15px;color:var(--ink3)}
.tmeta .ok,.tmeta .ok svg{color:var(--ok)}
.trust .tacts{display:flex;flex-direction:column;gap:8px;align-items:stretch}
.trustbox h3{margin-bottom:12px}
.chattrust{display:flex;align-items:center;gap:8px;flex:none}
.ministars{display:inline-flex;align-items:center;gap:6px;padding:4px 8px;border-radius:999px}
.ministars:hover{background:var(--panel2)}
.ministars small{color:var(--ink2);font-size:13px;font-weight:600}
.profcard{padding:14px 0 16px;border-bottom:1px solid var(--line)}
.phead{display:flex;gap:12px;align-items:flex-start}
.phead .pinfo{flex:1;min-width:0}
.phead .pinfo b{display:block;font-size:16px}
.pedit{margin-top:14px;padding-top:14px;border-top:1px solid var(--line)}
.pedit .field:last-of-type{margin-bottom:8px}
.rsum{display:flex;align-items:center;gap:10px;margin-bottom:14px}
.rsum b{font-size:26px;letter-spacing:-.5px}
.rsum small{color:var(--ink3)}
.rlist{display:flex;flex-direction:column;gap:14px}
.ritem{border-top:1px solid var(--line);padding-top:12px}
.ritem:first-child{border-top:0;padding-top:0}
.rtop{display:flex;align-items:center;gap:10px}
.rtop div{flex:1;min-width:0}
.rtop b{display:block;font-size:14.5px}
.rtop small{color:var(--ink3);font-size:12.5px}
.ritem p{margin:8px 0 0;color:var(--ink2);white-space:pre-wrap;overflow-wrap:anywhere}
.btn.block{width:100%}
@media (max-width:859px){
  .detail{display:flex;flex-direction:column}
  .galcol{display:contents}
  .galwrap{order:1}
  .info{order:2}
  .box.desc{order:3;margin-top:0}
  .trust .tacts{flex-direction:row;width:100%}
  .trust .tacts .btn{flex:1}
  .chattrust .btn.sm{padding:0 10px}
  .chattrust .ministars{display:none}
}

/* ---------- chat: status ticks, unread, typing, connection ---------- */
.bub.grp{margin-top:-3px}
.bub .btxt{white-space:pre-wrap}
.bmeta{float:right;display:inline-flex;align-items:center;gap:3px;margin:6px 0 -2px 10px;font-size:10.5px;line-height:1;opacity:.72;white-space:nowrap;position:relative;top:3px}
.bub.me .bmeta{opacity:.85}
.tick{display:inline-flex;align-items:center}
.tick svg{width:17px;height:12px;fill:none;stroke:currentColor;stroke-width:1.9;stroke-linecap:round;stroke-linejoin:round}
.tick.sending svg,.tick.failed svg{width:12px;height:12px;stroke-width:1.7}
.tick.sent{color:inherit;opacity:.62}
.tick.read{color:#4FB6FF;opacity:1}
.cc[data-theme="dark"] .bub.me .tick.read{color:#0B6BCB}
@media (prefers-color-scheme:dark){.cc:not([data-theme="light"]) .bub.me .tick.read{color:#0B6BCB}}
.th .tick{margin-right:3px;vertical-align:-1px}
.th .tick.sent{color:var(--ink3)}
.th .tick.read{color:#2E8FE0}
.tick.failed{color:var(--danger);opacity:1}
.bub.failed{opacity:.75}
.bfail{align-self:flex-end;display:flex;gap:10px;align-items:center;font-size:12px;color:var(--danger);margin:2px 2px 4px}
.bfail button{font-weight:700;color:var(--danger);text-decoration:underline}
.ucount{min-width:21px;height:21px;padding:0 6px;border-radius:999px;background:var(--gold);color:#13294B;font-size:11.5px;font-weight:700;display:grid;place-items:center;flex:none}
.segdot{display:inline-grid;place-items:center;min-width:18px;height:18px;padding:0 5px;margin-left:6px;border-radius:999px;background:var(--gold);color:#13294B;font-size:11px;font-weight:700;vertical-align:1px}
.typing-t{color:var(--ok);font-style:normal;font-weight:600}
.connbar{font-size:12.5px;text-align:center;padding:6px 12px;background:var(--gold-soft);color:var(--gold-ink);border-bottom:1px solid var(--line)}
.older{align-self:center;font-size:12.5px;font-weight:600;color:var(--ink2);background:var(--panel2);padding:6px 14px;border-radius:999px;margin-bottom:8px}
.compose{padding-bottom:calc(10px + env(safe-area-inset-bottom,0px))}
.compose textarea{max-height:140px}
.spin{width:28px;height:28px;border-radius:50%;border:3px solid var(--line);border-top-color:var(--gold);margin:28px auto;animation:ccspin .8s linear infinite}
@keyframes ccspin{to{transform:rotate(360deg)}}
.card.skel .ph{background:var(--panel2);aspect-ratio:1;border-radius:var(--r-md);animation:ccpulse 1.2s ease-in-out infinite alternate}
.card.skel .cbody i{display:block;height:11px;border-radius:6px;background:var(--panel2);margin-top:8px}
.card.skel .cbody i:nth-child(1){width:40%}.card.skel .cbody i:nth-child(3){width:65%}
@keyframes ccpulse{from{opacity:.55}to{opacity:1}}
/* ---------- sound settings ---------- */
.notify{border-top:1px solid var(--line);padding-top:12px;margin-top:4px}
.notify h3{font-size:15px;margin:0 0 2px}
.notify .setrow{flex-wrap:nowrap}
.notify .setrow>span{min-width:0}
.srow{display:flex;align-items:center;gap:10px;flex:none}
.switch{width:46px;height:28px;border-radius:999px;background:var(--panel3);position:relative;flex:none;transition:background .2s}
.switch i{position:absolute;top:3px;left:3px;width:22px;height:22px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.3);transition:transform .2s}
.switch.on{background:var(--ok)}
.switch.on i{transform:translateX(18px)}
.interests{padding:0 0 12px}
.interests small{display:block;color:var(--ink3);margin-bottom:8px}
.interests .chips{display:flex;flex-wrap:wrap;gap:8px}
/* phones: the chat fills the screen and the keyboard never hides the input */
@media (max-width:859px){
  .bub{max-width:84%}
}
/* desktop: the listing column scrolls normally and the Message/Call/Save row
   stays pinned under the header for the whole listing */
@media (min-width:860px){
  .detail{align-items:stretch}
  .info{position:static}
  .info .acts.desk{position:sticky;top:48px;z-index:6;background:var(--bg);padding:14px 0 10px;margin-top:4px;box-shadow:0 8px 12px -12px rgba(14,28,51,.35)}
}
.th .ts,.th .tl{display:block}
.chat .ch .mid{min-width:72px}
@media (max-width:859px){
  .chattrust>small.muted{display:none}
}
@media (max-width:520px){
  .chattrust .btn.sm{width:36px;padding:0;justify-content:center;font-size:0;gap:0}
  .chattrust .btn.sm svg{width:18px;height:18px}
}
/* photos: always show the whole picture, never crop */
.ph .thbg,.tph .thbg{position:absolute;inset:-12px;background-size:cover;background-position:center;filter:blur(16px) saturate(.9);opacity:.35}
.ph img.thimg{position:relative;}
.tph{position:relative}
.tph img.thimg{position:relative;width:100%;height:100%;object-fit:contain}
.card:hover .ph img.thimg{transform:none}
/* full-screen photo viewer */
.gal .slide img{max-height:none}
.galwrap{width:100%;min-width:0}
.gal .expand{position:absolute;left:12px;bottom:12px;width:34px;height:34px;border-radius:50%;display:grid;place-items:center;background:rgba(10,18,32,.55);color:#fff;pointer-events:none}
.gal .expand svg{width:18px;height:18px}
.pviewer{position:fixed;inset:0;z-index:200;background:#05080E;color:#fff;display:flex;flex-direction:column}
.lbtrack{flex:1;min-height:0;display:flex;overflow-x:auto;overflow-y:hidden;scroll-snap-type:x mandatory;scrollbar-width:none;overscroll-behavior:contain}
.lbtrack::-webkit-scrollbar{display:none}
.lbslide{flex:none;width:100%;height:100%;scroll-snap-align:center;display:flex;align-items:center;justify-content:center;
  padding:calc(56px + env(safe-area-inset-top,0px)) 0 calc(12px + env(safe-area-inset-bottom,0px));touch-action:pan-x pinch-zoom}
.lbslide img{max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;display:block;user-select:none;-webkit-user-drag:none}
.lbtop{position:absolute;top:0;left:0;right:0;display:flex;justify-content:space-between;align-items:center;padding:calc(10px + env(safe-area-inset-top,0px)) 12px 10px 18px;background:linear-gradient(rgba(0,0,0,.55),transparent)}
.lbcount{font-size:14px;font-weight:600}
.lbclose{width:44px;height:44px;border-radius:50%;display:grid;place-items:center;background:rgba(255,255,255,.14);color:#fff}
.lbnav{position:absolute;top:50%;transform:translateY(-50%);width:48px;height:48px;border-radius:50%;display:grid;place-items:center;background:rgba(255,255,255,.16);color:#fff}
.lbnav[disabled]{opacity:.25}
.lbnav.p{left:14px}.lbnav.n{right:14px}
.lbthumbs{display:flex;gap:8px;justify-content:center;padding:8px 12px calc(12px + env(safe-area-inset-bottom,0px));overflow-x:auto}
.lbthumbs button{width:52px;height:52px;border-radius:8px;overflow:hidden;flex:none;opacity:.5;border:2px solid transparent}
.lbthumbs button.on{opacity:1;border-color:var(--gold)}
.lbthumbs img{width:100%;height:100%;object-fit:cover}
@media (max-width:859px){.lbnav{display:none}}

/* ================= motion: fast, purposeful, interruptible ================= */
.cc{--ease-out:cubic-bezier(.23,1,.32,1);--ease-drawer:cubic-bezier(.32,.72,0,1)}
.btn,.chip,.tab .ic,.ibtn,.rnd,.heart,.seg button,.topic,.zone,.tabbar a,.postfab,.lbthumbs button,.thumbs button{
  transition:transform .16s var(--ease-out),background-color .16s,border-color .16s,color .16s,box-shadow .2s var(--ease-out),opacity .16s}
.btn:active:not([disabled]),.chip:active,.ibtn:active,.rnd:active,.heart:active,.seg button:active,.topic:active,.zone:active,.postfab:active{transform:scale(.97)}
.tab:active .ic{transform:scale(.94)}
.sheet{animation:rise .34s var(--ease-drawer)}
.scrim{animation:fade .2s var(--ease-out)}
.toast{animation:toastin .26s var(--ease-out)}
@keyframes toastin{from{opacity:0;transform:translate(-50%,10px) scale(.96)}}
.pviewer{animation:fade .18s var(--ease-out)}
.lbslide img{animation:lbin .26s var(--ease-out)}
@keyframes lbin{from{opacity:.4;transform:scale(.96)}}
@media (hover:hover){
  .card .ph{transition:box-shadow .25s var(--ease-out),transform .25s var(--ease-out)}
  .card:hover .ph{box-shadow:0 10px 28px -14px rgba(14,28,51,.45);transform:translateY(-2px)}
}
@media (prefers-reduced-motion:reduce){.cc *,.cc *::before,.cc *::after{animation-duration:.01ms!important;transition-duration:.01ms!important}}
.cc :focus-visible{outline:2.5px solid var(--gold);outline-offset:2px}

/* ================= marketplace grid: premium, whole photos ================= */
.ph{border-radius:14px;box-shadow:inset 0 0 0 1px rgba(14,28,51,.06)}
.ph .thbg{opacity:.5}
.price{font-variant-numeric:tabular-nums;font-size:17px;letter-spacing:-.2px}
.cmeta{font-size:12.5px}
@media (max-width:380px){.grid{gap:14px 10px}}
@media (min-width:1500px){.grid{grid-template-columns:repeat(6,minmax(0,1fr))}}
.tab.carrytab .ic{background:linear-gradient(135deg,#0F3B5F,#2A7AB0);color:#fff;border-color:transparent}
.m-saved{display:none}
@media (max-width:859px){.m-saved{display:grid}}

/* ================= luggage space ================= */
.carry{padding-bottom:40px}
.chero{display:flex;gap:18px;align-items:flex-end;justify-content:space-between;flex-wrap:wrap;margin:14px 0 10px;padding:22px 22px 20px;border-radius:var(--r-lg);
  color:#fff;background:radial-gradient(120% 140% at 100% 0%,#2A7AB0 0%,#0F3B5F 55%,#0B2440 100%);position:relative;overflow:hidden}
.chero::after{content:"";position:absolute;right:-30px;top:-30px;width:180px;height:180px;border-radius:50%;border:1px solid rgba(255,255,255,.14);box-shadow:0 0 0 28px rgba(255,255,255,.04)}
.chero h1{margin:0 0 6px;font-size:clamp(21px,4.6vw,28px);letter-spacing:-.3px}
.chero p{margin:0;max-width:560px;opacity:.86;line-height:1.55;font-size:14.5px}
.chero .btn{position:relative;z-index:1}
.chow{list-style:none;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;padding:0;margin:0 0 16px}
.chow li{display:flex;gap:10px;align-items:flex-start;font-size:13px;color:var(--ink2);background:var(--panel);border:1px solid var(--line);border-radius:14px;padding:10px 12px;line-height:1.45}
.chow li span{flex:none;width:22px;height:22px;border-radius:50%;display:grid;place-items:center;background:var(--gold-soft);color:var(--gold-ink);font-weight:700;font-size:12px}
@media (max-width:640px){.chow{grid-template-columns:1fr;gap:6px}.chow li{padding:8px 12px}}
.ctools{display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-bottom:12px}
.dirseg{flex:none}
.csearch{flex:1;min-width:220px;display:flex;align-items:center;gap:8px;height:44px;padding:0 14px;border-radius:999px;background:var(--panel);border:1px solid var(--line)}
.csearch input{flex:1;min-width:0;border:0;background:transparent;font:inherit;color:var(--ink);outline:none}
.csearch svg{width:18px;height:18px;color:var(--ink3)}
.zones{display:flex;gap:8px;overflow-x:auto;scrollbar-width:none;padding-bottom:4px}
.zones::-webkit-scrollbar{display:none}
.zone{flex:none;display:inline-flex;align-items:center;gap:8px;height:38px;padding:0 14px;border-radius:999px;border:1px solid var(--line);background:var(--panel);font-weight:600;font-size:13.5px;color:var(--ink2)}
.zone i{font-style:normal;min-width:22px;height:22px;padding:0 6px;border-radius:999px;display:grid;place-items:center;background:var(--panel2);font-size:12px;color:var(--ink2)}
.zone.on{background:var(--primary);color:var(--on-primary);border-color:var(--primary)}
.zone.on i{background:rgba(255,255,255,.2);color:inherit}
.zones.states{margin-top:8px}
.zones.states .chip b{margin-left:4px;opacity:.7}
.ccount{margin:14px 2px 8px;font-size:13px;color:var(--ink3)}
.sched{border:1px solid var(--line);border-radius:var(--r-lg);background:var(--panel);overflow:hidden}
.thead,.trow{display:grid;grid-template-columns:78px minmax(0,1.7fr) 104px 104px 110px minmax(0,1.2fr) 200px;gap:12px;align-items:center;padding:0 16px}
.thead{height:40px;font-size:12px;font-weight:600;color:var(--ink3);text-transform:uppercase;letter-spacing:.3px;border-bottom:1px solid var(--line);background:var(--panel2)}
.tmonth{padding:10px 16px 6px;font-weight:700;font-size:13.5px;color:var(--gold-ink);background:color-mix(in srgb,var(--gold-soft) 55%,var(--panel));border-bottom:1px solid var(--line);text-transform:capitalize}
.trow{min-height:78px;padding-top:10px;padding-bottom:10px;border-bottom:1px solid var(--line);cursor:pointer;transition:background-color .15s}
.trow:last-child{border-bottom:0}
@media (hover:hover){.trow:hover{background:var(--panel2)}}
.trow.full{opacity:.6}
.tdate{width:58px;height:58px;border-radius:14px;background:var(--panel2);display:flex;flex-direction:column;align-items:center;justify-content:center;line-height:1.05}
.tdate b{font-size:21px;font-variant-numeric:tabular-nums}
.tdate span{font-size:11.5px;font-weight:600;color:var(--ink2)}
.tdate small{font-size:10.5px;color:var(--ink3)}
.troutec{min-width:0}
.tline{display:flex;align-items:center;gap:6px;flex-wrap:wrap;font-size:15px;margin:3px 0 2px}
.tline svg{width:16px;height:16px;color:var(--ink3)}
.dirtag{display:inline-block;font-size:11px;font-weight:700;padding:2px 8px;border-radius:999px}
.dirtag.us_th{background:#E3F0FA;color:#0F3B5F}
.dirtag.th_us{background:#FBEFD8;color:#7A5212}
.when{font-size:12px;color:var(--ink3)}
.when.soon{color:var(--danger);font-weight:600}
.tcell{font-size:14px;font-variant-numeric:tabular-nums}
.tcell small{display:none}
.tcell .kg{color:var(--ok)}
.tuser{display:flex;gap:10px;align-items:center;min-width:0}
.tuser span:last-child{min-width:0}
.tuser b{display:block;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tuser small.ok{display:inline-flex;gap:3px;align-items:center;color:var(--ok);font-size:11.5px}
.tuser small.ok svg{width:12px;height:12px}
.tav{flex:none;width:34px;height:34px;border-radius:50%;display:grid;place-items:center;background:var(--gold-soft);color:var(--gold-ink);font-weight:700}
.tacts{display:flex;gap:6px;justify-content:flex-end;flex-wrap:wrap}
.btn.sm.icon{width:36px;padding:0}
@media (max-width:1100px){
  .thead{display:none}
  .trow{grid-template-columns:62px repeat(3,minmax(0,1fr));grid-template-areas:"d r r r" "d c0 c1 c2" "u u u u" "a a a a";row-gap:8px;column-gap:10px;padding:14px}
  .tdate{grid-area:d;align-self:start}
  .troutec{grid-area:r}
  .tcell{display:flex;flex-direction:column;font-size:13.5px}
  .tcell small{display:block;font-size:11px;color:var(--ink3)}
  .trow>.tcell:nth-child(3){grid-area:c0}
  .trow>.tcell:nth-child(4){grid-area:c1}
  .trow>.tcell:nth-child(5){grid-area:c2}
  .tuser{grid-area:u;padding-top:8px;border-top:1px dashed var(--line)}
  .tacts{grid-area:a;justify-content:stretch}
  .tacts .btn.pri{flex:1}
}
.carrysafe{margin-top:18px}
.trip{border:1px solid var(--line);border-radius:var(--r-lg);background:var(--panel);padding:16px;margin:12px 0}
.troute{display:flex;align-items:center;gap:10px;flex-wrap:wrap;font-size:17px}
.tarrow{width:34px;height:34px;border-radius:50%;display:grid;place-items:center;background:#E3F0FA;color:#0F3B5F}
.tarrow svg{width:18px;height:18px}
.tgrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin-top:14px}
.tgrid div{background:var(--panel2);border-radius:12px;padding:10px 12px;display:flex;flex-direction:column;gap:2px}
.tgrid small{font-size:11.5px;color:var(--ink3)}
.tgrid strong{font-size:14.5px}
.accepts{margin-top:12px}
.accepts small{font-size:11.5px;color:var(--ink3)}
.accepts div{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}
.chip.sm{height:28px;font-size:12.5px;padding:0 10px}
.pagetitle{display:none;margin:18px 0 12px;font-size:24px}
@media (min-width:860px){.pagetitle{display:block}}
.cform .two{display:grid;grid-template-columns:1fr 1fr;gap:12px}
@media (max-width:520px){.cform .two{grid-template-columns:1fr}}
.cform .field em{font-style:normal;color:var(--ink3);font-weight:400;font-size:12px}
.zipok{display:block;margin-top:5px;font-size:12px;color:var(--ink3)}
.zipok.ok{color:var(--ok);font-weight:600}
.suffix{position:relative;display:flex;align-items:center}
.suffix .inp{width:100%;padding-right:62px}
.suffix i{position:absolute;right:14px;font-style:normal;color:var(--ink3);font-size:13px}
.suffix i.pre{left:14px;right:auto}
.suffix i.pre+.inp{padding-left:28px}
.seg.sm button{height:36px}
.subh{margin:18px 0 10px;font-size:15px}
.btn.wide{width:100%;margin-top:14px;height:52px;font-size:16px}

/* ================= typing indicator: three dots, no word ================= */
.typingbub{display:inline-flex;align-items:center;gap:4px;align-self:flex-start;padding:12px 14px;min-height:0}
.typingbub i,.tdots i{display:block;width:7px;height:7px;border-radius:50%;background:var(--ink3);animation:ccdot 1.2s ease-in-out infinite}
.typingbub i:nth-child(2),.tdots i:nth-child(2){animation-delay:.15s}
.typingbub i:nth-child(3),.tdots i:nth-child(3){animation-delay:.3s}
.tdots{display:inline-flex;gap:3px;align-items:center;height:14px;vertical-align:middle}
.tdots i{width:5px;height:5px;background:var(--ok)}
@keyframes ccdot{0%,60%,100%{transform:translateY(0);opacity:.45}30%{transform:translateY(-4px);opacity:1}}
@media (prefers-reduced-motion:reduce){.typingbub i,.tdots i{animation:none;opacity:.7}}
/* phones: chat is its own full-height screen; header and input never scroll away */
@media (max-width:859px){
  .cc[data-r="chat"] .inbox{overscroll-behavior:contain}
  .cc[data-r="chat"] .chat{height:100%;display:flex;flex-direction:column}
  .cc[data-r="chat"] .chat .ch{flex:none}
  .cc[data-r="chat"] .msgs{flex:1;min-height:0;overscroll-behavior:contain}
  .cc[data-r="chat"] .compose{flex:none}
}
/* ================= trip form: from → to, secure contact ================= */
.route2 .field>span{display:flex;align-items:center;gap:8px}
.route2 .ft{font-size:11px;font-weight:700;letter-spacing:.3px;text-transform:uppercase;padding:2px 8px;border-radius:999px;background:#E3F0FA;color:#0F3B5F}
.route2 select.inp{appearance:auto;cursor:pointer}
.securebox{display:flex;gap:12px;align-items:flex-start;padding:14px;border-radius:14px;background:color-mix(in srgb,var(--ok) 9%,var(--panel));border:1px solid color-mix(in srgb,var(--ok) 25%,var(--line));margin:4px 0 6px}
.securebox b{display:block;font-size:14.5px;margin-bottom:3px}
.securebox p{margin:0;font-size:13.5px;line-height:1.55;color:var(--ink2)}
.sic{flex:none;width:34px;height:34px;border-radius:50%;display:grid;place-items:center;background:var(--ok);color:#fff}
.sic svg{width:18px;height:18px}
/* ================= legal pages ================= */
.legalpage{padding-bottom:24px}
.lhero{margin:12px 0 0;border-radius:var(--r-lg);background:radial-gradient(120% 160% at 100% 0%,#1E4E7C 0%,#0E2340 55%,#0A1A30 100%);color:#fff;overflow:hidden;position:relative}
.lhero::after{content:"";position:absolute;right:-60px;top:-60px;width:260px;height:260px;border-radius:50%;background:radial-gradient(circle,rgba(201,162,77,.28),transparent 70%)}
.lhero-in{position:relative;z-index:1;padding:clamp(22px,4vw,44px) clamp(20px,4vw,48px) clamp(26px,4vw,40px);max-width:820px}
.lswitch{display:inline-flex;max-width:100%;overflow-x:auto;scrollbar-width:none;gap:4px;padding:4px;border-radius:999px;background:rgba(255,255,255,.1);margin-bottom:22px}
.lswitch a{white-space:nowrap;padding:7px 14px;border-radius:999px;font-size:13px;font-weight:600;color:rgba(255,255,255,.75);text-decoration:none;transition:background-color .16s,color .16s}
.lswitch a.on{background:#fff;color:#0E2340}
.lhero h1{margin:0;font-size:clamp(28px,5.5vw,44px);letter-spacing:-.8px;line-height:1.1}
.ldate{margin:10px 0 0;font-size:13px;color:rgba(255,255,255,.62)}
.lintro{margin:18px 0 0;font-size:clamp(15px,2vw,17px);line-height:1.65;color:rgba(255,255,255,.88);max-width:680px}
.lglance{margin:28px 0 8px}
.lglance>h2,.ltoc small{display:block;font-size:12px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;color:var(--ink3);margin:0 0 12px}
.lgrid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px}
.lcard{padding:18px;border-radius:16px;background:var(--panel);border:1px solid var(--line);box-shadow:0 1px 2px rgba(14,28,51,.04)}
.lcard b{display:block;margin:12px 0 6px;font-size:15px;letter-spacing:-.1px}
.lcard p{margin:0;font-size:13.5px;line-height:1.55;color:var(--ink2)}
.lic{width:38px;height:38px;border-radius:12px;display:grid;place-items:center;background:color-mix(in srgb,var(--gold) 16%,var(--panel));color:var(--gold-ink);flex:none}
.lic svg{width:19px;height:19px}
.lbody{display:grid;grid-template-columns:240px minmax(0,1fr);gap:48px;margin-top:36px;align-items:start}
.ltoc{position:sticky;top:88px}
.ltoc ol{list-style:none;margin:0;padding:0;border-left:1px solid var(--line)}
.ltoc button{display:block;width:100%;text-align:left;padding:7px 0 7px 14px;margin-left:-1px;border-left:2px solid transparent;font-size:13.5px;color:var(--ink3);line-height:1.4;transition:color .16s,border-color .16s}
.ltoc button:hover{color:var(--ink)}
.ltoc button.on{color:var(--ink);border-left-color:var(--gold);font-weight:600}
.ldoc{max-width:720px}
.ldoc .lead{font-size:16px;line-height:1.7;margin:0 0 8px}
.lsec{scroll-margin-top:90px;padding:26px 0;border-bottom:1px solid var(--line)}
.lsec:first-of-type{padding-top:0}
.lsec h2{display:flex;align-items:baseline;gap:14px;margin:0 0 12px;font-size:20px;letter-spacing:-.3px}
.lsec h2 span{font-size:12.5px;font-weight:700;color:var(--gold-ink);font-variant-numeric:tabular-nums;letter-spacing:.5px}
.lsec p,.lsec li{font-size:15px;line-height:1.75;color:var(--ink2)}
.lsec p{margin:0}
.lsec ul{margin:0;padding:0;list-style:none;display:grid;gap:10px}
.lsec li{position:relative;padding-left:20px}
.lsec li::before{content:"";position:absolute;left:2px;top:.72em;width:6px;height:6px;border-radius:50%;background:var(--gold)}
.ldoc a{color:var(--primary);text-decoration:underline;text-underline-offset:3px}
.lcontact{display:flex;gap:16px;align-items:center;flex-wrap:wrap;margin:32px 0 16px;padding:20px;border-radius:16px;background:var(--panel);border:1px solid var(--line)}
.lcontact>div{flex:1;min-width:200px}
.lcontact b{display:block;font-size:16px}
.lcontact p{margin:4px 0 0;font-size:14px;color:var(--ink2)}
.lcontact .btn{text-decoration:none}
.lnext{display:flex;align-items:center;gap:10px;padding:18px 20px;border-radius:16px;border:1px solid var(--line);text-decoration:none!important;color:var(--ink)!important;transition:border-color .16s,transform .16s var(--ease-out)}
.lnext small{color:var(--ink3);font-size:12.5px}
.lnext b{flex:1;font-size:16px}
.lnext svg{width:18px;height:18px;color:var(--ink3)}
@media (hover:hover){.lnext:hover{border-color:var(--gold);transform:translateY(-1px)}}
@media (max-width:1000px){.lgrid{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (max-width:859px){
  .lhero{margin-top:0}
  .lbody{grid-template-columns:1fr;gap:18px;margin-top:24px}
  .ltoc{position:static;padding:16px;border-radius:16px;background:var(--panel);border:1px solid var(--line)}
  .ltoc ol{border-left:0;display:grid;gap:2px;counter-reset:t}
  .ltoc button{padding:6px 0;border-left:0}
  .ltoc button.on{border-left:0}
}
@media (max-width:520px){.lswitch a{padding:7px 11px;font-size:12.5px}.lgrid{grid-template-columns:1fr}.lcard{display:grid;grid-template-columns:auto 1fr;column-gap:14px}.lcard .lic{grid-row:span 2}.lcard b{margin:0 0 4px}}
.legallinks{display:flex;gap:14px;justify-content:center;flex-wrap:wrap;margin:6px 0 10px;font-size:13px}
.legallinks a{color:var(--ink2);text-decoration:underline;text-underline-offset:2px}
.agree{font-size:12.5px;color:var(--ink3);text-align:center;margin:12px 0 0;line-height:1.5}
.agree a{color:var(--ink2);text-decoration:underline}

/* ================= site footer: follows light / dark / system theme ================= */
.sitefoot{
  --sf-bg:var(--panel); --sf-head:var(--ink); --sf-text:var(--ink2); --sf-muted:var(--ink2);
  --sf-line:var(--line); --sf-chip:var(--panel2); --sf-accent:var(--gold-ink);
  margin-top:56px;background:var(--sf-bg);color:var(--sf-text);border-top:1px solid var(--sf-line);
  position:relative;padding-bottom:calc(84px + env(safe-area-inset-bottom,0px))
}
/* hairline of gold across the top edge */
.sitefoot::before{content:"";position:absolute;left:0;right:0;top:-1px;height:1px;background:linear-gradient(90deg,transparent,color-mix(in srgb,var(--gold) 55%,transparent),transparent)}
.sf-in{max-width:1200px;margin:0 auto;padding:52px 20px 36px;display:grid;grid-template-columns:minmax(0,1.6fr) repeat(3,minmax(0,1fr));gap:40px}
.sf-logo{display:inline-flex;align-items:center;gap:10px;text-decoration:none;color:var(--sf-head)}
.sf-logo img{width:44px;height:auto}
.sf-logo b{display:block;font-size:19px;letter-spacing:-.2px;color:var(--sf-head)}
.sf-logo small{display:block;font-size:12px;color:var(--sf-accent);margin-top:-2px}
.sf-brand p{margin:16px 0 18px;font-size:14px;line-height:1.65;max-width:360px;color:var(--sf-text)}
.sf-trust{list-style:none;margin:0;padding:0;display:flex;flex-wrap:wrap;gap:8px}
.sf-trust li{display:inline-flex;align-items:center;gap:6px;padding:6px 11px;border-radius:999px;background:var(--sf-chip);border:1px solid var(--sf-line);font-size:12.5px;font-weight:500;color:var(--sf-head)}
.sf-trust svg{width:14px;height:14px;color:var(--sf-accent)}
.sf-col h3{margin:4px 0 14px;font-size:12px;font-weight:700;letter-spacing:.9px;text-transform:uppercase;color:var(--sf-head)}
.sf-col ul{list-style:none;margin:0;padding:0;display:grid;gap:10px}
.sf-col a{color:var(--sf-text);text-decoration:none;font-size:14px;transition:color .16s var(--ease-out)}
.sf-col a:hover,.sf-col a:focus-visible{color:var(--sf-head)}
@media (hover:hover){.sf-col a:hover{text-decoration:underline;text-underline-offset:3px;text-decoration-color:var(--sf-accent)}}
.sf-bar{max-width:1200px;margin:0 auto;padding:18px 20px 22px;border-top:1px solid var(--sf-line);display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;font-size:12.5px;color:var(--sf-muted)}
/* dark (chosen or system): sit a step deeper than the page for a rich, grounded finish */
.cc[data-theme="dark"] .sitefoot{--sf-bg:color-mix(in srgb,var(--bg) 55%,#000);--sf-muted:var(--ink3);--sf-chip:rgba(255,255,255,.05)}
@media (prefers-color-scheme:dark){.cc:not([data-theme="light"]) .sitefoot{--sf-bg:color-mix(in srgb,var(--bg) 55%,#000);--sf-muted:var(--ink3);--sf-chip:rgba(255,255,255,.05)}}
@media (min-width:860px){.sitefoot{padding-bottom:0}}
@media (max-width:859px){
  .sf-in{grid-template-columns:repeat(2,minmax(0,1fr));gap:28px 20px;padding:36px 20px 26px}
  .sf-brand{grid-column:1/-1}
  .sf-col:last-child{grid-column:1/-1}
  .sf-bar{flex-direction:column;gap:4px}
}

/* ================= community rooms ================= */
.roomspage,.roompage{padding-bottom:30px}
.rhero{display:flex;gap:18px;align-items:flex-end;justify-content:space-between;flex-wrap:wrap;margin:14px 0 16px;padding:24px;border-radius:var(--r-lg);color:#fff;
  background:radial-gradient(120% 150% at 100% 0%,#8C6A22 0%,#3A2E14 45%,#13294B 100%);position:relative;overflow:hidden}
.rhero h1{margin:0 0 6px;font-size:clamp(22px,4.6vw,30px);letter-spacing:-.4px}
.rhero p{margin:0;max-width:540px;opacity:.88;line-height:1.55}
.rstate{width:auto;min-width:150px;height:44px;border-radius:999px}
.rsec{font-size:13px;font-weight:700;letter-spacing:.6px;text-transform:uppercase;color:var(--ink3);margin:22px 2px 10px}
.rmine{display:flex;gap:10px;overflow-x:auto;scrollbar-width:none;padding-bottom:4px}
.rmine::-webkit-scrollbar{display:none}
.rmini{flex:none;display:flex;gap:10px;align-items:center;padding:10px 14px 10px 10px;border-radius:16px;background:var(--panel);border:1px solid var(--line);text-decoration:none;color:var(--ink);max-width:260px}
.rmini b{display:block;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:180px}
.rmini small{font-size:12px;color:var(--ink3)}
.ravatar{flex:none;border-radius:16px;display:grid;place-items:center;color:#fff;font-weight:700;box-shadow:inset 0 0 0 1px rgba(255,255,255,.12)}
.rgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:14px}
.rcard{display:flex;flex-direction:column;gap:10px;padding:16px;border-radius:18px;background:var(--panel);border:1px solid var(--line);text-decoration:none;color:var(--ink);transition:transform .2s var(--ease-out),box-shadow .2s var(--ease-out)}
@media (hover:hover){.rcard:hover{transform:translateY(-2px);box-shadow:0 12px 28px -18px rgba(14,28,51,.45)}}
.rcard-top{display:flex;gap:12px;align-items:center}
.rcard-h{min-width:0}
.rcard-h b{display:block;font-size:16px;line-height:1.3}
.rcard-h small{display:inline-flex;align-items:center;gap:4px;font-size:12.5px;color:var(--ink3)}
.rcard-h small svg{width:13px;height:13px}
.rcard p{margin:0;font-size:13.5px;color:var(--ink2);line-height:1.5;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.rcard-meta{display:flex;flex-wrap:wrap;gap:6px 12px;align-items:center;font-size:12.5px;color:var(--ink3);margin-top:auto}
.rtime{margin-left:auto}
.rbadge{display:inline-flex;align-items:center;height:22px;padding:0 9px;border-radius:999px;background:var(--gold-soft);color:var(--gold-ink);font-size:11.5px;font-weight:700}
.rbadge.ok{background:color-mix(in srgb,var(--ok) 14%,var(--panel));color:var(--ok)}
.rhead{margin:14px 0 12px;padding:18px;border-radius:var(--r-lg);background:var(--panel);border:1px solid var(--line)}
.rhead-top{display:flex;gap:14px;align-items:center;flex-wrap:wrap}
.rhead-h{flex:1;min-width:200px}
.rhead-h h1{margin:0;font-size:clamp(20px,4vw,26px);letter-spacing:-.3px}
.rhead-h p{margin:4px 0 0;font-size:13px;color:var(--ink3);line-height:1.5}
.rhead-h p svg{width:13px;height:13px;vertical-align:-2px;margin-right:3px}
.rhead-acts{display:flex;gap:8px;align-items:center}
.rdesc{margin:12px 0 0;color:var(--ink2);line-height:1.6}
.rnote{margin-top:12px;padding:10px 12px;border-radius:12px;background:var(--gold-soft);color:var(--gold-ink);font-size:13.5px}
.rnote.bad{background:color-mix(in srgb,var(--danger) 12%,var(--panel));color:var(--danger)}
.rrules{margin-top:12px;border-top:1px dashed var(--line);padding-top:10px}
.rrules .linkish{display:inline-flex;gap:6px;align-items:center;font-weight:600;color:var(--ink2)}
.rrules .linkish svg{width:15px;height:15px}
.rrules p{white-space:pre-wrap;margin:8px 0 0;font-size:14px;line-height:1.7;color:var(--ink2)}
.rcompose{padding:14px;border-radius:18px;background:var(--panel);border:1px solid var(--line);margin-bottom:14px}
.rcompose textarea{width:100%;resize:vertical;min-height:56px;border:0;background:var(--panel2)}
.rcompose-bar{display:flex;justify-content:space-between;gap:10px;margin-top:10px}
.rcompose-bar label.btn{cursor:pointer}
.rcompose-bar label.disabled{opacity:.5;pointer-events:none}
.rphotos-edit{display:flex;gap:8px;flex-wrap:wrap;margin-top:10px}
.rphotos-edit span{position:relative;width:72px;height:72px;border-radius:12px;overflow:hidden;background:var(--panel2)}
.rphotos-edit img{width:100%;height:100%;object-fit:cover}
.rphotos-edit button{position:absolute;top:3px;right:3px;width:22px;height:22px;border-radius:50%;background:rgba(10,18,32,.65);color:#fff;display:grid;place-items:center}
.rphotos-edit button svg{width:12px;height:12px}
.rjoin{display:flex;gap:10px;align-items:center;justify-content:center;flex-wrap:wrap;padding:14px;border-radius:16px;background:var(--panel2);color:var(--ink2);font-size:14px;margin-bottom:14px;text-align:center}
.rfeed{display:grid;gap:12px}
.rpost{padding:16px;border-radius:18px;background:var(--panel);border:1px solid var(--line)}
.rpost.dim{opacity:.7}
.rpost header{display:flex;gap:10px;align-items:center}
.rpost-h{flex:1;min-width:0}
.rpost-h b{display:flex;gap:6px;align-items:center;font-size:14.5px}
.rpost-h small{font-size:12px;color:var(--ink3)}
.rrole{font-size:10.5px;font-weight:700;padding:1px 7px;border-radius:999px;background:var(--gold-soft);color:var(--gold-ink)}
.rpin{color:var(--gold-ink);font-weight:600}
.rstatus{margin-top:10px;font-size:12.5px;font-weight:600;color:var(--danger)}
.rtext{white-space:pre-wrap;margin:10px 0 0;line-height:1.65;font-size:15px;overflow-wrap:anywhere}
.rtext.sm{font-size:13.5px;margin-top:4px}
.rpics{display:grid;gap:4px;margin-top:12px;border-radius:14px;overflow:hidden}
.rpics.n1{grid-template-columns:1fr}.rpics.n2{grid-template-columns:1fr 1fr}.rpics.n3{grid-template-columns:2fr 1fr}.rpics.n4{grid-template-columns:1fr 1fr}
.rpics.n3 button:first-child{grid-row:span 2}
.rpics button{padding:0;background:var(--panel2);aspect-ratio:4/3;overflow:hidden;cursor:zoom-in}
.rpics.n1 button{aspect-ratio:auto;max-height:480px}
.rpics.n3 button:first-child{aspect-ratio:auto}
.rpics img{width:100%;height:100%;object-fit:cover;display:block}
.rpics.n1 img{object-fit:contain;max-height:480px;background:var(--panel2)}
.rpost footer{margin-top:10px;padding-top:8px;border-top:1px solid var(--line)}
.rpost footer .linkish{display:inline-flex;gap:6px;align-items:center;font-size:13.5px;color:var(--ink2);font-weight:600}
.rpost footer svg{width:16px;height:16px}
.rmenu{position:relative}
.rmenu-pop{position:absolute;right:0;top:40px;z-index:20;min-width:170px;padding:6px;border-radius:14px;background:var(--panel);border:1px solid var(--line);box-shadow:var(--shadow);animation:fade .15s var(--ease-out)}
.rmenu-pop button{display:block;width:100%;text-align:left;padding:9px 12px;border-radius:9px;font-size:14px;color:var(--ink)}
.rmenu-pop button:hover{background:var(--panel2)}
.rmenu-pop button.danger{color:var(--danger)}
.rcomments{margin-top:10px;display:grid;gap:10px}
.rcomment{display:flex;gap:10px;align-items:flex-start}
.rcomment>div{flex:1;min-width:0;padding:8px 12px;border-radius:14px;background:var(--panel2)}
.rcomment b{font-size:13.5px}
.rcomment small{font-size:11.5px;color:var(--ink3)}
.rcomment p{margin:2px 0 0;font-size:14px;white-space:pre-wrap;overflow-wrap:anywhere}
.tav.sm{width:28px;height:28px;font-size:12px}
.ibtn.sm{width:30px;height:30px}
.rcomment-in{display:flex;gap:8px}
.rcomment-in .inp{flex:1;height:40px}
.spin.sm{width:20px;height:20px;margin:10px auto}
.sheet.polite{text-align:center;max-width:420px}
.sheet.polite .pic{font-size:36px;display:block;margin-bottom:6px}
.sheet.polite h3{margin:0 0 6px}
.sheet.polite p{margin:0 0 12px;color:var(--ink2);line-height:1.6}
.pwords{display:flex;gap:6px;justify-content:center;flex-wrap:wrap;margin-bottom:16px}
.pwords span{padding:3px 10px;border-radius:999px;background:color-mix(in srgb,var(--danger) 12%,var(--panel));color:var(--danger);font-size:13px;font-weight:600}
.pacts{display:grid;gap:8px}
.rvis{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.rvopt{text-align:left;padding:12px 14px;border-radius:14px;border:1.5px solid var(--line);background:var(--panel)}
.rvopt b{display:block;font-size:14.5px}
.rvopt small{font-size:12.5px;color:var(--ink3)}
.rvopt.on{border-color:var(--primary);box-shadow:0 0 0 3px var(--primary-soft)}
.rrules-t{font-weight:600;color:var(--ink)}
.rmanage-h{display:flex;gap:12px;align-items:center;margin:16px 0 14px}
.rmanage-h h1{margin:0;font-size:20px}
.rmembers{display:grid;gap:8px;margin-top:14px}
.rmrow{display:flex;gap:12px;align-items:center;padding:12px;border-radius:14px;background:var(--panel);border:1px solid var(--line);margin-bottom:8px}
.rmrow.top{align-items:flex-start}
.rmrow-h{flex:1;min-width:0}
.rmrow-h b{display:block;font-size:14.5px}
.rmrow-h small{font-size:12.5px;color:var(--ink3)}
.rmrow-a{display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end}
.inp.sm{height:34px;width:auto;padding:0 10px;font-size:13px}
.radmin{display:flex;gap:10px;align-items:center;flex-wrap:wrap;padding:12px;border-radius:14px;background:var(--panel2);margin-bottom:14px}
@media (max-width:520px){.rvis{grid-template-columns:1fr}.rhead-acts{width:100%}.rhead-acts .btn{flex:1}}

/* ================= profile photo ================= */
.avedit{position:relative;padding:0;border-radius:50%;flex:none;cursor:pointer}
.avedit .avcam{position:absolute;right:-2px;bottom:-2px;width:28px;height:28px;border-radius:50%;display:grid;place-items:center;background:var(--primary);color:var(--on-primary);border:2.5px solid var(--panel);transition:transform .16s var(--ease-out)}
.avedit .avcam svg{width:14px;height:14px}
@media (hover:hover){.avedit:hover .avcam{transform:scale(1.08)}}
.sheet.aved{max-width:380px;text-align:center}
.avstage{position:relative;margin:4px auto 0;border-radius:18px;overflow:hidden;background:var(--panel2);touch-action:none;cursor:grab;user-select:none}
.avstage:active{cursor:grabbing}
.avstage img{position:absolute;left:0;top:0;max-width:none;pointer-events:none}
.avmask{position:absolute;inset:0;border-radius:18px;background:radial-gradient(circle at center,transparent 139px,rgba(8,16,30,.55) 140px);pointer-events:none}
.avstage::after{content:"";position:absolute;inset:0;margin:auto;width:280px;height:280px;border-radius:50%;box-shadow:inset 0 0 0 2px rgba(255,255,255,.9);pointer-events:none}
.avhint{font-size:13px;color:var(--ink3);margin:12px 0 8px}
.avzoom{display:flex;gap:10px;align-items:center;margin:0 6px 16px}
.avzoom svg{width:16px;height:16px;color:var(--ink3)}
.avzoom input{flex:1;accent-color:var(--gold)}
.avacts{display:grid;gap:8px}
.av.chav{margin-right:2px}
.avedit .av{border-radius:50%}
.profcard .phead .pinfo{flex:1;min-width:0}
.profcard .phead .pinfo .trow small,.profcard .phead .pinfo .trow .muted{white-space:nowrap}
.tph{position:relative}
.av.thav{position:absolute;right:2px;bottom:2px;border:2px solid var(--panel);font-size:10px!important}
.tuser .av,.rpost header .av,.rcomment .av,.rmrow .av{flex:none}

/* ================= ApnaCircle wordmark ================= */
.wm b i,.sf-logo b i{font-style:normal}
.wm b .wa,.sf-logo b .wa{color:var(--ink)}
.wm b .wc,.sf-logo b .wc{color:#E8670A}
.cc[data-theme="dark"] .wm b .wc,.cc[data-theme="dark"] .sf-logo b .wc{color:#FF9A3D}
@media (prefers-color-scheme:dark){.cc:not([data-theme="light"]) .wm b .wc,.cc:not([data-theme="light"]) .sf-logo b .wc{color:#FF9A3D}}
.wm span,.sf-logo small{letter-spacing:.2px}

/* desktop header: keep the search usable with 4 nav items */
.m-saved{display:grid}
.search.d{min-width:200px}
@media (max-width:1439px){.account-link .user-short{display:none}.account-link{padding-left:6px;padding-right:6px}}
@media (max-width:1180px){.dnav a{font-size:0;gap:0;padding-left:10px;padding-right:10px}.dnav a svg{margin:0}.dnav a.account-link{font-size:inherit}}

/* ================= most-needed hub & details ================= */
.needs{margin:14px 0 6px}
.needs-h{display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;margin:0 2px 10px}
.needs-h h2{margin:0;font-size:clamp(17px,3.6vw,21px);letter-spacing:-.2px}
.needs-h span{font-size:13px;color:var(--ink3)}
.needs-row{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:10px}
.need{position:relative;display:flex;flex-direction:column;gap:6px;min-height:132px;padding:14px;border-radius:18px;color:#fff;text-decoration:none;overflow:hidden;
  background:linear-gradient(145deg,var(--g1),var(--g2));box-shadow:0 10px 24px -18px rgba(11,31,58,.7);transition:transform .2s var(--ease-out),box-shadow .2s var(--ease-out)}
.need::after{content:"";position:absolute;right:-26px;bottom:-26px;width:90px;height:90px;border-radius:50%;background:rgba(255,255,255,.1)}
@media (hover:hover){.need:hover{transform:translateY(-3px);box-shadow:0 16px 30px -18px rgba(11,31,58,.8)}}
.need b{font-size:15px;line-height:1.25}
.need small{font-size:12px;line-height:1.4;opacity:.88}
.need-ic{width:38px;height:38px;border-radius:12px;display:grid;place-items:center;background:rgba(255,255,255,.18)}
.need-ic svg{width:20px;height:20px}
.need-ic.big{width:54px;height:54px;border-radius:16px;flex:none}
.need-ic.big svg{width:28px;height:28px}
@media (max-width:1200px){.needs-row{grid-template-columns:repeat(4,minmax(0,1fr))}}
@media (max-width:859px){.needs-row{display:flex;overflow-x:auto;scroll-snap-type:x mandatory;scrollbar-width:none;padding-bottom:4px}.needs-row::-webkit-scrollbar{display:none}
  .need{flex:none;width:44%;min-width:160px;scroll-snap-align:start;min-height:124px}}
.needpage{padding-bottom:30px}
.needhero{display:flex;gap:16px;align-items:center;flex-wrap:wrap;margin:14px 0 14px;padding:22px;border-radius:var(--r-lg);color:#fff;background:linear-gradient(135deg,var(--g1),var(--g2))}
.needhero>div{flex:1;min-width:220px}
.needhero h1{margin:0 0 4px;font-size:clamp(21px,4.4vw,28px);letter-spacing:-.3px}
.needhero p{margin:0;opacity:.9;line-height:1.55;max-width:620px}
.needfilter{display:flex;gap:8px;align-items:center;overflow-x:auto;scrollbar-width:none;padding-bottom:4px;margin-bottom:8px}
.needfilter::-webkit-scrollbar{display:none}
.needfilter .chip{flex:none}
.nf-l{flex:none;font-size:12.5px;color:var(--ink3);margin-left:4px}
.chip.comp{display:inline-flex;gap:6px;align-items:center}
.chip.comp svg{width:14px;height:14px}
.fest{margin:6px 0 14px}
.fest-h{display:flex;gap:10px;align-items:baseline;flex-wrap:wrap;margin:0 2px 8px}
.fest-h h3{margin:0;font-size:15px}
.fest-h small{font-size:12.5px;color:var(--ink3)}
.fest-row{display:flex;gap:8px;overflow-x:auto;scrollbar-width:none;padding-bottom:4px}
.fest-row::-webkit-scrollbar{display:none}
.festcard{flex:none;min-width:128px;text-align:left;display:flex;flex-direction:column;gap:1px;padding:10px 12px;border-radius:14px;background:var(--panel);border:1px solid var(--line)}
.festcard b{font-size:14px}
.festcard .native{font-size:13px;color:var(--gold-ink)}
.festcard small{font-size:11.5px;color:var(--ink3)}
.festcard .mo{color:var(--ink2);font-weight:600}
.festcard.on{border-color:#C2417F;box-shadow:0 0 0 3px color-mix(in srgb,#C2417F 22%,transparent)}
.mbadges{display:flex;flex-wrap:wrap;gap:4px;margin:4px 0 2px}
.mb{font-size:11px;font-weight:600;padding:2px 7px;border-radius:999px;background:var(--panel2);color:var(--ink2);max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.mb.visa{background:color-mix(in srgb,#6C4FB0 16%,var(--panel));color:#5B3FA0}
.cc[data-theme="dark"] .mb.visa{color:#C9B8F5}
.metapanel{margin:12px 0;padding:14px 16px;border-radius:var(--r-lg);background:var(--panel);border:1px solid var(--line)}
.metapanel dl{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px 16px;margin:0}
.metapanel dt{font-size:11.5px;color:var(--ink3)}
.metapanel dd{margin:2px 0 0;font-size:14.5px;font-weight:600}
@media (max-width:520px){.metapanel dl{grid-template-columns:1fr}}
.warnbox{display:flex;gap:10px;align-items:flex-start;margin-top:12px;padding:12px 14px;border-radius:14px;background:color-mix(in srgb,var(--danger) 9%,var(--panel));color:var(--ink);font-size:13.5px;line-height:1.55;border:1px solid color-mix(in srgb,var(--danger) 25%,var(--line))}
.warnbox svg{flex:none;width:18px;height:18px;color:var(--danger);margin-top:2px}
.warnbox.wide{margin-top:18px}
.warnbox a{color:var(--danger);font-weight:600;white-space:nowrap}
.detailbox{display:grid;gap:4px;padding:14px;margin:6px 0 12px;border-radius:16px;background:var(--panel2)}
.detailbox .two{display:grid;grid-template-columns:1fr 1fr;gap:12px}
@media (max-width:520px){.detailbox .two{grid-template-columns:1fr}}
.langchips .chip{font-size:14px}
.compcheck{display:flex;gap:12px;align-items:flex-start;padding:14px;margin:6px 0 10px;border-radius:14px;border:1.5px solid color-mix(in srgb,#2A7AB0 45%,var(--line));background:color-mix(in srgb,#2A7AB0 8%,var(--panel));cursor:pointer}
.compcheck input{width:20px;height:20px;margin-top:2px;accent-color:#2A7AB0}
.compcheck b{display:block;font-size:14.5px}
.compcheck small{font-size:13px;color:var(--ink2);line-height:1.5}
.compbox{display:flex;gap:10px;align-items:flex-start;margin-top:12px;padding:12px;border-radius:14px;background:color-mix(in srgb,#2A7AB0 10%,var(--panel))}
.compbox svg{flex:none;width:18px;height:18px;color:#2A7AB0;margin-top:2px}
.compbox b{display:block;font-size:14px}
.compbox small{font-size:12.5px;color:var(--ink2)}
.dirtag.comp{display:inline-flex;gap:4px;align-items:center;margin-left:6px;background:#E3F0FA;color:#0F3B5F}
.dirtag.comp svg{width:11px;height:11px}
.scams{display:grid;gap:12px;margin-top:6px}
.scam{padding:16px;border-radius:18px;background:var(--panel);border:1px solid var(--line)}
.scam h3{display:flex;gap:10px;align-items:center;margin:0 0 8px;font-size:16px}
.scam h3 span{flex:none;width:26px;height:26px;border-radius:50%;display:grid;place-items:center;background:color-mix(in srgb,var(--danger) 14%,var(--panel));color:var(--danger);font-size:13px}
.scam p{margin:0;color:var(--ink2);line-height:1.65;font-size:14.5px}
.scam .how{margin-top:10px;padding:10px 12px;border-radius:12px;background:color-mix(in srgb,var(--ok) 9%,var(--panel))}
.scam .how b{font-size:12.5px;color:var(--ok)}
.scam .how p{color:var(--ink);font-size:14px}
.reportbox{margin-top:16px;padding:16px;border-radius:18px;background:var(--panel2)}
.reportbox h3{margin:0 0 8px;font-size:16px}
.reportbox ul{margin:0 0 8px;padding-left:18px;line-height:1.9}
.reportbox a{color:var(--primary);font-weight:600}
.reportbox p{margin:0;font-size:13px;color:var(--ink3)}

/* ================= launch: cities, sections, verification, Founding Members ================= */
.citybar{display:flex;gap:8px;align-items:center;overflow-x:auto;scrollbar-width:none;margin:12px 0 2px;padding-bottom:4px}
.citybar::-webkit-scrollbar{display:none}
.citybar .chip{flex:none}
.cb-l{flex:none;display:inline-flex;gap:4px;align-items:center;font-size:12.5px;font-weight:700;color:var(--ink2)}
.cb-l svg{width:14px;height:14px}
.chip.city{display:inline-flex;gap:6px;align-items:center}
.soon{font-style:normal;font-size:10px;font-weight:700;padding:1px 6px;border-radius:999px;background:var(--gold-soft);color:var(--gold-ink)}
.joinbanner,.verifybanner{display:flex;gap:10px;align-items:center;margin:10px 0;padding:12px 14px;border-radius:14px;text-decoration:none;font-weight:600;font-size:14px}
.joinbanner{background:linear-gradient(90deg,#FFF1E3,#FFF8E6);color:#7A3E00;border:1px solid #F6D3A8}
.verifybanner{background:color-mix(in srgb,#1E7B34 10%,var(--panel));color:var(--ink);border:1px solid color-mix(in srgb,#1E7B34 30%,var(--line))}
.joinbanner span,.verifybanner span{flex:1}
.joinbanner svg,.verifybanner svg{width:18px;height:18px;flex:none}
.cc[data-theme="dark"] .joinbanner{background:color-mix(in srgb,#FF7A00 14%,var(--panel));color:#FFD1A3;border-color:color-mix(in srgb,#FF7A00 30%,var(--line))}
.hsections{margin-top:6px}
.hsec{margin:18px 0 6px}
.hsec-h{display:flex;justify-content:space-between;align-items:baseline;margin:0 2px 8px}
.hsec-h h2{margin:0;font-size:17px;letter-spacing:-.2px}
.hsec-h a,.hsec-h .linkish{font-size:13px;font-weight:600;color:var(--primary);text-decoration:none}
.hsec-h.fresh{margin-top:22px}
.hsec-row{display:flex;gap:12px;overflow-x:auto;scroll-snap-type:x proximity;scrollbar-width:none;padding-bottom:6px}
.hsec-row::-webkit-scrollbar{display:none}
.hsec-row>.card{flex:none;width:clamp(150px,42vw,210px);scroll-snap-align:start}
.hsec-row.groups>.rcard{flex:none;width:clamp(240px,70vw,300px)}
.badge.ex{background:#FFF1E3;color:#9A4A00;border:1px dashed #E8670A}
.exbanner{display:flex;gap:10px;align-items:flex-start;margin:10px 0;padding:12px 14px;border-radius:14px;background:#FFF4E8;color:#7A3E00;border:1px dashed #E8670A;font-size:14px;line-height:1.5}
.exbanner svg{flex:none;width:18px;height:18px;margin-top:1px}
.cc[data-theme="dark"] .exbanner{background:color-mix(in srgb,#FF7A00 12%,var(--panel));color:#FFD1A3}
.sbadges{display:flex;flex-wrap:wrap;gap:6px;margin:8px 0}
.sbadge{display:inline-flex;gap:5px;align-items:center;font-size:12px;font-weight:600;padding:4px 10px;border-radius:999px}
.sbadge svg{width:13px;height:13px}
.sbadge.founding{background:linear-gradient(90deg,#FFE7CC,#FFF3D6);color:#8A4B00}
.sbadge.social{background:color-mix(in srgb,#1E7B34 12%,var(--panel));color:#1E7B34}
.verifypage{padding-bottom:30px}
.vmsg{margin:0 0 12px;padding:12px 14px;border-radius:14px;background:color-mix(in srgb,var(--danger) 10%,var(--panel));font-weight:600;font-size:14px}
.vmsg.ok{background:color-mix(in srgb,var(--ok) 12%,var(--panel));color:var(--ok)}
.vstep{padding:16px;border-radius:18px;background:var(--panel);border:1px solid var(--line);margin-bottom:10px}
.vstep-h{display:flex;gap:10px;align-items:center}
.vstep-h b{flex:1;font-size:15.5px}
.vnum{width:28px;height:28px;border-radius:50%;display:grid;place-items:center;background:var(--panel2);font-weight:700;font-size:13px}
.vnum svg{width:15px;height:15px}
.vstep.done .vnum{background:var(--ok);color:#fff}
.vok{font-size:12px;font-weight:700;color:var(--ok)}
.vbody{margin-top:12px}
.vbody p{margin:0 0 10px;font-size:13.5px;line-height:1.55}
.votp{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.votp .inp{width:170px;letter-spacing:3px;font-variant-numeric:tabular-nums}
.vsocial{display:grid;gap:8px}
.btn.social{justify-content:center;text-decoration:none;font-weight:700}
.btn.social.google{background:#fff;color:#1f1f1f;border:1px solid #dadce0}
.btn.social.facebook{background:#1877F2;color:#fff;border-color:#1877F2}
.btn.social.linkedin{background:#0A66C2;color:#fff;border-color:#0A66C2}
.vlinked{display:flex;gap:8px;align-items:center;margin-bottom:8px;font-size:14px}
.vlinked svg{width:16px;height:16px;color:var(--ok)}
.vlinked b{flex:1}
.joinpage{padding-bottom:30px}
.joinhero{margin:14px 0;padding:clamp(22px,4vw,40px);border-radius:var(--r-lg);color:#fff;background:radial-gradient(120% 140% at 100% 0%,#FF7A00 0%,#B8501A 40%,#0B1F3A 100%)}
.joinhero h1{margin:10px 0 8px;font-size:clamp(24px,5vw,36px);letter-spacing:-.5px}
.joinhero p{margin:0;max-width:640px;line-height:1.6;opacity:.92}
.jbadge{display:inline-flex;gap:6px;align-items:center;padding:5px 12px;border-radius:999px;background:rgba(255,255,255,.18);font-size:12.5px;font-weight:700}
.jbadge svg{width:14px;height:14px}
.jacts{display:flex;gap:10px;flex-wrap:wrap;margin-top:18px}
.btn.wa{background:#1DA851;color:#fff;border-color:#1DA851;text-decoration:none}
.btn.ghost{background:rgba(255,255,255,.14);color:#fff;border-color:rgba(255,255,255,.3)}
.promises{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin:16px 0}
.promise{padding:16px;border-radius:16px;background:var(--panel);border:1px solid var(--line)}
.promise b{display:block;margin:10px 0 4px;font-size:15px}
.promise p{margin:0;font-size:13.5px;color:var(--ink2);line-height:1.55}
@media (max-width:860px){.promises{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (max-width:520px){.promises{grid-template-columns:1fr}}
.jcities h2{font-size:17px;margin:20px 2px 10px}
.jcity-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:10px}
.jcity{position:relative;text-align:left;display:flex;flex-direction:column;gap:4px;padding:14px;border-radius:16px;background:var(--panel);border:1px solid var(--line)}
.jcity b{font-size:15px}
.jcity small{font-size:12px;color:var(--ink3)}
.jstat{font-size:13px;font-weight:600}
.jstat.gold{color:#B8501A}
.jcity .soon{position:absolute;top:10px;right:10px}

/* ================= elder care · invites · founding badge ================= */
.needs-row{grid-template-columns:repeat(8,minmax(0,1fr))}
@media (max-width:1300px){.needs-row{grid-template-columns:repeat(4,minmax(0,1fr))}}
@media (max-width:859px){.needs-row{display:flex}}
.carepage{padding-bottom:30px}
.chow{list-style:none;margin:0 0 12px;padding:0;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}
.chow li{display:flex;gap:8px;align-items:center;padding:10px 12px;border-radius:14px;background:var(--panel);border:1px solid var(--line);font-size:13px}
.chow span{flex:none;width:22px;height:22px;border-radius:50%;display:grid;place-items:center;background:var(--gold-soft);color:var(--gold-ink);font-weight:700;font-size:12px}
@media (max-width:700px){.chow{grid-template-columns:1fr}}
.cmonth h3{font-size:13px;letter-spacing:.6px;text-transform:uppercase;color:var(--ink3);margin:18px 2px 8px}
.carerow{display:grid;grid-template-columns:120px minmax(0,1fr) auto;gap:14px;align-items:center;padding:14px;border-radius:16px;background:var(--panel);border:1px solid var(--line);margin-bottom:8px;border-left:4px solid #B0558F}
.carerow.offer{border-left-color:#1E7B34}
.cdate{display:flex;flex-direction:column;align-items:flex-start;font-size:13px}
.cdate b{font-size:15px}
.cdate span{color:var(--ink3);font-size:12px}
.ckind{display:inline-block;font-size:11px;font-weight:700;padding:2px 8px;border-radius:999px;background:#F7E6F1;color:#7A2A62;margin-bottom:4px}
.ckind.offer{background:#E4F3E8;color:#1E7B34}
.cc[data-theme="dark"] .ckind{background:color-mix(in srgb,#B0558F 22%,var(--panel));color:#F2C6E2}
.cc[data-theme="dark"] .ckind.offer{background:color-mix(in srgb,#1E7B34 22%,var(--panel));color:#A8E6B8}
.ctitle2{display:block;font-weight:700;font-size:15px;color:var(--ink);text-decoration:none;margin-bottom:4px}
.cwho{display:flex;flex-direction:column;align-items:flex-end;gap:6px;font-size:13px}
.cwho span{font-weight:600}
@media (max-width:700px){.carerow{grid-template-columns:1fr}.cwho{flex-direction:row;align-items:center;justify-content:space-between}.cdate{flex-direction:row;gap:6px;align-items:baseline}}
.nbadge{display:inline-grid;place-items:center;min-width:18px;height:18px;padding:0 5px;margin-left:6px;border-radius:999px;background:#E8670A;color:#fff;font-size:11px;font-weight:800}
.reqs{font-style:normal;font-weight:700;color:#E8670A}
.invbanner{display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-top:12px;padding:12px 14px;border-radius:14px;background:color-mix(in srgb,#E8670A 10%,var(--panel));border:1px solid color-mix(in srgb,#E8670A 30%,var(--line));font-size:14px}
.invbanner span{flex:1;min-width:200px}
.invbanner svg{width:18px;height:18px;color:#E8670A}
.sheet.invite{max-width:520px}
.invnote{display:flex;gap:6px;align-items:center;font-size:13px;color:var(--ink2);margin:-4px 0 10px}
.invnote svg{width:14px;height:14px}
.shares{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px;margin:6px 0 14px}
.share{display:flex;flex-direction:column;align-items:center;gap:6px;padding:12px 6px;border-radius:14px;text-decoration:none;font-size:12.5px;font-weight:700;color:#fff;border:0}
.share svg{width:26px;height:26px}
.share.whatsapp{background:#1DA851}.share.facebook{background:#1877F2}.share.telegram{background:#229ED9}
.share.more{background:var(--panel2);color:var(--ink)}
.invlang{display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:8px;font-size:12.5px;color:var(--ink3)}
.invmsg{white-space:pre-wrap;font-family:inherit;font-size:13px;line-height:1.55;max-height:220px;overflow:auto;margin:0 0 12px;padding:12px;border-radius:12px;background:var(--panel2);color:var(--ink)}
.invacts .btn{width:100%;justify-content:center}
.fbadge{display:inline-flex;align-items:center;gap:10px;padding:6px 14px 6px 6px;border-radius:999px;background:linear-gradient(135deg,#FFE3A3,#F5B94A 55%,#E8670A);color:#3D1F00;box-shadow:0 6px 18px -8px rgba(232,103,10,.7),inset 0 0 0 1px rgba(255,255,255,.5)}
.fb-medal{width:30px;height:30px;border-radius:50%;display:grid;place-items:center;background:#0B1F3A;color:#FFD27A}
.fb-medal svg{width:16px;height:16px}
.fb-text{display:flex;flex-direction:column;line-height:1.1}
.fb-text b{font-size:14px;letter-spacing:.2px}
.fb-text i{font-style:normal;font-size:10.5px;font-weight:800;letter-spacing:1.2px;text-transform:uppercase}
.fbadge.big{padding:8px 18px 8px 8px}
.fbadge.big .fb-medal{width:40px;height:40px}
.fbadge.big .fb-text b{font-size:17px}
.fbadge.closed{filter:grayscale(1);opacity:.8}
.jstrong{font-size:clamp(16px,2.4vw,19px);font-weight:700;line-height:1.5;margin:0 0 8px !important;opacity:1 !important}

/* ================= Founding Member ✦ after the name ================= */
.tname{display:inline-flex;align-items:center;gap:6px}
.fmark{position:relative;display:inline-flex}
.fmark-btn{width:22px;height:22px;display:grid;place-items:center;border-radius:50%;font-size:15px;line-height:1;color:#E8A317;background:transparent;cursor:help;padding:0}
.fmark-btn:focus-visible{outline:2px solid #E8A317;outline-offset:2px}
.fmark-tip{position:absolute;left:-12px;top:calc(100% + 10px);transform:translateY(-4px);z-index:30;min-width:220px;max-width:min(280px,80vw);padding:10px 14px;border-radius:12px;background:var(--panel);border:1px solid var(--line);box-shadow:0 12px 30px -12px rgba(11,31,58,.35);font-weight:400;text-align:left;white-space:normal;opacity:0;visibility:hidden;pointer-events:none;transition:opacity .15s,transform .15s,visibility .15s}
.fmark-tip::before{content:"";position:absolute;top:-6px;left:18px;width:10px;height:10px;background:var(--panel);border-left:1px solid var(--line);border-top:1px solid var(--line);transform:rotate(45deg)}
.fmark-tip b{display:block;font-size:14px;color:var(--ink);margin-bottom:2px}
.fmark-tip span{display:block;font-size:13px;color:var(--ink3);line-height:1.45}
.fmark.open .fmark-tip,.fmark-btn:focus-visible + .fmark-tip{opacity:1;visibility:visible;transform:translateY(0)}
@media (hover:hover){.fmark:hover .fmark-tip{opacity:1;visibility:visible;transform:translateY(0)}}
`;
